/**
 * Sales Performance Dashboard - Fresh Build
 * Clean, sheet-connected dashboard with no hardcoded data
 */

import React, { useState, useMemo, useCallback, useEffect, useRef, lazy, Suspense } from 'react';
import { useDashboardData, useGoalsData, useBookSalesforceData, useActivityAggregates, useCanonicalCallsRaw, useApolloEmailData, useBookData, useApolloContactEmails, usePipelineApprovals, useHistoricalFunded, useDatamerchStatus, useCompanyIntelIndex, usePaymentAlerts, useAccountStatusOverrides, useDealLevelStatusOverrides } from './WebAPIConnection';
import {
  ComposedChart, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ScatterChart, Scatter, ZAxis, Cell, LabelList,
  PieChart, Pie, AreaChart, Area
} from 'recharts';
import MTDReviewCard from './components/MTDReviewCard';
import FunnelStoryCard from './components/FunnelStoryCard';
import ActivityTrendsCard from './components/ActivityTrendsCard';
import DiagnosisCard from './components/DiagnosisCard';
import OverviewTabRedesign from './components/OverviewTabRedesign';
import GoalTrackingTab from './components/GoalTrackingTab';
import LoadingFallback from './components/LoadingFallback';
import './responsive.css'; // Mobile responsive styles (Feb 2, 2026)

// ============================================
// CODE SPLITTING: Lazy-loaded tab components
// These load on-demand when user navigates to them
// ============================================
const LastWeekTab = lazy(() => import('./components/LastWeekTab'));
const ActivityTab = lazy(() => import('./components/ActivityTab'));
const FunnelTab = lazy(() => import('./components/FunnelTab'));
const ReviewsTab = lazy(() => import('./components/ReviewsTab'));
const BookTab = lazy(() => import('./components/BookTab'));

// Helper function to filter out invalid funded/revenue records
// Excludes: $0 deals, declined deals, closed lost deals
function filterValidDeals(records) {
  return records.filter(r => {
    const amount = parseFloat(r.Amount) || parseFloat(r.Revenue) || 0;

    // Exclude ONLY if Amount is 0
    // Note: We import only Funded stage from Salesforce, so Declined/Closed Lost dates
    // are intentionally kept (deals can be re-opened after being closed)
    return amount > 0;
  });
}

// Activity Points calculation with date-based formula (Dec 16, 2025)
// Before cutover: Calls + (Apps × 10)
// After cutover:  Calls + SMS + (Apps × 5)
const ACTIVITY_FORMULA_CUTOVER_DATE = '2025-12-16';

function calculateActivityPoints(calls, sms, apps, date) {
  // For dates before cutover, use old formula (no SMS, Apps × 10)
  if (date && date < ACTIVITY_FORMULA_CUTOVER_DATE) {
    return calls + (apps * 10);
  }
  // For dates on/after cutover, use new formula
  return calls + sms + (apps * 5);
}

// Colors for Revenue Trends Comparison chart (Feb 2, 2026)
const REP_COMPARISON_COLORS = {
  'Team Avg': '#94a3b8',  // Gray (dashed)
  'Patrick': '#f59e0b',   // Amber
  'Marcin': '#3b82f6',    // Blue
  'Matthew': '#8b5cf6',   // Purple
  'Damian': '#06b6d4',    // Cyan
  'Antonio': '#14b8a6',   // Teal
  'Alex': '#f43f5e',      // Rose
};

// Year Selector for Yearly Review Tab (legacy - kept for backwards compatibility)
const YearSelector = ({ selectedYear, setSelectedYear }) => {
  const years = [2025, 2026];
  return (
    <div style={{
      display: 'flex',
      gap: '8px',
      marginBottom: '20px'
    }}>
      {years.map(year => (
        <button
          key={year}
          onClick={() => setSelectedYear(year)}
          style={{
            padding: '10px 24px',
            borderRadius: '8px',
            border: selectedYear === year ? '2px solid #3b82f6' : '1px solid #e2e8f0',
            backgroundColor: selectedYear === year ? '#eff6ff' : 'white',
            color: selectedYear === year ? '#3b82f6' : '#64748b',
            fontWeight: selectedYear === year ? 600 : 500,
            fontSize: '15px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          {year}
        </button>
      ))}
    </div>
  );
};

// Phase 3 (Feb 2026): Goal Progress Bar Component for Reviews Tab
const GoalProgressBar = ({ current, goal, formatValue = (v) => v.toLocaleString() }) => {
  if (!goal || goal === 0) {
    return null; // Don't show progress bar if no goal set
  }

  const progressPercent = (current / goal) * 100;

  // Color coding: <70% red, 70-89% yellow, ≥90% green
  let color = '#EF4444'; // Red
  if (progressPercent >= 90) {
    color = '#10B981'; // Green
  } else if (progressPercent >= 70) {
    color = '#F59E0B'; // Yellow/Orange
  }

  return (
    <div style={{
      marginTop: '16px',
      paddingTop: '12px',
      borderTop: '1px solid rgba(255,255,255,0.2)'
    }}>
      {/* Progress Bar */}
      <div style={{
        width: '100%',
        height: '10px',
        backgroundColor: 'rgba(255,255,255,0.3)',
        borderRadius: '5px',
        overflow: 'hidden',
        marginBottom: '8px'
      }}>
        <div style={{
          width: `${Math.min(progressPercent, 100)}%`,
          height: '100%',
          backgroundColor: color,
          transition: 'width 0.3s ease',
          borderRadius: '5px'
        }} />
      </div>

      {/* Goal Text */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '13px',
        color: 'rgba(255,255,255,0.95)',
        fontWeight: '500'
      }}>
        <span style={{ color, fontWeight: '600' }}>{progressPercent.toFixed(1)}% to goal</span>
        <span>Goal: <strong>{formatValue(goal)}</strong></span>
      </div>
    </div>
  );
};

// Helper function to get goals for a period
const getGoalsForPeriod = (goalsData, periodType, year, month, quarter, week, rep = null) => {
  if (!goalsData || !Array.isArray(goalsData) || goalsData.length === 0) {
    return { revenueGoal: 0, fundedGoal: 0, appsGoal: 0, callsGoal: 0, smsGoal: 0 };
  }

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];

  let relevantMonths = [];

  if (periodType === 'week' || periodType === 'month') {
    relevantMonths = [monthNames[month]];
  } else if (periodType === 'quarter') {
    const startMonth = (quarter - 1) * 3;
    relevantMonths = [monthNames[startMonth], monthNames[startMonth + 1], monthNames[startMonth + 2]];
  } else {
    relevantMonths = monthNames; // Year: all 12 months
  }

  // Filter goals by year, months, and optionally rep
  // FIXED: Parse "January 2026" → month: "January", year: 2026
  const filteredGoals = goalsData.filter(g => {
    const monthParts = g.Month ? g.Month.split(' ') : [];
    const goalYear = monthParts.length > 1 ? parseInt(monthParts[1]) : null;
    const goalMonthName = monthParts.length > 0 ? monthParts[0] : '';

    const yearMatch = goalYear === year;
    const monthMatch = relevantMonths.includes(goalMonthName);
    const repMatch = rep ? g.Rep === rep : true;

    return yearMatch && monthMatch && repMatch;
  });

  // Sum goals
  let revenueGoal = filteredGoals.reduce((sum, g) => sum + (parseFloat(g.Revenue_Goal) || 0), 0);
  let fundedGoal = filteredGoals.reduce((sum, g) => sum + (parseFloat(g.Funded_Units_Goal) || 0), 0);
  let appsGoal = filteredGoals.reduce((sum, g) => sum + (parseFloat(g.Apps_Goal) || 0), 0);
  let callsGoal = filteredGoals.reduce((sum, g) => sum + (parseFloat(g.Activity_Calls_Goal) || 0), 0);
  let smsGoal = 0; // No Activity_SMS_Goal column exists in sheet

  // For week view, divide by 4
  if (periodType === 'week') {
    revenueGoal = revenueGoal / 4;
    fundedGoal = Math.ceil(fundedGoal / 4);
    appsGoal = Math.ceil(appsGoal / 4);
    callsGoal = Math.ceil(callsGoal / 4);
    // smsGoal stays 0 (no column)
  }

  return { revenueGoal, fundedGoal, appsGoal, callsGoal, smsGoal };
};

// Phase 2 (Feb 2026): Period Selector Component for Reviews Tab
const PeriodSelector = ({
  periodType, setPeriodType,
  selectedYear, setSelectedYear,
  selectedPeriodMonth, setSelectedPeriodMonth,
  selectedQuarter, setSelectedQuarter,
  selectedWeek, setSelectedWeek
}) => {
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const years = [2024, 2025, 2026]; // Dynamic range

  // Calculate weeks in selected month
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const weeksInMonth = Math.ceil(getDaysInMonth(selectedYear, selectedPeriodMonth) / 7);

  // Get date range display text
  const getDateRangeText = () => {
    if (periodType === 'year') {
      return `January 1 - December 31, ${selectedYear}`;
    }
    if (periodType === 'quarter') {
      const quarterMonths = {
        1: { start: 'January 1', end: 'March 31' },
        2: { start: 'April 1', end: 'June 30' },
        3: { start: 'July 1', end: 'September 30' },
        4: { start: 'October 1', end: 'December 31' }
      };
      return `${quarterMonths[selectedQuarter].start} - ${quarterMonths[selectedQuarter].end}, ${selectedYear}`;
    }
    if (periodType === 'month') {
      const daysInMonth = getDaysInMonth(selectedYear, selectedPeriodMonth);
      return `${monthNames[selectedPeriodMonth]} 1-${daysInMonth}, ${selectedYear}`;
    }
    if (periodType === 'week') {
      const startDay = (selectedWeek - 1) * 7 + 1;
      const daysInMonth = getDaysInMonth(selectedYear, selectedPeriodMonth);
      const endDay = Math.min(selectedWeek * 7, daysInMonth);
      return `${monthNames[selectedPeriodMonth]} ${startDay}-${endDay}, ${selectedYear}`;
    }
    return '';
  };

  const periodButtons = [
    { key: 'week', label: 'Week' },
    { key: 'month', label: 'Month' },
    { key: 'quarter', label: 'Quarter' },
    { key: 'year', label: 'Year' }
  ];

  const dropdownStyle = {
    padding: '10px 16px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    backgroundColor: 'white',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    cursor: 'pointer',
    minWidth: '120px'
  };

  return (
    <div style={{
      backgroundColor: '#f8fafc',
      borderRadius: '12px',
      padding: '16px 20px',
      marginBottom: '24px',
      border: '1px solid #e2e8f0'
    }}>
      {/* Period Type Buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
        <span style={{ fontSize: '14px', fontWeight: 600, color: '#64748b' }}>View:</span>
        <div style={{ display: 'flex', gap: '8px' }}>
          {periodButtons.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setPeriodType(key)}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: periodType === key ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                backgroundColor: periodType === key ? '#eff6ff' : 'white',
                color: periodType === key ? '#3b82f6' : '#64748b',
                fontWeight: periodType === key ? 600 : 500,
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Conditional Dropdowns */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '14px', fontWeight: 600, color: '#64748b' }}>Period:</span>

        {/* Year Dropdown - Always visible */}
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          style={dropdownStyle}
        >
          {years.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>

        {/* Quarter Dropdown - Only for Quarter view */}
        {periodType === 'quarter' && (
          <select
            value={selectedQuarter}
            onChange={(e) => setSelectedQuarter(parseInt(e.target.value))}
            style={dropdownStyle}
          >
            <option value={1}>Q1 (Jan-Mar)</option>
            <option value={2}>Q2 (Apr-Jun)</option>
            <option value={3}>Q3 (Jul-Sep)</option>
            <option value={4}>Q4 (Oct-Dec)</option>
          </select>
        )}

        {/* Month Dropdown - For Month and Week views */}
        {(periodType === 'month' || periodType === 'week') && (
          <select
            value={selectedPeriodMonth}
            onChange={(e) => {
              setSelectedPeriodMonth(parseInt(e.target.value));
              setSelectedWeek(1); // Reset week when month changes
            }}
            style={dropdownStyle}
          >
            {monthNames.map((month, idx) => (
              <option key={month} value={idx}>{month}</option>
            ))}
          </select>
        )}

        {/* Week Dropdown - Only for Week view */}
        {periodType === 'week' && (
          <select
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
            style={dropdownStyle}
          >
            {Array.from({ length: weeksInMonth }, (_, i) => i + 1).map(week => {
              const startDay = (week - 1) * 7 + 1;
              const daysInMonth = getDaysInMonth(selectedYear, selectedPeriodMonth);
              const endDay = Math.min(week * 7, daysInMonth);
              return (
                <option key={week} value={week}>
                  Week {week} ({startDay}-{endDay})
                </option>
              );
            })}
          </select>
        )}

        {/* Date Range Display */}
        <div style={{
          marginLeft: 'auto',
          fontSize: '13px',
          color: '#64748b',
          fontWeight: 500,
          backgroundColor: 'white',
          padding: '8px 12px',
          borderRadius: '6px',
          border: '1px solid #e2e8f0'
        }}>
          📅 {getDateRangeText()}
        </div>
      </div>
    </div>
  );
};



export default function Dashboard() {
  // Compute standalone book mode BEFORE hook calls (stable for component lifetime)
  const [standaloneBookMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('standalone') === '1' && params.get('tab') === 'book';
  });

  // Skip heavy loads in standalone book mode
  const { data: fullData, loading, error, lastUpdated, refresh } = useDashboardData({ skip: standaloneBookMode });
  const { goals, loading: goalsLoading } = useGoalsData({ skip: standaloneBookMode });

  // Lightweight 2-tab fetch for standalone book mode
  const { data: bookSfData, loading: bookSfLoading, error: bookSfError } = useBookSalesforceData({ autoLoad: standaloneBookMode });

  // Tier 2: Background fetch historical SMS + Dials_TalkTime (13-month rolling window)
  // Tab-gated: fires on first visit to any tab that needs full date-range data
  const { data: activityAggregates, loading: activityAggregatesLoading, load: loadActivityAggregates } = useActivityAggregates({ autoLoad: true });

  // ws-sales-calls-sms-migration Phase 2.1 (D-SCSM-9) — canonical raw rows for
  // short-range path (today/yesterday/last7) on ActivityTab. Renewals scope,
  // 30-day buffer window. Parallel fetch; if it fails ActivityTab falls back
  // to legacy `rawCallsData` via the length>0 guard inside the component.
  const { data: canonicalRawCalls } = useCanonicalCallsRaw({ autoLoad: !standaloneBookMode, days: 30, team: 'Renewals' });

  const { data: apolloEmailData, loading: apolloEmailLoading, error: apolloEmailError, load: loadApolloEmail } = useApolloEmailData({ autoLoad: !standaloneBookMode });
  const { data: bookData, loading: bookLoading, error: bookError, load: loadBook } = useBookData({ autoLoad: false });
  const { data: apolloContactEmails, load: loadApolloContacts } = useApolloContactEmails({ autoLoad: false });
  const { data: pipelineData, loading: pipelineLoading, error: pipelineError, load: loadPipeline } = usePipelineApprovals({ autoLoad: !standaloneBookMode });
  const { data: historicalFundedData, load: loadHistoricalFunded } = useHistoricalFunded({ autoLoad: false });
  const { data: datamerchData, load: loadDatamerch } = useDatamerchStatus({ autoLoad: false });
  const { data: companyIntelIndex, load: loadCompanyIntelIndex } = useCompanyIntelIndex({ autoLoad: false });
  const { data: paymentAlertData, load: loadPaymentAlerts } = usePaymentAlerts({ autoLoad: false });
  // Phase 4 — account-level Notes + deal-level overrides
  const { data: accountStatusOverridesData, load: loadAccountStatusOverrides, refresh: refreshAccountStatusOverrides } = useAccountStatusOverrides({ autoLoad: false });
  const { data: dealLevelStatusOverridesData, load: loadDealLevelStatusOverrides, refresh: refreshDealLevelStatusOverrides } = useDealLevelStatusOverrides({ autoLoad: false });

  // Merge data source — downstream code references `data` unchanged
  const data = standaloneBookMode ? bookSfData : fullData;

  // =========================================================================
  // MERGED DATA SOURCES — Two-Tier Loading (March 2026)
  // =========================================================================
  // When Tier 2 historical data arrives, use it (full history, no period filter).
  // Until then, fall back to Tier 1 current-month data from `data.tabs`.
  // These are raw arrays — use directly for ActivityTab/BookTab props.
  // For useMemo blocks that expect { data: [...] } wrapper objects, wrap inline.
  // =========================================================================
  const fullSmsData = useMemo(() => {
    // In standalone book mode, data === bookSfData which has raw SMS rows.
    // Skip the aggregate shortcut — agg_daily_activity has no per-account detail.
    if (standaloneBookMode) {
      return data?.tabs?.SMS?.data || [];
    }
    if (activityAggregates?.daily?.length > 0) {
      return activityAggregates.daily;
    }
    return data?.tabs?.SMS?.data || data?.tabs?.SMS || [];
  }, [standaloneBookMode, activityAggregates, data]);

  const fullDialsData = useMemo(() => {
    // In standalone book mode, data === bookSfData which has raw Dials_TalkTime rows.
    // Skip the aggregate shortcut — agg_daily_activity has no per-account detail.
    if (standaloneBookMode) {
      return data?.tabs?.Dials_TalkTime?.data || [];
    }
    if (activityAggregates?.daily?.length > 0) {
      return activityAggregates.daily;
    }
    return data?.tabs?.Dials_TalkTime?.data || data?.tabs?.Dials_TalkTime || [];
  }, [standaloneBookMode, activityAggregates, data]);

  // Data object with full historical SMS + Dials overriding the current-month-only Tier 1 data.
  // Used for GoalTrackingTab (which receives `data` and accesses data.tabs internally).
  const dataWithFullActivity = useMemo(() => {
    if (!data || !data.tabs) return data;
    return {
      ...data,
      tabs: {
        ...data.tabs,
        Dials_TalkTime: { ...data.tabs.Dials_TalkTime, data: fullDialsData },
        SMS: { ...data.tabs.SMS, data: fullSmsData }
      },
      monthlyAggregates: activityAggregates?.monthly || null
    };
  }, [data, fullDialsData, fullSmsData, activityAggregates]);

  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tab') || 'goal-tracking';
  });
  const [standaloneMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('standalone') === '1';
  });

  // Tab-gated loading: trigger deferred hooks when user first visits a tab
  const triggeredHooksRef = useRef({});

  useEffect(() => {
    // Book hooks only fire in standalone book mode (Book tab removed from dashboard nav)
    if (activeTab === 'book' && standaloneBookMode && !triggeredHooksRef.current.book) {
      triggeredHooksRef.current.book = true;
      loadBook();
      loadApolloContacts();
      loadHistoricalFunded();
      loadDatamerch();
      loadCompanyIntelIndex();
      loadPaymentAlerts();
      loadAccountStatusOverrides();
      loadDealLevelStatusOverrides();
    }
    if ((activeTab === 'activity' || activeTab === 'email') && !triggeredHooksRef.current.apollo) {
      triggeredHooksRef.current.apollo = true;
      loadApolloEmail();
    }
    if ((activeTab === 'funnel' || activeTab === 'reviews') && !triggeredHooksRef.current.pipeline) {
      triggeredHooksRef.current.pipeline = true;
      loadPipeline();
    }
    // Tier 2: Historical activity data — load on first visit to any tab that needs full date-range data
    const historicalTabs = ['goal-tracking', 'weekly', 'monthly', 'historical', 'activity', 'yearly-review'];
    if (!standaloneBookMode && fullData && historicalTabs.includes(activeTab) && !triggeredHooksRef.current.historicalActivity) {
      triggeredHooksRef.current.historicalActivity = true;
      console.log('🔄 [Tier 2] Tab-gated: Loading activity aggregates for ' + activeTab + ' tab...');
      loadActivityAggregates();
    }
  }, [activeTab, fullData, standaloneBookMode]);
  const [activeWeeklyTab, setActiveWeeklyTab] = useState('this-week'); // Sub-tab for Weekly parent
  const [activeMonthlyTab, setActiveMonthlyTab] = useState('this-month'); // Sub-tab for Monthly parent
  const [activeHistoricalTab, setActiveHistoricalTab] = useState('rep-vs-rep'); // Sub-tab for Historical parent (Nov 28, 2025)
  const [activeEmailTab, setActiveEmailTab] = useState('today'); // NEW (Dec 2025): Sub-tab for Email parent
  const [activeYearlyTab, setActiveYearlyTab] = useState('team-review'); // NEW (Jan 2026): Sub-tab for Yearly Review parent
  const [legacyDropdownOpen, setLegacyDropdownOpen] = useState(false); // NEW (Jan 27, 2026): Legacy dropdown state

  // Legacy tabs definition (Jan 27, 2026)
  const legacyTabs = ['weekly', 'monthly', 'historical', 'email', 'yearly-review'];
  const isLegacyTabActive = legacyTabs.includes(activeTab);
  const getLegacyTabLabel = (tab) => {
    const labels = { 'weekly': 'Weekly', 'monthly': 'Monthly', 'historical': 'Historical', 'email': 'Email', 'yearly-review': 'Yearly Review' };
    return labels[tab] || tab;
  };

  // Close legacy dropdown when clicking outside (Jan 27, 2026)
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (legacyDropdownOpen && !event.target.closest('.legacy-dropdown')) {
        setLegacyDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [legacyDropdownOpen]);

  const [selectedYear, setSelectedYear] = useState(2026); // NEW (Jan 2026): Year selector for Yearly Review tab
  const [selectedRep, setSelectedRep] = useState('All Reps');

  // Phase 2 (Feb 2026): Period selector state for Reviews tab
  const [periodType, setPeriodType] = useState('year'); // 'week', 'month', 'quarter', 'year'
  const [selectedPeriodMonth, setSelectedPeriodMonth] = useState(new Date().getMonth()); // 0-11
  const [selectedQuarter, setSelectedQuarter] = useState(Math.floor(new Date().getMonth() / 3) + 1); // 1-4
  const [selectedWeek, setSelectedWeek] = useState(1); // 1-5
  const [displayGranularity, setDisplayGranularity] = useState('auto'); // 'auto' | 'day' | 'week' | 'month' (Feb 4, 2026)

  // State for visible reps in Revenue Trends Comparison chart (Feb 2, 2026)
  const [visibleReps, setVisibleReps] = useState({});

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [developerMode, setDeveloperMode] = useState(() => {
    // Load from localStorage for persistence across sessions (Feb 2, 2026)
    if (typeof window !== 'undefined') {
      return localStorage.getItem('dashboardDevMode') === 'true';
    }
    return false;
  }); // Dev Mode: Show chart numbers for admin/developer collaboration + controls Legacy menu visibility
  const [useNewOverview, setUseNewOverview] = useState(true); // Toggle for new Overview tab redesign (Dec 29, 2025)

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refresh();
    setIsRefreshing(false);
  };

  const formatLastUpdated = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 minute ago';
    if (diffMins < 60) return `${diffMins} minutes ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hour ago';
    if (diffHours < 24) return `${diffHours} hours ago`;

    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  // Get current month name and year for proper filtering
  const currentMonth = new Date().toLocaleString('default', { month: 'long' });
  const currentYear = new Date().getFullYear();
  const currentMonthYear = `${currentMonth} ${currentYear}`;  // For goals: "January 2026"
  const lastMonth = new Date(new Date().setMonth(new Date().getMonth() - 1)).toLocaleString('default', { month: 'long' });
  const lastMonthDate = new Date(new Date().setMonth(new Date().getMonth() - 1));
  const lastMonthYear = `${lastMonth} ${lastMonthDate.getFullYear()}`;  // For last month goals

  // Jorge departed April 2026 — include him only for periods before that
  const includeJorge = selectedYear < 2026 ||
    (selectedYear === 2026 && periodType === 'year') ||
    (selectedYear === 2026 && periodType === 'quarter' && selectedQuarter < 2) ||
    (selectedYear === 2026 && (periodType === 'month' || periodType === 'week') && selectedPeriodMonth < 3);

  // Reset stale selectedRep if Jorge is no longer in scope for the current period
  React.useEffect(() => {
    if (!includeJorge && selectedRep === 'Jorge Verdesoto') {
      setSelectedRep('All Reps');
    }
  }, [includeJorge, selectedRep]);

  const reps = useMemo(() => {
    if (includeJorge) {
      return ['Patrick McTiernan', 'Jorge Verdesoto', 'Marcin Maleski', 'Matthew Vecchione', 'Damian Pomykala', 'Antonio Vagnone', 'Alex Golden'];
    }
    return ['Patrick McTiernan', 'Marcin Maleski', 'Matthew Vecchione', 'Damian Pomykala', 'Antonio Vagnone', 'Alex Golden'];
  }, [includeJorge]);

  // Current-month views (Goal Tracking, Funnel) use a date-fixed rep list.
  // includeJorge defaults true because periodType defaults to 'year' — but those tabs
  // always show the current month, so Jorge must be excluded from April 2026 onward
  // regardless of the Yearly Review period selector state.
  const currentMonthReps = useMemo(() => {
    if (new Date() >= new Date(2026, 3, 1)) {
      return reps.filter(r => r !== 'Jorge Verdesoto');
    }
    return reps;
  }, [reps]);

  // Initialize visibleReps when selectedRep changes (for Rep Review mode) (Feb 2, 2026)
  // NOTE: repFirstNames kept separate from `reps` to avoid infinite loop (adding `reps` to deps caused re-render cycle)
  React.useEffect(() => {
    if (activeYearlyTab !== 'rep-review') return;

    const selectedFirstName = selectedRep?.split(' ')[0] || '';
    const repFirstNames = includeJorge
      ? ['Patrick', 'Jorge', 'Marcin', 'Matthew', 'Damian', 'Antonio', 'Alex']
      : ['Patrick', 'Marcin', 'Matthew', 'Damian', 'Antonio', 'Alex'];

    setVisibleReps({
      'Team Avg': true,
      ...Object.fromEntries(repFirstNames.map(name => [
        name,
        name === selectedFirstName
      ]))
    });
  }, [selectedRep, activeYearlyTab, includeJorge]); // Removed `reps` - was causing infinite loop

  // Helper: Check if record is from the target year (uses Date column)
  const isYear = (record, year) => {
    if (!record?.Date) return false;
    return new Date(record.Date).getFullYear() === year;
  };

  // =========================================================================
  // OVERVIEW TAB CALCULATIONS (self-contained, used only by Overview)
  // =========================================================================

  // Overview: Current month data filtered (with year check from Date column)
  const overviewCurrentMonthData = useMemo(() => {
    if (!data || !data.tabs) return null;
    const monthlyAggs = (activityAggregates && activityAggregates.monthly) ? activityAggregates.monthly : [];
    const callAggs = monthlyAggs.filter(r => r.Month === currentMonth && r.Year === currentYear);
    return {
      calls: callAggs,
      apps: data.tabs.Apps_In?.data?.filter(r => r.Month === currentMonth && isYear(r, currentYear)) || [],
      revenue: data.tabs.Revenue?.data?.filter(r => r.Month === currentMonth && isYear(r, currentYear)) || [],
      funded: data.tabs.Funded?.data?.filter(r => r.Month === currentMonth && isYear(r, currentYear)) || []
    };
  }, [data, currentMonth, currentYear, activityAggregates]);

  // Overview: Team goals (sum of all rep goals) - uses Month Year format
  const overviewRepGoals = useMemo(() => {
    if (!goals) return null;
    const teamGoals = goals.filter(g => g.Month === currentMonthYear);
    if (teamGoals.length === 0) return null;
    return {
      apps: teamGoals.reduce((sum, g) => sum + (parseInt(g.Apps_Goal) || 0), 0),
      approved: teamGoals.reduce((sum, g) => sum + (parseInt(g.Approved_Goal) || 0), 0),
      funded: teamGoals.reduce((sum, g) => sum + (parseInt(g.Funded_Units_Goal) || 0), 0),
      revenue: teamGoals.reduce((sum, g) => sum + (parseInt(g.Revenue_Goal) || 0), 0),
      fundedAmount: teamGoals.reduce((sum, g) => sum + (parseInt(g.Funded_Goal) || 0), 0),
      activityCallGoal: teamGoals.reduce((sum, g) => sum + (parseInt(g.Activity_Call_Goal) || 0), 0)
    };
  }, [goals, currentMonthYear]);

  // Overview: Daily Revenue Pacing for LineChart
  const overviewDailyRevenuePacing = useMemo(() => {
    if (!overviewCurrentMonthData || !overviewRepGoals || !overviewRepGoals.revenue) return [];

    const now = new Date();
    const currentYear = now.getFullYear();
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthIndex = monthNames.indexOf(currentMonth);
    const daysInMonth = new Date(currentYear, monthIndex + 1, 0).getDate();

    let businessDaysInMonth = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(currentYear, monthIndex, d);
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) businessDaysInMonth++;
    }

    const dailyGoal = overviewRepGoals.revenue / businessDaysInMonth;
    const revenueDates = [...new Set(
      overviewCurrentMonthData.revenue.map(r => r.Date?.split('T')[0]).filter(Boolean)
    )].sort();

    let cumulativeActual = 0;
    let cumulativeExpected = 0;

    return revenueDates.map((date, idx) => {
      const dayRevenue = overviewCurrentMonthData.revenue
        .filter(r => r.Date?.split('T')[0] === date)
        .reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
      cumulativeActual += dayRevenue;
      cumulativeExpected += dailyGoal;
      return { day: `Day ${idx + 1}`, date: date, actual: cumulativeActual, expected: cumulativeExpected };
    });
  }, [overviewCurrentMonthData, overviewRepGoals, currentMonth]);

  // Overview: Metrics for Activity Boost calculation
  const overviewMetrics = useMemo(() => {
    if (!overviewCurrentMonthData) return { calls: 0, appsIn: 0 };
    return {
      calls: overviewCurrentMonthData.calls.reduce((s, r) => s + (parseInt(r.TotalCalls) || 0), 0),
      appsIn: overviewCurrentMonthData.apps.length
    };
  }, [overviewCurrentMonthData]);

  // Overview: Business days helper functions
  const getOverviewBusinessDaysElapsed = useCallback(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthIndex = monthNames.indexOf(currentMonth);
    const today = now.getDate();
    let businessDays = 0;
    for (let d = 1; d <= today; d++) {
      const date = new Date(currentYear, monthIndex, d);
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) businessDays++;
    }
    return businessDays;
  }, [currentMonth]);

  const getOverviewBusinessDaysInMonth = useCallback(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthIndex = monthNames.indexOf(currentMonth);
    const daysInMonth = new Date(currentYear, monthIndex + 1, 0).getDate();
    let businessDays = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(currentYear, monthIndex, d);
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) businessDays++;
    }
    return businessDays;
  }, [currentMonth]);

  // =========================================================================
  // YEARLY REVIEW TAB CALCULATIONS (Jan 2026)
  // =========================================================================

  const allYearlyData = useMemo(() => {
    if (!data || !data.tabs) return {};

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];

    const result = {};

    monthNames.forEach(month => {
      result[month] = {};

      reps.forEach(rep => {
        // Filter by month AND selectedYear (using Date column)
        const filterByMonthYear = (tabData) => {
          return (tabData?.data || []).filter(r => {
            if (r.Month !== month) return false;
            if (!r.Date) return false;
            if (r.Rep !== rep) return false;
            return new Date(r.Date).getFullYear() === selectedYear;
          });
        };

        const monthlyAggs = (activityAggregates && activityAggregates.monthly) ? activityAggregates.monthly : [];
        const calls = monthlyAggs.filter(r => r.Month === month && r.Year === selectedYear && r.Rep === rep);
        const sms = monthlyAggs.filter(r => r.Month === month && r.Year === selectedYear && r.Rep === rep);
        const appsIn = filterByMonthYear(data.tabs.Apps_In);
        const approved = filterByMonthYear(data.tabs.Approved);
        const fundedRaw = filterByMonthYear(data.tabs.Funded);
        const revenueRaw = filterByMonthYear(data.tabs.Revenue);

        // Filter out $0 deals for funded/revenue
        const funded = fundedRaw.filter(r => parseFloat(r.Amount) > 0 || parseFloat(r.Revenue) > 0);
        const revenue = revenueRaw.filter(r => parseFloat(r.Revenue) > 0);

        const totalRevenue = revenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
        const totalTalkTime = calls.reduce((sum, r) => sum + (parseFloat(r.TalkMinutes) || 0), 0);
        const totalSmsCount = sms.reduce((sum, r) => sum + (parseInt(r.TotalSMS) || 0), 0);

        const callCount = calls.reduce((s, r) => s + (parseInt(r.TotalCalls) || 0), 0);
        result[month][rep] = {
          calls: callCount,
          sms: totalSmsCount,
          talkTime: totalTalkTime,
          appsIn: appsIn.length,
          approved: approved.length,
          funded: funded.length,
          revenue: totalRevenue,
          avgDealSize: funded.length > 0 ? totalRevenue / funded.length : 0,
          callToApp: callCount > 0 ? (appsIn.length / callCount * 100) : 0,
          appToApproved: appsIn.length > 0 ? (approved.length / appsIn.length * 100) : 0,
          approvedToFunded: approved.length > 0 ? (funded.length / approved.length * 100) : 0,
          appToFunded: appsIn.length > 0 ? (funded.length / appsIn.length * 100) : 0
        };
      });
    });

    return result;
  }, [data, selectedYear, reps, activityAggregates]);

  const yearlyTotals = useMemo(() => {
    if (!allYearlyData || Object.keys(allYearlyData).length === 0) return null;

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];

    let totalRevenue = 0;
    let totalFunded = 0;
    let totalApps = 0;
    let totalApproved = 0;
    let totalCalls = 0;
    let totalSms = 0;
    let totalTalkTime = 0;

    // Monthly breakdown for charts
    const monthlyData = [];

    monthNames.forEach(month => {
      const monthData = allYearlyData[month];
      if (!monthData) return;

      let monthRevenue = 0;
      let monthFunded = 0;
      let monthApps = 0;
      let monthApproved = 0;
      let monthCalls = 0;
      let monthSms = 0;
      let monthTalkTime = 0;

      reps.forEach(rep => {
        const repData = monthData[rep];
        if (!repData) return;

        monthRevenue += repData.revenue || 0;
        monthFunded += repData.funded || 0;
        monthApps += repData.appsIn || 0;
        monthApproved += repData.approved || 0;
        monthCalls += repData.calls || 0;
        monthSms += repData.sms || 0;
        monthTalkTime += repData.talkTime || 0;
      });

      totalRevenue += monthRevenue;
      totalFunded += monthFunded;
      totalApps += monthApps;
      totalApproved += monthApproved;
      totalCalls += monthCalls;
      totalSms += monthSms;
      totalTalkTime += monthTalkTime;

      monthlyData.push({
        month: month.substring(0, 3), // "Jan", "Feb", etc.
        monthFull: month,
        revenue: monthRevenue,
        funded: monthFunded,
        apps: monthApps,
        approved: monthApproved,
        calls: monthCalls,
        sms: monthSms,
        talkTime: monthTalkTime,
        conversionRate: monthApps > 0 ? (monthFunded / monthApps * 100) : 0
      });
    });

    return {
      totalRevenue,
      totalFunded,
      totalApps,
      totalApproved,
      totalCalls,
      totalSms,
      totalTalkTime,
      avgDealSize: totalFunded > 0 ? totalRevenue / totalFunded : 0,
      avgConversion: totalApps > 0 ? (totalFunded / totalApps * 100) : 0,
      monthlyData
    };
  }, [allYearlyData, reps]);

  // Phase 2 (Feb 2026): Period-filtered data for Reviews tab
  // Helper function to calculate date range based on period type
  const getPeriodDateRange = useCallback((pType, year, month, quarter, week) => {
    const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();

    if (pType === 'year') {
      return {
        start: new Date(year, 0, 1),
        end: new Date(year, 11, 31, 23, 59, 59, 999)
      };
    }
    if (pType === 'quarter') {
      const quarterStartMonth = (quarter - 1) * 3;
      const quarterEndMonth = quarterStartMonth + 2;
      return {
        start: new Date(year, quarterStartMonth, 1),
        end: new Date(year, quarterEndMonth, getDaysInMonth(year, quarterEndMonth), 23, 59, 59, 999)
      };
    }
    if (pType === 'month') {
      return {
        start: new Date(year, month, 1),
        end: new Date(year, month, getDaysInMonth(year, month), 23, 59, 59, 999)
      };
    }
    if (pType === 'week') {
      const startDay = (week - 1) * 7 + 1;
      const daysInMonth = getDaysInMonth(year, month);
      const endDay = Math.min(week * 7, daysInMonth);
      return {
        start: new Date(year, month, startDay),
        end: new Date(year, month, endDay, 23, 59, 59, 999)
      };
    }
    return { start: null, end: null };
  }, []);

  // Resolve display granularity - 'auto' picks based on period type (Feb 4, 2026)
  const resolveGranularity = useCallback((displayGran, pType) => {
    if (displayGran !== 'auto') return displayGran;
    // Auto defaults: Year→Month, Quarter→Week, Month→Week, Week→Day
    const autoMap = { year: 'month', quarter: 'week', month: 'week', week: 'day' };
    return autoMap[pType] || 'month';
  }, []);

  // Period-filtered totals for Reviews tab
  const periodFilteredTotals = useMemo(() => {
    if (!data || !data.tabs) return null;

    const { start, end } = getPeriodDateRange(
      periodType, selectedYear, selectedPeriodMonth, selectedQuarter, selectedWeek
    );
    if (!start || !end) return null;

    // Helper to filter data by date range - handles different date field names
    const filterByDateRange = (tabData, dateField = 'Date') => {
      return (tabData?.data || []).filter(r => {
        // Try the specified date field first, then fall back to Date
        const dateStr = r[dateField] || r.Date;
        if (!dateStr) return false;
        const rowDate = new Date(dateStr);
        if (isNaN(rowDate.getTime())) return false; // Invalid date check
        return rowDate >= start && rowDate <= end;
      });
    };

    // Helper for funded - uses csbs__Funded_Date__c primarily
    const filterFundedByDateRange = (tabData) => {
      return (tabData?.data || []).filter(r => {
        const dateStr = r.csbs__Funded_Date__c || r.Date;
        if (!dateStr) return false;
        const rowDate = new Date(dateStr);
        if (isNaN(rowDate.getTime())) return false;
        return rowDate >= start && rowDate <= end;
      });
    };

    // Get all data within period using appropriate date fields
    const calls = filterByDateRange({ data: fullDialsData }, 'Date');
    const sms = filterByDateRange({ data: fullSmsData }, 'Date');
    const appsIn = filterByDateRange(data.tabs.Apps_In, 'Date');
    const approved = filterByDateRange(data.tabs.Approved, 'Date');
    const fundedRaw = filterFundedByDateRange(data.tabs.Funded);
    const revenueRaw = filterFundedByDateRange(data.tabs.Revenue); // Revenue also uses funded date

    // Filter out $0 deals
    const funded = fundedRaw.filter(r => parseFloat(r.Amount) > 0 || parseFloat(r.Revenue) > 0);
    const revenue = revenueRaw.filter(r => parseFloat(r.Revenue) > 0);

    const totalRevenue = revenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
    const totalTalkTime = calls.reduce((sum, r) => sum + (parseFloat(r.TalkMinutes) || 0), 0);
    const totalSmsCount = sms.reduce((sum, r) => sum + (parseInt(r.TotalSMS) || 0), 0);

    // Generate chart data based on period type granularity
    const chartData = [];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();

    // Helper to get date from record (handles funded date field)
    const getRecordDate = (r, isFundedOrRevenue = false) => {
      if (isFundedOrRevenue) {
        return new Date(r.csbs__Funded_Date__c || r.Date);
      }
      return new Date(r.Date);
    };

    // Helper to check if date is in range
    const isInRange = (r, rangeStart, rangeEnd, isFundedOrRevenue = false) => {
      const d = getRecordDate(r, isFundedOrRevenue);
      if (isNaN(d.getTime())) return false;
      return d >= rangeStart && d <= rangeEnd;
    };

    // Feb 4, 2026: Resolve display granularity (independent from period type)
    const resolvedGranularity = resolveGranularity(displayGranularity, periodType);

    if (resolvedGranularity === 'month') {
      // Monthly breakdown - iterate from start to end by months
      let currentMonth = new Date(start.getFullYear(), start.getMonth(), 1);
      while (currentMonth <= end) {
        const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0, 23, 59, 59, 999);
        const effectiveEnd = monthEnd > end ? end : monthEnd;
        const effectiveStart = currentMonth < start ? start : currentMonth;

        const monthRevenue = revenue.filter(r => isInRange(r, effectiveStart, effectiveEnd, true))
          .reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
        const monthFunded = funded.filter(r => isInRange(r, effectiveStart, effectiveEnd, true)).length;
        const monthApps = appsIn.filter(r => isInRange(r, effectiveStart, effectiveEnd, false)).length;
        const monthApproved = approved.filter(r => isInRange(r, effectiveStart, effectiveEnd, false)).length;
        const monthCalls = calls.filter(r => isInRange(r, effectiveStart, effectiveEnd, false)).reduce((s,r)=>s+(parseInt(r.TotalCalls)||0),0);
        const monthSms = sms.filter(r => isInRange(r, effectiveStart, effectiveEnd, false))
          .reduce((sum, r) => sum + (parseInt(r.TotalSMS) || 0), 0);

        chartData.push({
          label: monthNames[currentMonth.getMonth()].substring(0, 3),
          labelFull: `${monthNames[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`,
          revenue: monthRevenue,
          funded: monthFunded,
          apps: monthApps,
          approved: monthApproved,
          calls: monthCalls,
          sms: monthSms,
          conversionRate: monthApps > 0 ? (monthFunded / monthApps * 100) : 0
        });

        currentMonth.setMonth(currentMonth.getMonth() + 1);
      }
    } else if (resolvedGranularity === 'week') {
      // Weekly breakdown - iterate from start to end by weeks
      let weekStart = new Date(start);
      let weekNum = 1;

      while (weekStart <= end) {
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        if (weekEnd > end) weekEnd.setTime(end.getTime());
        weekEnd.setHours(23, 59, 59, 999);

        const wRevenue = revenue.filter(r => isInRange(r, weekStart, weekEnd, true))
          .reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
        const wFunded = funded.filter(r => isInRange(r, weekStart, weekEnd, true)).length;
        const wApps = appsIn.filter(r => isInRange(r, weekStart, weekEnd, false)).length;
        const wApproved = approved.filter(r => isInRange(r, weekStart, weekEnd, false)).length;
        const wCalls = calls.filter(r => isInRange(r, weekStart, weekEnd, false)).reduce((s,r)=>s+(parseInt(r.TotalCalls)||0),0);
        const wSms = sms.filter(r => isInRange(r, weekStart, weekEnd, false))
          .reduce((sum, r) => sum + (parseInt(r.TotalSMS) || 0), 0);

        chartData.push({
          label: `W${weekNum}`,
          labelFull: `Week ${weekNum}: ${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`,
          revenue: wRevenue,
          funded: wFunded,
          apps: wApps,
          approved: wApproved,
          calls: wCalls,
          sms: wSms,
          conversionRate: wApps > 0 ? (wFunded / wApps * 100) : 0
        });

        weekStart.setDate(weekStart.getDate() + 7);
        weekNum++;
      }
    } else if (resolvedGranularity === 'day') {
      // Daily breakdown - iterate from start to end (max 60 days for performance)
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      let currentDay = new Date(start);
      let dayCount = 0;
      const maxDays = 60;

      while (currentDay <= end && dayCount < maxDays) {
        const dayEnd = new Date(currentDay);
        dayEnd.setHours(23, 59, 59, 999);

        const dRevenue = revenue.filter(r => isInRange(r, currentDay, dayEnd, true))
          .reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
        const dFunded = funded.filter(r => isInRange(r, currentDay, dayEnd, true)).length;
        const dApps = appsIn.filter(r => isInRange(r, currentDay, dayEnd, false)).length;
        const dApproved = approved.filter(r => isInRange(r, currentDay, dayEnd, false)).length;
        const dCalls = calls.filter(r => isInRange(r, currentDay, dayEnd, false)).reduce((s,r)=>s+(parseInt(r.TotalCalls)||0),0);
        const dSms = sms.filter(r => isInRange(r, currentDay, dayEnd, false))
          .reduce((sum, r) => sum + (parseInt(r.TotalSMS) || 0), 0);

        chartData.push({
          label: `${dayNames[currentDay.getDay()]} ${currentDay.getDate()}`,
          labelFull: `${dayNames[currentDay.getDay()]} ${monthNames[currentDay.getMonth()]} ${currentDay.getDate()}`,
          revenue: dRevenue,
          funded: dFunded,
          apps: dApps,
          approved: dApproved,
          calls: dCalls,
          sms: dSms,
          conversionRate: dApps > 0 ? (dFunded / dApps * 100) : 0
        });

        currentDay.setDate(currentDay.getDate() + 1);
        dayCount++;
      }

      if (dayCount >= maxDays) {
        console.warn(`[Team Review] Daily view capped at ${maxDays} days for performance`);
      }
    }

    // Phase 3: Calculate goals for this period (Team level - sum of all reps)
    const periodGoals = getGoalsForPeriod(
      goals,
      periodType,
      selectedYear,
      selectedPeriodMonth,
      selectedQuarter,
      selectedWeek,
      null // null = team goals (sum of all reps)
    );

    return {
      totalRevenue,
      totalFunded: funded.length,
      totalApps: appsIn.length,
      totalApproved: approved.length,
      totalCalls: calls.reduce((s,r)=>s+(parseInt(r.TotalCalls)||0),0),
      totalSms: totalSmsCount,
      totalTalkTime,
      avgDealSize: funded.length > 0 ? totalRevenue / funded.length : 0,
      avgConversion: appsIn.length > 0 ? (funded.length / appsIn.length * 100) : 0,
      chartData,
      goals: periodGoals, // Phase 3: Add goals
      resolvedGranularity, // Feb 4, 2026: For UI to know current granularity
      periodLabel: periodType === 'year' ? `${selectedYear}` :
                   periodType === 'quarter' ? `Q${selectedQuarter} ${selectedYear}` :
                   periodType === 'month' ? `${monthNames[selectedPeriodMonth]} ${selectedYear}` :
                   `Week ${selectedWeek}, ${monthNames[selectedPeriodMonth]} ${selectedYear}`
    };
  }, [data, periodType, selectedYear, selectedPeriodMonth, selectedQuarter, selectedWeek, getPeriodDateRange, goals, displayGranularity, resolveGranularity, fullDialsData, fullSmsData]);

  const yearlyRepMetrics = useMemo(() => {
    if (!allYearlyData || Object.keys(allYearlyData).length === 0) return [];

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];

    // Helper for empty months
    function emptyMonth() {
      return { revenue: 0, funded: 0, apps: 0, approved: 0, calls: 0, sms: 0, talkTime: 0, conversionRate: 0 };
    }

    // Determine which months to include based on period type
    let monthsToInclude = [];
    if (periodType === 'year') {
      monthsToInclude = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]; // All months
    } else if (periodType === 'quarter') {
      const quarterStartMonth = (selectedQuarter - 1) * 3;
      monthsToInclude = [quarterStartMonth, quarterStartMonth + 1, quarterStartMonth + 2];
    } else if (periodType === 'month') {
      monthsToInclude = [selectedPeriodMonth];
    } else if (periodType === 'week') {
      monthsToInclude = [selectedPeriodMonth]; // Week is within a month
    }

    // For week filtering, calculate the week date range
    const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
    let weekStartDay = 1, weekEndDay = 31;
    if (periodType === 'week') {
      weekStartDay = (selectedWeek - 1) * 7 + 1;
      weekEndDay = Math.min(selectedWeek * 7, getDaysInMonth(selectedYear, selectedPeriodMonth));
    }

    return reps.map(rep => {
      let totalRevenue = 0;
      let totalFunded = 0;
      let totalApps = 0;
      let totalApproved = 0;
      let totalCalls = 0;
      let totalSms = 0;
      let totalTalkTime = 0;
      let activeMonths = 0;
      let bestMonth = { month: '', revenue: 0 };
      const monthlyBreakdown = [];

      monthNames.forEach((month, monthIdx) => {
        const repData = allYearlyData[month]?.[rep];
        if (!repData) {
          monthlyBreakdown.push({ month, ...emptyMonth() });
          return;
        }

        // Build monthly breakdown for all months (for table display)
        monthlyBreakdown.push({
          month,
          revenue: repData.revenue,
          funded: repData.funded,
          apps: repData.appsIn,
          approved: repData.approved,
          calls: repData.calls,
          sms: repData.sms,
          talkTime: repData.talkTime,
          conversionRate: repData.appToFunded
        });

        // Only sum totals for months in the selected period
        if (!monthsToInclude.includes(monthIdx)) return;

        // Count as active month if there's any revenue
        if (repData.revenue > 0) {
          activeMonths++;
          if (repData.revenue > bestMonth.revenue) {
            bestMonth = { month, revenue: repData.revenue };
          }
        }

        totalRevenue += repData.revenue || 0;
        totalFunded += repData.funded || 0;
        totalApps += repData.appsIn || 0;
        totalApproved += repData.approved || 0;
        totalCalls += repData.calls || 0;
        totalSms += repData.sms || 0;
        totalTalkTime += repData.talkTime || 0;
      });

      // DEBUG: Log rep totals
      // Phase 3: Calculate goals for this specific rep
      const repGoals = getGoalsForPeriod(
        goals,
        periodType,
        selectedYear,
        selectedPeriodMonth,
        selectedQuarter,
        selectedWeek,
        rep // specific rep name
      );

      return {
        rep,
        totalRevenue,
        totalFunded,
        totalApps,
        totalApproved,
        totalCalls,
        totalSms,
        totalTalkTime,
        activeMonths,
        bestMonth,
        avgDealSize: totalFunded > 0 ? totalRevenue / totalFunded : 0,
        avgConversion: totalApps > 0 ? (totalFunded / totalApps * 100) : 0,
        monthlyBreakdown,
        goals: repGoals // Phase 3: Add goals
      };
    });
  }, [allYearlyData, reps, periodType, selectedQuarter, selectedPeriodMonth, selectedWeek, selectedYear, goals]);

  // Revenue comparison data for multi-line chart (Feb 2, 2026)
  // Transforms monthly data to show each rep as a separate series
  const repComparisonChartData = React.useMemo(() => {
    if (!periodFilteredTotals?.chartData || !yearlyRepMetrics) return [];

    return periodFilteredTotals.chartData.map(month => {
      const monthLabel = month.label;
      const dataPoint = { label: monthLabel };

      // Calculate team average (exclude reps with 0 revenue)
      const activeReps = yearlyRepMetrics.filter(r => r.totalRevenue > 0);
      dataPoint['Team Avg'] = activeReps.length > 0
        ? Math.round(month.revenue / activeReps.length)
        : 0;

      // Add each rep's revenue for this month
      yearlyRepMetrics.forEach(rep => {
        const firstName = rep.rep.split(' ')[0];
        const repMonth = rep.monthlyBreakdown?.find(m =>
          m.month?.substring(0, 3) === monthLabel
        );
        dataPoint[firstName] = repMonth?.revenue || 0;
      });

      return dataPoint;
    });
  }, [periodFilteredTotals, yearlyRepMetrics]);

  // Rep-specific breakdown data with selected granularity (Feb 4, 2026)
  const repBreakdownData = useMemo(() => {
    if (!data || !data.tabs || !selectedRep || selectedRep === 'All Reps') return [];

    const resolvedGran = resolveGranularity(displayGranularity, periodType);
    const { start, end } = getPeriodDateRange(periodType, selectedYear, selectedPeriodMonth, selectedQuarter, selectedWeek);
    if (!start || !end) return [];

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Filter data for selected rep within date range
    const filterRepData = (tabData) => {
      return (tabData?.data || []).filter(r => {
        if (r.Rep !== selectedRep) return false;
        const dateStr = r.Date;
        if (!dateStr) return false;
        const rowDate = new Date(dateStr);
        if (isNaN(rowDate.getTime())) return false;
        return rowDate >= start && rowDate <= end;
      });
    };

    const filterRepFunded = (tabData) => {
      return (tabData?.data || []).filter(r => {
        if (r.Rep !== selectedRep) return false;
        const dateStr = r.csbs__Funded_Date__c || r.Date;
        if (!dateStr) return false;
        const rowDate = new Date(dateStr);
        if (isNaN(rowDate.getTime())) return false;
        return rowDate >= start && rowDate <= end;
      });
    };

    const calls = filterRepData({ data: fullDialsData });
    const sms = filterRepData({ data: fullSmsData });
    const appsIn = filterRepData(data.tabs.Apps_In);
    const approved = filterRepData(data.tabs.Approved);
    const funded = filterRepFunded(data.tabs.Funded).filter(r => parseFloat(r.Amount) > 0 || parseFloat(r.Revenue) > 0);
    const revenue = filterRepFunded(data.tabs.Revenue).filter(r => parseFloat(r.Revenue) > 0);

    // Helper to check if record is in range
    const isInRange = (r, rangeStart, rangeEnd, isFundedOrRevenue = false) => {
      const dateStr = isFundedOrRevenue ? (r.csbs__Funded_Date__c || r.Date) : r.Date;
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return false;
      return d >= rangeStart && d <= rangeEnd;
    };

    const breakdown = [];

    if (resolvedGran === 'month') {
      // Monthly breakdown
      let currentMonth = new Date(start.getFullYear(), start.getMonth(), 1);
      while (currentMonth <= end) {
        const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0, 23, 59, 59, 999);
        const effectiveEnd = monthEnd > end ? end : monthEnd;
        const effectiveStart = currentMonth < start ? start : currentMonth;

        const mRevenue = revenue.filter(r => isInRange(r, effectiveStart, effectiveEnd, true))
          .reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
        const mFunded = funded.filter(r => isInRange(r, effectiveStart, effectiveEnd, true)).length;
        const mApps = appsIn.filter(r => isInRange(r, effectiveStart, effectiveEnd, false)).length;
        const mCalls = calls.filter(r => isInRange(r, effectiveStart, effectiveEnd, false)).reduce((s,r)=>s+(parseInt(r.TotalCalls)||0),0);
        const mSms = sms.filter(r => isInRange(r, effectiveStart, effectiveEnd, false))
          .reduce((sum, r) => sum + (parseInt(r.TotalSMS) || 0), 0);

        breakdown.push({
          label: `${monthNames[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`,
          revenue: mRevenue,
          funded: mFunded,
          apps: mApps,
          calls: mCalls,
          sms: mSms,
          conversionRate: mApps > 0 ? (mFunded / mApps * 100) : 0
        });

        currentMonth.setMonth(currentMonth.getMonth() + 1);
      }
    } else if (resolvedGran === 'week') {
      // Weekly breakdown
      let weekStart = new Date(start);
      let weekNum = 1;

      while (weekStart <= end) {
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        if (weekEnd > end) weekEnd.setTime(end.getTime());
        weekEnd.setHours(23, 59, 59, 999);

        const wRevenue = revenue.filter(r => isInRange(r, weekStart, weekEnd, true))
          .reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
        const wFunded = funded.filter(r => isInRange(r, weekStart, weekEnd, true)).length;
        const wApps = appsIn.filter(r => isInRange(r, weekStart, weekEnd, false)).length;
        const wCalls = calls.filter(r => isInRange(r, weekStart, weekEnd, false)).reduce((s,r)=>s+(parseInt(r.TotalCalls)||0),0);
        const wSms = sms.filter(r => isInRange(r, weekStart, weekEnd, false))
          .reduce((sum, r) => sum + (parseInt(r.TotalSMS) || 0), 0);

        breakdown.push({
          label: `W${weekNum}`,
          revenue: wRevenue,
          funded: wFunded,
          apps: wApps,
          calls: wCalls,
          sms: wSms,
          conversionRate: wApps > 0 ? (wFunded / wApps * 100) : 0
        });

        weekStart.setDate(weekStart.getDate() + 7);
        weekNum++;
      }
    } else if (resolvedGran === 'day') {
      // Daily breakdown (max 60 days)
      let currentDay = new Date(start);
      let dayCount = 0;
      const maxDays = 60;

      while (currentDay <= end && dayCount < maxDays) {
        const dayEnd = new Date(currentDay);
        dayEnd.setHours(23, 59, 59, 999);

        const dRevenue = revenue.filter(r => isInRange(r, currentDay, dayEnd, true))
          .reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
        const dFunded = funded.filter(r => isInRange(r, currentDay, dayEnd, true)).length;
        const dApps = appsIn.filter(r => isInRange(r, currentDay, dayEnd, false)).length;
        const dCalls = calls.filter(r => isInRange(r, currentDay, dayEnd, false)).reduce((s,r)=>s+(parseInt(r.TotalCalls)||0),0);
        const dSms = sms.filter(r => isInRange(r, currentDay, dayEnd, false))
          .reduce((sum, r) => sum + (parseInt(r.TotalSMS) || 0), 0);

        breakdown.push({
          label: `${dayNames[currentDay.getDay()]} ${currentDay.getDate()}`,
          revenue: dRevenue,
          funded: dFunded,
          apps: dApps,
          calls: dCalls,
          sms: dSms,
          conversionRate: dApps > 0 ? (dFunded / dApps * 100) : 0
        });

        currentDay.setDate(currentDay.getDate() + 1);
        dayCount++;
      }
    }

    return breakdown;
  }, [data, selectedRep, periodType, selectedYear, selectedPeriodMonth, selectedQuarter, selectedWeek, displayGranularity, getPeriodDateRange, resolveGranularity, fullDialsData, fullSmsData]);

  if (standaloneBookMode ? bookSfLoading : (loading || goalsLoading)) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>{standaloneBookMode ? 'Loading Book data...' : 'Loading dashboard data...'}</p>
      </div>
    );
  }

  if (standaloneBookMode ? bookSfError : error) {
    return (
      <div className="error-container">
        <h2>Error Loading Data</h2>
        <p>Salesforce Data: {standaloneBookMode ? bookSfError : error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }


  return (
    <div className="dashboard">
      {!standaloneMode && <header className="dashboard-header">
        <h1>📊 Sales Performance Dashboard</h1>
        <div className="header-controls" style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end'
          }}>
            <span style={{fontSize: '11px', color: '#64748b', fontWeight: 500}}>LAST UPDATED</span>
            <span style={{fontSize: '13px', color: '#1e293b', fontWeight: 600}}>{formatLastUpdated(lastUpdated)}</span>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            style={{
              padding: '6px 12px',
              backgroundColor: isRefreshing ? '#94a3b8' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: isRefreshing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap'
            }}
          >
            <span style={{fontSize: '16px'}}>{isRefreshing ? '⏳' : '🔄'}</span>
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <label className="dev-mode-toggle" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '12px',
            color: developerMode ? '#dc2626' : '#64748b',
            fontWeight: developerMode ? 600 : 500,
            cursor: 'pointer',
            padding: '6px 10px',
            backgroundColor: developerMode ? '#fef2f2' : 'transparent',
            borderRadius: '4px',
            border: developerMode ? '1px solid #fecaca' : '1px solid transparent'
          }}>
            <input
              type="checkbox"
              checked={developerMode}
              onChange={(e) => {
                const newValue = e.target.checked;
                setDeveloperMode(newValue);
                localStorage.setItem('dashboardDevMode', newValue.toString());
              }}
              style={{ cursor: 'pointer' }}
            />
            Dev Mode
          </label>
          <select className="rep-dropdown-mobile" value={selectedRep} onChange={(e) => setSelectedRep(e.target.value)}>
            <option value="All Reps">All Reps</option>
            {reps.map(rep => (
              <option key={rep} value={rep}>{rep.split(' ')[0]}</option>
            ))}
          </select>
        </div>
      </header>}

      {!standaloneMode && <nav className="tab-navigation" style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '48px',
        padding: '0 20px',
        background: '#f8fafc',
        borderBottom: '1px solid #e2e8f0'
      }}>
        {/* Goal Tracking Tab */}
        <button
          onClick={() => setActiveTab('goal-tracking')}
          style={{
            padding: '14px 0',
            border: 'none',
            borderBottom: activeTab === 'goal-tracking' ? '3px solid #3b82f6' : '3px solid transparent',
            background: 'transparent',
            fontSize: '14px',
            fontWeight: activeTab === 'goal-tracking' ? 600 : 500,
            cursor: 'pointer',
            transition: 'all 0.2s',
            color: activeTab === 'goal-tracking' ? '#1e293b' : '#64748b',
            textAlign: 'center'
          }}
        >
          Goal Tracking
        </button>

        {/* Legacy Dropdown (Jan 27, 2026) - Contains: Weekly, Monthly, Historical, Email, Yearly Review */}
        {/* Feb 2, 2026: Only show Legacy menu when Dev Mode is enabled */}
        {developerMode && (
        <div style={{ position: 'relative' }} className="legacy-dropdown">
          <button
            onClick={() => setLegacyDropdownOpen(!legacyDropdownOpen)}
            style={{
              padding: '14px 0',
              border: 'none',
              borderBottom: isLegacyTabActive ? '3px solid #3b82f6' : '3px solid transparent',
              background: 'transparent',
              fontSize: '14px',
              fontWeight: isLegacyTabActive ? 600 : 500,
              cursor: 'pointer',
              transition: 'all 0.2s',
              color: isLegacyTabActive ? '#1e293b' : '#64748b',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            {isLegacyTabActive ? `Legacy (${getLegacyTabLabel(activeTab)})` : 'Legacy'}
            <span style={{ fontSize: '10px' }}>{legacyDropdownOpen ? '▲' : '▼'}</span>
          </button>
          {legacyDropdownOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              marginTop: '0',
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: '0 0 8px 8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              minWidth: '200px',
              zIndex: 1000,
              overflow: 'hidden'
            }}>
              {/* Weekly */}
              <button
                onClick={() => { setActiveTab('weekly'); setLegacyDropdownOpen(false); }}
                style={{ display: 'block', width: '100%', padding: '12px 16px', border: 'none', background: activeTab === 'weekly' ? '#f0f9ff' : 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '14px', color: '#374151' }}
                onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                onMouseLeave={(e) => e.target.style.background = activeTab === 'weekly' ? '#f0f9ff' : 'transparent'}
              >
                📅 Weekly
              </button>
              {/* Monthly */}
              <button
                onClick={() => { setActiveTab('monthly'); setLegacyDropdownOpen(false); }}
                style={{ display: 'block', width: '100%', padding: '12px 16px', border: 'none', background: activeTab === 'monthly' ? '#f0f9ff' : 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '14px', color: '#374151' }}
                onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                onMouseLeave={(e) => e.target.style.background = activeTab === 'monthly' ? '#f0f9ff' : 'transparent'}
              >
                📆 Monthly
              </button>
              {/* Historical with sub-menu */}
              <div style={{ borderTop: '1px solid #e2e8f0' }}>
                <button
                  onClick={() => { setActiveTab('historical'); setActiveHistoricalTab('rep-vs-rep'); setLegacyDropdownOpen(false); }}
                  style={{ display: 'block', width: '100%', padding: '12px 16px', border: 'none', background: activeTab === 'historical' ? '#f0f9ff' : 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '14px', color: '#374151' }}
                  onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.background = activeTab === 'historical' ? '#f0f9ff' : 'transparent'}
                >
                  📊 Historical - Rep v Rep
                </button>
                <button
                  onClick={() => { setActiveTab('historical'); setActiveHistoricalTab('single-rep'); setLegacyDropdownOpen(false); }}
                  style={{ display: 'block', width: '100%', padding: '12px 16px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '14px', color: '#64748b', paddingLeft: '28px' }}
                  onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  → Single Rep Deep-Dive
                </button>
                <button
                  onClick={() => { setActiveTab('historical'); setActiveHistoricalTab('team-snapshot'); setLegacyDropdownOpen(false); }}
                  style={{ display: 'block', width: '100%', padding: '12px 16px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '14px', color: '#64748b', paddingLeft: '28px' }}
                  onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  → Team Snapshot
                </button>
              </div>
              {/* Email with sub-menu */}
              <div style={{ borderTop: '1px solid #e2e8f0' }}>
                <button
                  onClick={() => { setActiveTab('email'); setActiveEmailTab('today'); setLegacyDropdownOpen(false); }}
                  style={{ display: 'block', width: '100%', padding: '12px 16px', border: 'none', background: activeTab === 'email' ? '#f0f9ff' : 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '14px', color: '#374151' }}
                  onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.background = activeTab === 'email' ? '#f0f9ff' : 'transparent'}
                >
                  📧 Email - Today
                </button>
                <button
                  onClick={() => { setActiveTab('email'); setActiveEmailTab('weekly-trends'); setLegacyDropdownOpen(false); }}
                  style={{ display: 'block', width: '100%', padding: '12px 16px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '14px', color: '#64748b', paddingLeft: '28px' }}
                  onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  → Weekly Trends
                </button>
                <button
                  onClick={() => { setActiveTab('email'); setActiveEmailTab('rep-comparison'); setLegacyDropdownOpen(false); }}
                  style={{ display: 'block', width: '100%', padding: '12px 16px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '14px', color: '#64748b', paddingLeft: '28px' }}
                  onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  → Rep Comparison
                </button>
              </div>
              {/* Yearly Review with sub-menu */}
              <div style={{ borderTop: '1px solid #e2e8f0' }}>
                <button
                  onClick={() => { setActiveTab('yearly-review'); setActiveYearlyTab('team-review'); setLegacyDropdownOpen(false); }}
                  style={{ display: 'block', width: '100%', padding: '12px 16px', border: 'none', background: activeTab === 'yearly-review' ? '#f0f9ff' : 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '14px', color: '#374151' }}
                  onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.background = activeTab === 'yearly-review' ? '#f0f9ff' : 'transparent'}
                >
                  📅 Yearly Review - Team
                </button>
                <button
                  onClick={() => { setActiveTab('yearly-review'); setActiveYearlyTab('rep-review'); setLegacyDropdownOpen(false); }}
                  style={{ display: 'block', width: '100%', padding: '12px 16px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', fontSize: '14px', color: '#64748b', paddingLeft: '28px' }}
                  onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.target.style.background = 'transparent'}
                >
                  → Rep Review
                </button>
              </div>
            </div>
          )}
          <style>{`
            .legacy-dropdown:hover .legacy-dropdown-menu { display: block !important; }
            .tab-navigation button:hover { color: #1e293b !important; }
          `}</style>
        </div>
        )}

        {/* Book Tab — removed from nav; accessible only via ?tab=book&standalone=1 */}

        {/* Activity Tab */}
        <button
          onClick={() => setActiveTab('activity')}
          style={{
            padding: '14px 0',
            border: 'none',
            borderBottom: activeTab === 'activity' ? '3px solid #3b82f6' : '3px solid transparent',
            background: 'transparent',
            fontSize: '14px',
            fontWeight: activeTab === 'activity' ? 600 : 500,
            cursor: 'pointer',
            transition: 'all 0.2s',
            color: activeTab === 'activity' ? '#1e293b' : '#64748b',
            textAlign: 'center'
          }}
        >
          Activity
        </button>

        {/* Funnel Tab */}
        <button
          onClick={() => setActiveTab('funnel')}
          style={{
            padding: '14px 0',
            border: 'none',
            borderBottom: activeTab === 'funnel' ? '3px solid #3b82f6' : '3px solid transparent',
            background: 'transparent',
            fontSize: '14px',
            fontWeight: activeTab === 'funnel' ? 600 : 500,
            cursor: 'pointer',
            transition: 'all 0.2s',
            color: activeTab === 'funnel' ? '#1e293b' : '#64748b',
            textAlign: 'center'
          }}
        >
          Funnel
        </button>

        {/* Reviews Tab (Feb 2, 2026) - Promotes Yearly Review from Legacy dropdown */}
        <button
          onClick={() => setActiveTab('yearly-review')}
          style={{
            padding: '14px 0',
            border: 'none',
            borderBottom: activeTab === 'yearly-review' ? '3px solid #3b82f6' : '3px solid transparent',
            background: 'transparent',
            fontSize: '14px',
            fontWeight: activeTab === 'yearly-review' ? 600 : 500,
            cursor: 'pointer',
            transition: 'all 0.2s',
            color: activeTab === 'yearly-review' ? '#1e293b' : '#64748b',
            textAlign: 'center'
          }}
        >
          Reviews
        </button>

        {/* Lender Analytics Tab (renamed from Reviews - Feb 1, 2026) */}
        <button
          onClick={() => setActiveTab('reviews')}
          style={{
            padding: '14px 0',
            border: 'none',
            borderBottom: activeTab === 'reviews' ? '3px solid #3b82f6' : '3px solid transparent',
            background: 'transparent',
            fontSize: '14px',
            fontWeight: activeTab === 'reviews' ? 600 : 500,
            cursor: 'pointer',
            transition: 'all 0.2s',
            color: activeTab === 'reviews' ? '#1e293b' : '#64748b',
            textAlign: 'center'
          }}
        >
          Lender Analytics
        </button>
        {/* MOVED TO FIRST POSITION AS "Overview" (Jan 15, 2026)
        <button
          onClick={() => setActiveTab('goal-tracking')}
          style={{
            padding: '14px 0',
            border: 'none',
            borderBottom: activeTab === 'goal-tracking' ? '3px solid #3b82f6' : '3px solid transparent',
            background: 'transparent',
            fontSize: '14px',
            fontWeight: activeTab === 'goal-tracking' ? 600 : 500,
            cursor: 'pointer',
            transition: 'all 0.2s',
            color: activeTab === 'goal-tracking' ? '#1e293b' : '#64748b',
            textAlign: 'center'
          }}
        >
          Goal Tracking
        </button>
        */}
      </nav>}

      {/* Sub-tab navigation for Weekly */}
      {activeTab === 'weekly' && (
        <nav className="sub-tab-navigation" style={{
          backgroundColor: '#f8fafc',
          borderBottom: '2px solid #e2e8f0',
          padding: '8px 20px',
          display: 'flex',
          gap: '12px'
        }}>
          <button
            className={activeWeeklyTab === 'this-week' ? 'sub-tab-active' : 'sub-tab'}
            onClick={() => setActiveWeeklyTab('this-week')}
            style={{
              padding: '8px 16px',
              backgroundColor: activeWeeklyTab === 'this-week' ? '#3b82f6' : 'transparent',
              color: activeWeeklyTab === 'this-week' ? 'white' : '#64748b',
              border: activeWeeklyTab === 'this-week' ? 'none' : '1px solid #cbd5e1',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            This Week
          </button>
          <button
            className={activeWeeklyTab === 'last-week' ? 'sub-tab-active' : 'sub-tab'}
            onClick={() => setActiveWeeklyTab('last-week')}
            style={{
              padding: '8px 16px',
              backgroundColor: activeWeeklyTab === 'last-week' ? '#3b82f6' : 'transparent',
              color: activeWeeklyTab === 'last-week' ? 'white' : '#64748b',
              border: activeWeeklyTab === 'last-week' ? 'none' : '1px solid #cbd5e1',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Last Week
          </button>
        </nav>
      )}

      {/* Sub-tab navigation for Monthly */}
      {activeTab === 'monthly' && (
        <nav className="sub-tab-navigation" style={{
          backgroundColor: '#f8fafc',
          borderBottom: '2px solid #e2e8f0',
          padding: '8px 20px',
          display: 'flex',
          gap: '12px'
        }}>
          <button
            className={activeMonthlyTab === 'this-month' ? 'sub-tab-active' : 'sub-tab'}
            onClick={() => setActiveMonthlyTab('this-month')}
            style={{
              padding: '8px 16px',
              backgroundColor: activeMonthlyTab === 'this-month' ? '#3b82f6' : 'transparent',
              color: activeMonthlyTab === 'this-month' ? 'white' : '#64748b',
              border: activeMonthlyTab === 'this-month' ? 'none' : '1px solid #cbd5e1',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            This Month ({currentMonth})
          </button>
          <button
            className={activeMonthlyTab === 'last-month' ? 'sub-tab-active' : 'sub-tab'}
            onClick={() => setActiveMonthlyTab('last-month')}
            style={{
              padding: '8px 16px',
              backgroundColor: activeMonthlyTab === 'last-month' ? '#3b82f6' : 'transparent',
              color: activeMonthlyTab === 'last-month' ? 'white' : '#64748b',
              border: activeMonthlyTab === 'last-month' ? 'none' : '1px solid #cbd5e1',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Last Month ({lastMonth})
          </button>
        </nav>
      )}

      <main className="dashboard-content">
        {/* ✨ Overview Tab - Redesign Dec 29, 2025 */}
        {activeTab === 'overview' && useNewOverview && (
          <OverviewTabRedesign
            data={data}
            goals={goals}
            reps={reps}
            month={currentMonth}
            selectedRep={selectedRep}
            setSelectedRep={setSelectedRep}
            developerMode={developerMode}
          />
        )}

        {/* Goal Tracking Tab - Redesigned Overview (Jan 14, 2026) */}
        {activeTab === 'goal-tracking' && (
          <GoalTrackingTab
            data={dataWithFullActivity}
            goals={goals}
            reps={reps}
            month={currentMonth}
            year={new Date().getFullYear()}
            monthYear={currentMonthYear}
            selectedRep={selectedRep}
            setSelectedRep={setSelectedRep}
            developerMode={developerMode}
            activityAggregatesDaily={(activityAggregates && activityAggregates.daily) ? activityAggregates.daily : []}
          />
        )}

        {/* OLD Overview Tab - kept for reference/fallback (toggle useNewOverview to false to use) */}
        {activeTab === 'overview' && !useNewOverview && (
          <div className="overview-tab">
            <div style={{
              backgroundColor: '#f8fafc',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '24px',
              borderLeft: '4px solid #3b82f6'
            }}>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', color: '#1e293b' }}>
                Welcome to the Renewals Sales Dashboard
              </h2>
              <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
                Track your month-to-date performance, see what you need to hit your goals, and get actionable insights to improve.
              </p>
            </div>

            {/* Revenue Pacing Chart - C-1 */}
            {overviewDailyRevenuePacing && overviewDailyRevenuePacing.length > 0 && (
              <div style={{
                padding: '20px',
                background: '#fff',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                marginBottom: '24px',
                marginTop: '24px',
                position: 'relative'
              }}>
                {developerMode && <div className="dev-chart-label">C-1</div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>📈 Revenue Pacing</h3>
                </div>
                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px', marginTop: '4px' }}>
                  Cumulative revenue vs expected daily trajectory to goal
                </p>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={overviewDailyRevenuePacing}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="#64748b" />
                    <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} stroke="#64748b" />
                    <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, '']} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="expected"
                      stroke="#94a3b8"
                      strokeDasharray="5 5"
                      name="Target Pace"
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="actual"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      name="Actual"
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}


            <MTDReviewCard
              data={data}
              goals={goals}
              reps={reps}
              month={currentMonth}
              selectedView={selectedRep}
              setSelectedView={setSelectedRep}
              developerMode={developerMode}
            />

            {/* Activity Boost Insight Panel - K-4 */}
            {overviewRepGoals && (() => {
              const businessDaysElapsed = getOverviewBusinessDaysElapsed();
              const businessDaysInMonth = getOverviewBusinessDaysInMonth();
              const remainingBusinessDays = businessDaysInMonth - businessDaysElapsed;
              const projectedCalls = businessDaysElapsed > 0 ? (overviewMetrics.calls / businessDaysElapsed) * businessDaysInMonth : overviewMetrics.calls;
              const projectedApps = businessDaysElapsed > 0 ? (overviewMetrics.appsIn / businessDaysElapsed) * businessDaysInMonth : overviewMetrics.appsIn;
              const boostedCalls = projectedCalls * 1.2;
              const appsGoal = overviewRepGoals.apps || 0;
              const approvedGoal = overviewRepGoals.approved || 0;
              const appsToApprovedRate = projectedApps > 0 ? (approvedGoal / projectedApps * 100) : 0;
              const appsToApprovedRateBoosted = (projectedApps * 1.2) > 0 ? (approvedGoal / (projectedApps * 1.2) * 100) : 0;
              const numReps = reps.length;
              const extraCallsPerDay = remainingBusinessDays > 0 ? ((projectedCalls * 0.20) / remainingBusinessDays / numReps) : 0;
              return (
                <div style={{
                  marginTop: '24px',
                  padding: '20px',
                  backgroundColor: '#f0f9ff',
                  borderRadius: '12px',
                  border: '2px solid #3b82f6',
                  position: 'relative'
                }}>
                  {developerMode && <div className="dev-card-label">K-4</div>}
                  <h4 style={{ marginBottom: '12px', fontSize: '18px', fontWeight: 700, color: '#1e40af' }}>
                    💪 ACTIVITY BOOST INSIGHT
                  </h4>
                  <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#1e293b' }}>
                    <p style={{ margin: '0 0 12px 0' }}>
                      <strong>Current:</strong> Projected {Math.round(projectedCalls).toLocaleString()} calls by month end
                    </p>
                    <p style={{ margin: '0 0 12px 0', padding: '12px', backgroundColor: '#ecfdf5', borderRadius: '6px', border: '1px solid #10b981' }}>
                      <strong style={{ color: '#065f46' }}>🚀 +20% Activity Path:</strong> +{extraCallsPerDay.toFixed(0)} calls/rep/day → {Math.round(boostedCalls).toLocaleString()} calls → Easier conversions!
                    </p>
                    <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
                      Example: Apps→Approved needs {appsToApprovedRate.toFixed(1)}% now, but only {appsToApprovedRateBoosted.toFixed(1)}% with +20% activity
                    </p>
                  </div>
                </div>
              );
            })()}


            {/* ✨ NEW (Nov 17, 2025): Trending Analysis Components */}
            {data && data.tabs && (
              <>
                <FunnelStoryCard
                  data={data}
                  selectedRep={selectedRep}
                  month={currentMonth}
                  reps={reps}
                  developerMode={developerMode}
                />

                <ActivityTrendsCard
                  data={data}
                  selectedRep={selectedRep}
                  month={currentMonth}
                  reps={reps}
                  developerMode={developerMode}
                />

                <DiagnosisCard
                  data={data}
                  selectedRep={selectedRep}
                  month={currentMonth}
                  reps={reps}
                  developerMode={developerMode}
                />
              </>
            )}
          </div>
        )}

        {activeTab === 'weekly' && activeWeeklyTab === 'this-week' && (
          <ThisWeekTab
            data={data}
            goals={goals}
            selectedRep={selectedRep}
            reps={reps}
            developerMode={developerMode}
            year={currentYear}
            monthYear={currentMonthYear}
          />
        )}
        {activeTab === 'weekly' && activeWeeklyTab === 'last-week' && (
          <Suspense fallback={<LoadingFallback />}>
            <LastWeekTab
              data={data}
              goals={goals}
              selectedRep={selectedRep}
              reps={reps}
              developerMode={developerMode}
              year={currentYear}
              monthYear={currentMonthYear}
            />
          </Suspense>
        )}
        {activeTab === 'monthly' && activeMonthlyTab === 'this-month' && (
          <ThisMonthTab
            data={data}
            goals={goals}
            selectedRep={selectedRep}
            month={currentMonth}
            year={currentYear}
            monthYear={currentMonthYear}
            reps={currentMonthReps}
            developerMode={developerMode}
          />
        )}
        {activeTab === 'monthly' && activeMonthlyTab === 'last-month' && (
          <LastMonthTab
            data={data}
            goals={goals}
            selectedRep={selectedRep}
            month={lastMonth}
            year={lastMonthDate.getFullYear()}
            monthYear={lastMonthYear}
            reps={reps}
            developerMode={developerMode}
          />
        )}
        {activeTab === 'historical' && (
          <HistoricalTab
            data={data}
            selectedRep={selectedRep}
            reps={reps}
            goals={goals}
            activeHistoricalTab={activeHistoricalTab}
            setActiveHistoricalTab={setActiveHistoricalTab}
            developerMode={developerMode}
          />
        )}
        {/* NEW (Dec 2025): Email Analytics Tab */}
        {activeTab === 'email' && (
          apolloEmailLoading ? (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', minHeight: '400px', padding: '40px'
            }}>
              <div style={{
                width: '40px', height: '40px', border: '4px solid #e2e8f0',
                borderTopColor: '#3b82f6', borderRadius: '50%',
                animation: 'spin 1s linear infinite', marginBottom: '16px'
              }} />
              <div style={{ fontSize: '16px', fontWeight: 500, color: '#64748b' }}>
                Loading email data...
              </div>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : apolloEmailError ? (
            <div style={{
              padding: '40px', textAlign: 'center', backgroundColor: '#fef2f2',
              borderRadius: '12px', margin: '20px 0', border: '1px solid #fecaca'
            }}>
              <h3 style={{ color: '#dc2626', marginBottom: '8px' }}>Error Loading Email Data</h3>
              <p style={{ color: '#7f1d1d', fontSize: '14px' }}>{apolloEmailError}</p>
            </div>
          ) : (
            <EmailAnalyticsTab
              apolloEmailData={apolloEmailData}
              selectedRep={selectedRep}
              reps={reps}
              activeEmailTab={activeEmailTab}
              setActiveEmailTab={setActiveEmailTab}
              developerMode={developerMode}
            />
          )
        )}
        {/* ==================== YEARLY REVIEW TAB ==================== */}
        {activeTab === 'yearly-review' && (
          <div className="reviews-tab-container" style={{ padding: '0 24px 24px 24px' }}>

            {/* Tab Header */}
            <div className="reviews-header" style={{ marginBottom: '24px' }}>
              <h1 style={{
                fontSize: '28px',
                fontWeight: 700,
                color: '#1e293b',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                📊 {periodFilteredTotals?.periodLabel || `${selectedYear}`} Review
              </h1>
              <p style={{ color: '#64748b', fontSize: '15px', margin: 0 }}>
                {activeYearlyTab === 'team-review'
                  ? 'Team performance summary and metrics'
                  : 'Individual rep deep-dive and growth analysis'}
              </p>
            </div>

            {/* Period Selector (Phase 2 - Feb 2026) */}
            <PeriodSelector
              periodType={periodType}
              setPeriodType={setPeriodType}
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
              selectedPeriodMonth={selectedPeriodMonth}
              setSelectedPeriodMonth={setSelectedPeriodMonth}
              selectedQuarter={selectedQuarter}
              setSelectedQuarter={setSelectedQuarter}
              selectedWeek={selectedWeek}
              setSelectedWeek={setSelectedWeek}
            />

            {/* Granularity Selector (Feb 4, 2026) */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px',
              padding: '12px 16px',
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                Group by:
              </span>
              {[
                { value: 'auto', label: 'Auto' },
                { value: 'day', label: 'Day' },
                { value: 'week', label: 'Week' },
                { value: 'month', label: 'Month' }
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setDisplayGranularity(opt.value)}
                  style={{
                    padding: '6px 16px',
                    borderRadius: '6px',
                    border: displayGranularity === opt.value ? '2px solid #3b82f6' : '1px solid #d1d5db',
                    background: displayGranularity === opt.value ? '#eff6ff' : 'white',
                    color: displayGranularity === opt.value ? '#1d4ed8' : '#6b7280',
                    fontWeight: displayGranularity === opt.value ? 600 : 500,
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  {opt.label}
                </button>
              ))}
              {displayGranularity === 'auto' && (
                <span style={{ fontSize: '13px', color: '#9ca3af', marginLeft: '4px' }}>
                  ({periodType === 'year' ? 'Monthly' : periodType === 'quarter' ? 'Weekly' : periodType === 'month' ? 'Weekly' : 'Daily'})
                </span>
              )}
            </div>

            {/* Sub-tab Toggle Buttons */}
            <div className="reviews-sub-tabs" style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '24px'
            }}>
              <button
                onClick={() => setActiveYearlyTab('team-review')}
                style={{
                  padding: '12px 28px',
                  borderRadius: '10px',
                  border: 'none',
                  backgroundColor: activeYearlyTab === 'team-review' ? '#10b981' : '#f1f5f9',
                  color: activeYearlyTab === 'team-review' ? 'white' : '#64748b',
                  fontWeight: 600,
                  fontSize: '15px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  boxShadow: activeYearlyTab === 'team-review' ? '0 4px 12px rgba(16, 185, 129, 0.3)' : 'none'
                }}
              >
                📊 Team Review
              </button>
              <button
                onClick={() => setActiveYearlyTab('rep-review')}
                style={{
                  padding: '12px 28px',
                  borderRadius: '10px',
                  border: 'none',
                  backgroundColor: activeYearlyTab === 'rep-review' ? '#3b82f6' : '#f1f5f9',
                  color: activeYearlyTab === 'rep-review' ? 'white' : '#64748b',
                  fontWeight: 600,
                  fontSize: '15px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease',
                  boxShadow: activeYearlyTab === 'rep-review' ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none'
                }}
              >
                👤 Rep Review
              </button>
            </div>

            {/* ========== TEAM REVIEW SUB-TAB ========== */}
            {activeYearlyTab === 'team-review' && (
              <div className="reviews-team-section">

                {/* Quick Summary Cards - Gradient Style */}
                <div className="reviews-kpi-cards" style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 1fr)',
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  {/* Total Revenue */}
                  <div style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    borderRadius: '12px',
                    padding: '20px',
                    color: 'white'
                  }}>
                    <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Total Revenue</div>
                    <div style={{ fontSize: '28px', fontWeight: 700 }}>
                      ${periodFilteredTotals?.totalRevenue?.toLocaleString() || '0'}
                    </div>
                    <GoalProgressBar
                      current={periodFilteredTotals?.totalRevenue || 0}
                      goal={periodFilteredTotals?.goals?.revenueGoal || 0}
                      formatValue={(v) => `$${v.toLocaleString()}`}
                    />
                  </div>

                  {/* Total Funded */}
                  <div style={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    borderRadius: '12px',
                    padding: '20px',
                    color: 'white'
                  }}>
                    <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Total Funded Deals</div>
                    <div style={{ fontSize: '28px', fontWeight: 700 }}>
                      {periodFilteredTotals?.totalFunded?.toLocaleString() || '0'}
                    </div>
                    <GoalProgressBar
                      current={periodFilteredTotals?.totalFunded || 0}
                      goal={periodFilteredTotals?.goals?.fundedGoal || 0}
                      formatValue={(v) => v.toLocaleString()}
                    />
                  </div>

                  {/* Total Apps */}
                  <div style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    borderRadius: '12px',
                    padding: '20px',
                    color: 'white'
                  }}>
                    <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Total Apps In</div>
                    <div style={{ fontSize: '28px', fontWeight: 700 }}>
                      {periodFilteredTotals?.totalApps?.toLocaleString() || '0'}
                    </div>
                    <GoalProgressBar
                      current={periodFilteredTotals?.totalApps || 0}
                      goal={periodFilteredTotals?.goals?.appsGoal || 0}
                      formatValue={(v) => v.toLocaleString()}
                    />
                  </div>

                  {/* Avg Deal Size */}
                  <div style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: '12px',
                    padding: '20px',
                    color: 'white'
                  }}>
                    <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Avg Deal Size</div>
                    <div style={{ fontSize: '28px', fontWeight: 700 }}>
                      ${periodFilteredTotals?.avgDealSize?.toLocaleString(undefined, {maximumFractionDigits: 0}) || '0'}
                    </div>
                  </div>

                  {/* Team Conversion */}
                  <div style={{
                    background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                    borderRadius: '12px',
                    padding: '20px',
                    color: 'white'
                  }}>
                    <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Apps→Funded %</div>
                    <div style={{ fontSize: '28px', fontWeight: 700 }}>
                      {periodFilteredTotals?.avgConversion?.toFixed(1) || '0'}%
                    </div>
                  </div>
                </div>

                {/* Activity Summary Cards */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  {/* Total Calls */}
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#64748b', marginBottom: '8px' }}>📞 Total Calls</div>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b' }}>
                      {periodFilteredTotals?.totalCalls?.toLocaleString() || '0'}
                    </div>
                  </div>

                  {/* Total SMS */}
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#64748b', marginBottom: '8px' }}>💬 Total SMS</div>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b' }}>
                      {periodFilteredTotals?.totalSms?.toLocaleString() || '0'}
                    </div>
                  </div>

                  {/* Total Talk Time */}
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#64748b', marginBottom: '8px' }}>⏱️ Total Talk Time</div>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b' }}>
                      {Math.floor((periodFilteredTotals?.totalTalkTime || 0) / 60)}h {Math.round((periodFilteredTotals?.totalTalkTime || 0) % 60)}m
                    </div>
                  </div>

                  {/* Total Approved */}
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#64748b', marginBottom: '8px' }}>✅ Total Approved</div>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b' }}>
                      {periodFilteredTotals?.totalApproved?.toLocaleString() || '0'}
                    </div>
                  </div>
                </div>

                {/* Rep Performance Table */}
                <div className="reviews-rep-table-container" style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  padding: '20px',
                  marginBottom: '24px'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#1e293b',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    👥 Rep Performance Summary
                  </h3>

                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f3f4f6' }}>
                        <th style={{ textAlign: 'left', padding: '12px 8px', color: '#374151', fontSize: '14px', fontWeight: 700, borderBottom: '2px solid #e5e7eb' }}>Rep</th>
                        <th style={{ textAlign: 'right', padding: '12px 8px', color: '#374151', fontSize: '14px', fontWeight: 700, borderBottom: '2px solid #e5e7eb' }}>Revenue</th>
                        <th style={{ textAlign: 'right', padding: '12px 8px', color: '#374151', fontSize: '14px', fontWeight: 700, borderBottom: '2px solid #e5e7eb' }}>Funded</th>
                        <th style={{ textAlign: 'right', padding: '12px 8px', color: '#374151', fontSize: '14px', fontWeight: 700, borderBottom: '2px solid #e5e7eb' }}>Apps</th>
                        <th style={{ textAlign: 'right', padding: '12px 8px', color: '#374151', fontSize: '14px', fontWeight: 700, borderBottom: '2px solid #e5e7eb' }}>Calls</th>
                        <th style={{ textAlign: 'right', padding: '12px 8px', color: '#374151', fontSize: '14px', fontWeight: 700, borderBottom: '2px solid #e5e7eb' }}>Conv %</th>
                        <th style={{ textAlign: 'right', padding: '12px 8px', color: '#374151', fontSize: '14px', fontWeight: 700, borderBottom: '2px solid #e5e7eb' }}>Avg Deal</th>
                        <th style={{ textAlign: 'left', padding: '12px 8px', color: '#374151', fontSize: '14px', fontWeight: 700, borderBottom: '2px solid #e5e7eb' }}>Best Month</th>
                      </tr>
                    </thead>
                    <tbody>
                      {yearlyRepMetrics
                        ?.sort((a, b) => b.totalRevenue - a.totalRevenue)
                        ?.map((rep, idx) => (
                        <tr key={rep.rep} style={{
                          borderBottom: '1px solid #f1f5f9',
                          backgroundColor: idx === 0 ? '#fefce8' : 'transparent'
                        }}>
                          <td style={{ padding: '12px 8px', fontWeight: 600, color: '#1e293b' }}>
                            {idx === 0 && '🏆 '}{rep.rep?.split(' ')[0]}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', color: '#10b981', fontWeight: 600 }}>
                            ${rep.totalRevenue?.toLocaleString()}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', color: '#1e293b' }}>
                            {rep.totalFunded}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', color: '#1e293b' }}>
                            {rep.totalApps}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', color: '#1e293b' }}>
                            {rep.totalCalls?.toLocaleString()}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', color: '#1e293b' }}>
                            {rep.avgConversion?.toFixed(1)}%
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', color: '#1e293b' }}>
                            ${rep.avgDealSize?.toLocaleString(undefined, {maximumFractionDigits: 0})}
                          </td>
                          <td style={{ padding: '12px 8px', color: '#64748b', fontSize: '13px' }}>
                            {rep.bestMonth?.month ? `${rep.bestMonth.month} ($${(rep.bestMonth.revenue/1000).toFixed(0)}k)` : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* ========== MONTHLY TREND CHARTS ========== */}

                {/* Revenue & Funded Trend */}
                <div className="reviews-chart" style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  padding: '20px',
                  marginBottom: '24px'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#1e293b',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    💰 Revenue & Funded Deals
                  </h3>

                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={periodFilteredTotals?.chartData || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 13 }} interval={0} angle={periodType === 'quarter' ? -45 : 0} textAnchor={periodType === 'quarter' ? 'end' : 'middle'} height={periodType === 'quarter' ? 60 : 30} />
                      <YAxis
                        yAxisId="left"
                        tick={{ fill: '#64748b', fontSize: 13 }}
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: '#64748b', fontSize: 13 }}
                      />
                      <Tooltip
                        contentStyle={{ fontSize: '13px' }}
                        formatter={(value, name) => {
                          if (name === 'Revenue') return [`$${value.toLocaleString()}`, name];
                          return [value, name];
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: '13px' }} />
                      <Bar yAxisId="right" dataKey="funded" name="Funded Deals" fill="#8b5cf6" radius={[4, 4, 0, 0]}>
                        <LabelList dataKey="funded" position="top" style={{ fontSize: 12, fill: '#8b5cf6', fontWeight: 600 }} />
                      </Bar>
                      <Line yAxisId="left" type="monotone" dataKey="revenue" name="Revenue" stroke="#10b981" strokeWidth={3} dot={{ r: 4 }}>
                        <LabelList dataKey="revenue" position="top" formatter={(value) => `$${(value/1000).toFixed(0)}k`} style={{ fontSize: 12, fill: '#10b981', fontWeight: 600 }} />
                      </Line>
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                {/* Activity Trend - Calls & SMS */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  padding: '20px',
                  marginBottom: '24px'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#1e293b',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    📞 Activity Volume
                  </h3>

                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={periodFilteredTotals?.chartData || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 13 }} interval={0} angle={periodType === 'quarter' ? -45 : 0} textAnchor={periodType === 'quarter' ? 'end' : 'middle'} height={periodType === 'quarter' ? 60 : 30} />
                      <YAxis tick={{ fill: '#64748b', fontSize: 13 }} />
                      <Tooltip contentStyle={{ fontSize: '13px' }} />
                      <Legend wrapperStyle={{ fontSize: '13px' }} />
                      <Bar dataKey="calls" name="Calls" fill="#3b82f6" radius={[4, 4, 0, 0]}>
                        <LabelList dataKey="calls" position="top" style={{ fontSize: 12, fill: '#3b82f6', fontWeight: 600 }} />
                      </Bar>
                      <Bar dataKey="sms" name="SMS" fill="#f59e0b" radius={[4, 4, 0, 0]}>
                        <LabelList dataKey="sms" position="top" style={{ fontSize: 12, fill: '#f59e0b', fontWeight: 600 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Conversion Funnel Trend */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  padding: '20px',
                  marginBottom: '24px'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#1e293b',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    🔄 Funnel Performance
                  </h3>

                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={periodFilteredTotals?.chartData || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 13 }} interval={0} angle={periodType === 'quarter' ? -45 : 0} textAnchor={periodType === 'quarter' ? 'end' : 'middle'} height={periodType === 'quarter' ? 60 : 30} />
                      <YAxis tick={{ fill: '#64748b', fontSize: 13 }} />
                      <Tooltip contentStyle={{ fontSize: '13px' }} />
                      <Legend wrapperStyle={{ fontSize: '13px' }} />
                      <Line type="monotone" dataKey="apps" name="Apps In" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }}>
                        <LabelList dataKey="apps" position="top" style={{ fontSize: 12, fill: '#3b82f6', fontWeight: 600 }} />
                      </Line>
                      <Line type="monotone" dataKey="approved" name="Approved" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }}>
                        <LabelList dataKey="approved" position="top" style={{ fontSize: 12, fill: '#8b5cf6', fontWeight: 600 }} />
                      </Line>
                      <Line type="monotone" dataKey="funded" name="Funded" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }}>
                        <LabelList dataKey="funded" position="bottom" style={{ fontSize: 12, fill: '#10b981', fontWeight: 600 }} />
                      </Line>
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Conversion Rate Trend */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  padding: '20px',
                  marginBottom: '24px'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#1e293b',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    📈 Apps→Funded Conversion Rate %
                  </h3>

                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={periodFilteredTotals?.chartData || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 13 }} interval={0} angle={periodType === 'quarter' ? -45 : 0} textAnchor={periodType === 'quarter' ? 'end' : 'middle'} height={periodType === 'quarter' ? 60 : 30} />
                      <YAxis
                        tick={{ fill: '#64748b', fontSize: 13 }}
                        tickFormatter={(value) => `${value.toFixed(0)}%`}
                        domain={[0, 'auto']}
                      />
                      <Tooltip contentStyle={{ fontSize: '13px' }} formatter={(value) => [`${value.toFixed(1)}%`, 'Conversion Rate']} />
                      <Line
                        type="monotone"
                        dataKey="conversionRate"
                        name="Conversion Rate"
                        stroke="#06b6d4"
                        strokeWidth={3}
                        dot={{ r: 4, fill: '#06b6d4' }}
                      >
                        <LabelList dataKey="conversionRate" position="top" formatter={(value) => `${value.toFixed(1)}%`} style={{ fontSize: 12, fill: '#06b6d4', fontWeight: 600 }} />
                      </Line>
                    </LineChart>
                  </ResponsiveContainer>
                </div>

              </div>
            )}

            {/* ========== REP REVIEW SUB-TAB ========== */}
            {activeYearlyTab === 'rep-review' && (
              <div>

                {/* Rep Selector */}
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  padding: '20px',
                  marginBottom: '24px'
                }}>
                  <label style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#64748b',
                    marginRight: '12px'
                  }}>
                    Select Rep:
                  </label>
                  <select
                    value={selectedRep}
                    onChange={(e) => setSelectedRep(e.target.value)}
                    style={{
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#1e293b',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      minWidth: '200px'
                    }}
                  >
                    {reps.map(rep => (
                      <option key={rep} value={rep}>{rep}</option>
                    ))}
                  </select>
                </div>

                {/* Rep Summary Header */}
                {(() => {
                  const repData = yearlyRepMetrics?.find(r => r.rep === selectedRep);
                  if (!repData) return <div>No data available for {selectedRep}</div>;

                  // Find rep's rank
                  const sortedByRevenue = [...(yearlyRepMetrics || [])].sort((a, b) => b.totalRevenue - a.totalRevenue);
                  const revenueRank = sortedByRevenue.findIndex(r => r.rep === selectedRep) + 1;

                  return (
                    <>
                      {/* Rep Header Card */}
                      <div style={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        borderRadius: '12px',
                        padding: '24px',
                        color: 'white',
                        marginBottom: '24px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <h2 style={{ fontSize: '24px', fontWeight: 700, margin: '0 0 8px 0' }}>
                              👤 {selectedRep?.split(' ')[0]}'s {selectedYear} Review
                            </h2>
                            <p style={{ margin: 0, opacity: 0.9, fontSize: '15px' }}>
                              {repData.activeMonths} active months • Best month: {repData.bestMonth?.month || 'N/A'}
                            </p>
                          </div>
                          <div style={{
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            borderRadius: '8px',
                            padding: '12px 20px',
                            textAlign: 'center'
                          }}>
                            <div style={{ fontSize: '13px', opacity: 0.9 }}>Revenue Rank</div>
                            <div style={{ fontSize: '28px', fontWeight: 700 }}>#{revenueRank}</div>
                          </div>
                        </div>
                      </div>

                      {/* Rep KPI Cards */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(5, 1fr)',
                        gap: '16px',
                        marginBottom: '24px'
                      }}>
                        <div style={{
                          backgroundColor: 'white',
                          borderRadius: '12px',
                          padding: '20px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>💰 Total Revenue</div>
                          <div style={{ fontSize: '24px', fontWeight: 700, color: '#10b981' }}>
                            ${repData.totalRevenue?.toLocaleString()}
                          </div>
                          {repData.goals?.revenueGoal > 0 && (
                            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                              <div style={{ width: '100%', height: '8px', backgroundColor: '#E5E7EB', borderRadius: '4px', overflow: 'hidden', marginBottom: '6px' }}>
                                <div style={{ width: `${Math.min((repData.totalRevenue / repData.goals.revenueGoal) * 100, 100)}%`, height: '100%', backgroundColor: (repData.totalRevenue / repData.goals.revenueGoal) >= 0.9 ? '#10B981' : (repData.totalRevenue / repData.goals.revenueGoal) >= 0.7 ? '#F59E0B' : '#EF4444', borderRadius: '4px' }} />
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#6B7280' }}>
                                <span style={{ color: (repData.totalRevenue / repData.goals.revenueGoal) >= 0.9 ? '#10B981' : (repData.totalRevenue / repData.goals.revenueGoal) >= 0.7 ? '#F59E0B' : '#EF4444' }}>{((repData.totalRevenue / repData.goals.revenueGoal) * 100).toFixed(1)}%</span>
                                <span>Goal: ${repData.goals.revenueGoal?.toLocaleString()}</span>
                              </div>
                            </div>
                          )}
                        </div>

                        <div style={{
                          backgroundColor: 'white',
                          borderRadius: '12px',
                          padding: '20px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>🎯 Funded Deals</div>
                          <div style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b' }}>
                            {repData.totalFunded}
                          </div>
                          {repData.goals?.fundedGoal > 0 && (
                            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                              <div style={{ width: '100%', height: '8px', backgroundColor: '#E5E7EB', borderRadius: '4px', overflow: 'hidden', marginBottom: '6px' }}>
                                <div style={{ width: `${Math.min((repData.totalFunded / repData.goals.fundedGoal) * 100, 100)}%`, height: '100%', backgroundColor: (repData.totalFunded / repData.goals.fundedGoal) >= 0.9 ? '#10B981' : (repData.totalFunded / repData.goals.fundedGoal) >= 0.7 ? '#F59E0B' : '#EF4444', borderRadius: '4px' }} />
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#6B7280' }}>
                                <span style={{ color: (repData.totalFunded / repData.goals.fundedGoal) >= 0.9 ? '#10B981' : (repData.totalFunded / repData.goals.fundedGoal) >= 0.7 ? '#F59E0B' : '#EF4444' }}>{((repData.totalFunded / repData.goals.fundedGoal) * 100).toFixed(1)}%</span>
                                <span>Goal: {repData.goals.fundedGoal}</span>
                              </div>
                            </div>
                          )}
                        </div>

                        <div style={{
                          backgroundColor: 'white',
                          borderRadius: '12px',
                          padding: '20px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>📋 Apps In</div>
                          <div style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b' }}>
                            {repData.totalApps}
                          </div>
                          {repData.goals?.appsGoal > 0 && (
                            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                              <div style={{ width: '100%', height: '8px', backgroundColor: '#E5E7EB', borderRadius: '4px', overflow: 'hidden', marginBottom: '6px' }}>
                                <div style={{ width: `${Math.min((repData.totalApps / repData.goals.appsGoal) * 100, 100)}%`, height: '100%', backgroundColor: (repData.totalApps / repData.goals.appsGoal) >= 0.9 ? '#10B981' : (repData.totalApps / repData.goals.appsGoal) >= 0.7 ? '#F59E0B' : '#EF4444', borderRadius: '4px' }} />
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#6B7280' }}>
                                <span style={{ color: (repData.totalApps / repData.goals.appsGoal) >= 0.9 ? '#10B981' : (repData.totalApps / repData.goals.appsGoal) >= 0.7 ? '#F59E0B' : '#EF4444' }}>{((repData.totalApps / repData.goals.appsGoal) * 100).toFixed(1)}%</span>
                                <span>Goal: {repData.goals.appsGoal}</span>
                              </div>
                            </div>
                          )}
                        </div>

                        <div style={{
                          backgroundColor: 'white',
                          borderRadius: '12px',
                          padding: '20px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>📈 Conversion %</div>
                          <div style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b' }}>
                            {repData.avgConversion?.toFixed(1)}%
                          </div>
                        </div>

                        <div style={{
                          backgroundColor: 'white',
                          borderRadius: '12px',
                          padding: '20px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>💵 Avg Deal Size</div>
                          <div style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b' }}>
                            ${repData.avgDealSize?.toLocaleString(undefined, {maximumFractionDigits: 0})}
                          </div>
                        </div>
                      </div>

                      {/* Activity Cards */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '16px',
                        marginBottom: '24px'
                      }}>
                        <div style={{
                          backgroundColor: 'white',
                          borderRadius: '12px',
                          padding: '20px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>📞 Total Calls</div>
                          <div style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b' }}>
                            {repData.totalCalls?.toLocaleString()}
                          </div>
                        </div>

                        <div style={{
                          backgroundColor: 'white',
                          borderRadius: '12px',
                          padding: '20px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>💬 Total SMS</div>
                          <div style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b' }}>
                            {repData.totalSms?.toLocaleString()}
                          </div>
                        </div>

                        <div style={{
                          backgroundColor: 'white',
                          borderRadius: '12px',
                          padding: '20px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>⏱️ Talk Time</div>
                          <div style={{ fontSize: '24px', fontWeight: 700, color: '#1e293b' }}>
                            {Math.floor((repData.totalTalkTime || 0) / 60)}h {Math.round((repData.totalTalkTime || 0) % 60)}m
                          </div>
                        </div>
                      </div>

                      {/* Monthly Breakdown Table */}
                      <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        padding: '20px',
                        marginBottom: '24px'
                      }}>
                        <h3 style={{
                          fontSize: '18px',
                          fontWeight: 600,
                          color: '#1e293b',
                          marginBottom: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          📅 {periodFilteredTotals?.resolvedGranularity === 'day' ? 'Daily' : periodFilteredTotals?.resolvedGranularity === 'week' ? 'Weekly' : 'Monthly'} Breakdown
                        </h3>

                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                              <th style={{ textAlign: 'left', padding: '12px 8px', color: '#64748b', fontSize: '13px', fontWeight: 600 }}>
                                {periodFilteredTotals?.resolvedGranularity === 'day' ? 'Day' : periodFilteredTotals?.resolvedGranularity === 'week' ? 'Week' : 'Month'}
                              </th>
                              <th style={{ textAlign: 'right', padding: '12px 8px', color: '#64748b', fontSize: '13px', fontWeight: 600 }}>Revenue</th>
                              <th style={{ textAlign: 'right', padding: '12px 8px', color: '#64748b', fontSize: '13px', fontWeight: 600 }}>Funded</th>
                              <th style={{ textAlign: 'right', padding: '12px 8px', color: '#64748b', fontSize: '13px', fontWeight: 600 }}>Apps</th>
                              <th style={{ textAlign: 'right', padding: '12px 8px', color: '#64748b', fontSize: '13px', fontWeight: 600 }}>Calls</th>
                              <th style={{ textAlign: 'right', padding: '12px 8px', color: '#64748b', fontSize: '13px', fontWeight: 600 }}>SMS</th>
                              <th style={{ textAlign: 'right', padding: '12px 8px', color: '#64748b', fontSize: '13px', fontWeight: 600 }}>Conv %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {/* Feb 4, 2026: Use repBreakdownData for granularity-aware display */}
                            {repBreakdownData.map((row, idx) => (
                              <tr key={row.label || idx} style={{
                                borderBottom: '1px solid #f1f5f9',
                                backgroundColor: row.revenue === 0 ? '#f8fafc' : 'transparent'
                              }}>
                                <td style={{ padding: '12px 8px', fontWeight: 500, color: '#1e293b' }}>
                                  {row.label}
                                </td>
                                <td style={{ padding: '12px 8px', textAlign: 'right', color: row.revenue > 0 ? '#10b981' : '#cbd5e1', fontWeight: 600 }}>
                                  ${row.revenue?.toLocaleString()}
                                </td>
                                <td style={{ padding: '12px 8px', textAlign: 'right', color: row.funded > 0 ? '#1e293b' : '#cbd5e1' }}>
                                  {row.funded}
                                </td>
                                <td style={{ padding: '12px 8px', textAlign: 'right', color: row.apps > 0 ? '#1e293b' : '#cbd5e1' }}>
                                  {row.apps}
                                </td>
                                <td style={{ padding: '12px 8px', textAlign: 'right', color: row.calls > 0 ? '#1e293b' : '#cbd5e1' }}>
                                  {row.calls?.toLocaleString()}
                                </td>
                                <td style={{ padding: '12px 8px', textAlign: 'right', color: row.sms > 0 ? '#1e293b' : '#cbd5e1' }}>
                                  {row.sms?.toLocaleString()}
                                </td>
                                <td style={{ padding: '12px 8px', textAlign: 'right', color: row.conversionRate > 0 ? '#1e293b' : '#cbd5e1' }}>
                                  {row.conversionRate?.toFixed(1)}%
                                </td>
                              </tr>
                            ))}
                            {repBreakdownData.length === 0 && (
                              <tr>
                                <td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>
                                  No data for selected period
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Rep Revenue Chart */}
                      <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        padding: '20px',
                        marginBottom: '24px'
                      }}>
                        <h3 style={{
                          fontSize: '18px',
                          fontWeight: 600,
                          color: '#1e293b',
                          marginBottom: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          📈 {selectedRep?.split(' ')[0]}'s {periodFilteredTotals?.resolvedGranularity === 'day' ? 'Daily' : periodFilteredTotals?.resolvedGranularity === 'week' ? 'Weekly' : 'Monthly'} Revenue
                        </h3>

                        <ResponsiveContainer width="100%" height={280}>
                          {/* Feb 4, 2026: Use repBreakdownData for granularity-aware display */}
                          <ComposedChart data={repBreakdownData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 11 }} />
                            <YAxis
                              yAxisId="left"
                              tick={{ fill: '#64748b', fontSize: 12 }}
                              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                            />
                            <YAxis
                              yAxisId="right"
                              orientation="right"
                              tick={{ fill: '#64748b', fontSize: 12 }}
                            />
                            <Tooltip
                              formatter={(value, name) => {
                                if (name === 'Revenue') return [`$${value.toLocaleString()}`, name];
                                return [value, name];
                              }}
                            />
                            <Legend />
                            <Bar yAxisId="right" dataKey="funded" name="Funded" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                            <Line yAxisId="left" type="monotone" dataKey="revenue" name="Revenue" stroke="#10b981" strokeWidth={3} dot={{ r: 4 }} />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Revenue Trends Comparison - Rep vs Team Average (Feb 2, 2026) */}
                      <div className="reviews-chart" style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        padding: '20px',
                        marginBottom: '24px'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '16px',
                          flexWrap: 'wrap',
                          gap: '12px'
                        }}>
                          <h3 style={{
                            fontSize: '18px',
                            fontWeight: 600,
                            color: '#1e293b',
                            margin: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            📈 Revenue Trends Comparison
                          </h3>

                          {/* Rep Toggle Checkboxes */}
                          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                            {['Team Avg', ...reps.map(r => r.split(' ')[0])].map(repName => (
                              <label
                                key={repName}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  fontWeight: 500
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={visibleReps[repName] !== false}
                                  onChange={() => setVisibleReps(prev => ({
                                    ...prev,
                                    [repName]: !prev[repName]
                                  }))}
                                  style={{ cursor: 'pointer' }}
                                />
                                <span style={{ color: REP_COMPARISON_COLORS[repName] || '#64748b' }}>
                                  {repName}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {repComparisonChartData.length > 0 ? (
                          <ResponsiveContainer width="100%" height={350}>
                            <LineChart
                              data={repComparisonChartData}
                              margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis
                                dataKey="label"
                                tick={{ fill: '#64748b', fontSize: 11 }}
                              />
                              <YAxis
                                tick={{ fill: '#64748b', fontSize: 12 }}
                                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                              />
                              <Tooltip
                                formatter={(value, name) => [`$${value.toLocaleString()}`, name]}
                                contentStyle={{
                                  borderRadius: '8px',
                                  border: '1px solid #e2e8f0',
                                  backgroundColor: 'white'
                                }}
                              />
                              <Legend />

                              {/* Team Average - dashed gray line */}
                              {visibleReps['Team Avg'] !== false && (
                                <Line
                                  type="monotone"
                                  dataKey="Team Avg"
                                  stroke={REP_COMPARISON_COLORS['Team Avg']}
                                  strokeWidth={2}
                                  strokeDasharray="5 5"
                                  dot={false}
                                  name="Team Avg"
                                />
                              )}

                              {/* Individual Rep Lines */}
                              {reps.map(rep => {
                                const firstName = rep.split(' ')[0];
                                if (visibleReps[firstName] === false) return null;

                                const isSelectedRep = firstName === selectedRep?.split(' ')[0];

                                return (
                                  <Line
                                    key={rep}
                                    type="monotone"
                                    dataKey={firstName}
                                    stroke={REP_COMPARISON_COLORS[firstName] || '#64748b'}
                                    strokeWidth={isSelectedRep ? 3 : 2}
                                    dot={{ r: isSelectedRep ? 5 : 3 }}
                                    name={firstName}
                                  />
                                );
                              })}
                            </LineChart>
                          </ResponsiveContainer>
                        ) : (
                          <div style={{
                            height: '350px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#94a3b8'
                          }}>
                            No data available for the selected period
                          </div>
                        )}

                        {/* Comparison Insight */}
                        <div style={{
                          marginTop: '16px',
                          padding: '12px 16px',
                          background: '#f0f9ff',
                          borderRadius: '8px',
                          borderLeft: '4px solid #3b82f6',
                          fontSize: '13px',
                          color: '#1e40af'
                        }}>
                          💡 <strong>Tip:</strong> Check the boxes above to compare {selectedRep?.split(' ')[0]} against other reps.
                        </div>
                      </div>

                      {/* Growth Insights */}
                      <div style={{
                        backgroundColor: '#f0fdf4',
                        borderRadius: '12px',
                        border: '1px solid #bbf7d0',
                        padding: '20px'
                      }}>
                        <h3 style={{
                          fontSize: '18px',
                          fontWeight: 600,
                          color: '#166534',
                          marginBottom: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          🌱 Growth Focus for {selectedYear + 1}
                        </h3>
                        <p style={{ color: '#166534', margin: 0, lineHeight: 1.6 }}>
                          {repData.avgConversion < 30
                            ? `Focus on improving conversion rate (currently ${repData.avgConversion?.toFixed(1)}%). Review deal qualification and lender matching.`
                            : repData.totalCalls < 5000
                              ? `Strong conversion at ${repData.avgConversion?.toFixed(1)}%! Opportunity to increase call volume for more apps.`
                              : `Great year! Maintain momentum with ${repData.totalFunded} deals at $${repData.avgDealSize?.toLocaleString(undefined, {maximumFractionDigits: 0})} avg. Look for larger deal opportunities.`
                          }
                        </p>
                      </div>
                    </>
                  );
                })()}

              </div>
            )}

          </div>
        )}
        {/* ==================== BOOK TAB (standalone only) ==================== */}
        {activeTab === 'book' && standaloneBookMode && (
          <Suspense fallback={<LoadingFallback />}>
            <BookTab
              data={bookData}
              loading={bookLoading}
              error={bookError}
              callData={fullDialsData}
              smsData={fullSmsData}
              emailData={Array.isArray(apolloContactEmails) ? apolloContactEmails : []}
              historicalFundedData={historicalFundedData}
              datamerchData={datamerchData}
              companyIntelIndexData={Array.isArray(companyIntelIndex) ? companyIntelIndex : []}
              paymentAlertData={paymentAlertData}
              accountStatusOverridesData={accountStatusOverridesData}
              dealLevelStatusOverridesData={dealLevelStatusOverridesData}
              refreshAccountStatusOverrides={refreshAccountStatusOverrides}
              refreshDealLevelStatusOverrides={refreshDealLevelStatusOverrides}
            />
          </Suspense>
        )}
        {/* ==================== ACTIVITY TAB (Jan 2026) ==================== */}
        {activeTab === 'activity' && (
          <Suspense fallback={<LoadingFallback />}>
            <ActivityTab
              callsData={fullDialsData}
              smsData={fullSmsData}
              emailData={apolloEmailData}
              appsData={Array.isArray(data?.tabs?.Apps_In?.data) ? data.tabs.Apps_In.data : []}
              fundedData={Array.isArray(data?.tabs?.Funded?.data) ? data.tabs.Funded.data : []}
              rawCallsData={Array.isArray(data?.tabs?.Dials_TalkTime?.data) ? data.tabs.Dials_TalkTime.data : []}
              canonicalRawCalls={canonicalRawCalls}
              rawSmsData={Array.isArray(data?.tabs?.SMS?.data) ? data.tabs.SMS.data : []}
              selectedRep={selectedRep}
              apolloEmailLoading={apolloEmailLoading}
              apolloEmailError={apolloEmailError}
            />
          </Suspense>
        )}
        {/* ==================== FUNNEL TAB (Jan 2026) ==================== */}
        {activeTab === 'funnel' && (
          <Suspense fallback={<LoadingFallback />}>
            <FunnelTab
              data={data}
              goals={goals}
              selectedRep={selectedRep}
              reps={currentMonthReps}
              pipelineData={pipelineData}
              pipelineLoading={pipelineLoading}
              pipelineError={pipelineError}
            />
          </Suspense>
        )}
        {/* ==================== REVIEWS TAB (Jan 2026) ==================== */}
        {activeTab === 'reviews' && (
          <Suspense fallback={<LoadingFallback />}>
            <ReviewsTab
              historicalSubmissions={pipelineData?.historicalSubmissions || []}
              activeSubmissions={pipelineData?.submissions || []}
              data={data}
              selectedRep={selectedRep}
              pipelineLoading={pipelineLoading}
              pipelineError={pipelineError}
            />
          </Suspense>
        )}
      </main>
    </div>
  );
}

/**
 * Calculate weekly breakdown from raw tab data
 * @param {Object} tabData - The tab data object
 * @param {string} month - Month name (e.g., "January")
 * @param {string} selectedRep - Rep name or "All Reps"
 * @param {number} year - Year to filter by (uses Date column)
 */
function calculateWeeklyData(tabData, month, selectedRep, year = new Date().getFullYear()) {
  if (!tabData || !tabData.data) return [];

  // Filter by month AND year (using Date column for year check)
  const monthData = tabData.data.filter(row => {
    if (row.Month !== month) return false;
    if (!row.Date) return false;
    return new Date(row.Date).getFullYear() === year;
  });

  // Filter by rep if specified
  const filtered = selectedRep === 'All Reps'
    ? monthData
    : monthData.filter(row => row.Rep === selectedRep);

  // Group by week
  const weeklyGroups = {};
  filtered.forEach(row => {
    const week = row.Week || 'Unknown';
    if (!weeklyGroups[week]) {
      weeklyGroups[week] = [];
    }
    weeklyGroups[week].push(row);
  });

  // Convert to array and sort by week
  const weeks = Object.keys(weeklyGroups).sort();
  return weeks.map(week => ({
    week,
    data: weeklyGroups[week],
    count: weeklyGroups[week].length
  }));
}

/**
 * Calculate business days remaining in current month
 * Excludes Saturdays and Sundays
 */
function getBusinessDaysRemaining() {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const lastDay = new Date(year, month + 1, 0).getDate();

  let businessDays = 0;
  for (let day = today.getDate() + 1; day <= lastDay; day++) {
    const date = new Date(year, month, day);
    const dayOfWeek = date.getDay();
    // 0 = Sunday, 6 = Saturday
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      businessDays++;
    }
  }

  return businessDays;
}

/**
 * Calculate business days elapsed in current month (from day 1 to today)
 * Excludes Saturdays and Sundays
 */
function getBusinessDaysElapsed() {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();

  let businessDays = 0;
  for (let day = 1; day <= today.getDate(); day++) {
    const date = new Date(year, month, day);
    const dayOfWeek = date.getDay();
    // 0 = Sunday, 6 = Saturday
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      businessDays++;
    }
  }

  return businessDays;
}

/**
 * Calculate business days left in current week (today through Friday)
 * Excludes Saturdays and Sundays
 * Returns: 1-5 (Mon=5, Tue=4, Wed=3, Thu=2, Fri=1, Sat/Sun=5 for next week)
 */
function getBusinessDaysLeftInWeek() {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat

  // If Saturday (6) or Sunday (0), return 5 (full week ahead: Mon-Fri)
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return 5;
  }

  // Monday=1 → 5 days left (Mon-Fri)
  // Tuesday=2 → 4 days left (Tue-Fri)
  // Wednesday=3 → 3 days left (Wed-Fri)
  // Thursday=4 → 2 days left (Thu-Fri)
  // Friday=5 → 1 day left (Fri)
  return 6 - dayOfWeek;
}

/**
 * Calculate total business days in current month
 * Excludes Saturdays and Sundays
 */
function getBusinessDaysInMonth() {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const lastDay = new Date(year, month + 1, 0).getDate();

  let businessDays = 0;
  for (let day = 1; day <= lastDay; day++) {
    const date = new Date(year, month, day);
    const dayOfWeek = date.getDay();
    // 0 = Sunday, 6 = Saturday
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      businessDays++;
    }
  }

  return businessDays;
}

/**
 * Get business days (Mon-Fri) for a specific month by name
 * @param {string} monthName - e.g., 'November', 'October'
 * @param {number} year - defaults to 2025
 */
function getBusinessDaysForMonth(monthName, year = 2025) {
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const monthIndex = monthNames.indexOf(monthName);
  if (monthIndex === -1) return 20; // Default fallback
  
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  let businessDays = 0;
  
  for (let day = 1; day <= lastDay; day++) {
    const date = new Date(year, monthIndex, day);
    const dayOfWeek = date.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      businessDays++;
    }
  }
  return businessDays;
}

/**
 * Calculate historical metrics for a rep across last N months (excluding current month)
 * Returns baseline conversion rates and averages
 * Now properly handles year boundaries using Date column
 */
function calculateHistoricalMetrics(data, rep, currentMonth, monthsToInclude = 6, currentYear = new Date().getFullYear()) {
  if (!data || !data.tabs) return null;

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const currentMonthIndex = monthNames.indexOf(currentMonth);

  // Get last N months with their respective years (excluding current month)
  const historicalMonths = [];
  let yearTracker = currentYear;
  for (let i = 1; i <= monthsToInclude; i++) {
    const monthIndex = (currentMonthIndex - i + 12) % 12;
    // If we wrapped around (month index went up), we're in the previous year
    if (i === 1 && monthIndex > currentMonthIndex) {
      yearTracker = currentYear - 1;
    } else if (monthIndex === 11 && historicalMonths.length > 0) {
      // Crossed into previous year (December)
      const prevMonth = monthNames.indexOf(historicalMonths[historicalMonths.length - 1].month);
      if (prevMonth < monthIndex) yearTracker--;
    }
    historicalMonths.push({ month: monthNames[monthIndex], year: yearTracker });
  }

  // Helper to filter by month AND year (using Date column)
  const filterByMonthYear = (dataArray, month, year) => {
    return dataArray?.filter(r => {
      if (r.Month !== month) return false;
      if (!r.Date) return false;
      return new Date(r.Date).getFullYear() === year;
    }) || [];
  };

  // Calculate metrics for each historical month
  const monthlyMetrics = historicalMonths.map(({ month, year }) => {
    const appsIn = filterByMonthYear(data.tabs.Apps_In?.data, month, year).filter(r => r.Rep === rep);
    const approved = filterByMonthYear(data.tabs.Approved?.data, month, year).filter(r => r.Rep === rep);
    const funded = filterValidDeals(filterByMonthYear(data.tabs.Funded?.data, month, year).filter(r => r.Rep === rep));
    const revenue = filterValidDeals(filterByMonthYear(data.tabs.Revenue?.data, month, year).filter(r => r.Rep === rep));
    const monthlyAggs3419 = (data.monthlyAggregates || []);
    const callRows = monthlyAggs3419.filter(function(r) { return r.Month === month && r.Rep === rep && parseInt(r.Year) === year; });
    const smsRows = monthlyAggs3419.filter(function(r) { return r.Month === month && r.Rep === rep && parseInt(r.Year) === year; });
    var callCount3419 = callRows.reduce(function(s, r) { return s + (parseInt(r.TotalCalls) || 0); }, 0);
    var smsCount3419 = smsRows.reduce(function(s, r) { return s + (parseInt(r.TotalSMS) || 0); }, 0);

    const totalRevenue = revenue.reduce((sum, row) => sum + (parseFloat(row.Revenue) || 0), 0);

    return {
      month,
      appsIn: appsIn.length,
      approved: approved.length,
      funded: funded.length,
      revenue: totalRevenue,
      calls: callCount3419,
      sms: smsCount3419,
      callsToApps: callCount3419 > 0 ? (appsIn.length / callCount3419 * 100) : 0,
      smsToApps: smsCount3419 > 0 ? (appsIn.length / smsCount3419 * 100) : 0,
      appsToApproved: appsIn.length > 0 ? (approved.length / appsIn.length * 100) : 0,
      approvedToFunded: approved.length > 0 ? (funded.length / approved.length * 100) : 0,
      avgDealSize: funded.length > 0 ? totalRevenue / funded.length : 0
    };
  }).filter(m => m.appsIn > 0 || m.revenue > 0); // Only include months with activity

  if (monthlyMetrics.length === 0) return null;

  // Calculate averages across historical months
  const avg = {
    callsToApps: monthlyMetrics.reduce((sum, m) => sum + m.callsToApps, 0) / monthlyMetrics.length,
    smsToApps: monthlyMetrics.reduce((sum, m) => sum + m.smsToApps, 0) / monthlyMetrics.length,
    appsToApproved: monthlyMetrics.reduce((sum, m) => sum + m.appsToApproved, 0) / monthlyMetrics.length,
    approvedToFunded: monthlyMetrics.reduce((sum, m) => sum + m.approvedToFunded, 0) / monthlyMetrics.length,
    avgDealSize: monthlyMetrics.reduce((sum, m) => sum + m.avgDealSize, 0) / monthlyMetrics.length,
    monthlyRevenue: monthlyMetrics.reduce((sum, m) => sum + m.revenue, 0) / monthlyMetrics.length,
    monthlyApps: monthlyMetrics.reduce((sum, m) => sum + m.appsIn, 0) / monthlyMetrics.length,
    monthlyCalls: monthlyMetrics.reduce((sum, m) => sum + m.calls, 0) / monthlyMetrics.length,
    monthlySMS: monthlyMetrics.reduce((sum, m) => sum + m.sms, 0) / monthlyMetrics.length
  };

  return {
    averages: avg,
    monthlyData: monthlyMetrics,
    monthsIncluded: monthlyMetrics.length
  };
}

/**
 * PRESCRIPTIVE GUIDANCE GRID - For "All Reps" view
 * Shows what each rep needs to do side-by-side
 * Updated to handle year filtering properly
 */
function PrescriptiveGuidanceGrid({ data, goals, month, reps, year = new Date().getFullYear(), monthYear }) {
  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);

  const formatPercent = (value) => `${value.toFixed(1)}%`;

  const daysRemaining = getBusinessDaysRemaining();

  // Use monthYear for goals (e.g., "January 2026"), fallback to constructed month + year
  const goalMonth = monthYear || `${month} ${year}`;

  const repGuidance = reps.map(rep => {
    // Get rep's goals (using Month Year format)
    const goalRow = goals?.find(g => g.Month === goalMonth && g.Rep === rep);
    if (!goalRow) return null;

    const appsGoal = parseInt(goalRow.Apps_Goal || 0);

    // Get current metrics (with year filter from Date column)
    const appsIn = data.tabs.Apps_In?.data?.filter(r =>
      r.Month === month && r.Rep === rep && r.Date && new Date(r.Date).getFullYear() === year
    ) || [];

    // Get HISTORICAL conversion rates (more reliable than current month with limited data)
    const historical = calculateHistoricalMetrics(data, rep, month, 6, year);

    // Use historical conversion rates if available, otherwise fall back to current month
    let callsToApps = 3; // Default fallback
    let smsToApps = 5; // Default fallback

    if (historical) {
      callsToApps = historical.averages.callsToApps;
      smsToApps = historical.averages.smsToApps;
    }

    // Calculate what's needed based on HISTORICAL conversion rates
    const appsNeeded = Math.max(0, appsGoal - appsIn.length);
    const callsNeeded = callsToApps > 0 ? Math.ceil(appsNeeded / (callsToApps / 100)) : 0;
    const smsNeeded = smsToApps > 0 ? Math.ceil(appsNeeded / (smsToApps / 100)) : 0;
    const callsPerDay = daysRemaining > 0 ? Math.ceil(callsNeeded / daysRemaining) : 0;
    const smsPerDay = daysRemaining > 0 ? Math.ceil(smsNeeded / daysRemaining) : 0;

    return {
      rep,
      firstName: rep.split(' ')[0],
      appsGoal,
      appsNeeded,
      callsPerDay,
      smsPerDay,
      callsToApps,
      smsToApps
    };
  }).filter(Boolean);

  if (repGuidance.length === 0) return null;

  return (
    <section className="prescriptive-guidance-grid">
      {/* ⚠️ PENDING REMOVAL (Nov 8, 2025): Replaced by Overview tab's MTD Review Card */}
      <div style={{
        backgroundColor: '#fef3c7',
        border: '2px solid #f59e0b',
        borderRadius: '6px',
        padding: '12px 16px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span style={{ fontSize: '20px' }}>⚠️</span>
        <span style={{ fontWeight: 'bold', color: '#92400e' }}>
          PENDING REMOVAL: This feature is being replaced by the new Overview tab. Please use that instead.
        </span>
      </div>
      <h3>🎯 What Each Rep Needs To Do</h3>
      <p className="section-description">
        Daily activity targets to hit monthly goals. Based on each rep's personal conversion rates and apps still needed.
      </p>
      <div className="guidance-days-remaining">
        <strong>{daysRemaining}</strong> business days remaining in {month}
      </div>
      <div className="guidance-grid">
        {repGuidance.map(guide => (
          <div key={guide.rep} className="guidance-card-compact">
            <div className="guidance-rep-name">{guide.firstName}</div>
            <div className="guidance-gap">
              Needs <strong>{guide.appsNeeded}</strong> more apps
            </div>
            <div className="guidance-requirements-compact">
              <div className="requirement-compact">
                <div className="req-icon">📞</div>
                <div className="req-number">{guide.callsPerDay}</div>
                <div className="req-label">calls/day</div>
              </div>
              <div className="requirement-compact">
                <div className="req-icon">💬</div>
                <div className="req-number">{guide.smsPerDay}</div>
                <div className="req-label">texts/day</div>
              </div>
            </div>
            <div className="guidance-rates-compact">
              {formatPercent(guide.callsToApps)} calls→apps | {formatPercent(guide.smsToApps)} SMS→apps
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * REP COMPARISON GRID - For "All Reps" view
 * Shows all reps side-by-side for comparison
 * Updated to handle year filtering properly
 */
function RepComparisonGrid({ data, goals, month, reps, year = new Date().getFullYear(), monthYear }) {
  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);

  const formatPercent = (value) => `${value.toFixed(1)}%`;

  // Use monthYear for goals (e.g., "January 2026"), fallback to constructed month + year
  const goalMonth = monthYear || `${month} ${year}`;

  // Helper for data filtering with year check
  const filterData = (dataArray, rep) => {
    return dataArray?.filter(r =>
      r.Month === month && r.Rep === rep && r.Date && new Date(r.Date).getFullYear() === year
    ) || [];
  };

  const repData = reps.map(rep => {
    // Current month metrics (with year filter from Date column)
    const appsIn = filterData(data.tabs.Apps_In?.data, rep);
    const revenue = filterData(data.tabs.Revenue?.data, rep);
    const monthlyAggs = data.monthlyAggregates || [];
    const callAgg = monthlyAggs.filter(r => r.Month === month && r.Year === year && r.Rep === rep);
    const smsAgg = monthlyAggs.filter(r => r.Month === month && r.Year === year && r.Rep === rep);
    const callCount = callAgg.reduce((s, r) => s + (parseInt(r.TotalCalls) || 0), 0);
    const smsCount = smsAgg.reduce((s, r) => s + (parseInt(r.TotalSMS) || 0), 0);

    const currentRevenue = revenue.reduce((sum, row) => sum + (parseFloat(row.Revenue) || 0), 0);
    const currentCallsToApps = callCount > 0 ? (appsIn.length / callCount * 100) : 0;

    // Goal for this rep (using Month Year format)
    const goalRow = goals?.find(g => g.Month === goalMonth && g.Rep === rep);
    const revenueGoal = goalRow ? parseInt(goalRow.Revenue_Goal) : 0;
    const revenueProgress = revenueGoal > 0 ? (currentRevenue / revenueGoal * 100) : 0;

    // Historical metrics
    const historical = calculateHistoricalMetrics(data, rep, month, 6, year);

    // Comparison to historical
    const callsToAppsDiff = historical ? currentCallsToApps - historical.averages.callsToApps : 0;

    // Pace calculation based on BUSINESS DAYS
    let paceDiff = 0;
    if (historical) {
      const businessDaysElapsed = getBusinessDaysElapsed();
      const avgBusinessDaysPerMonth = 21; // Typical month has ~21 business days
      const expectedRevenueByNow = (historical.averages.monthlyRevenue / avgBusinessDaysPerMonth) * businessDaysElapsed;
      paceDiff = expectedRevenueByNow > 0 ? ((currentRevenue - expectedRevenueByNow) / expectedRevenueByNow * 100) : 0;
    }

    // Status
    let status = 'on-track';
    if (revenueProgress < 50) status = 'critical';
    else if (revenueProgress < 70) status = 'needs-attention';

    return {
      rep,
      firstName: rep.split(' ')[0],
      currentRevenue,
      revenueGoal,
      revenueProgress,
      currentCallsToApps,
      historicalCallsToApps: historical?.averages.callsToApps || 0,
      callsToAppsDiff,
      paceDiff,
      status,
      hasHistorical: !!historical
    };
  });

  return (
    <section className="rep-comparison-section">
      {/* ⚠️ PENDING REMOVAL (Nov 8, 2025): Replaced by Overview tab's MTD Review Card */}
      <div style={{
        backgroundColor: '#fef3c7',
        border: '2px solid #f59e0b',
        borderRadius: '6px',
        padding: '12px 16px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span style={{ fontSize: '20px' }}>⚠️</span>
        <span style={{ fontWeight: 'bold', color: '#92400e' }}>
          PENDING REMOVAL: This feature is being replaced by the new Overview tab. Please use that instead.
        </span>
      </div>
      <h3>Individual Rep Performance</h3>
      <p className="section-description">
        Revenue progress vs monthly goals. "vs avg" shows if they're ahead/behind their typical pace based on business days worked so far this month.
      </p>
      <div className="rep-comparison-grid">
        {repData.map(rep => (
          <div key={rep.rep} className={`rep-comparison-card ${rep.status}`}>
            <div className="rep-name">{rep.firstName}</div>

            <div className="rep-progress-section">
              <div className="progress-label">Revenue Progress</div>
              <div className="progress-bar-container">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${Math.min(rep.revenueProgress, 100)}%` }}
                ></div>
              </div>
              <div className="progress-text">
                {formatPercent(rep.revenueProgress)} ({formatCurrency(rep.currentRevenue)} / {formatCurrency(rep.revenueGoal)})
              </div>
            </div>

            {rep.hasHistorical && (
              <>
                <div className="rep-conversion-row">
                  <span className="label">Calls→Apps:</span>
                  <span className="value">{formatPercent(rep.currentCallsToApps)}</span>
                  <span className={`diff ${rep.callsToAppsDiff >= 0 ? 'positive' : 'negative'}`}>
                    {rep.callsToAppsDiff >= 0 ? '+' : ''}{formatPercent(rep.callsToAppsDiff)}
                  </span>
                </div>

                <div className="rep-pace-indicator">
                  <div className={`pace-badge ${rep.paceDiff >= 0 ? 'positive' : 'negative'}`}>
                    {rep.paceDiff >= 0 ? '↑' : '↓'} {Math.abs(rep.paceDiff).toFixed(0)}% vs avg
                  </div>
                </div>
              </>
            )}

            <div className={`rep-status-badge ${rep.status}`}>
              {rep.status === 'on-track' && '✅ On Track'}
              {rep.status === 'needs-attention' && '⚠️ Needs Attention'}
              {rep.status === 'critical' && '🚨 Critical'}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * REP DEEP DIVE - For individual rep view
 * Shows detailed personalized analysis for one rep
 * Updated to handle year filtering properly
 */
function RepDeepDive({ data, goals, month, rep, metrics, repGoals, year = new Date().getFullYear() }) {
  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);

  const formatPercent = (value) => `${value.toFixed(1)}%`;

  const historical = calculateHistoricalMetrics(data, rep, month, 6, year);
  const businessDaysRemaining = getBusinessDaysRemaining();
  const today = new Date().getDate();

  if (!historical || !repGoals) return null;

  // Calculate requirements
  const appsNeeded = Math.max(0, repGoals.apps - metrics.appsIn);
  const revenueNeeded = Math.max(0, repGoals.revenue - metrics.revenue);

  const callsNeededTotal = historical.averages.callsToApps > 0
    ? Math.ceil(appsNeeded / (historical.averages.callsToApps / 100))
    : 0;
  const smsNeededTotal = historical.averages.smsToApps > 0
    ? Math.ceil(appsNeeded / (historical.averages.smsToApps / 100))
    : 0;

  const callsPerBusinessDay = businessDaysRemaining > 0 ? Math.ceil(callsNeededTotal / businessDaysRemaining) : 0;
  const smsPerBusinessDay = businessDaysRemaining > 0 ? Math.ceil(smsNeededTotal / businessDaysRemaining) : 0;

  // Current pace projection
  const dailyRevenueAvg = today > 0 ? metrics.revenue / today : 0;
  const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
  const projectedRevenue = dailyRevenueAvg * daysInMonth;

  // Identify focus areas
  const insights = [];

  if (metrics.callsToApps < historical.averages.callsToApps * 0.9) {
    const diff = ((metrics.callsToApps - historical.averages.callsToApps) / historical.averages.callsToApps * 100);
    insights.push({
      type: 'warning',
      text: `Call conversion is ${Math.abs(diff).toFixed(0)}% below your 6-month average (${formatPercent(metrics.callsToApps)} vs ${formatPercent(historical.averages.callsToApps)})`
    });
  } else if (metrics.callsToApps > historical.averages.callsToApps * 1.1) {
    const diff = ((metrics.callsToApps - historical.averages.callsToApps) / historical.averages.callsToApps * 100);
    insights.push({
      type: 'success',
      text: `Call conversion UP ${diff.toFixed(0)}% vs avg! On track!`
    });
  }

  if (metrics.avgDealSize > historical.averages.avgDealSize * 1.1) {
    const diff = ((metrics.avgDealSize - historical.averages.avgDealSize) / historical.averages.avgDealSize * 100);
    insights.push({
      type: 'success',
      text: `Deal size is UP ${diff.toFixed(0)}% (${formatCurrency(metrics.avgDealSize)} vs ${formatCurrency(historical.averages.avgDealSize)})`
    });
  }

  if (businessDaysRemaining > 0 && appsNeeded > 0) {
    insights.push({
      type: 'action',
      text: `You need ${(appsNeeded / businessDaysRemaining).toFixed(1)} apps per business day for the next ${businessDaysRemaining} business days`
    });
  }

  return (
    <section className="rep-deep-dive">
      {/* ⚠️ PENDING REMOVAL (Nov 8, 2025): Replaced by Overview tab's MTD Review Card */}
      <div style={{
        backgroundColor: '#fef3c7',
        border: '2px solid #f59e0b',
        borderRadius: '6px',
        padding: '12px 16px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span style={{ fontSize: '20px' }}>⚠️</span>
        <span style={{ fontWeight: 'bold', color: '#92400e' }}>
          PENDING REMOVAL: This feature is being replaced by the new Overview tab. Please use that instead.
        </span>
      </div>
      <h3>📊 {rep.split(' ')[0]}'s Performance Deep Dive</h3>

      <div className="deep-dive-grid">
        {/* Historical Baseline */}
        <div className="deep-dive-card">
          <h4>Historical Baseline (Last {historical.monthsIncluded} Months)</h4>
          <div className="baseline-comparison">
            <div className="baseline-row">
              <span className="metric-name">Calls → Apps</span>
              <span className="historical">{formatPercent(historical.averages.callsToApps)}</span>
              <span className="arrow">→</span>
              <span className={`current ${metrics.callsToApps >= historical.averages.callsToApps ? 'positive' : 'negative'}`}>
                {formatPercent(metrics.callsToApps)}
              </span>
            </div>
            <div className="baseline-row">
              <span className="metric-name">SMS → Apps</span>
              <span className="historical">{formatPercent(historical.averages.smsToApps)}</span>
              <span className="arrow">→</span>
              <span className={`current ${metrics.smsToApps >= historical.averages.smsToApps ? 'positive' : 'negative'}`}>
                {formatPercent(metrics.smsToApps)}
              </span>
            </div>
            <div className="baseline-row">
              <span className="metric-name">Apps → Approved</span>
              <span className="historical">{formatPercent(historical.averages.appsToApproved)}</span>
              <span className="arrow">→</span>
              <span className={`current ${metrics.appsToApproved >= historical.averages.appsToApproved ? 'positive' : 'negative'}`}>
                {formatPercent(metrics.appsToApproved)}
              </span>
            </div>
            <div className="baseline-row">
              <span className="metric-name">Avg Deal Size</span>
              <span className="historical">{formatCurrency(historical.averages.avgDealSize)}</span>
              <span className="arrow">→</span>
              <span className={`current ${metrics.avgDealSize >= historical.averages.avgDealSize ? 'positive' : 'negative'}`}>
                {formatCurrency(metrics.avgDealSize)}
              </span>
            </div>
          </div>
          <div className="baseline-note">
            Historical avg = {formatPercent((historical.averages.callsToApps + historical.averages.smsToApps + historical.averages.appsToApproved + historical.averages.approvedToFunded) / 4)} conversion
          </div>
        </div>

        {/* Activity Requirements */}
        <div className="deep-dive-card">
          <h4>What You Need (Based on Conversion Rates)</h4>
          <div className="requirement-row">
            <div className="req-label">Apps Still Needed:</div>
            <div className="req-value big">{appsNeeded}</div>
          </div>
          <div className="requirement-row">
            <div className="req-label">Business Days Remaining:</div>
            <div className="req-value">{businessDaysRemaining}</div>
          </div>
          <div className="requirement-divider"></div>
          <div className="requirement-row highlight">
            <div className="req-label">📞 Calls per Business Day:</div>
            <div className="req-value big">{callsPerBusinessDay}</div>
            <div className="req-calc">({callsNeededTotal} calls ÷ {businessDaysRemaining} days)</div>
          </div>
          <div className="requirement-row highlight">
            <div className="req-label">💬 SMS per Business Day:</div>
            <div className="req-value big">{smsPerBusinessDay}</div>
            <div className="req-calc">({smsNeededTotal} SMS ÷ {businessDaysRemaining} days)</div>
          </div>
          <div className="requirement-note">
            Based on {formatPercent(historical.averages.callsToApps)} call conversion and {formatPercent(historical.averages.smsToApps)} SMS conversion
          </div>
        </div>

        {/* Projections */}
        <div className="deep-dive-card">
          <h4>Realistic Projections</h4>
          <div className="projection-row">
            <span className="proj-label">Current Daily Avg:</span>
            <span className="proj-value">{formatCurrency(dailyRevenueAvg)}/day</span>
          </div>
          <div className="projection-row">
            <span className="proj-label">Projected Month End:</span>
            <span className={`proj-value ${projectedRevenue >= repGoals.revenue ? 'positive' : 'negative'}`}>
              {formatCurrency(projectedRevenue)}
            </span>
          </div>
          <div className="projection-row">
            <span className="proj-label">Gap to Goal:</span>
            <span className={`proj-value ${projectedRevenue >= repGoals.revenue ? 'positive' : 'negative'}`}>
              {projectedRevenue >= repGoals.revenue ? '+' : ''}{formatCurrency(projectedRevenue - repGoals.revenue)}
            </span>
          </div>
          {projectedRevenue < repGoals.revenue && (
            <div className="projection-warning">
              ⚠️ Need to increase output by {((revenueNeeded / (businessDaysRemaining > 0 ? businessDaysRemaining : 1)) / dailyRevenueAvg * 100).toFixed(0)}% to hit goal
            </div>
          )}
        </div>

        {/* Personalized Insights */}
        <div className="deep-dive-card insights-card">
          <h4>🎯 Personalized Insights</h4>
          {insights.map((insight, idx) => (
            <div key={idx} className={`insight ${insight.type}`}>
              {insight.type === 'warning' && '⚠️ '}
              {insight.type === 'success' && '✅ '}
              {insight.type === 'action' && '🎯 '}
              {insight.text}
            </div>
          ))}
          {insights.length === 0 && (
            <div className="insight">All metrics tracking close to your historical averages</div>
          )}
        </div>
      </div>
    </section>
  );
}

// ============================================
// THIS MONTH TAB - Helper Components
// ============================================

/**
 * TM_MetricCard - Metric card with goal/expected values
 */
function TM_MetricCard({ label, value, goal, expected, businessDay, formatValue, subtext }) {
  const displayValue = formatValue ? formatValue(value) : value;
  const displayGoal = goal && formatValue ? formatValue(goal) : goal;
  const displayExpected = expected && formatValue ? formatValue(expected) : expected;

  return (
    <div className="metric-card" style={{
      padding: '16px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', color: '#64748b' }}>{label}</h3>
      <div style={{ fontSize: '32px', fontWeight: 700, marginBottom: '12px' }}>{displayValue}</div>
      {goal && (
        <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6' }}>
          <div>Goal: {displayGoal}</div>
          {expected !== undefined && (
            <div style={{ marginTop: '4px', color: '#64748b' }}>Expected by Day {businessDay}: {displayExpected}</div>
          )}
        </div>
      )}
      {subtext && (
        <div style={{ fontSize: '14px', color: '#64748b' }}>{subtext}</div>
      )}
    </div>
  );
}

/**
 * TM_ConversionCard - Conversion rate with historical comparison
 */
function TM_ConversionCard({ label, rate, historicalAvg }) {
  const diff = rate - historicalAvg;
  const isUp = diff >= 0;

  return (
    <div style={{
      padding: '16px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '28px', fontWeight: 700, marginBottom: '8px' }}>{rate.toFixed(1)}%</div>
      <div style={{ fontSize: '12px', color: '#64748b' }}>
        <strong>6-Mo Avg:</strong> {historicalAvg.toFixed(1)}%
        <span style={{ marginLeft: '6px', color: isUp ? '#10b981' : '#ef4444', fontWeight: 600 }}>
          {isUp ? '↗' : '↘'} {isUp ? '+' : ''}{diff.toFixed(1)}pp
        </span>
      </div>
    </div>
  );
}

/**
 * TM_GapRow - Table row for Gap Closure Math
 */
function TM_GapRow({ metric, current, goal, remaining, isCurrency, formatCurrency }) {
  const gap = goal - current;
  const dailyNeeded = remaining > 0 ? Math.ceil(gap / remaining) : 0;
  const formatVal = (v) => isCurrency ? formatCurrency(v) : v;

  return (
    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
      <td style={{ padding: '8px', fontWeight: 500 }}>{metric}</td>
      <td style={{ padding: '8px', textAlign: 'right' }}>{formatVal(current)}</td>
      <td style={{ padding: '8px', textAlign: 'right' }}>{formatVal(goal)}</td>
      <td style={{ padding: '8px', textAlign: 'right', color: gap > 0 ? '#ef4444' : '#10b981', fontWeight: 600 }}>
        {gap > 0 ? '-' : '+'}{formatVal(Math.abs(gap))}
      </td>
      <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>
        {gap <= 0 ? '✓ Done' : `${formatVal(dailyNeeded)}/day`}
      </td>
    </tr>
  );
}

/**
 * TM_ChannelCard - Channel efficiency display
 */
function TM_ChannelCard({ label, value, calculation }) {
  return (
    <div style={{
      padding: '16px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '24px', fontWeight: 700, marginBottom: '6px' }}>{value}</div>
      <div style={{ fontSize: '11px', color: '#94a3b8' }}>{calculation}</div>
    </div>
  );
}

/**
 * TM_FunnelStage - Funnel stage display
 */
function TM_FunnelStage({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center', minWidth: '70px' }}>
      <div style={{
        backgroundColor: color,
        color: 'white',
        padding: '12px 16px',
        borderRadius: '8px',
        fontWeight: 700,
        fontSize: '20px',
        marginBottom: '8px'
      }}>
        {value}
      </div>
      <div style={{ fontSize: '11px', fontWeight: 600, color: '#475569', textTransform: 'uppercase' }}>{label}</div>
    </div>
  );
}

/**
 * TM_FunnelArrow - Arrow between funnel stages
 */
function TM_FunnelArrow({ rate }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ fontSize: '24px', color: '#cbd5e1' }}>→</div>
      {rate && <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>{rate}</div>}
    </div>
  );
}

// ============================================
// LAST MONTH TAB - HELPER COMPONENTS
// ============================================

/**
 * LM_MetricCardWithBaseline - Metric card with 6-month baseline comparison
 */
function LM_MetricCardWithBaseline({ label, value, baseline, formatValue }) {
  const displayValue = formatValue(value);
  const displayBaseline = formatValue(baseline);
  const diff = value - baseline;
  const isUp = diff >= 0;

  return (
    <div style={{
      padding: '16px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '28px', fontWeight: 700, marginBottom: '8px' }}>{displayValue}</div>
      <div style={{ fontSize: '12px', color: '#64748b' }}>
        <strong>6-Mo Avg:</strong> {displayBaseline}
        <span style={{ marginLeft: '8px', color: isUp ? '#10b981' : '#ef4444', fontWeight: 600 }}>
          {isUp ? '↗' : '↘'} {isUp ? '+' : ''}{typeof value === 'number' && value > 1000 ? formatValue(diff) : diff}
        </span>
      </div>
    </div>
  );
}

/**
 * LM_SmallMetricCard - Compact metric card for secondary metrics
 */
function LM_SmallMetricCard({ label, value, color }) {
  return (
    <div style={{
      padding: '12px',
      backgroundColor: 'white',
      borderRadius: '6px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '24px', fontWeight: 700, color: color || '#1e293b' }}>{value}</div>
    </div>
  );
}

/**
 * LM_ConversionCardWithBaseline - Conversion rate card with 6-month baseline
 */
function LM_ConversionCardWithBaseline({ label, rate, baseline }) {
  const diff = rate - baseline;
  const isUp = diff >= 0;

  return (
    <div style={{
      padding: '16px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '28px', fontWeight: 700, marginBottom: '8px' }}>{rate.toFixed(1)}%</div>
      <div style={{ fontSize: '12px', color: '#64748b' }}>
        <strong>6-Mo Avg:</strong> {baseline.toFixed(1)}%
        <span style={{ marginLeft: '6px', color: isUp ? '#10b981' : '#ef4444', fontWeight: 600 }}>
          {isUp ? '↗' : '↘'} {isUp ? '+' : ''}{diff.toFixed(1)}pp
        </span>
      </div>
    </div>
  );
}

/**
 * LM_FunnelStage - Funnel stage display for Last Month
 */
function LM_FunnelStage({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center', minWidth: '70px' }}>
      <div style={{
        backgroundColor: color,
        color: 'white',
        padding: '12px 16px',
        borderRadius: '8px',
        fontWeight: 700,
        fontSize: '20px',
        marginBottom: '8px'
      }}>
        {value}
      </div>
      <div style={{ fontSize: '11px', fontWeight: 600, color: '#475569', textTransform: 'uppercase' }}>{label}</div>
    </div>
  );
}

/**
 * LM_FunnelArrow - Arrow with rate label for Last Month funnel
 */
function LM_FunnelArrow({ rate }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ fontSize: '24px', color: '#cbd5e1' }}>→</div>
      <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>{rate}</div>
    </div>
  );
}

/**
 * LM_FinalResultRow - Table row for Final Results table (mockup lines 1072-1097)
 */
function LM_FinalResultRow({ metric, actual, goal, formatValue }) {
  const shortfall = goal - actual;
  const achievement = ((actual / goal) * 100).toFixed(1);

  return (
    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
      <td style={{ padding: '8px', fontWeight: 500 }}>{metric}</td>
      <td style={{ padding: '8px', textAlign: 'right' }}>{formatValue(actual)}</td>
      <td style={{ padding: '8px', textAlign: 'right' }}>{formatValue(goal)}</td>
      <td style={{ padding: '8px', textAlign: 'right', color: shortfall > 0 ? '#ef4444' : '#10b981', fontWeight: 600 }}>
        {shortfall > 0 ? `-${formatValue(shortfall)}` : '✓'}
      </td>
      <td style={{ padding: '8px', textAlign: 'right' }}>
        <span style={{
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 600,
          backgroundColor: parseFloat(achievement) >= 100 ? '#d1fae5' : parseFloat(achievement) >= 90 ? '#fef3c7' : '#fee2e2',
          color: parseFloat(achievement) >= 100 ? '#065f46' : parseFloat(achievement) >= 90 ? '#92400e' : '#991b1b'
        }}>
          {achievement}%
        </span>
      </td>
    </tr>
  );
}

/**
 * THIS MONTH TAB - Real-time current month performance
 * Updated to handle year filtering properly
 */
function ThisMonthTab({ data, goals, selectedRep, month, reps, developerMode, year = new Date().getFullYear(), monthYear }) {
  // Helper: Use monthYear for goals (e.g., "January 2026"), fallback to month + year
  const goalMonth = monthYear || `${month} ${year}`;

  // Helper: Filter by month AND year using Date column
  const filterByMonthYear = (arr) =>
    (arr || []).filter(r => r.Month === month && r.Date && new Date(r.Date).getFullYear() === year);

  const [expandedWeek, setExpandedWeek] = useState(null); // Track which week is expanded (null, 'W1', 'W2', etc., or 'TOTAL')
  const [expandedWeekRep, setExpandedWeekRep] = useState(null); // Track which rep is expanded within the expanded week (null or rep name)

  // NEW: Collapsible sections state
  const [activeSection, setActiveSection] = useState('snapshot');
  const [collapsedSections, setCollapsedSections] = useState({
    snapshot: false,
    goals: false,
    weekly: false,
    why: false
  });

  // NEW: Section refs for scroll navigation
  const sectionRefs = {
    snapshot: useRef(null),
    goals: useRef(null),
    weekly: useRef(null),
    submissions: useRef(null),
    why: useRef(null)
  };

  // NEW: Scroll to section function
  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs[sectionId]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // NEW: Toggle section collapse
  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Get current month data from raw tabs (with year filter from Date column)
  const currentMonthData = useMemo(() => {
    if (!data || !data.tabs) return null;

    // Helper to filter by month AND year
    const filterByMonthYear = (dataArray) => {
      return dataArray?.filter(row =>
        row.Month === month && row.Date && new Date(row.Date).getFullYear() === year
      ) || [];
    };

    const appsIn = filterByMonthYear(data.tabs.Apps_In?.data);
    const approved = filterByMonthYear(data.tabs.Approved?.data);
    const funded = filterValidDeals(filterByMonthYear(data.tabs.Funded?.data));
    const revenue = filterValidDeals(filterByMonthYear(data.tabs.Revenue?.data));
    const calls = filterByMonthYear(data.tabs.Dials_TalkTime?.data);
    const sms = filterByMonthYear(data.tabs.SMS?.data);

    return { appsIn, approved, funded, revenue, calls, sms };
  }, [data, month, year]);

  // Get last month data for historical comparison (with year filter from Date column)
  const lastMonthData = useMemo(() => {
    if (!data || !data.tabs || !month) return null;

    // Calculate last month name and year
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const currentMonthIndex = monthNames.indexOf(month);
    const lastMonthIndex = (currentMonthIndex - 1 + 12) % 12;
    const lastMonthName = monthNames[lastMonthIndex];
    // If current month is January, last month was December of previous year
    const lastMonthYear = currentMonthIndex === 0 ? year - 1 : year;

    // Helper to filter by month AND year
    const filterByMonthYear = (dataArray) => {
      return dataArray?.filter(row =>
        row.Month === lastMonthName && row.Date && new Date(row.Date).getFullYear() === lastMonthYear
      ) || [];
    };

    const appsIn = filterByMonthYear(data.tabs.Apps_In?.data);
    const approved = filterByMonthYear(data.tabs.Approved?.data);
    const funded = filterValidDeals(filterByMonthYear(data.tabs.Funded?.data));
    const revenue = filterValidDeals(filterByMonthYear(data.tabs.Revenue?.data));
    const calls = filterByMonthYear(data.tabs.Dials_TalkTime?.data);
    const sms = filterByMonthYear(data.tabs.SMS?.data);

    return { appsIn, approved, funded, revenue, calls, sms };
  }, [data, month, year]);

  // Calculate last month conversion metrics for comparison
  const lastMonthMetrics = useMemo(() => {
    if (!lastMonthData) return null;

    const filterByRep = (arr) => selectedRep === 'All Reps'
      ? arr
      : arr.filter(row => row.Rep === selectedRep);

    const appsIn = filterByRep(lastMonthData.appsIn);
    const calls = filterByRep(lastMonthData.calls);
    const sms = filterByRep(lastMonthData.sms);
    const approved = filterByRep(lastMonthData.approved);
    const funded = filterByRep(lastMonthData.funded);

    // Calculate conversion rates
    const callsToApps = calls.length > 0 ? (appsIn.length / calls.length) * 100 : 0;
    const smsToApps = sms.length > 0 ? (appsIn.length / sms.length) * 100 : 0;
    const appsToApproved = appsIn.length > 0 ? (approved.length / appsIn.length) * 100 : 0;
    const approvedToFunded = approved.length > 0 ? (funded.length / approved.length) * 100 : 0;
    const appsToFunded = appsIn.length > 0 ? (funded.length / appsIn.length) * 100 : 0;

    return {
      callsToApps,
      smsToApps,
      appsToApproved,
      approvedToFunded,
      appsToFunded
    };
  }, [lastMonthData, selectedRep]);

  // Calculate metrics for selected rep or all reps
  const metrics = useMemo(() => {
    if (!currentMonthData) return null;

    const filterByRep = (arr) => selectedRep === 'All Reps'
      ? arr
      : arr.filter(row => row.Rep === selectedRep);

    const appsIn = filterByRep(currentMonthData.appsIn);
    const approved = filterByRep(currentMonthData.approved);
    const funded = filterByRep(currentMonthData.funded);
    const revenue = filterByRep(currentMonthData.revenue);
    const calls = filterByRep(currentMonthData.calls);
    const sms = filterByRep(currentMonthData.sms);

    const totalRevenue = revenue.reduce((sum, row) => sum + (parseFloat(row.Revenue) || 0), 0);
    const fundedAmount = funded.reduce((sum, row) => sum + (parseFloat(row.Amount) || 0), 0);
    const totalCalls = calls.length;
    const totalSMS = sms.length;

    // Conversion rates
    const callsToApps = totalCalls > 0 ? (appsIn.length / totalCalls * 100) : 0;
    const smsToApps = totalSMS > 0 ? (appsIn.length / totalSMS * 100) : 0;
    const appsToApproved = appsIn.length > 0 ? (approved.length / appsIn.length * 100) : 0;
    const approvedToFunded = approved.length > 0 ? (funded.length / approved.length * 100) : 0;

    // Efficiency metrics
    const revenuePerCall = totalCalls > 0 ? totalRevenue / totalCalls : 0;
    const revenuePerSMS = totalSMS > 0 ? totalRevenue / totalSMS : 0;
    const avgDealSize = funded.length > 0 ? totalRevenue / funded.length : 0;

    // Talk Time metrics (GROUP 3 addition)
    const totalTalkTime = calls.reduce((sum, call) =>
      sum + (parseFloat(call.Duration_Minutes) || 0), 0);
    const avgTalkTimePerCall = totalCalls > 0 ? totalTalkTime / totalCalls : 0;

    return {
      appsIn: appsIn.length,
      approved: approved.length,
      funded: funded.length,
      revenue: totalRevenue,
      fundedAmount: fundedAmount,
      calls: totalCalls,
      sms: totalSMS,
      callsToApps,
      smsToApps,
      appsToApproved,
      approvedToFunded,
      revenuePerCall,
      revenuePerSMS,
      avgDealSize,
      totalTalkTime,
      avgTalkTimePerCall
    };
  }, [currentMonthData, selectedRep]);

  // Calculate weekly performance breakdown (with year filter from Date column)
  const weeklyPerformance = useMemo(() => {
    if (!data || !data.tabs) return [];

    // Helper to filter by month, week, rep, AND year
    const filterData = (dataArray, week) => {
      return dataArray?.filter(r =>
        r.Month === month &&
        r.Week === week &&
        (selectedRep === 'All Reps' || r.Rep === selectedRep) &&
        r.Date && new Date(r.Date).getFullYear() === year
      ) || [];
    };

    const weeks = ['W1', 'W2', 'W3', 'W4', 'W5']; // Handle up to 5 weeks
    return weeks.map(week => {
      const weekApps = filterData(data.tabs.Apps_In?.data, week);
      const weekApproved = filterData(data.tabs.Approved?.data, week);
      const weekFunded = filterData(data.tabs.Funded?.data, week);
      const weekRevenue = filterData(data.tabs.Revenue?.data, week);
      const weekCalls = filterData(data.tabs.Dials_TalkTime?.data, week);
      const weekSMS = filterData(data.tabs.SMS?.data, week);

      const revenue = weekRevenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);

      return {
        week,
        apps: weekApps.length,
        approved: weekApproved.length,
        funded: weekFunded.length,
        revenue: revenue / 1000, // Convert to thousands for chart
        calls: weekCalls.length,
        sms: weekSMS.reduce((sum, r) => sum + (parseFloat(r.Count) || 0), 0)
      };
    }).filter(w => w.apps > 0 || w.revenue > 0 || w.calls > 0); // Only include weeks with data
  }, [data, month, selectedRep, year]);

  // Get previous month data for comparison (FULL previous month vs current MTD)
  // Updated to handle year filtering properly
  const previousMonthMetrics = useMemo(() => {
    if (!data || !data.tabs) return null;

    // Calculate previous month and year
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const currentMonthIndex = monthNames.indexOf(month);
    const previousMonthIndex = (currentMonthIndex - 1 + 12) % 12;
    const previousMonth = monthNames[previousMonthIndex];
    // If current month is January, previous month was December of last year
    const previousMonthYear = currentMonthIndex === 0 ? year - 1 : year;

    // Helper to filter by month AND year
    const filterByMonthYear = (dataArray) => {
      return dataArray?.filter(row =>
        row.Month === previousMonth && row.Date && new Date(row.Date).getFullYear() === previousMonthYear
      ) || [];
    };

    // Get previous month data (FULL MONTH - no pro-rating)
    const prevAppsIn = filterByMonthYear(data.tabs.Apps_In?.data);
    const prevApproved = filterByMonthYear(data.tabs.Approved?.data);
    const prevFunded = filterByMonthYear(data.tabs.Funded?.data);
    const prevRevenue = filterByMonthYear(data.tabs.Revenue?.data);
    const prevCalls = filterByMonthYear(data.tabs.Dials_TalkTime?.data);
    const prevSMS = filterByMonthYear(data.tabs.SMS?.data);

    // Filter by rep
    const filterByRep = (arr) => selectedRep === 'All Reps' ? arr : arr.filter(row => row.Rep === selectedRep);
    const appsIn = filterByRep(prevAppsIn);
    const approved = filterByRep(prevApproved);
    const funded = filterByRep(prevFunded);
    const revenue = filterByRep(prevRevenue);
    const calls = filterByRep(prevCalls);
    const sms = filterByRep(prevSMS);

    // Calculate full month totals (NO pro-rating)
    const fullMonthRevenue = revenue.reduce((sum, row) => sum + (parseFloat(row.Revenue) || 0), 0);
    const fullMonthCalls = calls.length;
    const fullMonthSMS = sms.reduce((sum, row) => sum + (parseFloat(row.Count) || 0), 0);
    const fullMonthFundedAmount = funded.reduce((sum, row) => sum + (parseFloat(row.Amount) || 0), 0);

    // Calculate conversion rates (using full month values)
    const callsToApps = fullMonthCalls > 0 ? (appsIn.length / fullMonthCalls * 100) : 0;
    const smsToApps = fullMonthSMS > 0 ? (appsIn.length / fullMonthSMS * 100) : 0;
    const appsToApproved = appsIn.length > 0 ? (approved.length / appsIn.length * 100) : 0;
    const approvedToFunded = approved.length > 0 ? (funded.length / approved.length * 100) : 0;
    const avgDealSize = funded.length > 0 ? fullMonthRevenue / funded.length : 0;

    const result = {
      month: previousMonth,
      appsIn: appsIn.length,
      approved: approved.length,
      funded: funded.length,
      revenue: fullMonthRevenue,
      calls: fullMonthCalls,
      sms: fullMonthSMS,
      fundedAmount: fullMonthFundedAmount,
      callsToApps,
      smsToApps,
      appsToApproved,
      approvedToFunded,
      avgDealSize
    };

    return result;
  }, [data, month, selectedRep, year]);

  // Calculate historical averages for conversion rate comparison (6-month lookback)
  // Updated to handle year filtering properly
  const historicalMetrics = useMemo(() => {
    if (!data || !data.tabs) return null;

    // For individual reps, use calculateHistoricalMetrics
    // For "All Reps", we'll calculate team-wide historical averages
    if (selectedRep === 'All Reps') {
      // Calculate team-wide historical metrics by aggregating all reps
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const currentMonthIndex = monthNames.indexOf(month);

      // Build historical months with their years
      const historicalMonths = [];
      let yearTracker = year;
      for (let i = 1; i <= 6; i++) {
        const monthIndex = (currentMonthIndex - i + 12) % 12;
        // If we wrapped to a higher month index, we're in the previous year
        if (i === 1 && monthIndex > currentMonthIndex) {
          yearTracker = year - 1;
        } else if (monthIndex === 11 && historicalMonths.length > 0) {
          const prevMonthIndex = monthNames.indexOf(historicalMonths[historicalMonths.length - 1].month);
          if (prevMonthIndex < monthIndex) yearTracker--;
        }
        historicalMonths.push({ month: monthNames[monthIndex], year: yearTracker });
      }

      // Helper to filter by month AND year
      const filterByMonthYear = (dataArray, histMonth, histYear) => {
        return dataArray?.filter(r =>
          r.Month === histMonth && r.Date && new Date(r.Date).getFullYear() === histYear
        ) || [];
      };

      const monthlyMetrics = historicalMonths.map(({ month: histMonth, year: histYear }) => {
        const appsIn = filterByMonthYear(data.tabs.Apps_In?.data, histMonth, histYear);
        const approved = filterByMonthYear(data.tabs.Approved?.data, histMonth, histYear);
        const funded = filterValidDeals(filterByMonthYear(data.tabs.Funded?.data, histMonth, histYear));
        const calls = filterByMonthYear(data.tabs.Dials_TalkTime?.data, histMonth, histYear);
        const sms = filterByMonthYear(data.tabs.SMS?.data, histMonth, histYear);

        return {
          month: histMonth,
          year: histYear,
          appsIn: appsIn.length,
          approved: approved.length,
          funded: funded.length,
          calls: calls.length,
          sms: sms.length,
          callsToApps: calls.length > 0 ? (appsIn.length / calls.length * 100) : 0,
          smsToApps: sms.length > 0 ? (appsIn.length / sms.length * 100) : 0,
          appsToApproved: appsIn.length > 0 ? (approved.length / appsIn.length * 100) : 0,
          approvedToFunded: approved.length > 0 ? (funded.length / approved.length * 100) : 0,
        };
      }).filter(m => m.appsIn > 0); // Only include months with activity

      if (monthlyMetrics.length === 0) return null;

      const avg = {
        callsToApps: monthlyMetrics.reduce((sum, m) => sum + m.callsToApps, 0) / monthlyMetrics.length,
        smsToApps: monthlyMetrics.reduce((sum, m) => sum + m.smsToApps, 0) / monthlyMetrics.length,
        appsToApproved: monthlyMetrics.reduce((sum, m) => sum + m.appsToApproved, 0) / monthlyMetrics.length,
        approvedToFunded: monthlyMetrics.reduce((sum, m) => sum + m.approvedToFunded, 0) / monthlyMetrics.length,
      };

      return {
        averages: avg,
        monthlyData: monthlyMetrics,
        monthsIncluded: monthlyMetrics.length
      };
    } else {
      // For individual reps
      return calculateHistoricalMetrics(data, selectedRep, month, 6, year);
    }
  }, [data, selectedRep, month, year]);

  // Calculate trending for conversion rates (comparing recent 3 months vs older 3 months)
  const conversionTrends = useMemo(() => {
    if (!historicalMetrics || !historicalMetrics.monthlyData || historicalMetrics.monthlyData.length < 4) {
      return null; // Need at least 4 months to calculate trends
    }

    const monthlyData = historicalMetrics.monthlyData;

    // Split into recent half and older half
    const midPoint = Math.floor(monthlyData.length / 2);
    const recentMonths = monthlyData.slice(0, midPoint); // Most recent months (first in array)
    const olderMonths = monthlyData.slice(midPoint); // Older months

    // Calculate averages for each period
    const recentAvg = {
      callsToApps: recentMonths.reduce((sum, m) => sum + m.callsToApps, 0) / recentMonths.length,
      smsToApps: recentMonths.reduce((sum, m) => sum + m.smsToApps, 0) / recentMonths.length,
      appsToApproved: recentMonths.reduce((sum, m) => sum + m.appsToApproved, 0) / recentMonths.length,
      approvedToFunded: recentMonths.reduce((sum, m) => sum + m.approvedToFunded, 0) / recentMonths.length,
    };

    const olderAvg = {
      callsToApps: olderMonths.reduce((sum, m) => sum + m.callsToApps, 0) / olderMonths.length,
      smsToApps: olderMonths.reduce((sum, m) => sum + m.smsToApps, 0) / olderMonths.length,
      appsToApproved: olderMonths.reduce((sum, m) => sum + m.appsToApproved, 0) / olderMonths.length,
      approvedToFunded: olderMonths.reduce((sum, m) => sum + m.approvedToFunded, 0) / olderMonths.length,
    };

    // Calculate trends (positive = improving, negative = declining)
    return {
      callsToApps: {
        trend: recentAvg.callsToApps - olderAvg.callsToApps,
        isImproving: recentAvg.callsToApps > olderAvg.callsToApps,
        recentAvg: recentAvg.callsToApps,
        olderAvg: olderAvg.callsToApps
      },
      smsToApps: {
        trend: recentAvg.smsToApps - olderAvg.smsToApps,
        isImproving: recentAvg.smsToApps > olderAvg.smsToApps,
        recentAvg: recentAvg.smsToApps,
        olderAvg: olderAvg.smsToApps
      },
      appsToApproved: {
        trend: recentAvg.appsToApproved - olderAvg.appsToApproved,
        isImproving: recentAvg.appsToApproved > olderAvg.appsToApproved,
        recentAvg: recentAvg.appsToApproved,
        olderAvg: olderAvg.appsToApproved
      },
      approvedToFunded: {
        trend: recentAvg.approvedToFunded - olderAvg.approvedToFunded,
        isImproving: recentAvg.approvedToFunded > olderAvg.approvedToFunded,
        recentAvg: recentAvg.approvedToFunded,
        olderAvg: olderAvg.approvedToFunded
      }
    };
  }, [historicalMetrics]);

  // Get goals for selected rep
  // Updated to use monthYear format (e.g., "January 2026")
  const repGoals = useMemo(() => {
    if (!goals) {
      return null;
    }

    // Use monthYear format for goal matching (e.g., "January 2026")
    const goalMonth = monthYear || `${month} ${year}`;

    if (selectedRep === 'All Reps') {
      // Sum goals for all reps
      const filteredGoals = goals.filter(g => g.Month === goalMonth);

      if (filteredGoals.length === 0) return null;

      return {
        apps: filteredGoals.reduce((sum, g) => sum + parseInt(g.Apps_Goal || 0), 0),
        approved: filteredGoals.reduce((sum, g) => sum + parseInt(g.Approved_Goal || 0), 0),
        funded: filteredGoals.reduce((sum, g) => sum + parseInt(g.Funded_Units_Goal || 0), 0),
        revenue: filteredGoals.reduce((sum, g) => sum + parseInt(g.Revenue_Goal || 0), 0),
        fundedAmount: filteredGoals.reduce((sum, g) => sum + parseInt(g.Funded_Goal || 0), 0),
        activityCallGoal: filteredGoals.reduce((sum, g) => sum + parseInt(g.Activity_Call_Goal || 0), 0)
      };
    }

    const goalRow = goals.find(g =>
      g.Month === goalMonth && g.Rep === selectedRep
    );

    return goalRow ? {
      apps: parseInt(goalRow.Apps_Goal || 0),
      approved: parseInt(goalRow.Approved_Goal || 0),
      funded: parseInt(goalRow.Funded_Units_Goal || 0),
      revenue: parseInt(goalRow.Revenue_Goal || 0),
      fundedAmount: parseInt(goalRow.Funded_Goal || 0),
      activityCallGoal: parseInt(goalRow.Activity_Call_Goal || 0)
    } : null;
  }, [goals, selectedRep, month, monthYear, year]);

  // GROUP 3: Daily Revenue Pacing for LineChart
  const dailyRevenuePacing = useMemo(() => {
    if (!currentMonthData || !repGoals || !repGoals.revenue) return [];

    // Get business days info
    const now = new Date();
    const currentYear = now.getFullYear();
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthIndex = monthNames.indexOf(month);
    const daysInMonth = new Date(currentYear, monthIndex + 1, 0).getDate();

    // Count business days in month
    let businessDaysInMonth = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(currentYear, monthIndex, d);
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) businessDaysInMonth++;
    }

    const dailyGoal = repGoals.revenue / businessDaysInMonth;

    // Get unique dates from Revenue tab
    const revenueDates = [...new Set(
      currentMonthData.revenue.map(r => r.Date?.split('T')[0]).filter(Boolean)
    )].sort();

    let cumulativeActual = 0;
    let cumulativeExpected = 0;

    return revenueDates.map((date, idx) => {
      const dayRevenue = currentMonthData.revenue
        .filter(r => r.Date?.split('T')[0] === date)
        .reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);

      cumulativeActual += dayRevenue;
      cumulativeExpected += dailyGoal;

      return {
        day: `Day ${idx + 1}`,
        date: date,
        actual: cumulativeActual,
        expected: cumulativeExpected
      };
    });
  }, [currentMonthData, repGoals, month]);

  // GROUP 3: Rep Efficiency Metrics for Consolidated Chart (All Reps only)
  const repEfficiencyMetrics = useMemo(() => {
    if (!data || !data.tabs || selectedRep !== 'All Reps') return null;

    const repNames = [...new Set(filterByMonthYear(data.tabs.Dials_TalkTime?.data).map(r => r.Rep) || [])];

    return repNames.map(rep => {
      const repCalls = filterByMonthYear(data.tabs.Dials_TalkTime?.data).filter(r => r.Rep === rep);
      const repApps = filterByMonthYear(data.tabs.Apps_In?.data).filter(r => r.Rep === rep);
      const repRevenue = filterByMonthYear(data.tabs.Revenue?.data).filter(r => r.Rep === rep);
      const totalRevenue = repRevenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);

      return {
        name: rep.split(' ')[0], // First name only for chart - MUST be "name" not "rep" for YAxis dataKey
        calls: repCalls.length,
        apps: repApps.length,
        revenue: totalRevenue / 1000, // In thousands
        callsToApps: repCalls.length > 0 ? (repApps.length / repCalls.length * 100) : 0,
        revenuePerCall: repCalls.length > 0 ? totalRevenue / repCalls.length : 0,
        avgDealSize: repApps.length > 0 ? totalRevenue / repApps.length : 0,
        revenuePerApp: repApps.length > 0 ? totalRevenue / repApps.length : 0
      };
    }).sort((a, b) => b.revenue - a.revenue);
  }, [data, month, selectedRep]);

  // FIX #6: Lender Submissions Metrics - STORY-DRIVEN
  const lenderSubmissionMetrics = useMemo(() => {
    if (!data || !data.tabs) return null;

    // Combine all deal stages for deduplication
    const allDeals = [
      ...(data.tabs.Underwriting?.data || []),
      ...(data.tabs.Approved?.data || []),
      ...(data.tabs.Funded?.data || []),
      ...(data.tabs.Declined_Lost?.data || [])
    ].filter(d => d.Month === month);

    // Dedupe by Opportunity_ID
    const uniqueDeals = [...new Map(allDeals.map(d => [d.Opportunity_ID, d])).values()];

    // Funded deals
    const fundedDeals = uniqueDeals.filter(d => d.Stage === 'Funded');
    const fundedCount = fundedDeals.length;
    const totalSubsOnFunded = fundedDeals.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);

    // Lost deals
    const lostDeals = uniqueDeals.filter(d => d.Stage === 'Closed Lost');
    const lostCount = lostDeals.length;
    const totalSubsOnLost = lostDeals.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);

    // All submissions totals
    const totalSubmissions = uniqueDeals.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);
    const totalDeclines = uniqueDeals.reduce((sum, d) => sum + (parseInt(d.Submissions_Declined) || 0), 0);
    const pendingSubmissions = uniqueDeals.reduce((sum, d) => sum + (parseInt(d.Submissions_Open) || 0), 0);

    // STORY-DRIVEN METRICS
    return {
      // Card 1: Avg Subs to Fund - "How many lenders to get a yes?"
      avgSubsToFund: fundedCount > 0 ? (totalSubsOnFunded / fundedCount) : 0,

      // Card 2: Avg Subs on Lost - "How many before giving up?"
      avgSubsOnLost: lostCount > 0 ? (totalSubsOnLost / lostCount) : 0,

      // Card 3: Decline Rate - "Are we picking wrong lenders?"
      declineRate: totalSubmissions > 0 ? ((totalDeclines / totalSubmissions) * 100) : 0,

      // Card 4: Pending - "Still waiting on response"
      pendingSubmissions,

      // Raw counts for insight text
      fundedCount,
      lostCount,
      totalSubmissions,
      totalDeclines
    };
  }, [data, month]);

  // Option D: Persistence Story Chart Data - Per Rep comparison
  const persistenceByRep = useMemo(() => {
    if (!data || !data.tabs || !reps || reps.length === 0) return [];

    // Combine all deal stages for deduplication
    const allDeals = [
      ...(data.tabs.Underwriting?.data || []),
      ...(data.tabs.Approved?.data || []),
      ...(data.tabs.Funded?.data || []),
      ...(data.tabs.Declined_Lost?.data || [])
    ].filter(d => d.Month === month);

    // Dedupe by Opportunity_ID
    const uniqueDeals = [...new Map(allDeals.map(d => [d.Opportunity_ID, d])).values()];

    return reps.map(rep => {
      const repFirstName = rep.split(' ')[0];

      // Rep's funded deals
      const repFunded = uniqueDeals.filter(d => d.Rep === rep && d.Stage === 'Funded');
      const repFundedCount = repFunded.length;
      const repSubsOnFunded = repFunded.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);

      // Rep's lost deals
      const repLost = uniqueDeals.filter(d => d.Rep === rep && d.Stage === 'Closed Lost');
      const repLostCount = repLost.length;
      const repSubsOnLost = repLost.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);

      return {
        name: repFirstName,
        avgSubsToFund: repFundedCount > 0 ? (repSubsOnFunded / repFundedCount) : 0,
        avgSubsOnLost: repLostCount > 0 ? (repSubsOnLost / repLostCount) : 0,
        fundedCount: repFundedCount,
        lostCount: repLostCount
      };
    }).filter(r => r.fundedCount > 0 || r.lostCount > 0); // Only show reps with deals
  }, [data, month, reps]);

  // FIX #7: Performance by Source (Pack_Source breakdown)
  const thisMonthPackSourceData = useMemo(() => {
    if (!data || !data.tabs) return [];

    const sources = new Set();
    const appsIn = filterByMonthYear(data.tabs.Apps_In?.data);
    const approved = filterByMonthYear(data.tabs.Approved?.data);
    const funded = filterByMonthYear(data.tabs.Funded?.data);
    const revenue = filterByMonthYear(data.tabs.Revenue?.data);

    // Collect all unique sources
    [...appsIn, ...approved, ...funded].forEach(r => sources.add(r.Pack_Source || 'Missing'));

    return Array.from(sources).map(source => {
      const sourceApps = appsIn.filter(r => (r.Pack_Source || 'Missing') === source).length;
      const sourceApproved = approved.filter(r => (r.Pack_Source || 'Missing') === source).length;
      const sourceRevenue = revenue.filter(r => (r.Pack_Source || 'Missing') === source)
        .reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);

      return {
        name: source,
        appsIn: sourceApps,
        approved: sourceApproved,
        revenue: sourceRevenue,
        convRate: sourceApps > 0 ? ((sourceApproved / sourceApps) * 100).toFixed(0) : 0,
        avgDealSize: sourceApproved > 0 ? (sourceRevenue / sourceApproved) : 0
      };
    }).sort((a, b) => b.revenue - a.revenue);
  }, [data, month]);

  // Calculate prescriptive guidance
  // ❌ REMOVED (Nov 8, 2025): Dead code - 'guidance' variable was calculated but never used in UI

  if (!metrics) {
    return <div className="no-data">No data available for {month}</div>;
  }

  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);

  const formatPercent = (value) => `${value.toFixed(1)}%`;

  // Helper to get comparison class (for color coding)
  const getComparisonClass = (current, previous) => {
    if (!previous || previous === 0) return 'neutral';
    const diff = current - previous;
    if (Math.abs(diff) < 0.5) return 'neutral'; // Less than 0.5% difference
    return diff > 0 ? 'positive' : 'negative';
  };

  // Helper to format comparison text
  const getComparisonText = (current, previous) => {
    if (!previous || previous === 0) return '';
    const diff = current - previous;
    const sign = diff > 0 ? '+' : '';
    return `${sign}${diff.toFixed(1)}%`;
  };

  // ❌ REMOVED (Nov 8, 2025): Dead code - 'getOnTrackStatus' function was defined but never called

  // Helper: Get weeks of the month with date ranges
  const getWeeksOfMonth = (month) => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthIndex = monthNames.indexOf(month);
    const year = new Date().getFullYear();

    // Get first and last day of month
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);

    const weeks = [];
    let weekNum = 1;
    let currentDate = new Date(firstDay);

    while (currentDate <= lastDay) {
      const weekStart = new Date(currentDate);
      const weekEnd = new Date(currentDate);
      weekEnd.setDate(currentDate.getDate() + 6);

      // Don't go past end of month
      if (weekEnd > lastDay) {
        weekEnd.setTime(lastDay.getTime());
      }

      const days = [];
      const iterDate = new Date(weekStart);
      while (iterDate <= weekEnd) {
        days.push(iterDate.toISOString().split('T')[0]);
        iterDate.setDate(iterDate.getDate() + 1);
      }

      weeks.push({
        weekNum: `W${weekNum}`,
        days: days,
        startDate: weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        endDate: weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        weekStart: weekStart,
        weekEnd: weekEnd
      });

      currentDate.setDate(weekEnd.getDate() + 1);
      weekNum++;
    }

    return weeks;
  };

  // Helper: Get days in a specific week
  const getDaysInWeek = (weekNum) => {
    const weeks = getWeeksOfMonth(month);
    const week = weeks.find(w => w.weekNum === weekNum);
    if (!week) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return week.days.map(dateStr => {
      const date = new Date(dateStr + 'T00:00:00');
      const isFuture = date > today;
      const isToday = date.toDateString() === today.toDateString();

      return {
        fullDate: dateStr,
        date: date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }),
        dayName: date.toLocaleDateString('en-US', { weekday: 'long' }),
        shortDayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        isFuture,
        isToday
      };
    });
  };

  // Helper: Get aggregated data for a specific week
  const getWeekData = (weekNum) => {
    if (!data || !data.tabs) return null;

    const weeks = getWeeksOfMonth(month);
    const week = weeks.find(w => w.weekNum === weekNum);
    if (!week) return null;

    const filterByDateRange = (arr) => arr.filter(row => {
      if (!row.Date) return false;
      const rowDate = new Date(row.Date.split('T')[0] + 'T00:00:00');
      return rowDate >= week.weekStart && rowDate <= week.weekEnd;
    });

    const filterByRep = (arr) => selectedRep === 'All Reps' ? arr : arr.filter(row => row.Rep === selectedRep);

    const weekAppsIn = filterByRep(filterByDateRange(data.tabs.Apps_In?.data?.filter(r => r.Month === month) || []));
    const weekApproved = filterByRep(filterByDateRange(data.tabs.Approved?.data?.filter(r => r.Month === month) || []));
    const weekFunded = filterByRep(filterByDateRange(filterValidDeals(data.tabs.Funded?.data?.filter(r => r.Month === month) || [])));
    const weekRevenue = filterByRep(filterByDateRange(filterValidDeals(data.tabs.Revenue?.data?.filter(r => r.Month === month) || [])));
    const weekCalls = filterByRep(filterByDateRange(data.tabs.Dials_TalkTime?.data?.filter(r => r.Month === month) || []));
    const weekSMS = filterByRep(filterByDateRange(data.tabs.SMS?.data?.filter(r => r.Month === month) || []));

    const connected = weekCalls.filter(call => parseFloat(call.Duration_Minutes || 0) >= 1.5).length;
    const uniqueCompanies = new Set(weekCalls.filter(call => call.Account && call.Account.trim() !== '').map(call => call.Account)).size;
    const uniqueConnects = new Set(weekCalls.filter(call => parseFloat(call.Duration_Minutes || 0) >= 1.5 && call.Account && call.Account.trim() !== '').map(call => call.Account)).size;
    const totalRevenue = weekRevenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
    const smsCount = weekSMS.reduce((sum, s) => sum + (parseFloat(s.Count) || 0), 0);

    // Activity Points: Date-based formula (Dec 16, 2025)
    const weekEndDate = week.weekEnd.toISOString().split('T')[0];
    const activityCount = calculateActivityPoints(weekCalls.length, smsCount, weekAppsIn.length, weekEndDate);

    return {
      calls: weekCalls.length,
      sms: smsCount,
      connected,
      uniqueCompanies,
      uniqueConnects,
      appsIn: weekAppsIn.length,
      approved: weekApproved.length,
      funded: weekFunded.length,
      revenue: totalRevenue,
      activityCount
    };
  };

  // Helper: Get aggregated data for a specific day
  const getDayDataForMonth = (fullDate) => {
    if (!data || !data.tabs) return null;

    const filterByDate = (arr) => arr.filter(row => row.Date?.split('T')[0] === fullDate);
    const filterByRep = (arr) => selectedRep === 'All Reps' ? arr : arr.filter(row => row.Rep === selectedRep);

    const dayAppsIn = filterByRep(filterByDate(data.tabs.Apps_In?.data?.filter(r => r.Month === month) || []));
    const dayApproved = filterByRep(filterByDate(data.tabs.Approved?.data?.filter(r => r.Month === month) || []));
    const dayFunded = filterByRep(filterByDate(filterValidDeals(data.tabs.Funded?.data?.filter(r => r.Month === month) || [])));
    const dayRevenue = filterByRep(filterByDate(filterValidDeals(data.tabs.Revenue?.data?.filter(r => r.Month === month) || [])));
    const dayCalls = filterByRep(filterByDate(data.tabs.Dials_TalkTime?.data?.filter(r => r.Month === month) || []));
    const daySMS = filterByRep(filterByDate(data.tabs.SMS?.data?.filter(r => r.Month === month) || []));

    const connected = dayCalls.filter(call => parseFloat(call.Duration_Minutes || 0) >= 1.5).length;
    const uniqueCompanies = new Set(dayCalls.filter(call => call.Account && call.Account.trim() !== '').map(call => call.Account)).size;
    const uniqueConnects = new Set(dayCalls.filter(call => parseFloat(call.Duration_Minutes || 0) >= 1.5 && call.Account && call.Account.trim() !== '').map(call => call.Account)).size;
    const totalRevenue = dayRevenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
    const smsCount = daySMS.reduce((sum, s) => sum + (parseFloat(s.Count) || 0), 0);

    // Activity Points: Date-based formula (Dec 16, 2025)
    const activityCount = calculateActivityPoints(dayCalls.length, smsCount, dayAppsIn.length, fullDate);

    return {
      calls: dayCalls.length,
      sms: smsCount,
      connected,
      uniqueCompanies,
      uniqueConnects,
      appsIn: dayAppsIn.length,
      approved: dayApproved.length,
      funded: dayFunded.length,
      revenue: totalRevenue,
      activityCount
    };
  };

  // Helper: Get per-rep breakdown for a specific day
  const getRepBreakdownForMonthDay = (fullDate) => {
    if (!data || !data.tabs || selectedRep !== 'All Reps') return [];

    const repBreakdowns = reps.map(rep => {
      // Get this rep's individual goal
      const repGoalRow = goals?.find(g => g.Rep === rep && g.Month === goalMonth);
      const repActivityGoal = repGoalRow ? parseInt(repGoalRow.Activity_Call_Goal || 0) : 0;

      // Get calls for this rep on this day
      const calls = data.tabs.Dials_TalkTime?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === fullDate && r.Month === month
      ) || [];

      // Calculate connected calls (>= 90 seconds) and unique companies
      const connected = calls.filter(call => parseFloat(call.Duration_Minutes || 0) >= 1.5).length;
      const uniqueCompanies = new Set(calls.filter(call => call.Account && call.Account.trim() !== '').map(call => call.Account)).size;
      const uniqueConnects = new Set(calls.filter(call => parseFloat(call.Duration_Minutes || 0) >= 1.5 && call.Account && call.Account.trim() !== '').map(call => call.Account)).size;

      // Get SMS for this rep on this day
      const sms = data.tabs.SMS?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === fullDate && r.Month === month
      ) || [];
      const smsCount = sms.reduce((sum, s) => sum + (parseFloat(s.Count) || 0), 0);

      // Get Apps In for this rep on this day
      const appsIn = data.tabs.Apps_In?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === fullDate && r.Month === month
      ) || [];

      // Get Approved for this rep on this day
      const approved = data.tabs.Approved?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === fullDate && r.Month === month
      ) || [];

      // Get Funded for this rep on this day
      const funded = filterValidDeals(data.tabs.Funded?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === fullDate && r.Month === month
      ) || []);

      // Get Revenue for this rep on this day
      const revenue = filterValidDeals(data.tabs.Revenue?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === fullDate && r.Month === month
      ) || []);
      const totalRevenue = revenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);

      // Activity Points: Date-based formula (Dec 16, 2025)
      const activityCount = calculateActivityPoints(calls.length, smsCount, appsIn.length, fullDate);

      return {
        rep: rep,
        calls: calls.length,
        sms: smsCount,
        connected: connected,
        uniqueCompanies: uniqueCompanies,
        uniqueConnects: uniqueConnects,
        appsIn: appsIn.length,
        approved: approved.length,
        funded: funded.length,
        revenue: totalRevenue,
        activityCount: activityCount,
        activityGoal: repActivityGoal
      };
    });

    return repBreakdowns;
  };

  // Helper: Get per-rep breakdown for a specific week
  const getRepsForWeek = (weekNum) => {
    if (!data || !data.tabs || selectedRep !== 'All Reps') return [];

    const weeks = getWeeksOfMonth(month);
    const week = weeks.find(w => w.weekNum === weekNum);
    if (!week) return [];

    const repBreakdowns = reps.map(rep => {
      // Get this rep's individual goal
      const repGoalRow = goals?.find(g => g.Rep === rep && g.Month === goalMonth);
      const repActivityGoal = repGoalRow ? parseInt(repGoalRow.Activity_Call_Goal || 0) * 5 : 0; // Weekly goal = daily × ~5 business days

      // Filter data for this week and this rep
      const filterByDateRange = (arr) => arr.filter(row => {
        if (!row.Date) return false;
        const rowDate = new Date(row.Date.split('T')[0] + 'T00:00:00');
        return rowDate >= week.weekStart && rowDate <= week.weekEnd;
      });

      const filterByRep = (arr) => arr.filter(row => row.Rep === rep);

      const weekCalls = filterByRep(filterByDateRange(data.tabs.Dials_TalkTime?.data?.filter(r => r.Month === month) || []));
      const weekSMS = filterByRep(filterByDateRange(data.tabs.SMS?.data?.filter(r => r.Month === month) || []));
      const weekAppsIn = filterByRep(filterByDateRange(data.tabs.Apps_In?.data?.filter(r => r.Month === month) || []));
      const weekApproved = filterByRep(filterByDateRange(data.tabs.Approved?.data?.filter(r => r.Month === month) || []));
      const weekFunded = filterByRep(filterByDateRange(filterValidDeals(data.tabs.Funded?.data?.filter(r => r.Month === month) || [])));
      const weekRevenue = filterByRep(filterByDateRange(filterValidDeals(data.tabs.Revenue?.data?.filter(r => r.Month === month) || [])));

      const connected = weekCalls.filter(call => parseFloat(call.Duration_Minutes || 0) >= 1.5).length;
      const uniqueCompanies = new Set(weekCalls.filter(call => call.Account && call.Account.trim() !== '').map(call => call.Account)).size;
      const uniqueConnects = new Set(weekCalls.filter(call => parseFloat(call.Duration_Minutes || 0) >= 1.5 && call.Account && call.Account.trim() !== '').map(call => call.Account)).size;
      const totalRevenue = weekRevenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
      const smsCount = weekSMS.reduce((sum, s) => sum + (parseFloat(s.Count) || 0), 0);

      // Activity Points: Date-based formula (Dec 16, 2025)
      const weekEndDate = week.weekEnd.toISOString().split('T')[0];
      const activityCount = calculateActivityPoints(weekCalls.length, smsCount, weekAppsIn.length, weekEndDate);

      return {
        rep: rep,
        calls: weekCalls.length,
        sms: smsCount,
        connected,
        uniqueCompanies,
        uniqueConnects,
        appsIn: weekAppsIn.length,
        approved: weekApproved.length,
        funded: weekFunded.length,
        revenue: totalRevenue,
        activityCount,
        activityGoal: repActivityGoal
      };
    });

    return repBreakdowns;
  };

  // Helper: Get daily breakdown for a specific rep within a specific week
  const getDaysForRepInWeek = (weekNum, rep) => {
    if (!data || !data.tabs) return [];

    const weeks = getWeeksOfMonth(month);
    const week = weeks.find(w => w.weekNum === weekNum);
    if (!week) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return week.days.map(dateStr => {
      const date = new Date(dateStr + 'T00:00:00');
      const isFuture = date > today;
      const isToday = date.toDateString() === today.toDateString();

      // Get data for this rep on this day
      const filterByDate = (arr) => arr.filter(row => row.Date?.split('T')[0] === dateStr);
      const filterByRep = (arr) => arr.filter(row => row.Rep === rep);

      const dayCalls = filterByRep(filterByDate(data.tabs.Dials_TalkTime?.data?.filter(r => r.Month === month) || []));
      const daySMS = filterByRep(filterByDate(data.tabs.SMS?.data?.filter(r => r.Month === month) || []));
      const dayAppsIn = filterByRep(filterByDate(data.tabs.Apps_In?.data?.filter(r => r.Month === month) || []));
      const dayApproved = filterByRep(filterByDate(data.tabs.Approved?.data?.filter(r => r.Month === month) || []));
      const dayFunded = filterByRep(filterByDate(filterValidDeals(data.tabs.Funded?.data?.filter(r => r.Month === month) || [])));
      const dayRevenue = filterByRep(filterByDate(filterValidDeals(data.tabs.Revenue?.data?.filter(r => r.Month === month) || [])));

      const connected = dayCalls.filter(call => parseFloat(call.Duration_Minutes || 0) >= 1.5).length;
      const uniqueCompanies = new Set(dayCalls.filter(call => call.Account && call.Account.trim() !== '').map(call => call.Account)).size;
      const uniqueConnects = new Set(dayCalls.filter(call => parseFloat(call.Duration_Minutes || 0) >= 1.5 && call.Account && call.Account.trim() !== '').map(call => call.Account)).size;
      const totalRevenue = dayRevenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
      const smsCount = daySMS.reduce((sum, s) => sum + (parseFloat(s.Count) || 0), 0);

      // Activity Points: Date-based formula (Dec 16, 2025)
      const activityCount = calculateActivityPoints(dayCalls.length, smsCount, dayAppsIn.length, dateStr);

      // Get rep's daily activity goal
      const repGoalRow = goals?.find(g => g.Rep === rep && g.Month === goalMonth);
      const repActivityGoal = repGoalRow ? parseInt(repGoalRow.Activity_Call_Goal || 0) : 0;

      return {
        fullDate: dateStr,
        date: date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }),
        dayName: date.toLocaleDateString('en-US', { weekday: 'long' }),
        shortDayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        isFuture,
        isToday,
        calls: dayCalls.length,
        sms: smsCount,
        connected,
        uniqueCompanies,
        uniqueConnects,
        appsIn: dayAppsIn.length,
        approved: dayApproved.length,
        funded: dayFunded.length,
        revenue: totalRevenue,
        activityCount,
        activityGoal: repActivityGoal
      };
    });
  };

  // Toggle functions
  const toggleWeekExpansion = (weekNum) => {
    if (expandedWeek === weekNum) {
      setExpandedWeek(null);
      setExpandedWeekRep(null); // Close any expanded reps
    } else {
      setExpandedWeek(weekNum);
      setExpandedWeekRep(null); // Reset rep expansion when switching weeks
    }
  };

  const toggleRepExpansion = (rep) => {
    setExpandedWeekRep(expandedWeekRep === rep ? null : rep);
  };

  return (
    <div className="this-month-tab">
      <h2>This Month: {month} (Real-Time)</h2>
      <p className="tab-subtitle">
        {selectedRep === 'All Reps' ? 'Team Performance' : selectedRep}
      </p>

      {/* Sticky Section Navigation */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: 'white',
        padding: '12px 16px',
        marginBottom: '20px',
        borderBottom: '2px solid #e2e8f0',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginRight: '8px' }}>
            JUMP TO:
          </span>
          {[
            { id: 'snapshot', label: '📊 Performance' },
            { id: 'goals', label: '🎯 Goals' },
            { id: 'weekly', label: '📅 Weekly' },
            { id: 'submissions', label: '📤 Submissions' }
          ].map(({ id, label }) => (
            <button
              key={id}
              onClick={() => scrollToSection(id)}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: activeSection === id ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                backgroundColor: activeSection === id ? '#eff6ff' : 'white',
                color: activeSection === id ? '#1e40af' : '#475569',
                fontWeight: activeSection === id ? 600 : 500,
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ============================================ */}
      {/* GROUP 5: KEY INSIGHT BOX                    */}
      {/* Quick diagnosis surfaced to top             */}
      {/* ============================================ */}
      {selectedRep === 'All Reps' && repEfficiencyMetrics && repEfficiencyMetrics.length > 0 && (() => {
        // Calculate top and bottom performers
        const sortedByCallsToApps = [...repEfficiencyMetrics].sort((a, b) => b.callsToApps - a.callsToApps);
        const topPerformer = sortedByCallsToApps[0];
        const needsHelp = sortedByCallsToApps[sortedByCallsToApps.length - 1];

        // Generate insight message
        const avgCallsToApps = repEfficiencyMetrics.reduce((sum, r) => sum + r.callsToApps, 0) / repEfficiencyMetrics.length;
        const insightMessage = topPerformer.callsToApps > avgCallsToApps * 1.5
          ? `${topPerformer.name} is significantly outperforming on call-to-app conversion. Consider having them share best practices with the team.`
          : `Team conversion rates are relatively consistent. Focus on increasing overall call volume to drive more applications.`;

        return (
          <div style={{
            padding: '16px 20px',
            marginBottom: '24px',
            borderRadius: '12px',
            border: '3px solid #3b82f6',
            backgroundColor: '#eff6ff',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '16px',
            position: 'relative'
          }}>
            {developerMode && <div className="dev-top-label">TOP-1</div>}
            <div style={{ fontSize: '32px' }}>💡</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e40af', marginBottom: '8px' }}>
                KEY INSIGHT
              </div>
              <div style={{ fontSize: '16px', color: '#334155', lineHeight: 1.6 }}>
                {insightMessage}
              </div>
              <div style={{ marginTop: '12px', display: 'flex', gap: '16px', fontSize: '14px', flexWrap: 'wrap' }}>
                <span style={{ color: '#059669' }}>
                  ⬆ Top: {topPerformer.name} ({topPerformer.callsToApps.toFixed(1)}% call→app)
                </span>
                <span style={{ color: '#dc2626' }}>
                  ⬇ Needs Help: {needsHelp.name} ({needsHelp.callsToApps.toFixed(1)}% call→app)
                </span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Individual Rep Key Insight */}
      {selectedRep !== 'All Reps' && metrics && (() => {
        const callsToApps = metrics.callsToApps || 0;
        let insightMessage;
        if (callsToApps >= 5) {
          insightMessage = `Strong conversion rate at ${callsToApps.toFixed(1)}%! Maintain current approach and focus on call volume.`;
        } else if (callsToApps >= 2) {
          insightMessage = `Conversion rate of ${callsToApps.toFixed(1)}% is average. Look for opportunities to improve qualification on calls.`;
        } else {
          insightMessage = `Conversion rate of ${callsToApps.toFixed(1)}% needs attention. Consider reviewing call recordings for improvement opportunities.`;
        }

        return (
          <div style={{
            padding: '16px 20px',
            marginBottom: '24px',
            borderRadius: '12px',
            border: '3px solid #3b82f6',
            backgroundColor: '#eff6ff',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '16px',
            position: 'relative'
          }}>
            {developerMode && <div className="dev-top-label">TOP-1</div>}
            <div style={{ fontSize: '32px' }}>💡</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e40af', marginBottom: '8px' }}>
                KEY INSIGHT - {selectedRep.split(' ')[0]}
              </div>
              <div style={{ fontSize: '16px', color: '#334155', lineHeight: 1.6 }}>
                {insightMessage}
              </div>
              <div style={{ marginTop: '12px', display: 'flex', gap: '16px', fontSize: '14px', flexWrap: 'wrap' }}>
                <span>📞 {metrics.calls} calls</span>
                <span>📝 {metrics.appsIn} apps</span>
                <span>💰 {formatCurrency(metrics.revenue)} revenue</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ============================================ */}
      {/* EXECUTIVE SUMMARY - Business Day Pacing     */}
      {/* "How are we doing?" answered in 5 seconds   */}
      {/* ============================================ */}
      {repGoals && selectedRep === 'All Reps' && (() => {
        // Calculate business-day pacing (not vs total goal)
        const businessDaysElapsed = getBusinessDaysElapsed();
        const businessDaysInMonth = getBusinessDaysInMonth();
        const dailyRevenueGoal = repGoals.revenue / businessDaysInMonth;
        const expectedRevenueByNow = dailyRevenueGoal * businessDaysElapsed;
        const pacePercentage = expectedRevenueByNow > 0 ? (metrics.revenue / expectedRevenueByNow) * 100 : 0;

        // New thresholds: 90% = On Track, 70% = Attention, <70% = Critical
        let status, statusLabel, statusEmoji, borderColor, bgColor;
        if (pacePercentage >= 90) {
          status = 'on-track';
          statusLabel = 'ON TRACK';
          statusEmoji = '🟢';
          borderColor = '#10b981';
          bgColor = '#f0fdf4';
        } else if (pacePercentage >= 70) {
          status = 'attention';
          statusLabel = 'ATTENTION';
          statusEmoji = '🟡';
          borderColor = '#f59e0b';
          bgColor = '#fffbeb';
        } else {
          status = 'critical';
          statusLabel = 'CRITICAL';
          statusEmoji = '🔴';
          borderColor = '#ef4444';
          bgColor = '#fef2f2';
        }

        // Find biggest gap (which metric is furthest behind pace)
        const gaps = [
          { name: 'Apps', current: metrics.appsIn, goal: repGoals.apps, expected: (repGoals.apps / businessDaysInMonth) * businessDaysElapsed },
          { name: 'Approved', current: metrics.approved, goal: repGoals.approved, expected: (repGoals.approved / businessDaysInMonth) * businessDaysElapsed },
          { name: 'Funded', current: metrics.funded, goal: repGoals.funded, expected: (repGoals.funded / businessDaysInMonth) * businessDaysElapsed },
          { name: 'Revenue', current: metrics.revenue, goal: repGoals.revenue, expected: expectedRevenueByNow }
        ].filter(g => g.goal > 0);

        const gapAnalysis = gaps.map(g => ({
          ...g,
          pacePercent: g.expected > 0 ? (g.current / g.expected) * 100 : 0,
          gap: g.expected - g.current
        })).sort((a, b) => a.pacePercent - b.pacePercent);

        const biggestGap = gapAnalysis[0];
        const dailyNeeded = biggestGap && (businessDaysInMonth - businessDaysElapsed) > 0
          ? Math.ceil((biggestGap.goal - biggestGap.current) / (businessDaysInMonth - businessDaysElapsed))
          : 0;

        // Find top performer and who needs help (by revenue progress)
        const repPerformance = reps.map(rep => {
          const repRevenue = filterByMonthYear(data.tabs.Revenue?.data).filter(r => r.Rep === rep);
          const repRevenueTotal = repRevenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
          const repGoalRow = goals?.find(g => g.Month === goalMonth && g.Rep === rep);
          const repRevenueGoal = repGoalRow ? parseInt(repGoalRow.Revenue_Goal || 0) : 0;
          const repExpected = repRevenueGoal > 0 ? (repRevenueGoal / businessDaysInMonth) * businessDaysElapsed : 0;
          const repPace = repExpected > 0 ? (repRevenueTotal / repExpected) * 100 : 0;

          // Calculate call→app conversion for context
          const repCalls = filterByMonthYear(data.tabs.Dials_TalkTime?.data).filter(r => r.Rep === rep);
          const repApps = filterByMonthYear(data.tabs.Apps_In?.data).filter(r => r.Rep === rep);
          const callToApp = repCalls.length > 0 ? (repApps.length / repCalls.length) * 100 : 0;

          return {
            name: rep.split(' ')[0],
            revenue: repRevenueTotal,
            pace: repPace,
            callToApp
          };
        }).sort((a, b) => b.pace - a.pace);

        const topPerformer = repPerformance[0];
        const needsHelp = repPerformance[repPerformance.length - 1];

        return (
          <section className="executive-summary" style={{
            padding: '20px',
            marginBottom: '24px',
            borderRadius: '12px',
            border: `3px solid ${borderColor}`,
            backgroundColor: bgColor,
            position: 'relative'
          }}>
            {developerMode && <div className="dev-top-label">TOP-2</div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: '#1e293b' }}>
                {month.toUpperCase()} STATUS: {statusEmoji} {statusLabel}
              </h2>
              <div style={{ fontSize: '13px', color: '#64748b' }}>
                Day {businessDaysElapsed} of {businessDaysInMonth} business days
              </div>
            </div>

            <div style={{ display: 'grid', gap: '8px' }}>
              {/* Revenue Status */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                <span style={{ fontWeight: 600, minWidth: '80px' }}>Revenue:</span>
                <span>{formatCurrency(metrics.revenue)} of {formatCurrency(repGoals.revenue)} goal</span>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 600,
                  backgroundColor: pacePercentage >= 90 ? '#d1fae5' : pacePercentage >= 70 ? '#fef3c7' : '#fee2e2',
                  color: pacePercentage >= 90 ? '#065f46' : pacePercentage >= 70 ? '#92400e' : '#991b1b'
                }}>
                  {pacePercentage.toFixed(0)}% of pace
                </span>
              </div>

              {/* Biggest Gap */}
              {biggestGap && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                  <span style={{ fontWeight: 600, minWidth: '80px' }}>Biggest gap:</span>
                  <span>
                    {biggestGap.name} ({biggestGap.current} of {Math.round(biggestGap.expected)} expected)
                    {dailyNeeded > 0 && ` — need +${dailyNeeded}/day to catch up`}
                  </span>
                </div>
              )}

              {/* Top/Bottom Performers */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                <span style={{ fontWeight: 600, minWidth: '80px' }}>Performers:</span>
                <span>
                  <span style={{ color: '#059669' }}>⬆ {topPerformer?.name}</span>
                  {topPerformer?.callToApp > 0 && ` (${topPerformer.callToApp.toFixed(1)}% call→app)`}
                  <span style={{ margin: '0 8px', color: '#94a3b8' }}>|</span>
                  <span style={{ color: '#dc2626' }}>⬇ {needsHelp?.name}</span>
                  {needsHelp?.callToApp > 0 && ` (${needsHelp.callToApp.toFixed(1)}% call→app)`}
                </span>
              </div>
            </div>
          </section>
        );
      })()}

      {/* ============================================ */}
      {/* SECTION 1: PERFORMANCE SNAPSHOT             */}
      {/* How are we performing this month?           */}
      {/* ============================================ */}
      <section
        ref={sectionRefs.snapshot}
        className="performance-snapshot-section"
        style={{
          scrollMarginTop: '80px',
          marginTop: '24px',
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #e2e8f0',
          position: 'relative'
        }}
      >
        {developerMode && <div className="dev-section-label">S-1</div>}
        {/* Collapsible Header */}
        <div
          onClick={() => toggleSection('snapshot')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            padding: '12px 0',
            borderBottom: '2px solid #e2e8f0',
            marginBottom: '16px'
          }}
        >
          <div>
            <h2 className="section-title" style={{ margin: 0 }}>📊 Performance Snapshot</h2>
            <p className="section-subtitle" style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
              How are we performing this month?
            </p>
          </div>
          <span style={{
            fontSize: '20px',
            color: '#64748b',
            transform: collapsedSections.snapshot ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            ▼
          </span>
        </div>

        {/* Collapsible Content */}
        {!collapsedSections.snapshot && (
        <>
        {/* FIX #1: Performance Snapshot - 6 cards per mockup spec */}
        <section className="performance-summary" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
        {(() => {
          const businessDaysElapsed = getBusinessDaysElapsed();
          const avgBusinessDaysPerMonth = 21;

          return (
            <>
              {/* 1. Apps In */}
              <div style={{ position: 'relative' }}>
                {developerMode && <div className="dev-card-label">K-1</div>}
                <TM_MetricCard
                  label="Apps In"
                  value={metrics.appsIn}
                  goal={repGoals?.apps}
                  expected={repGoals?.apps ? Math.round((repGoals.apps / avgBusinessDaysPerMonth) * businessDaysElapsed) : undefined}
                  businessDay={businessDaysElapsed}
                />
              </div>
              {/* 2. Approved */}
              <div style={{ position: 'relative' }}>
                {developerMode && <div className="dev-card-label">K-2</div>}
                <TM_MetricCard
                  label="Approved"
                  value={metrics.approved}
                  goal={repGoals?.approved}
                  expected={repGoals?.approved ? Math.round((repGoals.approved / avgBusinessDaysPerMonth) * businessDaysElapsed) : undefined}
                  businessDay={businessDaysElapsed}
                />
              </div>
              {/* 3. Funded */}
              <div style={{ position: 'relative' }}>
                {developerMode && <div className="dev-card-label">K-3</div>}
                <TM_MetricCard
                  label="Funded"
                  value={metrics.funded}
                  goal={repGoals?.funded > 0 ? repGoals.funded : undefined}
                  expected={repGoals?.funded > 0 ? Math.round((repGoals.funded / avgBusinessDaysPerMonth) * businessDaysElapsed) : undefined}
                  businessDay={businessDaysElapsed}
                />
              </div>
              {/* 4. Revenue */}
              <div style={{ position: 'relative' }}>
                {developerMode && <div className="dev-card-label">K-4</div>}
                <TM_MetricCard
                  label="Revenue"
                  value={metrics.revenue}
                  goal={repGoals?.revenue}
                  expected={repGoals?.revenue ? Math.round((repGoals.revenue / avgBusinessDaysPerMonth) * businessDaysElapsed) : undefined}
                  businessDay={businessDaysElapsed}
                  formatValue={formatCurrency}
                />
              </div>
              {/* 5. Total Talk Time */}
              <div style={{
                padding: '16px',
                backgroundColor: '#fff',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                minWidth: '140px',
                position: 'relative'
              }}>
                {developerMode && <div className="dev-card-label">K-5</div>}
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Total Talk Time</div>
                <div style={{ fontSize: '28px', fontWeight: 700, color: '#1e293b' }}>
                  {Math.floor(metrics.totalTalkTime / 60)}h {Math.round(metrics.totalTalkTime % 60)}m
                </div>
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                  {metrics.totalTalkTime.toFixed(0)} minutes total
                </div>
              </div>
              {/* 6. Avg per Call */}
              <div style={{
                padding: '16px',
                backgroundColor: '#fff',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                minWidth: '140px',
                position: 'relative'
              }}>
                {developerMode && <div className="dev-card-label">K-6</div>}
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Avg per Call</div>
                <div style={{ fontSize: '28px', fontWeight: 700, color: '#1e293b' }}>
                  {metrics.avgTalkTimePerCall.toFixed(1)}m
                </div>
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                  From {metrics.calls.toLocaleString()} calls
                </div>
              </div>
            </>
          );
        })()}
      </section>

        </>
        )}
      </section>
      {/* End Section 1: Performance Snapshot */}

      {/* ============================================ */}
      {/* SECTION 2: GOAL TRENDING & PROJECTIONS      */}
      {/* How are we trending toward goals?           */}
      {/* ============================================ */}
      <section
        ref={sectionRefs.goals}
        className="goal-trending-section"
        style={{
          scrollMarginTop: '80px',
          marginTop: '24px',
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #e2e8f0',
          position: 'relative'
        }}
      >
        {developerMode && <div className="dev-section-label">S-2</div>}
        {/* Collapsible Header */}
        <div
          onClick={() => toggleSection('goals')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            padding: '12px 0',
            borderBottom: '2px solid #e2e8f0',
            marginBottom: '16px'
          }}
        >
          <div>
            <h2 className="section-title" style={{ margin: 0 }}>🎯 Goal Trending & Projections</h2>
            <p className="section-subtitle" style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
              How are we trending toward our goals?
            </p>
          </div>
          <span style={{
            fontSize: '20px',
            color: '#64748b',
            transform: collapsedSections.goals ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            ▼
          </span>
        </div>

        {/* Collapsible Content */}
        {!collapsedSections.goals && (
        <>
      {/* No goals message */}
      {!repGoals && (
        <div className="no-goals-message" style={{padding: '24px', textAlign: 'center', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #fbbf24'}}>
          <p style={{fontSize: '16px', color: '#92400e', margin: 0}}>
            ⚠️ No goals found for {month}. Set goals in the Goals sheet to track performance.
          </p>
        </div>
      )}

      {/* Gap Closure Math - What it takes to hit goal */}
      {repGoals && (() => {
        const businessDaysElapsed = getBusinessDaysElapsed();
        const businessDaysInMonth = getBusinessDaysInMonth();
        const businessDaysRemaining = businessDaysInMonth - businessDaysElapsed;

        // Calculate gaps and daily rates needed for each metric
        const gapData = [
          {
            metric: 'Apps',
            current: metrics.appsIn,
            goal: repGoals.apps,
            gap: repGoals.apps - metrics.appsIn,
            dailyNeeded: businessDaysRemaining > 0 ? Math.ceil((repGoals.apps - metrics.appsIn) / businessDaysRemaining) : 0
          },
          {
            metric: 'Approved',
            current: metrics.approved,
            goal: repGoals.approved,
            gap: repGoals.approved - metrics.approved,
            dailyNeeded: businessDaysRemaining > 0 ? Math.ceil((repGoals.approved - metrics.approved) / businessDaysRemaining) : 0
          },
          {
            metric: 'Funded',
            current: metrics.funded,
            goal: repGoals.funded,
            gap: repGoals.funded - metrics.funded,
            dailyNeeded: businessDaysRemaining > 0 ? Math.ceil((repGoals.funded - metrics.funded) / businessDaysRemaining) : 0
          },
          {
            metric: 'Revenue',
            current: metrics.revenue,
            goal: repGoals.revenue,
            gap: repGoals.revenue - metrics.revenue,
            dailyNeeded: businessDaysRemaining > 0 ? (repGoals.revenue - metrics.revenue) / businessDaysRemaining : 0,
            isCurrency: true
          }
        ].filter(d => d.goal > 0);

        return (
          <div style={{
            padding: '16px',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            marginBottom: '20px',
            position: 'relative'
          }}>
            {developerMode && <div className="dev-card-label">K-1</div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>📊 Gap Closure Math</h4>
              <span style={{ fontSize: '12px', color: '#64748b' }}>{businessDaysRemaining} days left</span>
            </div>

            <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: '#64748b' }}>Metric</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: '#64748b' }}>Current</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: '#64748b' }}>Goal</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: '#64748b' }}>Gap</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: '#64748b' }}>Daily Rate Needed</th>
                </tr>
              </thead>
              <tbody>
                {gapData.map(row => (
                  <tr key={row.metric} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px', fontWeight: 500 }}>{row.metric}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{row.isCurrency ? formatCurrency(row.current) : row.current}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{row.isCurrency ? formatCurrency(row.goal) : row.goal}</td>
                    <td style={{ padding: '8px', textAlign: 'right', color: row.gap > 0 ? '#ef4444' : '#10b981', fontWeight: 600 }}>
                      {row.gap > 0 ? '-' : '+'}{row.isCurrency ? formatCurrency(Math.abs(row.gap)) : Math.abs(row.gap)}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>
                      {row.gap <= 0 ? '✓ Done' : (row.isCurrency ? formatCurrency(row.dailyNeeded) + '/day' : row.dailyNeeded + '/day')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Unified Rep Performance Table - Consolidates goal progress + efficiency + status */}
      {repGoals && (
        <div className="unified-rep-table" style={{ position: 'relative' }}>
          {developerMode && <div className="dev-card-label">K-2</div>}
          <h4 style={{marginTop: '20px', marginBottom: '12px', fontSize: '16px', fontWeight: 600}}>👥 Rep Performance</h4>
          <table className="rep-breakdown-table" style={{fontSize: '13px'}}>
            <thead>
              <tr>
                <th>Rep</th>
                <th>Apps</th>
                <th>Approved</th>
                <th>Revenue</th>
                <th>Call→App</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {reps.map(rep => {
                // Get rep-specific data
                const repAppsIn = currentMonthData.appsIn.filter(row => row.Rep === rep);
                const repApproved = currentMonthData.approved.filter(row => row.Rep === rep);
                const repRevenue = currentMonthData.revenue.filter(row => row.Rep === rep);
                const repCalls = currentMonthData.calls.filter(row => row.Rep === rep);

                const repTotalRevenue = repRevenue.reduce((sum, row) => sum + (parseFloat(row.Revenue) || 0), 0);
                const repTotalCalls = repCalls.length;
                const repAppsCount = repAppsIn.length;

                // Get rep-specific goals
                const repGoal = goals?.find(g => g.Month === goalMonth && g.Rep === rep);
                if (!repGoal) return null;

                const appsGoal = parseInt(repGoal.Apps_Goal || 0);
                const approvedGoal = parseInt(repGoal.Approved_Goal || 0);
                const revenueGoal = parseInt(repGoal.Revenue_Goal || 0);

                // Progress calculations
                const appsProgress = appsGoal > 0 ? (repAppsCount / appsGoal * 100) : 0;
                const approvedProgress = approvedGoal > 0 ? (repApproved.length / approvedGoal * 100) : 0;
                const revenueProgress = revenueGoal > 0 ? (repTotalRevenue / revenueGoal * 100) : 0;

                // Efficiency: Call→App %
                const callToApp = repTotalCalls > 0 ? (repAppsCount / repTotalCalls * 100) : 0;

                // Status based on revenue pace (using 90/70 thresholds)
                const businessDaysElapsed = getBusinessDaysElapsed();
                const businessDaysInMonth = getBusinessDaysInMonth();
                const expectedRevenue = revenueGoal > 0 ? (revenueGoal / businessDaysInMonth) * businessDaysElapsed : 0;
                const pacePercent = expectedRevenue > 0 ? (repTotalRevenue / expectedRevenue) * 100 : 0;

                let status, statusColor, statusBg;
                if (pacePercent >= 90) {
                  status = '🟢 On Track'; statusColor = '#065f46'; statusBg = '#d1fae5';
                } else if (pacePercent >= 70) {
                  status = '🟡 Attention'; statusColor = '#92400e'; statusBg = '#fef3c7';
                } else {
                  status = '🔴 Critical'; statusColor = '#991b1b'; statusBg = '#fee2e2';
                }

                return (
                  <tr key={rep}>
                    <td style={{fontWeight: 600}}>{rep.split(' ')[0]}</td>
                    <td>
                      <span style={{fontWeight: 600}}>{repAppsCount}/{appsGoal}</span>
                      <span style={{fontSize: '11px', marginLeft: '4px', color: appsProgress >= 90 ? '#10b981' : appsProgress >= 70 ? '#f59e0b' : '#ef4444'}}>
                        ({appsProgress.toFixed(0)}%)
                      </span>
                    </td>
                    <td>
                      <span style={{fontWeight: 600}}>{repApproved.length}/{approvedGoal}</span>
                      <span style={{fontSize: '11px', marginLeft: '4px', color: approvedProgress >= 90 ? '#10b981' : approvedProgress >= 70 ? '#f59e0b' : '#ef4444'}}>
                        ({approvedProgress.toFixed(0)}%)
                      </span>
                    </td>
                    <td style={{fontWeight: 600}}>{formatCurrency(repTotalRevenue)}</td>
                    <td style={{fontWeight: 600}}>{callToApp.toFixed(1)}%</td>
                    <td style={{
                      fontWeight: 600,
                      fontSize: '12px',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      backgroundColor: pacePercent >= 100 ? '#d1fae5' : pacePercent >= 80 ? '#fef3c7' : '#fee2e2',
                      color: pacePercent >= 100 ? '#065f46' : pacePercent >= 80 ? '#92400e' : '#991b1b'
                    }}>
                      {Math.round(pacePercent)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Rep Action Items - Compact table replacing PrescriptiveGuidanceGrid */}
      {selectedRep === 'All Reps' && repGoals && (() => {
        const businessDaysElapsed = getBusinessDaysElapsed();
        const businessDaysInMonth = getBusinessDaysInMonth();
        const businessDaysRemaining = businessDaysInMonth - businessDaysElapsed;

        return (
          <div style={{ marginTop: '24px', position: 'relative' }}>
            {developerMode && <div className="dev-card-label">K-3</div>}
            <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>📋 Rep Action Items</h4>
            <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '12px' }}>
              What each rep needs to do to hit goals ({businessDaysRemaining} business days remaining)
            </p>
            <table className="rep-breakdown-table" style={{ fontSize: '13px' }}>
              <thead>
                <tr>
                  <th>Rep</th>
                  <th>Apps Needed</th>
                  <th>Calls/Day</th>
                  <th>Texts/Day</th>
                  <th>Priority Action</th>
                </tr>
              </thead>
              <tbody>
                {reps.map(rep => {
                  const repAppsIn = currentMonthData.appsIn.filter(row => row.Rep === rep);
                  const repCalls = currentMonthData.calls.filter(row => row.Rep === rep);
                  const repSMS = currentMonthData.sms.filter(row => row.Rep === rep);
                  const repGoal = goals?.find(g => g.Month === goalMonth && g.Rep === rep);

                  if (!repGoal) return null;

                  const appsGoal = parseInt(repGoal.Apps_Goal || 0);
                  const appsNeeded = Math.max(0, appsGoal - repAppsIn.length);

                  // Calculate daily rates needed
                  const callToAppRate = repCalls.length > 0 ? (repAppsIn.length / repCalls.length) : 0.05;
                  const smsToAppRate = repSMS.length > 0 ? (repAppsIn.length / repSMS.length) : 0.03;

                  const callsNeededTotal = callToAppRate > 0 ? appsNeeded / callToAppRate : appsNeeded * 20;
                  const smsNeededTotal = smsToAppRate > 0 ? appsNeeded / smsToAppRate : appsNeeded * 30;

                  const callsPerDay = businessDaysRemaining > 0 ? Math.ceil(callsNeededTotal / businessDaysRemaining) : 0;
                  const textsPerDay = businessDaysRemaining > 0 ? Math.ceil(smsNeededTotal / businessDaysRemaining) : 0;

                  // Priority action logic
                  const callEfficiency = repCalls.length > 0 ? (repAppsIn.length / repCalls.length * 100) : 0;
                  const avgCallEff = historicalMetrics?.averages?.callsToApps || 5;

                  let priorityAction, actionColor;
                  if (repAppsIn.length === 0 && repCalls.length < 50) {
                    priorityAction = '📞 Increase activity'; actionColor = '#ef4444';
                  } else if (callEfficiency < avgCallEff * 0.7) {
                    priorityAction = '🎯 Improve conversions'; actionColor = '#f59e0b';
                  } else if (appsNeeded > appsGoal * 0.5) {
                    priorityAction = '⚡ Push volume'; actionColor = '#f59e0b';
                  } else {
                    priorityAction = '✅ Maintain pace'; actionColor = '#10b981';
                  }

                  return (
                    <tr key={rep}>
                      <td style={{ fontWeight: 600 }}>{rep.split(' ')[0]}</td>
                      <td>{appsNeeded}</td>
                      <td>{callsPerDay}</td>
                      <td>{textsPerDay}</td>
                      <td style={{ color: actionColor, fontWeight: 600, fontSize: '12px' }}>{priorityAction}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Activity Boost Insight moved to Overview tab - see components-to-move-to-overview.js */}
        </>
        )}
      </section>
      {/* End Section 2: Goal Trending & Projections */}

      {/* ============================================ */}
      {/* SECTION 3: WEEKLY BREAKDOWN                 */}
      {/* 3-level drill-down: Week → Rep → Days       */}
      {/* ============================================ */}
      <section
        ref={sectionRefs.weekly}
        className="weekly-breakdown-section"
        style={{
          scrollMarginTop: '80px',
          marginTop: '24px',
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #e2e8f0',
          position: 'relative'
        }}
      >
        {developerMode && <div className="dev-section-label">S-3</div>}
        {/* Collapsible Header */}
        <div
          onClick={() => toggleSection('weekly')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            padding: '12px 0',
            borderBottom: '2px solid #e2e8f0',
            marginBottom: '16px'
          }}
        >
          <div>
            <h2 className="section-title" style={{ margin: 0 }}>📅 Weekly Breakdown</h2>
            <p className="section-subtitle" style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
              Week-by-week performance with drill-down
            </p>
          </div>
          <span style={{
            fontSize: '20px',
            color: '#64748b',
            transform: collapsedSections.weekly ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            ▼
          </span>
        </div>

        {/* Collapsible Content */}
        {!collapsedSections.weekly && (
        <>
        <p className="section-subtitle">Drill down from week to individual rep to daily performance</p>

        {/* GAP-4 FIX: Weekly Breakdown ComposedChart */}
        {weeklyPerformance && weeklyPerformance.length > 0 && (
          <div style={{
            padding: '16px',
            background: '#f8fafc',
            borderRadius: '8px',
            marginBottom: '20px',
            position: 'relative'
          }}>
            {developerMode && <div className="dev-chart-label">C-1</div>}
            <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#1e293b' }}>
              📊 Weekly Activity & Revenue Trend
            </h4>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={weeklyPerformance} margin={{ top: 20, right: 30, bottom: 5, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="week" tick={{ fontSize: 12 }} stroke="#64748b" />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke="#64748b" label={{ value: 'Count', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke="#10b981" tickFormatter={(v) => `$${v}k`} label={{ value: 'Revenue ($k)', angle: 90, position: 'insideRight', style: { fontSize: 11 } }} />
                <Tooltip formatter={(value, name) => name === 'Revenue ($k)' ? [`$${value.toFixed(1)}k`, name] : [value, name]} />
                <Legend wrapperStyle={{ paddingTop: '10px' }} />
                <Bar yAxisId="left" dataKey="calls" fill="#3b82f6" name="Calls" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="left" dataKey="sms" fill="#9333ea" name="SMS" radius={[4, 4, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} name="Revenue ($k)" dot={{ fill: '#10b981', r: 4 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="breakdown-table-container" style={{ position: 'relative' }}>
          {developerMode && <div className="dev-card-label">K-1</div>}
          <table className="breakdown-table">
            <thead>
              <tr>
                <th>Week / Rep / Day</th>
                <th>Calls</th>
                <th>SMS</th>
                <th>Connected #</th>
                <th>Unique Co's</th>
                <th>Unique Connects</th>
                <th>Apps</th>
                <th>Approved</th>
                <th>Funded</th>
                <th>Revenue</th>
                <th>Activity</th>
              </tr>
            </thead>
            <tbody>
              {/* Week rows */}
              {getWeeksOfMonth(month).map(week => {
                const weekData = getWeekData(week.weekNum);
                const isWeekExpanded = expandedWeek === week.weekNum;
                const canExpandWeek = weekData && (weekData.calls > 0 || weekData.appsIn > 0);

                return (
                  <React.Fragment key={week.weekNum}>
                    {/* Level 1: Week row */}
                    <tr
                      className={`week-row ${canExpandWeek ? 'expandable-row' : ''} ${isWeekExpanded ? 'expanded' : ''}`.trim()}
                      onClick={() => canExpandWeek && toggleWeekExpansion(week.weekNum)}
                      style={{
                        cursor: canExpandWeek ? 'pointer' : 'default',
                        backgroundColor: '#f9fafb',
                        fontWeight: '600'
                      }}
                    >
                      <td>
                        <strong>{week.weekNum}</strong>
                        <br />
                        <span style={{ fontSize: '12px', fontWeight: 'normal' }}>
                          {week.startDate} - {week.endDate}
                        </span>
                        {canExpandWeek && <span className="expand-icon">{isWeekExpanded ? ' ▼' : ' ▶'}</span>}
                      </td>
                      <td>{weekData ? weekData.calls : 0}</td>
                      <td>{weekData ? weekData.sms : 0}</td>
                      <td>{weekData ? weekData.connected : 0}</td>
                      <td>{weekData ? weekData.uniqueCompanies : 0}</td>
                      <td>{weekData ? weekData.uniqueConnects : 0}</td>
                      <td>{weekData ? weekData.appsIn : 0}</td>
                      <td>{weekData ? weekData.approved : 0}</td>
                      <td>{weekData ? weekData.funded : 0}</td>
                      <td>{weekData ? formatCurrency(weekData.revenue) : '$0'}</td>
                      <td>{weekData ? weekData.activityCount : 0}</td>
                    </tr>

                    {/* Level 2: Rep rows within expanded week */}
                    {isWeekExpanded && canExpandWeek && selectedRep === 'All Reps' && (
                      <>
                        {getRepsForWeek(week.weekNum)
                          .sort((a, b) => b.activityCount - a.activityCount)
                          .map(repData => {
                            const isRepExpanded = expandedWeekRep === repData.rep;
                            const canExpandRep = repData.calls > 0 || repData.appsIn > 0;

                            return (
                              <React.Fragment key={repData.rep}>
                                {/* Rep row showing weekly totals */}
                                <tr
                                  className={`rep-row ${canExpandRep ? 'expandable-row' : ''} ${isRepExpanded ? 'expanded' : ''}`.trim()}
                                  onClick={() => canExpandRep && toggleRepExpansion(repData.rep)}
                                  style={{
                                    cursor: canExpandRep ? 'pointer' : 'default',
                                    backgroundColor: '#f8fafc'
                                  }}
                                >
                                  <td style={{ paddingLeft: '30px' }}>
                                    <strong>{repData.rep.split(' ')[0]}</strong>
                                    {canExpandRep && <span className="expand-icon">{isRepExpanded ? ' ▼' : ' ▶'}</span>}
                                  </td>
                                  <td>{repData.calls}</td>
                                  <td>{repData.sms}</td>
                                  <td>{repData.connected}</td>
                                  <td>{repData.uniqueCompanies}</td>
                                  <td>{repData.uniqueConnects}</td>
                                  <td>{repData.appsIn}</td>
                                  <td>{repData.approved}</td>
                                  <td>{repData.funded}</td>
                                  <td>{formatCurrency(repData.revenue)}</td>
                                  <td style={{
                                    backgroundColor:
                                      repData.activityGoal === 0 ? 'transparent' :
                                      repData.activityCount >= repData.activityGoal ? '#d1fae5' :
                                      repData.activityCount >= repData.activityGoal * 0.8 ? '#fef3c7' : '#fee2e2',
                                    fontWeight: '600'
                                  }}>
                                    {repData.activityCount}/{repData.activityGoal}
                                  </td>
                                </tr>

                                      {/* Level 3: Daily breakdown for expanded rep */}
                                      {isRepExpanded && canExpandRep && (
                                        <tr className="expanded-detail-row">
                                          <td colSpan="11" style={{ paddingLeft: '40px', backgroundColor: '#fafafa' }}>
                                            <div className="day-breakdown-container">
                                              <h4 style={{ fontSize: '14px', margin: '10px 0' }}>
                                                Daily Breakdown for {repData.rep.split(' ')[0]} - {week.weekNum}
                                              </h4>
                                              <table className="day-breakdown-table" style={{
                                                width: '95%',
                                                fontSize: '13px',
                                                marginLeft: '20px'
                                              }}>
                                                <thead>
                                                  <tr style={{ backgroundColor: '#e5e7eb' }}>
                                                    <th>Day</th>
                                                    <th>Calls</th>
                                                    <th>SMS</th>
                                                    <th>Connected #</th>
                                                    <th>Unique Co's</th>
                                                    <th>Unique Connects</th>
                                                    <th>Apps</th>
                                                    <th>Approved</th>
                                                    <th>Funded</th>
                                                    <th>Revenue</th>
                                                    <th>Activity</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {getDaysForRepInWeek(week.weekNum, repData.rep).map(day => (
                                                    <tr key={day.fullDate}
                                                      className={`${day.isToday ? 'today-row' : ''} ${day.isFuture ? 'future-row' : ''}`.trim()}
                                                      style={{
                                                        backgroundColor: day.isFuture ? '#f3f4f6' : 'white'
                                                      }}
                                                    >
                                                      <td>
                                                        <strong>{day.shortDayName}</strong>
                                                        <br />
                                                        <span style={{ fontSize: '11px' }}>{day.date}</span>
                                                      </td>
                                                      <td>{day.isFuture ? '-' : day.calls}</td>
                                                      <td>{day.isFuture ? '-' : day.sms}</td>
                                                      <td>{day.isFuture ? '-' : day.connected}</td>
                                                      <td>{day.isFuture ? '-' : day.uniqueCompanies}</td>
                                                      <td>{day.isFuture ? '-' : day.uniqueConnects}</td>
                                                      <td>{day.isFuture ? '-' : day.appsIn}</td>
                                                      <td>{day.isFuture ? '-' : day.approved}</td>
                                                      <td>{day.isFuture ? '-' : day.funded}</td>
                                                      <td>{day.isFuture ? '-' : formatCurrency(day.revenue)}</td>
                                                      <td style={{
                                                        backgroundColor: day.isFuture ? 'transparent' :
                                                          day.activityGoal === 0 ? 'transparent' :
                                                          day.activityCount >= day.activityGoal ? '#d1fae5' :
                                                          day.activityCount >= day.activityGoal * 0.8 ? '#fef3c7' : '#fee2e2',
                                                        fontWeight: '600'
                                                      }}>
                                                        {day.isFuture ? '-' : `${day.activityCount}/${day.activityGoal}`}
                                                      </td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                              </React.Fragment>
                            );
                          })}
                      </>
                    )}
                  </React.Fragment>
                );
              })}

              {/* TOTAL row - aggregates entire month */}
              {(() => {
                const isTotalExpanded = expandedWeek === 'TOTAL';
                const canExpandTotal = selectedRep === 'All Reps';

                return (
                  <React.Fragment>
                    <tr
                      className={`total-row ${canExpandTotal ? 'expandable-row' : ''} ${isTotalExpanded ? 'expanded' : ''}`.trim()}
                      onClick={() => canExpandTotal && toggleWeekExpansion('TOTAL')}
                      style={{
                        cursor: canExpandTotal ? 'pointer' : 'default',
                        backgroundColor: '#e0e7ff',
                        fontWeight: 'bold'
                      }}
                    >
                      <td>
                        <strong>TOTAL</strong>
                        {canExpandTotal && <span className="expand-icon">{isTotalExpanded ? ' ▼' : ' ▶'}</span>}
                      </td>
                      <td><strong>{metrics.calls}</strong></td>
                      <td><strong>{metrics.sms}</strong></td>
                      <td><strong>-</strong></td>
                      <td><strong>-</strong></td>
                      <td><strong>-</strong></td>
                      <td><strong>{metrics.appsIn}</strong></td>
                      <td><strong>{metrics.approved}</strong></td>
                      <td><strong>{metrics.funded}</strong></td>
                      <td><strong>{formatCurrency(metrics.revenue)}</strong></td>
                      <td><strong>{calculateActivityPoints(metrics.calls, metrics.sms, metrics.appsIn, new Date().toISOString().split('T')[0])}</strong></td>
                    </tr>

                    {/* Expandable: Per-rep MONTH totals */}
                    {isTotalExpanded && canExpandTotal && (
                      <tr className="expanded-detail-row">
                        <td colSpan="11" style={{ padding: '20px', backgroundColor: '#f0f4ff' }}>
                          <div className="rep-breakdown-container">
                            <h4>Per-Rep Month Totals for {month}</h4>
                            <table className="rep-breakdown-table">
                              <thead>
                                <tr>
                                  <th>Rep</th>
                                  <th>Calls</th>
                                  <th>SMS</th>
                                  <th>Apps</th>
                                  <th>Approved</th>
                                  <th>Funded</th>
                                  <th>Revenue</th>
                                  <th>Activity</th>
                                </tr>
                              </thead>
                              <tbody>
                                {reps.map(rep => {
                                  // Get rep's FULL MONTH data
                                  const repAppsIn = currentMonthData.appsIn.filter(row => row.Rep === rep);
                                  const repApproved = currentMonthData.approved.filter(row => row.Rep === rep);
                                  const repFunded = currentMonthData.funded.filter(row => row.Rep === rep);
                                  const repRevenue = currentMonthData.revenue.filter(row => row.Rep === rep);
                                  const repCalls = currentMonthData.calls.filter(row => row.Rep === rep);
                                  const repSMS = currentMonthData.sms.filter(row => row.Rep === rep);

                                  const repTotalRevenue = repRevenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
                                  const repTotalCalls = repCalls.length;
                                  const repTotalSMS = repSMS.reduce((sum, r) => sum + (parseFloat(r.Count) || 0), 0);
                                  const repAppsCount = repAppsIn.length;
                                  const repApprovedCount = repApproved.length;
                                  const repFundedCount = repFunded.length;

                                  // Get rep's MONTHLY activity goal
                                  const repGoalRow = goals?.find(g => g.Rep === rep && g.Month === goalMonth);
                                  const repActivityGoal = repGoalRow ? parseInt(repGoalRow.Activity_Call_Goal || 0) * 20 : 0; // Monthly goal = daily × 20 working days

                                  return (
                                    <tr key={rep}>
                                      <td><strong>{rep.split(' ')[0]}</strong></td>
                                      <td>{repTotalCalls}</td>
                                      <td>{repTotalSMS}</td>
                                      <td>{repAppsCount}</td>
                                      <td>{repApprovedCount}</td>
                                      <td>{repFundedCount}</td>
                                      <td>{formatCurrency(repTotalRevenue)}</td>
                                      <td style={{
                                        backgroundColor:
                                          repActivityGoal === 0 ? 'transparent' :
                                          calculateActivityPoints(repTotalCalls, repTotalSMS, repAppsCount, new Date().toISOString().split('T')[0]) >= repActivityGoal ? '#d1fae5' :
                                          calculateActivityPoints(repTotalCalls, repTotalSMS, repAppsCount, new Date().toISOString().split('T')[0]) >= repActivityGoal * 0.8 ? '#fef3c7' : '#fee2e2',
                                        fontWeight: '600'
                                      }}>
                                        {calculateActivityPoints(repTotalCalls, repTotalSMS, repAppsCount, new Date().toISOString().split('T')[0])}/{repActivityGoal}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })()}

              {/* TARGET row - show monthly goals */}
              {repGoals && (
                <tr className="target-row" style={{
                  backgroundColor: '#dbeafe',
                  fontWeight: 'bold',
                  fontStyle: 'italic'
                }}>
                  <td><strong>TARGET</strong></td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td>-</td>
                  <td><strong>{repGoals.apps || '-'}</strong></td>
                  <td><strong>{repGoals.approved || '-'}</strong></td>
                  <td><strong>{repGoals.funded || '-'}</strong></td>
                  <td><strong>{repGoals.revenue ? formatCurrency(repGoals.revenue) : '-'}</strong></td>
                  <td><strong>-</strong></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </>
        )}
      </section>
      {/* End Section 3: Weekly Breakdown */}

      {/* ============================================ */}
      {/* SECTION 4: LENDER SUBMISSIONS               */}
      {/* Submission efficiency metrics               */}
      {/* ============================================ */}
      <section
        ref={sectionRefs.submissions}
        className="lender-submissions-section"
        style={{
          scrollMarginTop: '80px',
          marginTop: '24px',
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid #e2e8f0',
          position: 'relative'
        }}
      >
        {developerMode && <div className="dev-section-label">S-4</div>}
        {/* Collapsible Header */}
        <div
          onClick={() => toggleSection('submissions')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            padding: '12px 0',
            borderBottom: '2px solid #e2e8f0',
            marginBottom: '16px'
          }}
        >
          <div>
            <h2 className="section-title" style={{ margin: 0 }}>📤 Lender Submissions</h2>
            <p className="section-subtitle" style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
              Submission efficiency metrics - does persistence pay off?
            </p>
          </div>
          <span style={{
            fontSize: '20px',
            color: '#64748b',
            transform: collapsedSections.submissions ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            ▼
          </span>
        </div>

        {/* Collapsible Content */}
        {!collapsedSections.submissions && lenderSubmissionMetrics && (
        <>
          {/* Insight Box - The Story */}
          {lenderSubmissionMetrics.fundedCount > 0 && lenderSubmissionMetrics.lostCount > 0 && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: lenderSubmissionMetrics.avgSubsToFund > lenderSubmissionMetrics.avgSubsOnLost ? '#f0fdf4' : '#fef3c7',
              borderRadius: '8px',
              border: `1px solid ${lenderSubmissionMetrics.avgSubsToFund > lenderSubmissionMetrics.avgSubsOnLost ? '#86efac' : '#fcd34d'}`,
              marginBottom: '16px',
              fontSize: '13px'
            }}>
              <strong>💡 Insight:</strong> Funded deals averaged <strong>{lenderSubmissionMetrics.avgSubsToFund.toFixed(1)}</strong> submissions vs Lost deals at <strong>{lenderSubmissionMetrics.avgSubsOnLost.toFixed(1)}</strong>
              {lenderSubmissionMetrics.avgSubsToFund > lenderSubmissionMetrics.avgSubsOnLost
                ? ' — persistence matters!'
                : ' — quality over quantity'}
            </div>
          )}

          {/* 4 Story-Driven Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '12px'
          }}>
            {/* Card 1: Avg Subs to Fund */}
            <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
              {developerMode && <div className="dev-card-label">K-7-1</div>}
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>Avg Subs to Fund</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#059669' }}>
                {lenderSubmissionMetrics.avgSubsToFund.toFixed(1)}
              </div>
              <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '4px' }}>
                from {lenderSubmissionMetrics.fundedCount} funded deals
              </div>
            </div>

            {/* Card 2: Avg Subs on Lost */}
            <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
              {developerMode && <div className="dev-card-label">K-7-2</div>}
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>Avg Subs on Lost</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#dc2626' }}>
                {lenderSubmissionMetrics.avgSubsOnLost.toFixed(1)}
              </div>
              <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '4px' }}>
                from {lenderSubmissionMetrics.lostCount} lost deals
              </div>
            </div>

            {/* Card 3: Decline Rate */}
            <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
              {developerMode && <div className="dev-card-label">K-7-3</div>}
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>Decline Rate</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#f59e0b' }}>
                {lenderSubmissionMetrics.declineRate.toFixed(0)}%
              </div>
              <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '4px' }}>
                {lenderSubmissionMetrics.totalDeclines} of {lenderSubmissionMetrics.totalSubmissions} declined
              </div>
            </div>

            {/* Card 4: Pending */}
            <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
              {developerMode && <div className="dev-card-label">K-7-4</div>}
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>Pending</div>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#3b82f6' }}>
                {lenderSubmissionMetrics.pendingSubmissions}
              </div>
              <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '4px' }}>
                awaiting response
              </div>
            </div>
          </div>
        </>
        )}
      </section>
      {/* End Section 4: Lender Submissions */}


    </div>
  );
}

/**
 * LAST MONTH TAB - Previous month final results
 * Updated to handle year filtering properly
 */
function LastMonthTab({ data, goals, selectedRep, month, reps, developerMode, year = new Date().getFullYear(), monthYear }) {
  // Helper: Use monthYear for goals (e.g., "December 2025"), fallback to month + year
  const goalMonth = monthYear || `${month} ${year}`;

  // Helper: Filter by month AND year using Date column
  const filterByMonthYear = (arr) =>
    (arr || []).filter(r => r.Month === month && r.Date && new Date(r.Date).getFullYear() === year);

  const [expandedWeek, setExpandedWeek] = useState(null); // Track which week is expanded (null, 'W1', 'W2', etc., or 'TOTAL')
  const [expandedWeekRep, setExpandedWeekRep] = useState(null); // Track which rep is expanded within the expanded week (null or rep name)

  // NEW: Collapsible sections state
  const [activeSection, setActiveSection] = useState('summary');
  const [collapsedSections, setCollapsedSections] = useState({});

  // Section refs for scrolling
  const sectionRefs = {
    summary: useRef(null),
    snapshot: useRef(null),
    submissions: useRef(null),
    goals: useRef(null),
    weekly: useRef(null),
    submissions: useRef(null),
    why: useRef(null)
  };

  // Scroll to section function
  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs[sectionId]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Toggle section collapse
  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Get last month data from raw tabs (with year filter from Date column)
  const lastMonthData = useMemo(() => {
    if (!data || !data.tabs) return null;

    // Helper to filter by month AND year
    const filterByMonthYear = (dataArray) => {
      return dataArray?.filter(row =>
        row.Month === month && row.Date && new Date(row.Date).getFullYear() === year
      ) || [];
    };

    const appsIn = filterByMonthYear(data.tabs.Apps_In?.data);
    const approved = filterByMonthYear(data.tabs.Approved?.data);
    const funded = filterValidDeals(filterByMonthYear(data.tabs.Funded?.data));
    const revenue = filterValidDeals(filterByMonthYear(data.tabs.Revenue?.data));
    const calls = filterByMonthYear(data.tabs.Dials_TalkTime?.data);
    const sms = filterByMonthYear(data.tabs.SMS?.data);

    return { appsIn, approved, funded, revenue, calls, sms };
  }, [data, month, year]);

  // Calculate metrics for selected rep or all reps
  const metrics = useMemo(() => {
    if (!lastMonthData) return null;

    const filterByRep = (arr) => selectedRep === 'All Reps'
      ? arr
      : arr.filter(row => row.Rep === selectedRep);

    const appsIn = filterByRep(lastMonthData.appsIn);
    const approved = filterByRep(lastMonthData.approved);
    const funded = filterByRep(lastMonthData.funded);
    const revenue = filterByRep(lastMonthData.revenue);
    const calls = filterByRep(lastMonthData.calls);
    const sms = filterByRep(lastMonthData.sms);

    const totalRevenue = revenue.reduce((sum, row) => sum + (parseFloat(row.Revenue) || 0), 0);
    const fundedAmount = funded.reduce((sum, row) => sum + (parseFloat(row.Amount) || 0), 0);
    const totalCalls = calls.length;
    const totalSMS = sms.length;

    // Conversion rates
    const callsToApps = totalCalls > 0 ? (appsIn.length / totalCalls * 100) : 0;
    const smsToApps = totalSMS > 0 ? (appsIn.length / totalSMS * 100) : 0;
    const appsToApproved = appsIn.length > 0 ? (approved.length / appsIn.length * 100) : 0;
    const approvedToFunded = approved.length > 0 ? (funded.length / approved.length * 100) : 0;

    // Efficiency metrics
    const revenuePerCall = totalCalls > 0 ? totalRevenue / totalCalls : 0;
    const revenuePerSMS = totalSMS > 0 ? totalRevenue / totalSMS : 0;
    const avgDealSize = funded.length > 0 ? totalRevenue / funded.length : 0;

    return {
      appsIn: appsIn.length,
      approved: approved.length,
      funded: funded.length,
      revenue: totalRevenue,
      fundedAmount: fundedAmount,
      calls: totalCalls,
      sms: totalSMS,
      callsToApps,
      smsToApps,
      appsToApproved,
      approvedToFunded,
      revenuePerCall,
      revenuePerSMS,
      avgDealSize
    };
  }, [lastMonthData, selectedRep]);

  // Calculate weekly performance breakdown
  const weeklyPerformance = useMemo(() => {
    if (!data || !data.tabs) return [];

    const weeks = ['W1', 'W2', 'W3', 'W4', 'W5'];
    return weeks.map(week => {
      const weekApps = filterByMonthYear(data.tabs.Apps_In?.data).filter(r => r.Week === week && (selectedRep === 'All Reps' || r.Rep === selectedRep));
      const weekApproved = filterByMonthYear(data.tabs.Approved?.data).filter(r => r.Week === week && (selectedRep === 'All Reps' || r.Rep === selectedRep));
      const weekFunded = filterByMonthYear(data.tabs.Funded?.data).filter(r => r.Week === week && (selectedRep === 'All Reps' || r.Rep === selectedRep));
      const weekRevenue = filterByMonthYear(data.tabs.Revenue?.data).filter(r => r.Week === week && (selectedRep === 'All Reps' || r.Rep === selectedRep));
      const weekCalls = filterByMonthYear(data.tabs.Dials_TalkTime?.data).filter(r => r.Week === week && (selectedRep === 'All Reps' || r.Rep === selectedRep));
      const weekSMS = filterByMonthYear(data.tabs.SMS?.data).filter(r => r.Week === week && (selectedRep === 'All Reps' || r.Rep === selectedRep));

      const revenue = weekRevenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);

      return {
        week,
        apps: weekApps.length,
        approved: weekApproved.length,
        funded: weekFunded.length,
        revenue: revenue / 1000,
        calls: weekCalls.length,
        sms: weekSMS.reduce((sum, r) => sum + (parseFloat(r.Count) || 0), 0)
      };
    }).filter(w => w.apps > 0 || w.revenue > 0 || w.calls > 0);
  }, [data, month, selectedRep]);

  // Get previous month data for comparison (month before last month)
  const previousMonthMetrics = useMemo(() => {
    if (!data || !data.tabs) return null;

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const lastMonthIndex = monthNames.indexOf(month);
    const previousMonth = lastMonthIndex > 0 ? monthNames[lastMonthIndex - 1] : null;

    if (!previousMonth) return null;

    const prevAppsIn = data.tabs.Apps_In?.data?.filter(row => row.Month === previousMonth) || [];
    const prevApproved = data.tabs.Approved?.data?.filter(row => row.Month === previousMonth) || [];
    const prevFunded = data.tabs.Funded?.data?.filter(row => row.Month === previousMonth) || [];
    const prevRevenue = data.tabs.Revenue?.data?.filter(row => row.Month === previousMonth) || [];
    const prevCalls = data.tabs.Dials_TalkTime?.data?.filter(row => row.Month === previousMonth) || [];
    const prevSMS = data.tabs.SMS?.data?.filter(row => row.Month === previousMonth) || [];

    const filterByRep = (arr) => selectedRep === 'All Reps' ? arr : arr.filter(row => row.Rep === selectedRep);
    const appsIn = filterByRep(prevAppsIn);
    const approved = filterByRep(prevApproved);
    const funded = filterByRep(prevFunded);
    const revenue = filterByRep(prevRevenue);
    const calls = filterByRep(prevCalls);
    const sms = filterByRep(prevSMS);

    const totalRevenue = revenue.reduce((sum, row) => sum + (parseFloat(row.Revenue) || 0), 0);
    const totalCalls = calls.length;
    const totalSMS = sms.reduce((sum, row) => sum + (parseFloat(row.Count) || 0), 0);
    const fundedAmount = funded.reduce((sum, row) => sum + (parseFloat(row.Amount) || 0), 0);

    // Calculate conversion rates
    const callsToApps = totalCalls > 0 ? (appsIn.length / totalCalls * 100) : 0;
    const smsToApps = totalSMS > 0 ? (appsIn.length / totalSMS * 100) : 0;
    const appsToApproved = appsIn.length > 0 ? (approved.length / appsIn.length * 100) : 0;
    const approvedToFunded = approved.length > 0 ? (funded.length / approved.length * 100) : 0;
    const avgDealSize = funded.length > 0 ? totalRevenue / funded.length : 0;

    return {
      month: previousMonth,
      appsIn: appsIn.length,
      approved: approved.length,
      funded: funded.length,
      revenue: totalRevenue,
      calls: totalCalls,
      sms: totalSMS,
      fundedAmount: fundedAmount,
      callsToApps,
      smsToApps,
      appsToApproved,
      approvedToFunded,
      avgDealSize
    };
  }, [data, month, selectedRep]);

  // 6-month historical metrics for conversion baseline
  const historicalMetrics = useMemo(() => {
    if (!data || !data.tabs) return null;

    // For "All Reps", calculate team-wide historical metrics
    if (selectedRep === 'All Reps') {
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December'];
      const currentMonthIndex = monthNames.indexOf(month);

      const historicalMonths = [];
      for (let i = 1; i <= 6; i++) {
        const monthIndex = (currentMonthIndex - i + 12) % 12;
        historicalMonths.push(monthNames[monthIndex]);
      }

      const monthlyMetrics = historicalMonths.map(histMonth => {
        const appsIn = data.tabs.Apps_In?.data?.filter(r => r.Month === histMonth) || [];
        const approved = data.tabs.Approved?.data?.filter(r => r.Month === histMonth) || [];
        const funded = filterValidDeals(data.tabs.Funded?.data?.filter(r => r.Month === histMonth) || []);
        const calls = data.tabs.Dials_TalkTime?.data?.filter(r => r.Month === histMonth) || [];
        const sms = data.tabs.SMS?.data?.filter(r => r.Month === histMonth) || [];

        return {
          month: histMonth,
          appsIn: appsIn.length,
          approved: approved.length,
          funded: funded.length,
          calls: calls.length,
          sms: sms.length,
          callsToApps: calls.length > 0 ? (appsIn.length / calls.length * 100) : 0,
          smsToApps: sms.length > 0 ? (appsIn.length / sms.length * 100) : 0,
          appsToApproved: appsIn.length > 0 ? (approved.length / appsIn.length * 100) : 0,
          approvedToFunded: approved.length > 0 ? (funded.length / approved.length * 100) : 0,
        };
      }).filter(m => m.appsIn > 0); // Only include months with activity

      if (monthlyMetrics.length === 0) return null;

      const avg = {
        callsToApps: monthlyMetrics.reduce((sum, m) => sum + m.callsToApps, 0) / monthlyMetrics.length,
        smsToApps: monthlyMetrics.reduce((sum, m) => sum + m.smsToApps, 0) / monthlyMetrics.length,
        appsToApproved: monthlyMetrics.reduce((sum, m) => sum + m.appsToApproved, 0) / monthlyMetrics.length,
        approvedToFunded: monthlyMetrics.reduce((sum, m) => sum + m.approvedToFunded, 0) / monthlyMetrics.length,
      };

      return {
        averages: avg,
        monthlyData: monthlyMetrics,  // CRITICAL: Required for conversionTrends
        monthsIncluded: monthlyMetrics.length
      };
    } else {
      // For individual reps, use the existing helper function
      return calculateHistoricalMetrics(data, selectedRep, month, 6);
    }
  }, [data, selectedRep, month]);

  // Conversion trends: Compare recent 3 months vs older 3 months
  const conversionTrends = useMemo(() => {
    if (!historicalMetrics || !historicalMetrics.monthlyData || historicalMetrics.monthlyData.length < 4) {
      return null; // Need at least 4 months to calculate trends
    }

    const monthlyData = historicalMetrics.monthlyData;

    // Split into recent half and older half
    const midPoint = Math.floor(monthlyData.length / 2);
    const recentMonths = monthlyData.slice(0, midPoint);
    const olderMonths = monthlyData.slice(midPoint);

    // Calculate averages for each period
    const recentAvg = {
      callsToApps: recentMonths.reduce((sum, m) => sum + m.callsToApps, 0) / recentMonths.length,
      smsToApps: recentMonths.reduce((sum, m) => sum + m.smsToApps, 0) / recentMonths.length,
      appsToApproved: recentMonths.reduce((sum, m) => sum + m.appsToApproved, 0) / recentMonths.length,
      approvedToFunded: recentMonths.reduce((sum, m) => sum + m.approvedToFunded, 0) / recentMonths.length,
    };

    const olderAvg = {
      callsToApps: olderMonths.reduce((sum, m) => sum + m.callsToApps, 0) / olderMonths.length,
      smsToApps: olderMonths.reduce((sum, m) => sum + m.smsToApps, 0) / olderMonths.length,
      appsToApproved: olderMonths.reduce((sum, m) => sum + m.appsToApproved, 0) / olderMonths.length,
      approvedToFunded: olderMonths.reduce((sum, m) => sum + m.approvedToFunded, 0) / olderMonths.length,
    };

    // Calculate trends (positive = improving, negative = declining)
    return {
      callsToApps: {
        trend: recentAvg.callsToApps - olderAvg.callsToApps,
        isImproving: recentAvg.callsToApps > olderAvg.callsToApps,
        recentAvg: recentAvg.callsToApps,
        olderAvg: olderAvg.callsToApps
      },
      smsToApps: {
        trend: recentAvg.smsToApps - olderAvg.smsToApps,
        isImproving: recentAvg.smsToApps > olderAvg.smsToApps,
        recentAvg: recentAvg.smsToApps,
        olderAvg: olderAvg.smsToApps
      },
      appsToApproved: {
        trend: recentAvg.appsToApproved - olderAvg.appsToApproved,
        isImproving: recentAvg.appsToApproved > olderAvg.appsToApproved,
        recentAvg: recentAvg.appsToApproved,
        olderAvg: olderAvg.appsToApproved
      },
      approvedToFunded: {
        trend: recentAvg.approvedToFunded - olderAvg.approvedToFunded,
        isImproving: recentAvg.approvedToFunded > olderAvg.approvedToFunded,
        recentAvg: recentAvg.approvedToFunded,
        olderAvg: olderAvg.approvedToFunded
      }
    };
  }, [historicalMetrics]);

  // Get goals for last month (CRITICAL: Use last month, not current month)
  // Updated to use monthYear format (e.g., "December 2025")
  const repGoals = useMemo(() => {
    if (!goals) return null;

    // Use monthYear format for goal matching
    const goalMonth = monthYear || `${month} ${year}`;

    const goalRow = goals.find(g =>
      g.Month === goalMonth &&
      (selectedRep === 'All Reps' || g.Rep === selectedRep)
    );

    if (selectedRep === 'All Reps') {
      const filteredGoals = goals.filter(g => g.Month === goalMonth);
      return {
        apps: filteredGoals.reduce((sum, g) => sum + parseInt(g.Apps_Goal || 0), 0),
        approved: filteredGoals.reduce((sum, g) => sum + parseInt(g.Approved_Goal || 0), 0),
        funded: filteredGoals.reduce((sum, g) => sum + parseInt(g.Funded_Units_Goal || g.Funded_Goal || 0), 0),
        revenue: filteredGoals.reduce((sum, g) => sum + parseInt(g.Revenue_Goal || 0), 0),
        activityCallGoal: filteredGoals.reduce((sum, g) => sum + parseInt(g.Activity_Call_Goal || 0), 0)
      };
    }

    return goalRow ? {
      apps: parseInt(goalRow.Apps_Goal || 0),
      approved: parseInt(goalRow.Approved_Goal || 0),
      funded: parseInt(goalRow.Funded_Units_Goal || goalRow.Funded_Goal || 0),
      revenue: parseInt(goalRow.Revenue_Goal || 0),
      activityCallGoal: parseInt(goalRow.Activity_Call_Goal || 0)
    } : null;
  }, [goals, selectedRep, month, monthYear, year]);

  // M-1: Lender Submissions Metrics for Last Month - STORY-DRIVEN
  const lenderSubmissionMetrics = useMemo(() => {
    if (!data || !data.tabs) return null;

    // Combine all deal stages for deduplication
    const allDeals = [
      ...(data.tabs.Underwriting?.data || []),
      ...(data.tabs.Approved?.data || []),
      ...(data.tabs.Funded?.data || []),
      ...(data.tabs.Declined_Lost?.data || [])
    ].filter(d => d.Month === month);

    // Dedupe by Opportunity_ID
    const uniqueDeals = [...new Map(allDeals.map(d => [d.Opportunity_ID, d])).values()];

    // Funded deals
    const fundedDeals = uniqueDeals.filter(d => d.Stage === 'Funded');
    const fundedCount = fundedDeals.length;
    const totalSubsOnFunded = fundedDeals.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);

    // Lost deals
    const lostDeals = uniqueDeals.filter(d => d.Stage === 'Closed Lost');
    const lostCount = lostDeals.length;
    const totalSubsOnLost = lostDeals.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);

    // All submissions totals
    const totalSubmissions = uniqueDeals.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);
    const totalDeclines = uniqueDeals.reduce((sum, d) => sum + (parseInt(d.Submissions_Declined) || 0), 0);
    const pendingSubmissions = uniqueDeals.reduce((sum, d) => sum + (parseInt(d.Submissions_Open) || 0), 0);

    // STORY-DRIVEN METRICS
    return {
      avgSubsToFund: fundedCount > 0 ? (totalSubsOnFunded / fundedCount) : 0,
      avgSubsOnLost: lostCount > 0 ? (totalSubsOnLost / lostCount) : 0,
      declineRate: totalSubmissions > 0 ? ((totalDeclines / totalSubmissions) * 100) : 0,
      pendingSubmissions,
      fundedCount,
      lostCount,
      totalSubmissions,
      totalDeclines
    };
  }, [data, month]);

  // M-1c: Persistence Story Chart Data - Per Rep comparison for Last Month
  const persistenceByRep = useMemo(() => {
    if (!data || !data.tabs || !reps || reps.length === 0) return [];

    // Combine all deal stages for deduplication
    const allDeals = [
      ...(data.tabs.Underwriting?.data || []),
      ...(data.tabs.Approved?.data || []),
      ...(data.tabs.Funded?.data || []),
      ...(data.tabs.Declined_Lost?.data || [])
    ].filter(d => d.Month === month);

    // Dedupe by Opportunity_ID
    const uniqueDeals = [...new Map(allDeals.map(d => [d.Opportunity_ID, d])).values()];

    return reps.map(rep => {
      const repFirstName = rep.split(' ')[0];

      // Rep's funded deals
      const repFunded = uniqueDeals.filter(d => d.Rep === rep && d.Stage === 'Funded');
      const repFundedCount = repFunded.length;
      const repSubsOnFunded = repFunded.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);

      // Rep's lost deals
      const repLost = uniqueDeals.filter(d => d.Rep === rep && d.Stage === 'Closed Lost');
      const repLostCount = repLost.length;
      const repSubsOnLost = repLost.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);

      return {
        name: repFirstName,
        avgSubsToFund: repFundedCount > 0 ? (repSubsOnFunded / repFundedCount) : 0,
        avgSubsOnLost: repLostCount > 0 ? (repSubsOnLost / repLostCount) : 0,
        fundedCount: repFundedCount,
        lostCount: repLostCount
      };
    }).filter(r => r.fundedCount > 0 || r.lostCount > 0);
  }, [data, month, reps]);

  // M-1b: Weekly Submissions by Rep (REAL submission data for chart)
  const weeklySubmissionsByRep = useMemo(() => {
    if (!data || !data.tabs || !reps || reps.length === 0) return [];

    // Get all deals with submission data
    const allDeals = [
      ...(data.tabs.Underwriting?.data || []),
      ...(data.tabs.Approved?.data || []),
      ...(data.tabs.Funded?.data || []),
      ...(data.tabs.Declined_Lost?.data || [])
    ].filter(d => d.Month === month);

    // Dedupe by Opportunity_ID to avoid double-counting
    const uniqueDeals = [...new Map(allDeals.map(d => [d.Opportunity_ID, d])).values()];

    // Get unique weeks
    const weeks = ['W1', 'W2', 'W3', 'W4', 'W5'];

    // Build data for chart: { week: 'W1', rep1: X, rep2: Y, ... }
    return weeks.map(week => {
      const weekData = { week };

      reps.forEach(rep => {
        const repFirstName = rep.split(' ')[0];
        const repDeals = uniqueDeals.filter(d => d.Week === week && d.Rep === rep);
        const repSubmissions = repDeals.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);
        weekData[repFirstName] = repSubmissions;
      });

      return weekData;
    }).filter(w => {
      // Only include weeks that have any submissions
      const hasSubmissions = Object.keys(w).some(k => k !== 'week' && w[k] > 0);
      return hasSubmissions;
    });
  }, [data, month, reps]);

  // M-2: Performance by Source (Pack_Source breakdown) for Last Month
  const lastMonthPackSourceData = useMemo(() => {
    if (!data || !data.tabs) return [];

    const sources = new Set();
    const appsIn = filterByMonthYear(data.tabs.Apps_In?.data);
    const approved = filterByMonthYear(data.tabs.Approved?.data);
    const funded = filterByMonthYear(data.tabs.Funded?.data);
    const revenue = filterByMonthYear(data.tabs.Revenue?.data);

    // Collect all unique sources
    [...appsIn, ...approved, ...funded].forEach(r => sources.add(r.Pack_Source || 'Missing'));

    return Array.from(sources).map(source => {
      const sourceApps = appsIn.filter(r => (r.Pack_Source || 'Missing') === source).length;
      const sourceApproved = approved.filter(r => (r.Pack_Source || 'Missing') === source).length;
      const sourceFunded = funded.filter(r => (r.Pack_Source || 'Missing') === source).length;
      const sourceRevenue = revenue.filter(r => (r.Pack_Source || 'Missing') === source)
        .reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);

      return {
        name: source,
        appsIn: sourceApps,
        approved: sourceApproved,
        funded: sourceFunded,
        revenue: sourceRevenue,
        convRate: sourceApps > 0 ? ((sourceApproved / sourceApps) * 100).toFixed(0) : 0,
        avgDealSize: sourceFunded > 0 ? (sourceRevenue / sourceFunded) : 0
      };
    }).sort((a, b) => b.revenue - a.revenue);
  }, [data, month]);

  // Helper: Get weeks of the month with date ranges
  const getWeeksOfMonth = (month) => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthIndex = monthNames.indexOf(month);
    const year = new Date().getFullYear();

    // Get first and last day of month
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);

    const weeks = [];
    let weekNum = 1;
    let currentDate = new Date(firstDay);

    while (currentDate <= lastDay) {
      const weekStart = new Date(currentDate);
      const weekEnd = new Date(currentDate);
      weekEnd.setDate(currentDate.getDate() + 6);

      // Don't go past end of month
      if (weekEnd > lastDay) {
        weekEnd.setTime(lastDay.getTime());
      }

      const days = [];
      const iterDate = new Date(weekStart);
      while (iterDate <= weekEnd) {
        days.push(iterDate.toISOString().split('T')[0]);
        iterDate.setDate(iterDate.getDate() + 1);
      }

      weeks.push({
        weekNum: `W${weekNum}`,
        days: days,
        startDate: weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        endDate: weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        weekStart: weekStart,
        weekEnd: weekEnd
      });

      currentDate.setDate(weekEnd.getDate() + 1);
      weekNum++;
    }

    return weeks;
  };

  // Helper: Get aggregated data for a specific week
  const getWeekData = (weekNum) => {
    if (!data || !data.tabs) return null;

    const weeks = getWeeksOfMonth(month);
    const week = weeks.find(w => w.weekNum === weekNum);
    if (!week) return null;

    const filterByDateRange = (arr) => arr.filter(row => {
      if (!row.Date) return false;
      const rowDate = new Date(row.Date.split('T')[0] + 'T00:00:00');
      return rowDate >= week.weekStart && rowDate <= week.weekEnd;
    });

    const filterByRep = (arr) => selectedRep === 'All Reps' ? arr : arr.filter(row => row.Rep === selectedRep);

    const weekAppsIn = filterByRep(filterByDateRange(data.tabs.Apps_In?.data?.filter(r => r.Month === month) || []));
    const weekApproved = filterByRep(filterByDateRange(data.tabs.Approved?.data?.filter(r => r.Month === month) || []));
    const weekFunded = filterByRep(filterByDateRange(filterValidDeals(data.tabs.Funded?.data?.filter(r => r.Month === month) || [])));
    const weekRevenue = filterByRep(filterByDateRange(filterValidDeals(data.tabs.Revenue?.data?.filter(r => r.Month === month) || [])));
    const weekCalls = filterByRep(filterByDateRange(data.tabs.Dials_TalkTime?.data?.filter(r => r.Month === month) || []));
    const weekSMS = filterByRep(filterByDateRange(data.tabs.SMS?.data?.filter(r => r.Month === month) || []));

    const connected = weekCalls.filter(call => parseFloat(call.Duration_Minutes || 0) >= 1.5).length;
    const uniqueCompanies = new Set(weekCalls.filter(call => call.Account && call.Account.trim() !== '').map(call => call.Account)).size;
    const uniqueConnects = new Set(weekCalls.filter(call => parseFloat(call.Duration_Minutes || 0) >= 1.5 && call.Account && call.Account.trim() !== '').map(call => call.Account)).size;
    const totalRevenue = weekRevenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
    const smsCount = weekSMS.reduce((sum, s) => sum + (parseFloat(s.Count) || 0), 0);

    // Activity Points: Date-based formula (Dec 16, 2025)
    const weekEndDate = week.weekEnd.toISOString().split('T')[0];
    const activityCount = calculateActivityPoints(weekCalls.length, smsCount, weekAppsIn.length, weekEndDate);

    return {
      calls: weekCalls.length,
      sms: smsCount,
      connected,
      uniqueCompanies,
      uniqueConnects,
      appsIn: weekAppsIn.length,
      approved: weekApproved.length,
      funded: weekFunded.length,
      revenue: totalRevenue,
      activityCount
    };
  };

  // Helper: Get per-rep breakdown for a specific week
  const getRepsForWeek = (weekNum) => {
    if (!data || !data.tabs || selectedRep !== 'All Reps') return [];

    const weeks = getWeeksOfMonth(month);
    const week = weeks.find(w => w.weekNum === weekNum);
    if (!week) return [];

    const repBreakdowns = reps.map(rep => {
      // Get this rep's individual goal
      const repGoalRow = goals?.find(g => g.Rep === rep && g.Month === goalMonth);
      const repActivityGoal = repGoalRow ? parseInt(repGoalRow.Activity_Call_Goal || 0) * 5 : 0; // Weekly goal = daily × ~5 business days

      // Filter data for this week and this rep
      const filterByDateRange = (arr) => arr.filter(row => {
        if (!row.Date) return false;
        const rowDate = new Date(row.Date.split('T')[0] + 'T00:00:00');
        return rowDate >= week.weekStart && rowDate <= week.weekEnd;
      });

      const filterByRep = (arr) => arr.filter(row => row.Rep === rep);

      const weekCalls = filterByRep(filterByDateRange(data.tabs.Dials_TalkTime?.data?.filter(r => r.Month === month) || []));
      const weekSMS = filterByRep(filterByDateRange(data.tabs.SMS?.data?.filter(r => r.Month === month) || []));
      const weekAppsIn = filterByRep(filterByDateRange(data.tabs.Apps_In?.data?.filter(r => r.Month === month) || []));
      const weekApproved = filterByRep(filterByDateRange(data.tabs.Approved?.data?.filter(r => r.Month === month) || []));
      const weekFunded = filterByRep(filterByDateRange(filterValidDeals(data.tabs.Funded?.data?.filter(r => r.Month === month) || [])));
      const weekRevenue = filterByRep(filterByDateRange(filterValidDeals(data.tabs.Revenue?.data?.filter(r => r.Month === month) || [])));

      const connected = weekCalls.filter(call => parseFloat(call.Duration_Minutes || 0) >= 1.5).length;
      const uniqueCompanies = new Set(weekCalls.filter(call => call.Account && call.Account.trim() !== '').map(call => call.Account)).size;
      const uniqueConnects = new Set(weekCalls.filter(call => parseFloat(call.Duration_Minutes || 0) >= 1.5 && call.Account && call.Account.trim() !== '').map(call => call.Account)).size;
      const totalRevenue = weekRevenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
      const smsCount = weekSMS.reduce((sum, s) => sum + (parseFloat(s.Count) || 0), 0);

      // Activity Points: Date-based formula (Dec 16, 2025)
      const weekEndDate = week.weekEnd.toISOString().split('T')[0];
      const activityCount = calculateActivityPoints(weekCalls.length, smsCount, weekAppsIn.length, weekEndDate);

      return {
        rep: rep,
        calls: weekCalls.length,
        sms: smsCount,
        connected,
        uniqueCompanies,
        uniqueConnects,
        appsIn: weekAppsIn.length,
        approved: weekApproved.length,
        funded: weekFunded.length,
        revenue: totalRevenue,
        activityCount,
        activityGoal: repActivityGoal
      };
    });

    return repBreakdowns;
  };

  // Helper: Get daily breakdown for a specific rep within a specific week
  const getDaysForRepInWeek = (weekNum, rep) => {
    if (!data || !data.tabs) return [];

    const weeks = getWeeksOfMonth(month);
    const week = weeks.find(w => w.weekNum === weekNum);
    if (!week) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return week.days.map(dateStr => {
      const date = new Date(dateStr + 'T00:00:00');
      const isFuture = date > today;
      const isToday = date.toDateString() === today.toDateString();

      // Get data for this rep on this day
      const filterByDate = (arr) => arr.filter(row => row.Date?.split('T')[0] === dateStr);
      const filterByRep = (arr) => arr.filter(row => row.Rep === rep);

      const dayCalls = filterByRep(filterByDate(data.tabs.Dials_TalkTime?.data?.filter(r => r.Month === month) || []));
      const daySMS = filterByRep(filterByDate(data.tabs.SMS?.data?.filter(r => r.Month === month) || []));
      const dayAppsIn = filterByRep(filterByDate(data.tabs.Apps_In?.data?.filter(r => r.Month === month) || []));
      const dayApproved = filterByRep(filterByDate(data.tabs.Approved?.data?.filter(r => r.Month === month) || []));
      const dayFunded = filterByRep(filterByDate(filterValidDeals(data.tabs.Funded?.data?.filter(r => r.Month === month) || [])));
      const dayRevenue = filterByRep(filterByDate(filterValidDeals(data.tabs.Revenue?.data?.filter(r => r.Month === month) || [])));

      const connected = dayCalls.filter(call => parseFloat(call.Duration_Minutes || 0) >= 1.5).length;
      const uniqueCompanies = new Set(dayCalls.filter(call => call.Account && call.Account.trim() !== '').map(call => call.Account)).size;
      const uniqueConnects = new Set(dayCalls.filter(call => parseFloat(call.Duration_Minutes || 0) >= 1.5 && call.Account && call.Account.trim() !== '').map(call => call.Account)).size;
      const totalRevenue = dayRevenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
      const smsCount = daySMS.reduce((sum, s) => sum + (parseFloat(s.Count) || 0), 0);

      // Activity Points: Date-based formula (Dec 16, 2025)
      const activityCount = calculateActivityPoints(dayCalls.length, smsCount, dayAppsIn.length, dateStr);

      // Get rep's daily activity goal
      const repGoalRow = goals?.find(g => g.Rep === rep && g.Month === goalMonth);
      const repActivityGoal = repGoalRow ? parseInt(repGoalRow.Activity_Call_Goal || 0) : 0;

      return {
        fullDate: dateStr,
        date: date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }),
        dayName: date.toLocaleDateString('en-US', { weekday: 'long' }),
        shortDayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        isFuture,
        isToday,
        calls: dayCalls.length,
        sms: smsCount,
        connected,
        uniqueCompanies,
        uniqueConnects,
        appsIn: dayAppsIn.length,
        approved: dayApproved.length,
        funded: dayFunded.length,
        revenue: totalRevenue,
        activityCount,
        activityGoal: repActivityGoal
      };
    });
  };

  // Toggle functions
  const toggleWeekExpansion = (weekNum) => {
    if (expandedWeek === weekNum) {
      setExpandedWeek(null);
      setExpandedWeekRep(null); // Close any expanded reps
    } else {
      setExpandedWeek(weekNum);
      setExpandedWeekRep(null); // Reset rep expansion when switching weeks
    }
  };

  const toggleRepExpansion = (rep) => {
    setExpandedWeekRep(expandedWeekRep === rep ? null : rep);
  };

  if (!metrics) {
    return <div className="no-data">No data available for {month}</div>;
  }

  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);

  const formatPercent = (value) => `${value.toFixed(1)}%`;

  // Helper to get comparison class (for color coding)
  const getComparisonClass = (current, previous) => {
    if (!previous || previous === 0) return 'neutral';
    const diff = current - previous;
    if (Math.abs(diff) < 0.5) return 'neutral'; // Less than 0.5% difference
    return diff > 0 ? 'positive' : 'negative';
  };

  // Helper to format comparison text
  const getComparisonText = (current, previous) => {
    if (!previous || previous === 0) return '';
    const diff = current - previous;
    const sign = diff > 0 ? '+' : '';
    return `${sign}${diff.toFixed(1)}%`;
  };

  return (
    <div className="last-month-tab">
      <h2>Last Month: {month} (Final Results)</h2>
      <p className="tab-subtitle">
        {selectedRep === 'All Reps' ? 'Team Performance' : selectedRep}
      </p>

      {/* A-1: Sticky Section Navigation */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: 'white',
        padding: '12px 0',
        borderBottom: '2px solid #e2e8f0',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginRight: '8px' }}>
            JUMP TO:
          </span>
          {[
            { id: 'summary', label: '📊 Summary' },
            { id: 'snapshot', label: '📈 Performance' },
            { id: 'submissions', label: '📤 Submissions' },
            { id: 'goals', label: '🎯 Goals' },
            { id: 'weekly', label: '📅 Weekly' }
          ].map(section => (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: activeSection === section.id ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                backgroundColor: activeSection === section.id ? '#eff6ff' : 'white',
                color: activeSection === section.id ? '#1e40af' : '#475569',
                fontWeight: activeSection === section.id ? 600 : 500,
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {section.label}
            </button>
          ))}
        </div>
      </div>

      {/* ============================================ */}
      {/* SECTION 1: EXECUTIVE SUMMARY (Mockup 260-414) */}
      {/* Collapsible card with Status + What Could Have Been */}
      {/* ============================================ */}
      <section
        ref={sectionRefs.summary}
        style={{
          scrollMarginTop: '80px',
          marginTop: '24px',
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0',
          position: 'relative'
        }}
      >
        {developerMode && <div className="dev-section-label">S-1</div>}
        <div
          onClick={() => toggleSection('summary')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            padding: '12px 0',
            borderBottom: '2px solid #e2e8f0',
            marginBottom: '16px'
          }}
        >
          <div>
            <h2 className="section-title" style={{ margin: 0 }}>📊 Executive Summary</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
              High-level month results with root cause analysis
            </p>
          </div>
          <span style={{
            fontSize: '20px',
            color: '#64748b',
            transform: collapsedSections.summary ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            ▼
          </span>
        </div>

        {!collapsedSections.summary && repGoals && selectedRep === 'All Reps' && (() => {
        // Calculate goal achievement (actual vs goal - not pace since month is complete)
        const revenuePercent = repGoals.revenue > 0 ? (metrics.revenue / repGoals.revenue) * 100 : 0;
        const missedBy = repGoals.revenue - metrics.revenue;

        // Thresholds: 90% = Hit, 70-90% = Close, <70% = Missed
        let status, statusLabel, statusEmoji, borderColor, bgColor;
        if (revenuePercent >= 90) {
          status = 'hit';
          statusLabel = 'GOAL HIT';
          statusEmoji = '✅';
          borderColor = '#10b981';
          bgColor = '#f0fdf4';
        } else if (revenuePercent >= 70) {
          status = 'close';
          statusLabel = 'CLOSE';
          statusEmoji = '🟡';
          borderColor = '#f59e0b';
          bgColor = '#fffbeb';
        } else {
          status = 'missed';
          statusLabel = 'GOAL MISSED';
          statusEmoji = '❌';
          borderColor = '#ef4444';
          bgColor = '#fef2f2';
        }

        // Find root cause - walk funnel TOP-DOWN to find FIRST major gap
        const appsPercent = repGoals.apps > 0 ? (metrics.appsIn / repGoals.apps) * 100 : 0;
        const approvedPercent = repGoals.approved > 0 ? (metrics.approved / repGoals.approved) * 100 : 0;
        const fundedPercent = repGoals.funded > 0 ? (metrics.funded / repGoals.funded) * 100 : 0;

        // Conversion rates
        const appToApprovedRate = metrics.appsIn > 0 ? (metrics.approved / metrics.appsIn) * 100 : 0;
        const approvedToFundedRate = metrics.approved > 0 ? (metrics.funded / metrics.approved) * 100 : 0;

        // Walk the funnel to find root cause
        let rootCause = null;

        if (appsPercent < 50) {
          // First gap: Not enough apps came in (top of funnel volume problem)
          rootCause = {
            type: 'volume',
            message: `App volume — only ${metrics.appsIn} apps entered funnel (${appsPercent.toFixed(0)}% of ${repGoals.apps} goal)`
          };
        } else if (appToApprovedRate < 50 && approvedPercent < appsPercent - 10) {
          // Second gap: Apps→Approved conversion dropped significantly
          rootCause = {
            type: 'conversion',
            message: `App→Approved conversion (${appToApprovedRate.toFixed(0)}% — ${metrics.approved} of ${metrics.appsIn} apps approved)`
          };
        } else if (approvedToFundedRate < 40 && fundedPercent < approvedPercent - 10) {
          // Third gap: Approved→Funded conversion dropped
          rootCause = {
            type: 'conversion',
            message: `Approved→Funded conversion (${approvedToFundedRate.toFixed(0)}% — ${metrics.funded} of ${metrics.approved} approved funded)`
          };
        } else if (appsPercent < 70) {
          // Fallback: Apps were low but not critically so
          rootCause = {
            type: 'volume',
            message: `App volume — ${metrics.appsIn} apps (${appsPercent.toFixed(0)}% of ${repGoals.apps} goal)`
          };
        }

        // Activity comparison (calls this month vs reasonable expectation)
        const totalActivity = metrics.calls + metrics.sms;

        // Find top performer and who needs improvement (by revenue)
        const repPerformance = reps.map(rep => {
          const repRevenue = filterByMonthYear(data.tabs.Revenue?.data).filter(r => r.Rep === rep);
          const repRevenueTotal = repRevenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
          const repGoalRow = goals?.find(g => g.Month === goalMonth && g.Rep === rep);
          const repRevenueGoal = repGoalRow ? parseInt(repGoalRow.Revenue_Goal || 0) : 0;
          const percent = repRevenueGoal > 0 ? (repRevenueTotal / repRevenueGoal) * 100 : 0;

          return {
            name: rep.split(' ')[0],
            revenue: repRevenueTotal,
            percent
          };
        }).sort((a, b) => b.percent - a.percent);

        const topPerformer = repPerformance[0];
        const needsImprovement = repPerformance[repPerformance.length - 1];

        // Calculate gap metrics for "What Could Have Been" panel
        const gapPercent = ((repGoals.revenue - metrics.revenue) / repGoals.revenue * 100).toFixed(0);
        const additionalAppsNeeded = repGoals.apps > metrics.appsIn ? repGoals.apps - metrics.appsIn : 0;
        const avgDealSize = metrics.avgDealSize || (metrics.funded > 0 ? metrics.revenue / metrics.funded : 0);
        const additionalFundedNeeded = avgDealSize > 0 ? Math.ceil(missedBy / avgDealSize) : 0;

        return (
          <section className="executive-summary" style={{
            padding: '24px',
            marginBottom: '24px',
            borderRadius: '12px',
            border: `3px solid ${borderColor}`,
            backgroundColor: bgColor
          }}>
            {/* Header - Month Result Banner */}
            <div style={{
              textAlign: 'center',
              marginBottom: '24px',
              paddingBottom: '16px',
              borderBottom: `2px solid ${borderColor}`
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '28px',
                fontWeight: 700,
                color: '#1e293b',
                letterSpacing: '-0.5px'
              }}>
                {month.toUpperCase()} RESULT: {statusEmoji} {statusLabel}
              </h2>
            </div>

            {/* Two-Panel Layout */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '24px',
              minHeight: '200px'
            }}>
              {/* LEFT PANEL: What Happened */}
              <div style={{
                padding: '20px',
                borderRadius: '12px',
                backgroundColor: '#fff',
                border: '1px solid #e2e8f0',
                position: 'relative'
              }}>
                {developerMode && <div className="dev-card-label">K-1</div>}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '20px',
                  fontSize: '16px',
                  fontWeight: 700,
                  color: '#1e293b'
                }}>
                  📊 WHAT HAPPENED
                </div>

                {/* Big Revenue Number */}
                <div style={{
                  fontSize: '42px',
                  fontWeight: 700,
                  color: '#1e293b',
                  lineHeight: 1,
                  marginBottom: '8px'
                }}>
                  {formatCurrency(metrics.revenue)}
                </div>

                {/* Progress Bar */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{
                    width: '100%',
                    height: '12px',
                    backgroundColor: '#e2e8f0',
                    borderRadius: '6px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${Math.min(revenuePercent, 100)}%`,
                      height: '100%',
                      backgroundColor: revenuePercent >= 90 ? '#10b981' : revenuePercent >= 70 ? '#f59e0b' : '#ef4444',
                      borderRadius: '6px',
                      transition: 'width 0.5s ease'
                    }} />
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '4px',
                    fontSize: '14px',
                    color: '#64748b'
                  }}>
                    <span><strong>{revenuePercent.toFixed(0)}%</strong> of goal</span>
                    <span>{formatCurrency(repGoals.revenue)} target</span>
                  </div>
                </div>

                {/* Root Cause */}
                {rootCause && (
                  <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                      ROOT CAUSE
                    </div>
                    <div style={{ fontSize: '14px', color: '#1e293b' }}>
                      {rootCause.message}
                    </div>
                  </div>
                )}

                {/* Performers */}
                <div style={{
                  marginTop: '16px',
                  display: 'flex',
                  gap: '16px'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '2px' }}>TOP</div>
                    <div style={{ fontSize: '16px', fontWeight: 600, color: '#059669' }}>
                      ⬆ {topPerformer?.name}
                    </div>
                    <div style={{ fontSize: '13px', color: '#64748b' }}>
                      {topPerformer && formatCurrency(topPerformer.revenue)}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '2px' }}>NEEDS WORK</div>
                    <div style={{ fontSize: '16px', fontWeight: 600, color: '#dc2626' }}>
                      ⬇ {needsImprovement?.name}
                    </div>
                    <div style={{ fontSize: '13px', color: '#64748b' }}>
                      {needsImprovement && formatCurrency(needsImprovement.revenue)}
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT PANEL: What Could Have Been */}
              <div style={{
                padding: '20px',
                borderRadius: '12px',
                backgroundColor: '#f5f3ff',
                border: '2px dashed #8b5cf6',
                position: 'relative'
              }}>
                {developerMode && <div className="dev-card-label">K-2</div>}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '20px',
                  fontSize: '16px',
                  fontWeight: 700,
                  color: '#5b21b6'
                }}>
                  🔮 WHAT COULD HAVE BEEN
                  <span style={{
                    padding: '2px 8px',
                    backgroundColor: '#d1fae5',
                    borderRadius: '4px',
                    fontSize: '10px',
                    color: '#065f46'
                  }}>
                    ✨ UNIQUE
                  </span>
                </div>

                {missedBy > 0 ? (
                  <>
                    {/* Gap Amount */}
                    <div style={{
                      fontSize: '36px',
                      fontWeight: 700,
                      color: '#ef4444',
                      lineHeight: 1,
                      marginBottom: '8px'
                    }}>
                      -{formatCurrency(missedBy)}
                    </div>
                    <div style={{
                      fontSize: '16px',
                      color: '#64748b',
                      marginBottom: '20px'
                    }}>
                      <strong>{gapPercent}%</strong> gap to goal
                    </div>

                    {/* What Was Needed */}
                    <div style={{
                      padding: '16px',
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      border: '1px solid #e9d5ff'
                    }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#5b21b6', marginBottom: '12px' }}>
                        TO HIT GOAL, NEEDED:
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '15px',
                          color: '#1e293b'
                        }}>
                          <span style={{
                            width: '24px',
                            height: '24px',
                            backgroundColor: '#dbeafe',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px'
                          }}>📥</span>
                          <strong>+{additionalAppsNeeded}</strong> more apps in
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '15px',
                          color: '#1e293b'
                        }}>
                          <span style={{
                            width: '24px',
                            height: '24px',
                            backgroundColor: '#d1fae5',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px'
                          }}>💰</span>
                          <strong>+{additionalFundedNeeded}</strong> more funded deals
                        </div>
                      </div>
                    </div>

                    {/* Math */}
                    <div style={{
                      marginTop: '16px',
                      fontSize: '14px',
                      color: '#5b21b6',
                      fontWeight: 500
                    }}>
                      → {avgDealSize > 0 ? `${additionalFundedNeeded} deals × ${formatCurrency(avgDealSize)} avg = ${formatCurrency(additionalFundedNeeded * avgDealSize)}` : 'Need more activity'}
                    </div>
                  </>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#059669',
                    fontSize: '18px',
                    fontWeight: 600
                  }}>
                    🎉 Goal achieved! No gap to close.
                  </div>
                )}
              </div>
            </div>
          </section>
        );
      })()}
      </section>

      {/* ============================================ */}
      {/* SECTION 2: PERFORMANCE SNAPSHOT (Mockup 420-534) */}
      {/* Collapsible with MetricCardWithBaseline */}
      {/* ============================================ */}
      <section
        ref={sectionRefs.snapshot}
        style={{
          scrollMarginTop: '80px',
          marginTop: '24px',
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0',
          position: 'relative'
        }}
      >
        {developerMode && <div className="dev-section-label">S-2</div>}
        {/* Collapsible Header */}
        <div
          onClick={() => toggleSection('snapshot')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            padding: '12px 0',
            borderBottom: '2px solid #e2e8f0',
            marginBottom: '16px'
          }}
        >
          <div>
            <h2 className="section-title" style={{ margin: 0 }}>📈 Performance Snapshot</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
              Key metrics with 6-month historical baseline comparison
            </p>
          </div>
          <span style={{
            fontSize: '20px',
            color: '#64748b',
            transform: collapsedSections.snapshot ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            ▼
          </span>
        </div>

        {!collapsedSections.snapshot && (
          <>
            {/* Metric Cards with Baseline - Mockup 462-493 */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{ position: 'relative' }}>
                {developerMode && <div className="dev-card-label">K-1</div>}
                <LM_MetricCardWithBaseline
                  label="Apps In"
                  value={metrics.appsIn}
                  baseline={historicalMetrics?.averages?.appsIn || 0}
                  formatValue={(v) => v}
                />
              </div>
              <div style={{ position: 'relative' }}>
                {developerMode && <div className="dev-card-label">K-2</div>}
                <LM_MetricCardWithBaseline
                  label="Approved"
                  value={metrics.approved}
                  baseline={historicalMetrics?.averages?.approved || 0}
                  formatValue={(v) => v}
                />
              </div>
              <div style={{ position: 'relative' }}>
                {developerMode && <div className="dev-card-label">K-3</div>}
                <LM_MetricCardWithBaseline
                  label="Funded"
                  value={metrics.funded}
                  baseline={historicalMetrics?.averages?.funded || 0}
                  formatValue={(v) => v}
                />
              </div>
              <div style={{ position: 'relative' }}>
                {developerMode && <div className="dev-card-label">K-4</div>}
                <LM_MetricCardWithBaseline
                  label="Revenue"
                  value={metrics.revenue}
                  baseline={historicalMetrics?.averages?.revenue || 0}
                  formatValue={formatCurrency}
                />
              </div>
            </div>

            {/* Conversion Rates with Baseline - Mockup 495-507 */}
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>Conversion Rates</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{ position: 'relative' }}>
                {developerMode && <div className="dev-card-label">K-5</div>}
                <LM_ConversionCardWithBaseline
                  label="Call → App"
                  rate={metrics.callsToApps}
                  baseline={historicalMetrics?.averages?.callsToApps || 0}
                />
              </div>
              <div style={{ position: 'relative' }}>
                {developerMode && <div className="dev-card-label">K-6</div>}
                <LM_ConversionCardWithBaseline
                  label="SMS → App"
                  rate={metrics.smsToApps}
                  baseline={historicalMetrics?.averages?.smsToApps || 0}
                />
              </div>
              <div style={{ position: 'relative' }}>
                {developerMode && <div className="dev-card-label">K-7</div>}
                <LM_ConversionCardWithBaseline
                  label="App → Approved"
                  rate={metrics.appsToApproved}
                  baseline={historicalMetrics?.averages?.appsToApproved || 0}
                />
              </div>
              <div style={{ position: 'relative' }}>
                {developerMode && <div className="dev-card-label">K-8</div>}
                <LM_ConversionCardWithBaseline
                  label="Approved → Funded"
                  rate={metrics.approvedToFunded}
                  baseline={historicalMetrics?.averages?.approvedToFunded || 0}
                />
              </div>
            </div>

            {/* Weekly Activity Trends Chart - Mockup 509-531 */}
            <div style={{
              padding: '20px',
              background: '#fff',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              marginBottom: '24px',
              position: 'relative'
            }}>
              {developerMode && <div className="dev-chart-label">C-1</div>}
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Weekly Activity Trends</h3>
              {weeklyPerformance && weeklyPerformance.length > 0 && (
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart data={weeklyPerformance}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="week" stroke="#4a5568" />
                    <YAxis yAxisId="left" stroke="#4a5568" />
                    <YAxis yAxisId="right" orientation="right" stroke="#10b981" />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="calls" fill="#3b82f6" name="Calls" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="left" dataKey="sms" fill="#9333ea" name="SMS" radius={[4, 4, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} name="Revenue ($k)" />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          </>
        )}
      </section>
      {/* End Performance Snapshot */}

      {/* A-6: Lender Submissions Section - Mockup lines 540-653 */}
      {lenderSubmissionMetrics && (
        <section
          ref={sectionRefs.submissions}
          style={{
            marginTop: '24px',
            scrollMarginTop: '80px',
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #e2e8f0',
            position: 'relative'
          }}
        >
          {developerMode && <div className="dev-section-label">S-3</div>}
          {/* Collapsible Header - Mockup lines 552-591 */}
          <div
            onClick={() => toggleSection('submissions')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              padding: '12px 0',
              borderBottom: '2px solid #e2e8f0',
              marginBottom: '16px'
            }}
          >
            <div>
              <h2 className="section-title" style={{ margin: 0 }}>📤 Lender Submissions</h2>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                Submission metrics and weekly breakdown by rep
              </p>
            </div>
            <span style={{
              fontSize: '20px',
              color: '#64748b',
              transform: collapsedSections.submissions ? 'rotate(-90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease'
            }}>
              ▼
            </span>
          </div>

          {!collapsedSections.submissions && (
            <>
              {/* Insight Box - The Persistence Story */}
              {lenderSubmissionMetrics.fundedCount > 0 && lenderSubmissionMetrics.lostCount > 0 && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: lenderSubmissionMetrics.avgSubsToFund > lenderSubmissionMetrics.avgSubsOnLost ? '#f0fdf4' : '#fef3c7',
                  borderRadius: '8px',
                  border: `1px solid ${lenderSubmissionMetrics.avgSubsToFund > lenderSubmissionMetrics.avgSubsOnLost ? '#86efac' : '#fcd34d'}`,
                  marginBottom: '16px',
                  fontSize: '13px'
                }}>
                  <strong>💡 Insight:</strong> Funded deals averaged <strong>{lenderSubmissionMetrics.avgSubsToFund.toFixed(1)}</strong> submissions vs Lost deals at <strong>{lenderSubmissionMetrics.avgSubsOnLost.toFixed(1)}</strong>
                  {lenderSubmissionMetrics.avgSubsToFund > lenderSubmissionMetrics.avgSubsOnLost
                    ? ' — persistence matters!'
                    : ' — quality over quantity'}
                </div>
              )}

              {/* 4 Story-Driven Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '12px',
                marginBottom: '24px'
              }}>
                {/* Card 1: Avg Subs to Fund */}
                <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
                  {developerMode && <div className="dev-card-label">K-1</div>}
                  <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>Avg Subs to Fund</div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#059669' }}>
                    {lenderSubmissionMetrics.avgSubsToFund.toFixed(1)}
                  </div>
                  <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '4px' }}>
                    from {lenderSubmissionMetrics.fundedCount} funded deals
                  </div>
                </div>

                {/* Card 2: Avg Subs on Lost */}
                <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
                  {developerMode && <div className="dev-card-label">K-2</div>}
                  <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>Avg Subs on Lost</div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#dc2626' }}>
                    {lenderSubmissionMetrics.avgSubsOnLost.toFixed(1)}
                  </div>
                  <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '4px' }}>
                    from {lenderSubmissionMetrics.lostCount} lost deals
                  </div>
                </div>

                {/* Card 3: Decline Rate */}
                <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
                  {developerMode && <div className="dev-card-label">K-3</div>}
                  <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>Decline Rate</div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#f59e0b' }}>
                    {lenderSubmissionMetrics.declineRate.toFixed(0)}%
                  </div>
                  <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '4px' }}>
                    {lenderSubmissionMetrics.totalDeclines} of {lenderSubmissionMetrics.totalSubmissions} declined
                  </div>
                </div>

                {/* Card 4: Pending */}
                <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
                  {developerMode && <div className="dev-card-label">K-4</div>}
                  <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>Pending</div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: '#3b82f6' }}>
                    {lenderSubmissionMetrics.pendingSubmissions}
                  </div>
                  <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '4px' }}>
                    awaiting response
                  </div>
                </div>
              </div>

              {/* Option D: Persistence Story Chart - Funded vs Lost by Rep */}
              <div style={{
                padding: '20px',
                background: '#fff',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                position: 'relative'
              }}>
                {developerMode && <div className="dev-chart-label">C-1</div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <div>
                    <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>Persistence by Rep</h3>
                    <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0' }}>
                      Avg submissions on funded deals vs lost deals - does persistence pay off?
                    </p>
                  </div>
                  <span style={{
                    padding: '4px 8px',
                    backgroundColor: '#dbeafe',
                    borderRadius: '4px',
                    fontSize: '10px',
                    color: '#1e40af',
                    fontWeight: 600
                  }}>
                    STORY-DRIVEN
                  </span>
                </div>
                {persistenceByRep && persistenceByRep.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={persistenceByRep} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" stroke="#4a5568" />
                      <YAxis type="category" dataKey="name" stroke="#4a5568" width={80} />
                      <Tooltip
                        formatter={(value, name) => [value.toFixed(1), name === 'avgSubsToFund' ? 'Avg to Fund' : 'Avg on Lost']}
                      />
                      <Legend formatter={(value) => value === 'avgSubsToFund' ? 'Funded Deals' : 'Lost Deals'} />
                      <Bar dataKey="avgSubsToFund" fill="#10b981" name="avgSubsToFund" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="avgSubsOnLost" fill="#ef4444" name="avgSubsOnLost" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                    No submission data available for this month
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      )}

      {/* ============================================ */}
      {/* SECTION 4: GOAL TRENDING - Mockup lines 659-761 */}
      {/* ============================================ */}
      <section
        ref={sectionRefs.goals}
        style={{
          marginTop: '24px',
          scrollMarginTop: '80px',
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0',
          position: 'relative'
        }}
      >
        {developerMode && <div className="dev-section-label">S-4</div>}
        {/* Collapsible Header - Mockup lines 671-697 */}
        <div
          onClick={() => toggleSection('goals')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            padding: '12px 0',
            borderBottom: '2px solid #e2e8f0',
            marginBottom: '16px'
          }}
        >
          <div>
            <h2 className="section-title" style={{ margin: 0 }}>🎯 Goal Trending</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
              Final results and rep performance breakdown
            </p>
          </div>
          <span style={{
            fontSize: '20px',
            color: '#64748b',
            transform: collapsedSections.goals ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            ▼
          </span>
        </div>

        {!collapsedSections.goals && (
          <>
            {/* Final Results Table - Mockup lines 702-727 */}
            <div style={{
              padding: '16px',
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              marginBottom: '20px',
              position: 'relative'
            }}>
              {developerMode && <div className="dev-card-label">K-1</div>}
              <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 600 }}>Monthly Final Results</h4>
              <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '8px', fontWeight: 600, color: '#64748b' }}>Metric</th>
                    <th style={{ textAlign: 'right', padding: '8px', fontWeight: 600, color: '#64748b' }}>Actual</th>
                    <th style={{ textAlign: 'right', padding: '8px', fontWeight: 600, color: '#64748b' }}>Goal</th>
                    <th style={{ textAlign: 'right', padding: '8px', fontWeight: 600, color: '#64748b' }}>Shortfall</th>
                    <th style={{ textAlign: 'right', padding: '8px', fontWeight: 600, color: '#64748b' }}>Achievement</th>
                  </tr>
                </thead>
                <tbody>
                  <LM_FinalResultRow metric="Apps In" actual={metrics.appsIn} goal={repGoals?.apps || 0} formatValue={(v) => v} />
                  <LM_FinalResultRow metric="Approved" actual={metrics.approved} goal={repGoals?.approved || 0} formatValue={(v) => v} />
                  <LM_FinalResultRow metric="Funded" actual={metrics.funded} goal={repGoals?.funded || 0} formatValue={(v) => v} />
                  <LM_FinalResultRow metric="Revenue" actual={metrics.revenue} goal={repGoals?.revenue || 0} formatValue={formatCurrency} />
                </tbody>
              </table>
            </div>

            {/* Rep Performance Table - Mockup lines 729-758 */}
            <div style={{ marginBottom: '24px', position: 'relative' }}>
              {developerMode && <div className="dev-card-label">K-2</div>}
              <h4 style={{ marginBottom: '12px', fontSize: '17px', fontWeight: 600 }}>👥 Rep Performance</h4>
              <table style={{ width: '100%', fontSize: '15px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '10px 12px' }}>Rep</th>
                    <th style={{ textAlign: 'right', padding: '10px 12px' }}>Apps</th>
                    <th style={{ textAlign: 'right', padding: '10px 12px' }}>Approved</th>
                    <th style={{ textAlign: 'right', padding: '10px 12px' }}>Funded</th>
                    <th style={{ textAlign: 'right', padding: '10px 12px' }}>Revenue</th>
                    <th style={{ textAlign: 'right', padding: '10px 12px' }}>Call→App</th>
                    <th style={{ textAlign: 'right', padding: '10px 12px' }}>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {reps.map((rep, idx) => {
                    const repAppsIn = lastMonthData.appsIn.filter(row => row.Rep === rep);
                    const repApproved = lastMonthData.approved.filter(row => row.Rep === rep);
                    const repFunded = lastMonthData.funded.filter(row => row.Rep === rep);
                    const repRevenue = lastMonthData.revenue.filter(row => row.Rep === rep);
                    const repCalls = lastMonthData.calls.filter(row => row.Rep === rep);

                    const repTotalRevenue = repRevenue.reduce((sum, row) => sum + (parseFloat(row.Revenue) || 0), 0);
                    const repTotalCalls = repCalls.length;
                    const callToApp = repTotalCalls > 0 ? (repAppsIn.length / repTotalCalls * 100) : 0;

                    const repGoal = goals?.find(g => g.Month === goalMonth && g.Rep === rep);
                    const revenueGoal = parseInt(repGoal?.Revenue_Goal || 0);
                    const revenueProgress = revenueGoal > 0 ? (repTotalRevenue / revenueGoal * 100) : 0;

                    let result;
                    if (revenueProgress >= 100) result = '✅ Hit';
                    else if (revenueProgress >= 90) result = '🟢 Close';
                    else if (revenueProgress >= 70) result = '🟡 Near';
                    else result = '🔴 Missed';

                    return (
                      <tr key={rep} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                        <td style={{ padding: '10px 12px', fontWeight: 500 }}>{rep.split(' ')[0]}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>{repAppsIn.length}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>{repApproved.length}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>{repFunded.length}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>{formatCurrency(repTotalRevenue)}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>{callToApp.toFixed(1)}%</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>{result}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {/* ============================================ */}
      {/* SECTION 5: WEEKLY BREAKDOWN - Mockup lines 767-847 */}
      {/* ============================================ */}
      <section
        ref={sectionRefs.weekly}
        style={{
          marginTop: '24px',
          scrollMarginTop: '80px',
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0',
          position: 'relative'
        }}
      >
        {developerMode && <div className="dev-section-label">S-5</div>}
        {/* Collapsible Header - Mockup lines 779-805 */}
        <div
          onClick={() => toggleSection('weekly')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            padding: '12px 0',
            borderBottom: '2px solid #e2e8f0',
            marginBottom: '16px'
          }}
        >
          <div>
            <h2 className="section-title" style={{ margin: 0 }}>📅 Weekly Breakdown</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
              Week-by-week performance summary
            </p>
          </div>
          <span style={{
            fontSize: '20px',
            color: '#64748b',
            transform: collapsedSections.weekly ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            ▼
          </span>
        </div>

        {!collapsedSections.weekly && (
        <>
        <p className="section-subtitle">Drill down from week to individual rep to daily performance</p>
        <div style={{ overflowX: 'auto', position: 'relative' }}>
          {developerMode && <div className="dev-card-label">K-1</div>}
          <table className="breakdown-table">
            <thead>
              <tr>
                <th>Week / Rep / Day</th>
                <th>Calls</th>
                <th>SMS</th>
                <th>Connected #</th>
                <th>Unique Co's</th>
                <th>Unique Connects</th>
                <th>Apps</th>
                <th>Approved</th>
                <th>Funded</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {/* Week rows */}
              {getWeeksOfMonth(month).map(week => {
                const weekData = getWeekData(week.weekNum);
                const isWeekExpanded = expandedWeek === week.weekNum;
                const canExpandWeek = weekData && (weekData.calls > 0 || weekData.appsIn > 0);

                return (
                  <React.Fragment key={week.weekNum}>
                    {/* Level 1: Week row */}
                    <tr
                      className={`week-row ${canExpandWeek ? 'expandable-row' : ''} ${isWeekExpanded ? 'expanded' : ''}`.trim()}
                      onClick={() => canExpandWeek && toggleWeekExpansion(week.weekNum)}
                      style={{
                        cursor: canExpandWeek ? 'pointer' : 'default',
                        backgroundColor: '#f9fafb',
                        fontWeight: '600'
                      }}
                    >
                      <td>
                        <strong>{week.weekNum}</strong>
                        <br />
                        <span style={{ fontSize: '12px', fontWeight: 'normal' }}>
                          {week.startDate} - {week.endDate}
                        </span>
                        {canExpandWeek && <span className="expand-icon">{isWeekExpanded ? ' ▼' : ' ▶'}</span>}
                      </td>
                      <td>{weekData ? weekData.calls : 0}</td>
                      <td>{weekData ? weekData.sms : 0}</td>
                      <td>{weekData ? weekData.connected : 0}</td>
                      <td>{weekData ? weekData.uniqueCompanies : 0}</td>
                      <td>{weekData ? weekData.uniqueConnects : 0}</td>
                      <td>{weekData ? weekData.appsIn : 0}</td>
                      <td>{weekData ? weekData.approved : 0}</td>
                      <td>{weekData ? weekData.funded : 0}</td>
                      <td>{weekData ? formatCurrency(weekData.revenue) : '$0'}</td>
                    </tr>

                    {/* Level 2: Rep rows within expanded week */}
                    {isWeekExpanded && canExpandWeek && selectedRep === 'All Reps' && (
                      <>
                        {getRepsForWeek(week.weekNum)
                          .sort((a, b) => b.revenue - a.revenue)
                          .map(repData => {
                            const isRepExpanded = expandedWeekRep === repData.rep;
                            const canExpandRep = repData.calls > 0 || repData.appsIn > 0;

                            return (
                              <React.Fragment key={repData.rep}>
                                {/* Rep row showing weekly totals */}
                                <tr
                                  className={`rep-row ${canExpandRep ? 'expandable-row' : ''} ${isRepExpanded ? 'expanded' : ''}`.trim()}
                                  onClick={() => canExpandRep && toggleRepExpansion(repData.rep)}
                                  style={{
                                    cursor: canExpandRep ? 'pointer' : 'default',
                                    backgroundColor: '#f8fafc'
                                  }}
                                >
                                  <td style={{ paddingLeft: '30px' }}>
                                    <strong>{repData.rep.split(' ')[0]}</strong>
                                    {canExpandRep && <span className="expand-icon">{isRepExpanded ? ' ▼' : ' ▶'}</span>}
                                  </td>
                                  <td>{repData.calls}</td>
                                  <td>{repData.sms}</td>
                                  <td>{repData.connected}</td>
                                  <td>{repData.uniqueCompanies}</td>
                                  <td>{repData.uniqueConnects}</td>
                                  <td>{repData.appsIn}</td>
                                  <td>{repData.approved}</td>
                                  <td>{repData.funded}</td>
                                  <td>{formatCurrency(repData.revenue)}</td>
                                </tr>

                                      {/* Level 3: Daily breakdown for expanded rep */}
                                      {isRepExpanded && canExpandRep && (
                                        <tr className="expanded-detail-row">
                                          <td colSpan="10" style={{ paddingLeft: '40px', backgroundColor: '#fafafa' }}>
                                            <div className="day-breakdown-container">
                                              <h4 style={{ fontSize: '14px', margin: '10px 0' }}>
                                                Daily Breakdown for {repData.rep.split(' ')[0]} - {week.weekNum}
                                              </h4>
                                              <table className="day-breakdown-table" style={{
                                                width: '95%',
                                                fontSize: '13px',
                                                marginLeft: '20px'
                                              }}>
                                                <thead>
                                                  <tr style={{ backgroundColor: '#e5e7eb' }}>
                                                    <th>Day</th>
                                                    <th>Calls</th>
                                                    <th>SMS</th>
                                                    <th>Connected #</th>
                                                    <th>Unique Co's</th>
                                                    <th>Unique Connects</th>
                                                    <th>Apps</th>
                                                    <th>Approved</th>
                                                    <th>Funded</th>
                                                    <th>Revenue</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {getDaysForRepInWeek(week.weekNum, repData.rep).map(day => (
                                                    <tr key={day.fullDate}>
                                                      <td>
                                                        <strong>{day.shortDayName}</strong>
                                                        <br />
                                                        <span style={{ fontSize: '11px' }}>{day.date}</span>
                                                      </td>
                                                      <td>{day.calls}</td>
                                                      <td>{day.sms}</td>
                                                      <td>{day.connected}</td>
                                                      <td>{day.uniqueCompanies}</td>
                                                      <td>{day.uniqueConnects}</td>
                                                      <td>{day.appsIn}</td>
                                                      <td>{day.approved}</td>
                                                      <td>{day.funded}</td>
                                                      <td>{formatCurrency(day.revenue)}</td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                              </React.Fragment>
                            );
                          })}
                      </>
                    )}
                  </React.Fragment>
                );
              })}

              {/* TOTAL row - aggregates entire month */}
              {(() => {
                const isTotalExpanded = expandedWeek === 'TOTAL';
                const canExpandTotal = selectedRep === 'All Reps';

                return (
                  <React.Fragment>
                    <tr
                      className={`total-row ${canExpandTotal ? 'expandable-row' : ''} ${isTotalExpanded ? 'expanded' : ''}`.trim()}
                      onClick={() => canExpandTotal && toggleWeekExpansion('TOTAL')}
                      style={{
                        cursor: canExpandTotal ? 'pointer' : 'default',
                        backgroundColor: '#e0e7ff',
                        fontWeight: 'bold'
                      }}
                    >
                      <td>
                        <strong>TOTAL</strong>
                        {canExpandTotal && <span className="expand-icon">{isTotalExpanded ? ' ▼' : ' ▶'}</span>}
                      </td>
                      <td><strong>{metrics.calls}</strong></td>
                      <td><strong>{metrics.sms}</strong></td>
                      <td><strong>-</strong></td>
                      <td><strong>-</strong></td>
                      <td><strong>-</strong></td>
                      <td><strong>{metrics.appsIn}</strong></td>
                      <td><strong>{metrics.approved}</strong></td>
                      <td><strong>{metrics.funded}</strong></td>
                      <td><strong>{formatCurrency(metrics.revenue)}</strong></td>
                    </tr>

                    {/* Expandable: Per-rep MONTH totals */}
                    {isTotalExpanded && canExpandTotal && (
                      <tr className="expanded-detail-row">
                        <td colSpan="10" style={{ padding: '20px', backgroundColor: '#f0f4ff' }}>
                          <div className="rep-breakdown-container">
                            <h4>Per-Rep Month Totals for {month}</h4>
                            <table className="rep-breakdown-table">
                              <thead>
                                <tr>
                                  <th>Rep</th>
                                  <th>Calls</th>
                                  <th>SMS</th>
                                  <th>Apps</th>
                                  <th>Approved</th>
                                  <th>Funded</th>
                                  <th>Revenue</th>
                                </tr>
                              </thead>
                              <tbody>
                                {reps.map(rep => {
                                  // Get rep's FULL MONTH data
                                  const repAppsIn = lastMonthData.appsIn.filter(row => row.Rep === rep);
                                  const repApproved = lastMonthData.approved.filter(row => row.Rep === rep);
                                  const repFunded = lastMonthData.funded.filter(row => row.Rep === rep);
                                  const repRevenue = lastMonthData.revenue.filter(row => row.Rep === rep);
                                  const repCalls = lastMonthData.calls.filter(row => row.Rep === rep);
                                  const repSMS = lastMonthData.sms.filter(row => row.Rep === rep);

                                  const repTotalRevenue = repRevenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
                                  const repTotalCalls = repCalls.length;
                                  const repTotalSMS = repSMS.reduce((sum, r) => sum + (parseFloat(r.Count) || 0), 0);
                                  const repAppsCount = repAppsIn.length;
                                  const repApprovedCount = repApproved.length;
                                  const repFundedCount = repFunded.length;

                                  return (
                                    <tr key={rep}>
                                      <td><strong>{rep.split(' ')[0]}</strong></td>
                                      <td>{repTotalCalls}</td>
                                      <td>{repTotalSMS}</td>
                                      <td>{repAppsCount}</td>
                                      <td>{repApprovedCount}</td>
                                      <td>{repFundedCount}</td>
                                      <td>{formatCurrency(repTotalRevenue)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })()}
            </tbody>
          </table>
        </div>
        </>
        )}
      </section>
      {/* End Section 5: Weekly Breakdown with Drill-Down */}



    </div>
  );
}

/**
 * UrgencyCard - Displays today's progress with urgency styling
 * Used in This Week tab to show calls/sms/apps status
 * Props: icon, label, done, needed, perHour, hoursLeft, action
 */
function UrgencyCard({ icon, label, done, needed, perHour, hoursLeft, action }) {
  const isComplete = needed === 0;
  const borderColor = isComplete ? '#10b981' : needed > 5 ? '#ef4444' : '#f59e0b';

  return (
    <div style={{
      padding: '20px',
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      borderLeft: `4px solid ${borderColor}`,
      textAlign: 'center',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    }}>
      <div style={{ fontSize: '32px', marginBottom: '8px' }}>{icon}</div>
      <div style={{ fontSize: '14px', fontWeight: 700, color: '#1e293b', marginBottom: '12px' }}>{label}</div>
      <div style={{ fontSize: '36px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>{done}</div>
      <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
        done, need <strong style={{ color: borderColor }}>{needed}</strong> more
      </div>
      {perHour && needed > 0 && (
        <div style={{ fontSize: '13px', color: '#1e40af', fontWeight: 600 }}>
          → {perHour} per hour for {hoursLeft}h
        </div>
      )}
      {action && needed > 0 && (
        <div style={{ fontSize: '13px', color: '#1e40af', fontWeight: 600 }}>
          → {action}
        </div>
      )}
      {isComplete && (
        <div style={{ fontSize: '13px', color: '#10b981', fontWeight: 600 }}>
          ✓ Target met!
        </div>
      )}
    </div>
  );
}

/**
 * THIS WEEK TAB - Daily Activity Monitoring & Coaching
 * Hyper-focused on individual rep performance this week
 */
function ThisWeekTab({ data, goals, selectedRep, reps, developerMode, year = new Date().getFullYear(), monthYear }) {
  // Year filtering: Use monthYear for goals (e.g., "January 2026")
  const currentMonth = new Date().toLocaleString('default', { month: 'long' });
  const currentMonthYear = monthYear || `${currentMonth} ${year}`;
  const [expandedDay, setExpandedDay] = useState(null); // Track which day is expanded for per-rep breakdown (null, date string, or 'TOTAL')
  const [activeSection, setActiveSection] = useState('summary'); // Track active nav section for highlighting

  // Collapsible sections state and refs for sticky navigation
  const [collapsedSections, setCollapsedSections] = useState({
    summary: false,
    urgency: false,
    daily: false,
    why: false
  });

  const sectionRefs = {
    summary: useRef(null),
    urgency: useRef(null),
    daily: useRef(null),
  };

  // Scroll to section when nav button clicked
  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs[sectionId]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Toggle section collapse
  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);

  const formatPercent = (value) => `${value.toFixed(1)}%`;

  // Get current week date range (Monday - Sunday)
  const getWeekDates = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Adjust to Monday
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    monday.setHours(0, 0, 0, 0);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    return { monday, sunday, today };
  };

  const { monday, sunday, today } = getWeekDates();
  const currentDayOfWeek = today.toLocaleDateString('en-US', { weekday: 'long' });
  const currentTime = today.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

  // Calculate days of week with dates (5 business days Mon-Fri only)
  const daysOfWeek = useMemo(() => {
    const days = [];
    for (let i = 0; i < 5; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      days.push({
        name: date.toLocaleDateString('en-US', { weekday: 'long' }),
        shortName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        date: date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }),
        fullDate: date.toISOString().split('T')[0],
        isToday: date.toDateString() === today.toDateString(),
        isPast: date < today && date.toDateString() !== today.toDateString(),
        isFuture: date > today
      });
    }
    return days;
  }, [monday, today]);

  // Get week's data for selected rep or all reps
  const weekData = useMemo(() => {
    if (!data || !data.tabs) return null;

    const weekDateStrings = daysOfWeek.map(d => d.fullDate);
    const isAllReps = selectedRep === 'All Reps';

    const result = {};
    daysOfWeek.forEach(day => {
      const dayData = {
        calls: 0,
        sms: 0,
        appsIn: 0,
        approved: 0,
        funded: 0,
        revenue: 0,
        activities: []
      };

      // Get calls - filter by rep or get all
      const calls = data.tabs.Dials_TalkTime?.data?.filter(r =>
        (isAllReps || r.Rep === selectedRep) && r.Date?.split('T')[0] === day.fullDate
      ) || [];
      dayData.calls = calls.length;
      dayData.connected = calls.filter(call => parseFloat(call.Duration_Minutes || 0) >= 1.5).length;
      dayData.uniqueCompanies = new Set(calls.filter(call => call.Account && call.Account.trim() !== '').map(call => call.Account)).size;
      dayData.uniqueConnects = new Set(calls.filter(call => parseFloat(call.Duration_Minutes || 0) >= 1.5 && call.Account && call.Account.trim() !== '').map(call => call.Account)).size;
      if (!isAllReps) {
        calls.forEach(call => {
          dayData.activities.push({
            type: 'call',
            time: 'N/A', // No time data available
            description: call.Subject || 'Call',
            duration: call.Duration_Minutes || 0,
            account: call.Account
          });
        });
      }

      // Get SMS - filter by rep or get all
      const sms = data.tabs.SMS?.data?.filter(r =>
        (isAllReps || r.Rep === selectedRep) && r.Date?.split('T')[0] === day.fullDate
      ) || [];
      dayData.sms = sms.reduce((sum, s) => sum + (parseFloat(s.Count) || 0), 0);
      if (!isAllReps) {
        sms.forEach(text => {
          dayData.activities.push({
            type: 'sms',
            time: 'N/A',
            description: `SMS to ${text.Contact || 'Contact'}`,
            account: text.Account,
            count: text.Count || 1
          });
        });
      }

      // Get Apps In - filter by rep or get all
      const appsIn = data.tabs.Apps_In?.data?.filter(r =>
        (isAllReps || r.Rep === selectedRep) && r.Date?.split('T')[0] === day.fullDate
      ) || [];
      dayData.appsIn = appsIn.length;
      if (!isAllReps) {
        appsIn.forEach(app => {
          dayData.activities.push({
            type: 'app',
            time: 'N/A',
            description: `App submitted: ${app.Opportunity || 'Opportunity'}`,
            account: app.Account,
            amount: parseFloat(app.Amount) || 0
          });
        });
      }

      // Get Approved - filter by rep or get all
      const approved = data.tabs.Approved?.data?.filter(r =>
        (isAllReps || r.Rep === selectedRep) && r.Date?.split('T')[0] === day.fullDate
      ) || [];
      dayData.approved = approved.length;

      // Get Funded - filter by rep or get all
      const funded = filterValidDeals(data.tabs.Funded?.data?.filter(r =>
        (isAllReps || r.Rep === selectedRep) && r.Date?.split('T')[0] === day.fullDate
      ) || []);
      dayData.funded = funded.length;

      // Get Revenue - filter by rep or get all
      const revenue = filterValidDeals(data.tabs.Revenue?.data?.filter(r =>
        (isAllReps || r.Rep === selectedRep) && r.Date?.split('T')[0] === day.fullDate
      ) || []);
      dayData.revenue = revenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);

      // Activity Points: Date-based formula (Dec 16, 2025)
      dayData.activityCount = calculateActivityPoints(dayData.calls, dayData.sms, dayData.appsIn, day.fullDate);

      result[day.name] = dayData;
    });

    return result;
  }, [data, selectedRep, daysOfWeek]);

  // Calculate weekly totals and targets
  const weeklyMetrics = useMemo(() => {
    if (!weekData) return null;

    const rep = selectedRep;
    // Use currentMonth, currentMonthYear from outer scope (function level)
    const isAllReps = rep === 'All Reps';

    // Get goals - either one rep or sum of all reps (using Month Year format)
    // NOTE: Don't return null if no goals - show data with 0 goals instead
    let appsGoalTotal = 0, revenueGoalTotal = 0, activityCallGoal = 0;

    if (goals) {
      if (isAllReps) {
        const allRepsGoals = goals.filter(g => g.Month === currentMonthYear);
        appsGoalTotal = allRepsGoals.reduce((sum, g) => sum + parseInt(g.Apps_Goal || 0), 0);
        revenueGoalTotal = allRepsGoals.reduce((sum, g) => sum + parseInt(g.Revenue_Goal || 0), 0);
        activityCallGoal = allRepsGoals.reduce((sum, g) => sum + parseInt(g.Activity_Call_Goal || 0), 0);
      } else {
        const repGoals = goals.find(g => g.Rep === rep && g.Month === currentMonthYear);
        if (repGoals) {
          appsGoalTotal = parseInt(repGoals.Apps_Goal || 0);
          revenueGoalTotal = parseInt(repGoals.Revenue_Goal || 0);
          activityCallGoal = parseInt(repGoals.Activity_Call_Goal || 0);
        }
      }
    }

    // Calculate weekly totals
    let totalCalls = 0, totalSMS = 0, totalApps = 0, totalApproved = 0, totalFunded = 0, totalRevenue = 0;
    let totalConnected = 0;
    let totalActivityCount = 0;
    const allCompaniesSet = new Set();
    const allConnectedCompaniesSet = new Set();
    let todayCalls = 0, todaySMS = 0, todayApps = 0, todayRevenue = 0;

    daysOfWeek.forEach(day => {
      const dayData = weekData[day.name];
      if (dayData) {
        totalCalls += dayData.calls;
        totalSMS += dayData.sms;
        totalApps += dayData.appsIn;
        totalApproved += dayData.approved;
        totalFunded += dayData.funded;
        totalRevenue += dayData.revenue;
        totalConnected += dayData.connected || 0;
        totalActivityCount += dayData.activityCount || 0;

        // Collect unique companies across all days
        const dayCalls = data.tabs.Dials_TalkTime?.data?.filter(r =>
          (selectedRep === 'All Reps' || r.Rep === selectedRep) && r.Date?.split('T')[0] === day.fullDate
        ) || [];
        dayCalls.forEach(call => {
          if (call.Account && call.Account.trim() !== '') {
            allCompaniesSet.add(call.Account);
            // Track connected companies (calls >= 90 seconds)
            if (parseFloat(call.Duration_Minutes || 0) >= 1.5) {
              allConnectedCompaniesSet.add(call.Account);
            }
          }
        });

        if (day.isToday) {
          todayCalls = dayData.calls;
          todaySMS = dayData.sms;
          todayApps = dayData.appsIn;
          todayRevenue = dayData.revenue;
        }
      }
    });

    // Calculate fixed daily and weekly targets from monthly goals
    const businessDaysInMonth = getBusinessDaysInMonth();
    const businessDaysThisWeek = 5; // Standard work week (Mon-Fri)

    // Daily targets: Monthly goal ÷ business days in month
    const dailyAppTarget = businessDaysInMonth > 0 ? Math.ceil(appsGoalTotal / businessDaysInMonth) : 0;
    const dailyRevenueTarget = businessDaysInMonth > 0 ? Math.ceil(revenueGoalTotal / businessDaysInMonth) : 0;

    // For calls/SMS, we need to work backwards from apps using conversion rates
    // Get historical conversion rates
    let conversionCallsToApps, conversionSmsToApps;
    if (isAllReps) {
      // Use simple team average for now
      conversionCallsToApps = 3;
      conversionSmsToApps = 5;
    } else {
      const historical = calculateHistoricalMetrics(data, rep, currentMonth, 6);
      conversionCallsToApps = historical ? historical.averages.callsToApps : 3;
      conversionSmsToApps = historical ? historical.averages.smsToApps : 5;
    }

    // Calculate daily calls/SMS needed to hit daily app target
    const dailyCallTarget = conversionCallsToApps > 0 ? Math.ceil(dailyAppTarget / (conversionCallsToApps / 100)) : 0;
    const dailySMSTarget = conversionSmsToApps > 0 ? Math.ceil(dailyAppTarget / (conversionSmsToApps / 100)) : 0;

    // Weekly targets: Daily target × 5 business days
    const weeklyCallTarget = dailyCallTarget * businessDaysThisWeek;
    const weeklySMSTarget = dailySMSTarget * businessDaysThisWeek;
    const weeklyAppTarget = dailyAppTarget * businessDaysThisWeek;
    const weeklyRevenueTarget = dailyRevenueTarget * businessDaysThisWeek;

    // Calculate what's needed today
    const todayCallsNeeded = Math.max(0, dailyCallTarget - todayCalls);
    const todaySMSNeeded = Math.max(0, dailySMSTarget - todaySMS);
    const todayAppsNeeded = Math.max(0, dailyAppTarget - todayApps);

    // Calculate actual conversion rates from week's performance
    const weekCallsToApps = totalCalls > 0 ? (totalApps / totalCalls * 100) : 0;
    const weekSmsToApps = totalSMS > 0 ? (totalApps / totalSMS * 100) : 0;
    const weekAppsToApproved = totalApps > 0 ? (totalApproved / totalApps * 100) : 0;
    const weekApprovedToFunded = totalApproved > 0 ? (totalFunded / totalApproved * 100) : 0;
    const weekAppsToFunded = totalApps > 0 ? (totalFunded / totalApps * 100) : 0;

    return {
      totals: { calls: totalCalls, sms: totalSMS, connected: totalConnected, uniqueCompanies: allCompaniesSet.size, uniqueConnects: allConnectedCompaniesSet.size, apps: totalApps, approved: totalApproved, funded: totalFunded, revenue: totalRevenue, activityCount: totalActivityCount },
      today: { calls: todayCalls, sms: todaySMS, apps: todayApps, revenue: todayRevenue },
      dailyTargets: { calls: dailyCallTarget, sms: dailySMSTarget, apps: dailyAppTarget, revenue: dailyRevenueTarget, activityCount: activityCallGoal },
      weeklyTargets: { calls: weeklyCallTarget, sms: weeklySMSTarget, apps: weeklyAppTarget, revenue: weeklyRevenueTarget },
      todayNeeded: { calls: todayCallsNeeded, sms: todaySMSNeeded, apps: todayAppsNeeded },
      conversions: { callsToApps: weekCallsToApps, smsToApps: weekSmsToApps, appsToApproved: weekAppsToApproved, approvedToFunded: weekApprovedToFunded, appsToFunded: weekAppsToFunded },
      gaps: {
        calls: totalCalls - weeklyCallTarget,
        sms: totalSMS - weeklySMSTarget,
        apps: totalApps - weeklyAppTarget,
        revenue: totalRevenue - weeklyRevenueTarget
      },
      activityCallGoal: activityCallGoal
    };
  }, [weekData, goals, selectedRep, daysOfWeek, data]);

  // Build trend data for Weekly Trend BarChart (only completed days + today)
  const trendData = useMemo(() => {
    if (!weekData || !daysOfWeek) return [];

    return daysOfWeek
      .filter(day => !day.isFuture) // Only past days and today
      .map(day => ({
        day: day.shortName,
        calls: weekData[day.name]?.calls || 0,
        sms: weekData[day.name]?.sms || 0,
        isToday: day.isToday
      }));
  }, [weekData, daysOfWeek]);

  // Check if behind target for warning message
  const isBehindTarget = weeklyMetrics && weeklyMetrics.gaps && (
    weeklyMetrics.gaps.calls < 0 || weeklyMetrics.gaps.sms < 0
  );

  // Get recent activities (last 20)
  const recentActivities = useMemo(() => {
    if (!weekData) return [];

    const activities = [];
    daysOfWeek.forEach(day => {
      const dayData = weekData[day.name];
      if (dayData && dayData.activities) {
        dayData.activities.forEach(act => {
          activities.push({
            ...act,
            day: day.name,
            dayShort: day.shortName,
            date: day.date
          });
        });
      }
    });

    // Sort by day (most recent first) and return last 20
    return activities.reverse().slice(0, 20);
  }, [weekData, daysOfWeek]);

  // Calculate per-rep TODAY metrics (for Urgency Section breakdown)
  const perRepTodayMetrics = useMemo(() => {
    if (!data || !data.tabs || selectedRep !== 'All Reps' || !goals) return [];

    // Use currentMonth, currentMonthYear from outer scope
    const businessDaysInMonth = getBusinessDaysInMonth();
    const todayFullDate = today.toISOString().split('T')[0];

    return reps.map(rep => {
      // Get this rep's individual goals (using Month Year format)
      const repGoalRow = goals.find(g => g.Rep === rep && g.Month === currentMonthYear);
      const appsGoal = repGoalRow ? parseInt(repGoalRow.Apps_Goal || 0) : 0;
      const activityCallGoal = repGoalRow ? parseInt(repGoalRow.Activity_Call_Goal || 0) : 0;

      // Calculate daily targets
      const dailyAppTarget = businessDaysInMonth > 0 ? Math.ceil(appsGoal / businessDaysInMonth) : 0;

      // Get historical conversion rates for this rep
      const historical = calculateHistoricalMetrics(data, rep, currentMonth, 6);
      const conversionCallsToApps = historical ? historical.averages.callsToApps : 3;
      const conversionSmsToApps = historical ? historical.averages.smsToApps : 5;

      // Calculate daily calls/SMS targets
      const dailyCallTarget = conversionCallsToApps > 0 ? Math.ceil(dailyAppTarget / (conversionCallsToApps / 100)) : 0;
      const dailySMSTarget = conversionSmsToApps > 0 ? Math.ceil(dailyAppTarget / (conversionSmsToApps / 100)) : 0;

      // Get today's actual performance for this rep
      const todayCalls = (data.tabs.Dials_TalkTime?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === todayFullDate
      ) || []).length;

      const todaySMS = (data.tabs.SMS?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === todayFullDate
      ) || []).reduce((sum, s) => sum + (parseFloat(s.Count) || 0), 0);

      const todayApps = (data.tabs.Apps_In?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === todayFullDate
      ) || []).length;

      // Calculate what's still needed today
      const callsNeeded = Math.max(0, dailyCallTarget - todayCalls);
      const smsNeeded = Math.max(0, dailySMSTarget - todaySMS);
      const appsNeeded = Math.max(0, dailyAppTarget - todayApps);

      // Calculate "urgency score" (total % behind across all 3 metrics)
      const callsPct = dailyCallTarget > 0 ? (todayCalls / dailyCallTarget * 100) : 100;
      const smsPct = dailySMSTarget > 0 ? (todaySMS / dailySMSTarget * 100) : 100;
      const appsPct = dailyAppTarget > 0 ? (todayApps / dailyAppTarget * 100) : 100;
      const avgPct = (callsPct + smsPct + appsPct) / 3;

      return {
        rep: rep,
        todayCalls,
        dailyCallTarget,
        callsNeeded,
        todaySMS,
        dailySMSTarget,
        smsNeeded,
        todayApps,
        dailyAppTarget,
        appsNeeded,
        avgPct, // Used for sorting
        isOnTrack: callsNeeded === 0 && smsNeeded === 0 && appsNeeded === 0
      };
    }).sort((a, b) => a.avgPct - b.avgPct); // Sort by urgency (lowest % first = most behind)
  }, [data, selectedRep, goals, reps, today]);

  // Calculate per-rep breakdown for a specific day (for expandable rows)
  const getRepBreakdownForDay = (fullDate) => {
    if (!data || !data.tabs || selectedRep !== 'All Reps') return [];

    // Use currentMonthYear from outer scope for goal matching

    const repBreakdowns = reps.map(rep => {
      // Get this rep's individual goal (using Month Year format)
      const repGoalRow = goals?.find(g => g.Rep === rep && g.Month === currentMonthYear);
      const repActivityGoal = repGoalRow ? parseInt(repGoalRow.Activity_Call_Goal || 0) : 0;
      // Get calls for this rep on this day
      const calls = data.tabs.Dials_TalkTime?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === fullDate
      ) || [];

      // Calculate connected calls (>= 90 seconds) and unique companies
      const connected = calls.filter(call => parseFloat(call.Duration_Minutes || 0) >= 1.5).length;
      const uniqueCompanies = new Set(calls.filter(call => call.Account && call.Account.trim() !== '').map(call => call.Account)).size;
      const uniqueConnects = new Set(calls.filter(call => parseFloat(call.Duration_Minutes || 0) >= 1.5 && call.Account && call.Account.trim() !== '').map(call => call.Account)).size;

      // Get SMS for this rep on this day
      const sms = data.tabs.SMS?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === fullDate
      ) || [];
      const smsCount = sms.reduce((sum, s) => sum + (parseFloat(s.Count) || 0), 0);

      // Get Apps In for this rep on this day
      const appsIn = data.tabs.Apps_In?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === fullDate
      ) || [];

      // Get Approved for this rep on this day
      const approved = data.tabs.Approved?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === fullDate
      ) || [];

      // Get Funded for this rep on this day
      const funded = filterValidDeals(data.tabs.Funded?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === fullDate
      ) || []);

      // Get Revenue for this rep on this day
      const revenue = filterValidDeals(data.tabs.Revenue?.data?.filter(r =>
        r.Rep === rep && r.Date?.split('T')[0] === fullDate
      ) || []);
      const totalRevenue = revenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);

      // Activity Points: Date-based formula (Dec 16, 2025)
      const activityCount = calculateActivityPoints(calls.length, smsCount, appsIn.length, fullDate);

      return {
        rep: rep,
        calls: calls.length,
        sms: smsCount,
        connected: connected,
        uniqueCompanies: uniqueCompanies,
        uniqueConnects: uniqueConnects,
        appsIn: appsIn.length,
        approved: approved.length,
        funded: funded.length,
        revenue: totalRevenue,
        activityCount: activityCount,
        activityGoal: repActivityGoal
      };
    });

    return repBreakdowns;
  };

  // Get per-rep breakdown for entire week (aggregated across all days)
  const getRepBreakdownForWeek = () => {
    if (!data || !data.tabs) return [];

    // Use currentMonthYear from outer scope for goal matching

    const repBreakdowns = reps.map(rep => {
      // Get this rep's individual goal (using Month Year format)
      const repGoalRow = goals?.find(g => g.Rep === rep && g.Month === currentMonthYear);
      const repActivityGoal = repGoalRow ? parseInt(repGoalRow.Activity_Call_Goal || 0) * 5 : 0; // Weekly goal = daily goal × 5

      // Initialize totals
      let totalCalls = 0;
      let totalSMS = 0;
      let totalConnected = 0;
      let totalAppsIn = 0;
      let totalApproved = 0;
      let totalFunded = 0;
      let totalRevenue = 0;

      const weekCompaniesSet = new Set();
      const weekConnectedCompaniesSet = new Set();

      // Loop through all days of the week and aggregate
      daysOfWeek.forEach(day => {
        if (day.isFuture) return; // Skip future days

        const fullDate = day.fullDate;

        // Get calls for this rep on this day
        const calls = data.tabs.Dials_TalkTime?.data?.filter(r =>
          r.Rep === rep && r.Date?.split('T')[0] === fullDate
        ) || [];

        totalCalls += calls.length;

        // Track connected calls and companies
        calls.forEach(call => {
          if (parseFloat(call.Duration_Minutes || 0) >= 1.5) {
            totalConnected++;
            if (call.Account && call.Account.trim() !== '') {
              weekConnectedCompaniesSet.add(call.Account);
            }
          }
          if (call.Account && call.Account.trim() !== '') {
            weekCompaniesSet.add(call.Account);
          }
        });

        // Get SMS for this rep on this day
        const sms = data.tabs.SMS?.data?.filter(r =>
          r.Rep === rep && r.Date?.split('T')[0] === fullDate
        ) || [];
        totalSMS += sms.reduce((sum, s) => sum + (parseFloat(s.Count) || 0), 0);

        // Get Apps In for this rep on this day
        const appsIn = data.tabs.Apps_In?.data?.filter(r =>
          r.Rep === rep && r.Date?.split('T')[0] === fullDate
        ) || [];
        totalAppsIn += appsIn.length;

        // Get Approved for this rep on this day
        const approved = data.tabs.Approved?.data?.filter(r =>
          r.Rep === rep && r.Date?.split('T')[0] === fullDate
        ) || [];
        totalApproved += approved.length;

        // Get Funded for this rep on this day
        const funded = filterValidDeals(data.tabs.Funded?.data?.filter(r =>
          r.Rep === rep && r.Date?.split('T')[0] === fullDate
        ) || []);
        totalFunded += funded.length;

        // Get Revenue for this rep on this day
        const revenue = filterValidDeals(data.tabs.Revenue?.data?.filter(r =>
          r.Rep === rep && r.Date?.split('T')[0] === fullDate
        ) || []);
        totalRevenue += revenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
      });

      // Activity Points: Date-based formula (Dec 16, 2025) - use today's date for current week aggregate
      const activityCount = calculateActivityPoints(totalCalls, totalSMS, totalAppsIn, new Date().toISOString().split('T')[0]);

      return {
        rep: rep,
        calls: totalCalls,
        sms: totalSMS,
        connected: totalConnected,
        uniqueCompanies: weekCompaniesSet.size,
        uniqueConnects: weekConnectedCompaniesSet.size,
        appsIn: totalAppsIn,
        approved: totalApproved,
        funded: totalFunded,
        revenue: totalRevenue,
        activityCount: activityCount,
        activityGoal: repActivityGoal
      };
    });

    return repBreakdowns;
  };

  // Toggle day expansion (only for All Reps view)
  const toggleDayExpansion = (fullDate) => {
    if (selectedRep !== 'All Reps') return; // Only allow expansion for All Reps view
    setExpandedDay(expandedDay === fullDate ? null : fullDate);
  };

  if (!weekData || !weeklyMetrics) {
    return <div className="this-week-tab"><p>Loading week data...</p></div>;
  }

  const isAllReps = selectedRep === 'All Reps';
  const repFirstName = isAllReps ? 'Team' : selectedRep.split(' ')[0];

  return (
    <div className="this-week-tab">
      <h2>📅 This Week - {repFirstName}'s Activity Monitor</h2>
      <p className="tab-description">
        Week of {monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {sunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        &nbsp;•&nbsp;Today is {currentDayOfWeek} at {currentTime}
      </p>

      {/* Sticky Navigation Bar */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: 'white',
        padding: '12px 16px',
        marginBottom: '20px',
        borderBottom: '2px solid #e2e8f0',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginRight: '8px' }}>
            JUMP TO:
          </span>
          {[
            { id: 'summary', label: '📊 Week Summary' },
            { id: 'urgency', label: "🚨 Today's Urgency" },
            { id: 'daily', label: '📅 Daily Breakdown' },
          ].map(({ id, label }) => (
            <button
              key={id}
              onClick={() => scrollToSection(id)}
              style={{
                padding: '8px 16px',
                backgroundColor: activeSection === id ? '#eff6ff' : 'white',
                border: activeSection === id ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                borderRadius: '20px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: activeSection === id ? 600 : 500,
                color: activeSection === id ? '#1e40af' : '#475569',
                transition: 'all 0.2s ease'
              }}
            onMouseOver={(e) => {
              if (activeSection !== id) {
                e.currentTarget.style.backgroundColor = '#eff6ff';
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.color = '#1e40af';
              }
            }}
            onMouseOut={(e) => {
              if (activeSection !== id) {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.color = '#475569';
              }
            }}
          >
            {label}
          </button>
        ))}
        </div>
      </div>

      {/* ============================================ */}
      {/* SECTION 1: WEEK AT A GLANCE                 */}
      {/* High-level weekly performance summary       */}
      {/* ============================================ */}
      <section
        ref={sectionRefs.summary}
        style={{
          scrollMarginTop: '80px',
          marginTop: '24px',
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0',
          position: 'relative'
        }}
      >
        {/* Developer Mode: Section Label */}
        {developerMode && (
          <div className="dev-section-label">S-1</div>
        )}
        {/* Collapsible Header */}
        <div
          onClick={() => toggleSection('summary')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            padding: '12px 0',
            borderBottom: '2px solid #e2e8f0',
            marginBottom: '16px'
          }}
        >
          <div>
            <h2 className="section-title" style={{ margin: 0 }}>📊 Week at a Glance</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
              High-level weekly performance summary
            </p>
          </div>
          <span style={{
            fontSize: '20px',
            color: '#64748b',
            transform: collapsedSections.summary ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>
            ▼
          </span>
        </div>

        {!collapsedSections.summary && (
          <>
            {/* KPI Summary Cards */}
            <div className="week-summary" style={{ marginBottom: '24px' }}>
              <div className="summary-card" style={{ position: 'relative' }}>
                {developerMode && <div className="dev-card-label">K-1</div>}
                <div className="card-icon">📞</div>
                <div className="card-content">
                  <h4>Calls This Week</h4>
                  <div className="metric-large">{weeklyMetrics.totals.calls} / {weeklyMetrics.weeklyTargets.calls}</div>
                  <div className={`metric-status ${weeklyMetrics.gaps.calls >= 0 ? 'on-track' : 'behind'}`}>
                    {weeklyMetrics.gaps.calls >= 0 ? '✅ On Track' : `⚠️ ${Math.abs(weeklyMetrics.gaps.calls)} behind`}
                  </div>
                </div>
              </div>

              <div className="summary-card" style={{ position: 'relative' }}>
                {developerMode && <div className="dev-card-label">K-2</div>}
                <div className="card-icon">💬</div>
                <div className="card-content">
                  <h4>SMS This Week</h4>
                  <div className="metric-large">{weeklyMetrics.totals.sms} / {weeklyMetrics.weeklyTargets.sms}</div>
                  <div className={`metric-status ${weeklyMetrics.gaps.sms >= 0 ? 'on-track' : 'behind'}`}>
                    {weeklyMetrics.gaps.sms >= 0 ? '✅ On Track' : `⚠️ ${Math.abs(weeklyMetrics.gaps.sms)} behind`}
                  </div>
                </div>
              </div>

              <div className="summary-card" style={{ position: 'relative' }}>
                {developerMode && <div className="dev-card-label">K-3</div>}
                <div className="card-icon">📋</div>
                <div className="card-content">
                  <h4>Apps This Week</h4>
                  <div className="metric-large">{weeklyMetrics.totals.apps} / {weeklyMetrics.weeklyTargets.apps}</div>
                  <div className={`metric-status ${weeklyMetrics.gaps.apps >= 0 ? 'on-track' : 'behind'}`}>
                    {weeklyMetrics.gaps.apps >= 0 ? '✅ On Track' : `⚠️ ${Math.abs(weeklyMetrics.gaps.apps)} behind`}
                  </div>
                </div>
              </div>

              <div className="summary-card" style={{ position: 'relative' }}>
                {developerMode && <div className="dev-card-label">K-4</div>}
                <div className="card-icon">📈</div>
                <div className="card-content">
                  <h4>Conversion Rate</h4>
                  <div className="metric-large">{formatPercent(weeklyMetrics.conversions.callsToApps)}</div>
                  <div className="metric-status">Calls → Apps</div>
                </div>
              </div>
            </div>

            {/* Target Calculation Explanation */}
            <div style={{
              fontSize: '12px',
              color: '#64748b',
              fontStyle: 'italic',
              marginBottom: '16px',
              padding: '8px 12px',
              background: '#f8fafc',
              borderRadius: '6px',
              borderLeft: '3px solid #3b82f6'
            }}>
              Weekly targets derived from monthly goals using conversion rates: Apps goal ÷ {weeklyMetrics.conversions.callsToApps.toFixed(1)}% = Calls needed (5-day week)
            </div>

            {/* Daily Activity Chart - NOW INSIDE Week at a Glance */}
            {trendData.length > 0 && (
              <div style={{
                padding: '20px',
                background: '#fff',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                marginBottom: '24px',
                border: '1px solid #e2e8f0',
                position: 'relative'
              }}>
                {/* Developer Mode: Chart Label */}
                {developerMode && (
                  <div className="dev-chart-label">C-1</div>
                )}
                <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 16px 0' }}>📈 Daily Activity So Far</h3>

                {isBehindTarget && weeklyMetrics && (
                  <div style={{
                    marginBottom: '12px',
                    padding: '8px 12px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#92400e'
                  }}>
                    ⚠️ <strong>Today ({currentDayOfWeek})</strong> is behind target - only {weeklyMetrics.today.calls} calls vs {weeklyMetrics.dailyTargets.calls} daily target. Need {weeklyMetrics.todayNeeded.calls} more calls.
                  </div>
                )}

                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="day" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="calls" fill="#3b82f6" name="Calls" radius={[4, 4, 0, 0]}>
                      <LabelList dataKey="calls" position="top" style={{ fontSize: 11, fill: '#3b82f6', fontWeight: 600 }} />
                    </Bar>
                    <Bar dataKey="sms" fill="#9333ea" name="SMS" radius={[4, 4, 0, 0]}>
                      <LabelList dataKey="sms" position="top" style={{ fontSize: 11, fill: '#9333ea', fontWeight: 600 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Conversion Rates - NOW INSIDE Week at a Glance */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 12px 0' }}>📈 Conversion Rates</h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '12px'
              }}>
                <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0', position: 'relative' }}>
                  {developerMode && <div className="dev-card-label">K-5</div>}
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>Call → App</div>
                  <div style={{ fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>{formatPercent(weeklyMetrics.conversions.callsToApps)}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>{weeklyMetrics.totals.apps} apps from {weeklyMetrics.totals.calls} calls</div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0', position: 'relative' }}>
                  {developerMode && <div className="dev-card-label">K-6</div>}
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>SMS → App</div>
                  <div style={{ fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>{formatPercent(weeklyMetrics.conversions.smsToApps)}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>{weeklyMetrics.totals.apps} apps from {weeklyMetrics.totals.sms} SMS</div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0', position: 'relative' }}>
                  {developerMode && <div className="dev-card-label">K-7</div>}
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>App → Approved</div>
                  <div style={{ fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>{formatPercent(weeklyMetrics.conversions.appsToApproved)}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>{weeklyMetrics.totals.approved} from {weeklyMetrics.totals.apps} apps</div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0', position: 'relative' }}>
                  {developerMode && <div className="dev-card-label">K-8</div>}
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>Approved → Funded</div>
                  <div style={{ fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>{formatPercent(weeklyMetrics.conversions.approvedToFunded)}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>{weeklyMetrics.totals.funded} from {weeklyMetrics.totals.approved} approved</div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0', position: 'relative' }}>
                  {developerMode && <div className="dev-card-label">K-9</div>}
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>App → Funded</div>
                  <div style={{ fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>{formatPercent(weeklyMetrics.conversions.appsToFunded)}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>{weeklyMetrics.totals.funded} from {weeklyMetrics.totals.apps} apps</div>
                </div>
              </div>
            </div>
        {/* Efficiency Metrics */}
        <section className="efficiency-metrics">
          <h3>Efficiency Metrics</h3>
          <div className="metrics-row">
            <div className="metric-card" style={{ position: 'relative' }}>
              {developerMode && <div className="dev-card-label">K-3</div>}
              <h4>Avg Deal Size</h4>
              <div className="metric-value">
                {weeklyMetrics.totals.funded > 0
                  ? formatCurrency(weeklyMetrics.totals.revenue / weeklyMetrics.totals.funded)
                  : '$0'}
              </div>
              <div className="metric-detail">
                {formatCurrency(weeklyMetrics.totals.revenue)} ÷ {weeklyMetrics.totals.funded} deals
              </div>
            </div>
            <div className="metric-card" style={{ position: 'relative' }}>
              {developerMode && <div className="dev-card-label">K-4</div>}
              <h4>Calls per App</h4>
              <div className="metric-value">
                {weeklyMetrics.totals.apps > 0
                  ? (weeklyMetrics.totals.calls / weeklyMetrics.totals.apps).toFixed(1)
                  : '0.0'}
              </div>
              <div className="metric-detail">
                {weeklyMetrics.totals.calls} calls ÷ {weeklyMetrics.totals.apps} apps
              </div>
            </div>
            <div className="metric-card" style={{ position: 'relative' }}>
              {developerMode && <div className="dev-card-label">K-5</div>}
              <h4>SMS per App</h4>
              <div className="metric-value">
                {weeklyMetrics.totals.apps > 0
                  ? (weeklyMetrics.totals.sms / weeklyMetrics.totals.apps).toFixed(1)
                  : '0.0'}
              </div>
              <div className="metric-detail">
                {weeklyMetrics.totals.sms} SMS ÷ {weeklyMetrics.totals.apps} apps
              </div>
            </div>
            <div className="metric-card" style={{ position: 'relative' }}>
              {developerMode && <div className="dev-card-label">K-6</div>}
              <h4>Apps per Funded</h4>
              <div className="metric-value">
                {weeklyMetrics.totals.funded > 0
                  ? (weeklyMetrics.totals.apps / weeklyMetrics.totals.funded).toFixed(1)
                  : '0.0'}
              </div>
              <div className="metric-detail">
                {weeklyMetrics.totals.apps} apps ÷ {weeklyMetrics.totals.funded} funded
              </div>
            </div>
          </div>
        </section>
          </>
        )}
      </section>

      {/* ============================================ */}
      {/* SECTION 2: TODAY'S URGENCY                  */}
      {/* What needs to happen today to hit targets   */}
      {/* ============================================ */}
      <section ref={sectionRefs.urgency} style={{
        scrollMarginTop: '80px',
        marginTop: '24px',
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        position: 'relative'
      }}>
        {/* Developer Mode: Section Label */}
        {developerMode && (
          <div className="dev-section-label">S-2</div>
        )}
        {/* Collapsible Header */}
        <div
          onClick={() => toggleSection('urgency')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            padding: '12px 0',
            borderBottom: !collapsedSections.urgency ? '2px solid #e2e8f0' : 'none'
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
              🚨 {currentDayOfWeek.toUpperCase()} - WHAT NEEDS TO HAPPEN TODAY
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
              Current Time: {currentTime} | Hours Left: ~{Math.max(0, 18 - today.getHours())} hours
            </p>
          </div>
          <span style={{
            fontSize: '20px',
            color: '#64748b',
            transform: collapsedSections.urgency ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>▼</span>
        </div>

        {!collapsedSections.urgency && (
          <>
            {/* Urgency Cards - Using UrgencyCard Component */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '16px',
              marginTop: '20px'
            }}>
              <div style={{ position: 'relative' }}>
                {developerMode && <div className="dev-card-label">K-1</div>}
                <UrgencyCard
                  icon="📞"
                  label="CALLS"
                  done={weeklyMetrics.today.calls}
                  needed={weeklyMetrics.todayNeeded.calls}
                  perHour={Math.ceil(weeklyMetrics.todayNeeded.calls / Math.max(1, 18 - today.getHours()))}
                  hoursLeft={Math.max(0, 18 - today.getHours())}
                />
              </div>
              <div style={{ position: 'relative' }}>
                {developerMode && <div className="dev-card-label">K-2</div>}
                <UrgencyCard
                  icon="💬"
                  label="SMS"
                  done={weeklyMetrics.today.sms}
                  needed={weeklyMetrics.todayNeeded.sms}
                  perHour={Math.ceil(weeklyMetrics.todayNeeded.sms / Math.max(1, 18 - today.getHours()))}
                  hoursLeft={Math.max(0, 18 - today.getHours())}
                />
              </div>
              <div style={{ position: 'relative' }}>
                {developerMode && <div className="dev-card-label">K-3</div>}
                <UrgencyCard
                  icon="📋"
                  label="APPS"
                  done={weeklyMetrics.today.apps}
                  needed={weeklyMetrics.todayNeeded.apps}
                  action="Focus on warm leads"
                />
              </div>
            </div>

          {/* Per-Rep Breakdown (only shown when viewing All Reps) */}
          {selectedRep === 'All Reps' && perRepTodayMetrics.length > 0 && (
            <div style={{ marginTop: '24px', background: 'white', borderRadius: '8px', padding: '16px', border: '2px solid #e2e8f0', borderLeft: '4px solid #3b82f6', position: 'relative' }}>
              {developerMode && <div className="dev-card-label">K-4</div>}
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#1e40af', borderBottom: '2px solid #3b82f6', paddingBottom: '8px' }}>
                📋 Per-Rep Today Status
              </h4>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #3b82f6' }}>
                      <th style={{ padding: '10px', textAlign: 'left', color: '#1e40af', fontWeight: '600' }}>Rep</th>
                      <th style={{ padding: '10px', textAlign: 'center', color: '#1e40af', fontWeight: '600' }}>Calls</th>
                      <th style={{ padding: '10px', textAlign: 'center', color: '#1e40af', fontWeight: '600' }}>Need</th>
                      <th style={{ padding: '10px', textAlign: 'center', color: '#1e40af', fontWeight: '600' }}>SMS</th>
                      <th style={{ padding: '10px', textAlign: 'center', color: '#1e40af', fontWeight: '600' }}>Need</th>
                      <th style={{ padding: '10px', textAlign: 'center', color: '#1e40af', fontWeight: '600' }}>Apps</th>
                      <th style={{ padding: '10px', textAlign: 'center', color: '#1e40af', fontWeight: '600' }}>Need</th>
                      <th style={{ padding: '10px', textAlign: 'center', color: '#1e40af', fontWeight: '600' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perRepTodayMetrics.map(repMetric => (
                      <tr key={repMetric.rep} style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '10px', fontWeight: '600', color: '#1a202c' }}>
                          {repMetric.rep.split(' ')[0]}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center', color: '#1a202c' }}>
                          {repMetric.todayCalls}/{repMetric.dailyCallTarget}
                        </td>
                        <td style={{
                          padding: '10px',
                          textAlign: 'center',
                          fontWeight: '700',
                          color: repMetric.callsNeeded === 0 ? '#48bb78' : '#fbbf24'
                        }}>
                          {repMetric.callsNeeded === 0 ? '✓' : repMetric.callsNeeded}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center', color: '#1a202c' }}>
                          {repMetric.todaySMS}/{repMetric.dailySMSTarget}
                        </td>
                        <td style={{
                          padding: '10px',
                          textAlign: 'center',
                          fontWeight: '700',
                          color: repMetric.smsNeeded === 0 ? '#48bb78' : '#fbbf24'
                        }}>
                          {repMetric.smsNeeded === 0 ? '✓' : repMetric.smsNeeded}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center', color: '#1a202c' }}>
                          {repMetric.todayApps}/{repMetric.dailyAppTarget}
                        </td>
                        <td style={{
                          padding: '10px',
                          textAlign: 'center',
                          fontWeight: '700',
                          color: repMetric.appsNeeded === 0 ? '#48bb78' : '#fbbf24'
                        }}>
                          {repMetric.appsNeeded === 0 ? '✓' : repMetric.appsNeeded}
                        </td>
                        <td style={{
                          padding: '10px',
                          textAlign: 'center',
                          fontWeight: '600',
                          color: repMetric.isOnTrack ? '#48bb78' : '#fbbf24'
                        }}>
                          {repMetric.isOnTrack ? '✅ On Track' : `${Math.round(repMetric.avgPct)}%`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          </>
        )}
      </section>

      {/* ============================================ */}
      {/* SECTION 3: DAILY BREAKDOWN                  */}
      {/* Day-by-day performance with rep drill-down  */}
      {/* ============================================ */}
      <section ref={sectionRefs.daily} style={{
        scrollMarginTop: '80px',
        marginTop: '24px',
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        position: 'relative'
      }}>
        {/* Developer Mode: Section Label */}
        {developerMode && (
          <div className="dev-section-label">S-3</div>
        )}
        {/* Collapsible Header */}
        <div
          onClick={() => toggleSection('daily')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            padding: '12px 0',
            borderBottom: !collapsedSections.daily ? '2px solid #e2e8f0' : 'none'
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
              📊 Daily Breakdown
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
              Day-by-day performance with rep drill-down
            </p>
          </div>
          <span style={{
            fontSize: '20px',
            color: '#64748b',
            transform: collapsedSections.daily ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}>▼</span>
        </div>

        {!collapsedSections.daily && (
        <div className="breakdown-table-container" style={{ marginTop: '16px' }}>
          <table className="breakdown-table">
            <thead>
              <tr>
                <th>Day</th>
                <th>Calls</th>
                <th>SMS</th>
                <th>Apps</th>
                <th>Approved</th>
                <th>Funded</th>
                <th>Revenue</th>
                <th>Activity</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {daysOfWeek.map(day => {
                const dayData = weekData[day.name];
                const dayStatus = day.isFuture ? 'future' :
                                day.isToday ? (dayData.calls >= weeklyMetrics.dailyTargets.calls ? 'on-track' : 'behind') :
                                (dayData.calls >= weeklyMetrics.dailyTargets.calls ? 'good' : 'slow');
                const isExpanded = expandedDay === day.fullDate;
                const canExpand = isAllReps && !day.isFuture;

                return (
                  <React.Fragment key={day.name}>
                    <tr
                      className={`
                        ${day.isToday ? 'today-row' : ''}
                        ${day.isFuture ? 'future-row' : ''}
                        ${canExpand ? 'expandable-row' : ''}
                        ${isExpanded ? 'expanded' : ''}
                      `.trim()}
                      onClick={() => canExpand && toggleDayExpansion(day.fullDate)}
                      style={{ cursor: canExpand ? 'pointer' : 'default' }}
                    >
                      <td>
                        <strong>{day.shortName}</strong>
                        <br />
                        <span className="date-small">{day.date}</span>
                        {canExpand && <span className="expand-icon">{isExpanded ? ' ▼' : ' ▶'}</span>}
                      </td>
                      <td>{day.isFuture ? '-' : dayData.calls}</td>
                      <td>{day.isFuture ? '-' : dayData.sms}</td>
                      <td>{day.isFuture ? '-' : dayData.appsIn}</td>
                      <td>{day.isFuture ? '-' : dayData.approved}</td>
                      <td>{day.isFuture ? '-' : dayData.funded}</td>
                      <td>{day.isFuture ? '-' : formatCurrency(dayData.revenue)}</td>
                      <td style={{
                        backgroundColor: day.isFuture ? 'transparent' :
                          weeklyMetrics.activityCallGoal === 0 ? 'transparent' :
                          dayData.activityCount >= weeklyMetrics.activityCallGoal ? '#d1fae5' :
                          dayData.activityCount >= weeklyMetrics.activityCallGoal * 0.8 ? '#fef3c7' : '#fee2e2',
                        fontWeight: '600'
                      }}>
                        {day.isFuture ? '-' : `${dayData.activityCount}/${weeklyMetrics.activityCallGoal}`}
                      </td>
                      <td>
                        {day.isFuture ? '⏳ Future' :
                         day.isToday ? (dayStatus === 'on-track' ? '✅ On Track' : '🔴 Behind') :
                         dayStatus === 'good' ? '✅ Good' : '⚠️ Slow'}
                      </td>
                    </tr>

                    {/* Expandable per-rep breakdown row */}
                    {isExpanded && canExpand && (
                      <tr className="expanded-detail-row">
                        <td colSpan="9">
                          <div className="rep-breakdown-container">
                            <h4>Per-Rep Breakdown for {day.shortName} {day.date}</h4>
                            <table className="rep-breakdown-table">
                              <thead>
                                <tr>
                                  <th>Rep</th>
                                  <th>Calls</th>
                                  <th>SMS</th>
                                  <th>Apps</th>
                                  <th>Approved</th>
                                  <th>Funded</th>
                                  <th>Revenue</th>
                                  <th>Activity</th>
                                </tr>
                              </thead>
                              <tbody>
                                {getRepBreakdownForDay(day.fullDate)
                                  .sort((a, b) => b.activityCount - a.activityCount)
                                  .map(repData => (
                                  <tr key={repData.rep}>
                                    <td><strong>{repData.rep.split(' ')[0]}</strong></td>
                                    <td>{repData.calls}</td>
                                    <td>{repData.sms}</td>
                                    <td>{repData.appsIn}</td>
                                    <td>{repData.approved}</td>
                                    <td>{repData.funded}</td>
                                    <td>{formatCurrency(repData.revenue)}</td>
                                    <td style={{
                                      backgroundColor:
                                        repData.activityGoal === 0 ? 'transparent' :
                                        repData.activityCount >= repData.activityGoal ? '#d1fae5' :
                                        repData.activityCount >= repData.activityGoal * 0.8 ? '#fef3c7' : '#fee2e2',
                                      fontWeight: '600'
                                    }}>
                                      {repData.activityCount}/{repData.activityGoal}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {/* Total Row - Expandable for All Reps view */}
              {(() => {
                const isTotalExpanded = expandedDay === 'TOTAL';
                const canExpandTotal = isAllReps;

                return (
                  <>
                    <tr
                      className={`total-row ${canExpandTotal ? 'expandable-row' : ''} ${isTotalExpanded ? 'expanded' : ''}`.trim()}
                      onClick={() => canExpandTotal && setExpandedDay(isTotalExpanded ? null : 'TOTAL')}
                      style={{ cursor: canExpandTotal ? 'pointer' : 'default' }}
                    >
                      <td>
                        <strong>TOTAL</strong>
                        {canExpandTotal && <span className="expand-icon">{isTotalExpanded ? ' ▼' : ' ▶'}</span>}
                      </td>
                <td><strong>{weeklyMetrics.totals.calls}</strong></td>
                <td><strong>{weeklyMetrics.totals.sms}</strong></td>
                <td><strong>{weeklyMetrics.totals.apps}</strong></td>
                <td><strong>{weeklyMetrics.totals.approved}</strong></td>
                <td><strong>{weeklyMetrics.totals.funded}</strong></td>
                <td><strong>{formatCurrency(weeklyMetrics.totals.revenue)}</strong></td>
                <td><strong>{weeklyMetrics.totals.activityCount}</strong></td>
                <td>-</td>
              </tr>

              {/* Expandable per-rep breakdown for Total (weekly totals) */}
              {isTotalExpanded && canExpandTotal && (
                <tr className="expanded-detail-row">
                  <td colSpan="9">
                    <div className="rep-breakdown-container">
                      <h4>Per-Rep Weekly Totals</h4>
                      <table className="rep-breakdown-table">
                        <thead>
                          <tr>
                            <th>Rep</th>
                            <th>Calls</th>
                            <th>SMS</th>
                            <th>Apps</th>
                            <th>Approved</th>
                            <th>Funded</th>
                            <th>Revenue</th>
                            <th>Activity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getRepBreakdownForWeek()
                            .sort((a, b) => b.activityCount - a.activityCount)
                            .map(repData => (
                              <tr key={repData.rep}>
                                <td><strong>{repData.rep.split(' ')[0]}</strong></td>
                                <td>{repData.calls}</td>
                                <td>{repData.sms}</td>
                                <td>{repData.appsIn}</td>
                                <td>{repData.approved}</td>
                                <td>{repData.funded}</td>
                                <td>{formatCurrency(repData.revenue)}</td>
                                <td style={{
                                  backgroundColor:
                                    repData.activityGoal === 0 ? 'transparent' :
                                    repData.activityCount >= repData.activityGoal ? '#d1fae5' :
                                    repData.activityCount >= repData.activityGoal * 0.8 ? '#fef3c7' : '#fee2e2',
                                  fontWeight: '600'
                                }}>
                                  {repData.activityCount}/{repData.activityGoal}
                                </td>
                              </tr>
                            ))
                          }
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              )}
            </>
                );
              })()}
              <tr className="target-row">
                <td><strong>TARGET</strong></td>
                <td>{weeklyMetrics.weeklyTargets.calls}</td>
                <td>{weeklyMetrics.weeklyTargets.sms}</td>
                <td>{weeklyMetrics.weeklyTargets.apps}</td>
                <td>-</td>
                <td>-</td>
                <td>{formatCurrency(weeklyMetrics.weeklyTargets.revenue)}</td>
                <td>{weeklyMetrics.activityCallGoal} (daily)</td>
                <td>-</td>
              </tr>
            </tbody>
          </table>
        </div>
        )}
      </section>

      {/* ============================================ */}
      {/* SECTION 4: RECENT ACTIVITY FEED             */}
      {/* Last 20 activities (single rep view only)   */}
      {/* ============================================ */}
      <section className="activity-feed" style={{ position: 'relative' }}>
        {/* Developer Mode: Section Label */}
        {developerMode && (
          <div className="dev-section-label">S-4</div>
        )}
        <h3>📋 Recent Activity (Last 20)</h3>
        <div className="activity-list">
          {recentActivities.length === 0 ? (
            <p className="no-activity">No activities recorded this week</p>
          ) : (
            recentActivities.map((activity, idx) => (
              <div key={idx} className="activity-item">
                <div className="activity-icon">
                  {activity.type === 'call' ? '📞' : activity.type === 'sms' ? '💬' : '📋'}
                </div>
                <div className="activity-details">
                  <div className="activity-description">{activity.description}</div>
                  <div className="activity-meta">
                    {activity.dayShort} {activity.date}
                    {activity.duration > 0 && ` • ${activity.duration} min`}
                    {activity.account && ` • ${activity.account}`}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

    </div>
  );
}

/**
 * HISTORICAL TAB - Trends over time
 */
/**
 * HISTORICAL TAB - Advanced Analytics & Visualizations
 * Dynamic months from actual sheet data
 */
function HistoricalTab({ data, selectedRep, reps, goals, activeHistoricalTab, setActiveHistoricalTab, developerMode }) {
  // Year handling for goal filters
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().toLocaleString('default', { month: 'long' });
  // goalMonth helper for goal lookups - defaults to current month + year
  // Individual uses may override with specific month + year combinations
  const goalMonth = `${currentMonth} ${currentYear}`;

  // State for rep tab navigation within Historical tab
  const [activeRepTab, setActiveRepTab] = useState(reps[0]);

  // State for legend toggling - track which reps are hidden
  const [hiddenReps, setHiddenReps] = useState([]);

  // State for Pack Source month filter (defaults to current month)
  const [packSourceMonth, setPackSourceMonth] = useState(null);

  // Rep vs Rep Sticky Navigation State (Dec 8, 2025 - V4.1)
  const [activeSectionTab, setActiveSectionTab] = useState('fundings');

  // Rep vs Rep Section Refs for sticky navigation (Dec 8, 2025 - V4.1)
  const repVsRepSectionRefs = {
    fundings: useRef(null),
    outreach: useRef(null),
    submissions: useRef(null),
    packsource: useRef(null),
    underwriting: useRef(null)
  };

  // Scroll to section function for Rep vs Rep sticky nav (Dec 8, 2025 - V4.1)
  const scrollToRepVsRepSection = (sectionId) => {
    setActiveSectionTab(sectionId);
    repVsRepSectionRefs[sectionId]?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  // ============================================
  // DEEP DIVE (Single Rep) State & Refs - Dec 8, 2025 - V4.2
  // ============================================

  // Deep Dive: Collapsible sections state (all start expanded)
  const [deepDiveCollapsed, setDeepDiveCollapsed] = useState({
    snapshot: false,
    dealPerformance: false,
    conversions: false,
    activity: false,
    submissions: false
  });

  // Deep Dive: Active JUMP TO section (for highlighting)
  const [activeDeepDiveSection, setActiveDeepDiveSection] = useState('snapshot');

  // Deep Dive: Section refs for scroll-to functionality
  const deepDiveSectionRefs = {
    snapshot: useRef(null),
    dealPerformance: useRef(null),
    conversions: useRef(null),
    activity: useRef(null),
    submissions: useRef(null)
  };

  // Deep Dive: Toggle section collapse
  const toggleDeepDiveSection = (sectionId) => {
    setDeepDiveCollapsed(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Deep Dive: Scroll to section function
  const scrollToDeepDiveSection = (sectionId) => {
    setActiveDeepDiveSection(sectionId);
    // Expand section if collapsed
    if (deepDiveCollapsed[sectionId]) {
      setDeepDiveCollapsed(prev => ({
        ...prev,
        [sectionId]: false
      }));
    }
    deepDiveSectionRefs[sectionId]?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  // ============================================

  // ============================================
  // TEAM SNAPSHOT State & Refs - Dec 10, 2025
  // ============================================

  // Team Snapshot: Collapsible sections state (all start expanded)
  const [teamCollapsed, setTeamCollapsed] = useState({
    summary: false,
    aggregates: false,
    submissions: false,
    rankings: false,
    funding: false,
    activity: false,
    packSource: false,
    table: false
  });

  // Team Snapshot: Active JUMP TO section (for highlighting)
  const [activeTeamSection, setActiveTeamSection] = useState('summary');

  // Team Snapshot: Pack Source tab state (for tabbed view)
  const [packSourceViewTab, setPackSourceViewTab] = useState('volume');

  // Team Snapshot: Expanded rep rows in table
  const [expandedRepRows, setExpandedRepRows] = useState({});

  // Team Snapshot: Section refs for scroll-to functionality
  const teamSectionRefs = {
    summary: useRef(null),
    aggregates: useRef(null),
    submissions: useRef(null),
    rankings: useRef(null),
    funding: useRef(null),
    activity: useRef(null),
    packSource: useRef(null),
    table: useRef(null)
  };

  // Team Snapshot: Toggle section collapse
  const toggleTeamSection = (sectionId) => {
    setTeamCollapsed(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Team Snapshot: Toggle rep row expansion in table
  const toggleRepRow = (repName) => {
    setExpandedRepRows(prev => ({
      ...prev,
      [repName]: !prev[repName]
    }));
  };

  // Team Snapshot: Scroll to section function
  const scrollToTeamSection = (sectionId) => {
    setActiveTeamSection(sectionId);
    // Expand section if collapsed
    if (teamCollapsed[sectionId]) {
      setTeamCollapsed(prev => ({
        ...prev,
        [sectionId]: false
      }));
    }
    teamSectionRefs[sectionId]?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  // ============================================

  // YTD Summary KPIs for Rep vs Rep (Dec 8, 2025 - V4.1)
  const ytdSummaryKpis = useMemo(() => {
    if (!data || !data.tabs) return { totalRevenue: 0, totalFunded: 0, avgDealSize: 0, appsToFundedRate: 0, activityVolume: 0 };

    const allRevenue = data.tabs.Revenue?.data || [];
    const allFunded = data.tabs.Funded?.data || [];
    const allAppsIn = data.tabs.Apps_In?.data || [];
    const allCalls = data.tabs.Dials_TalkTime?.data || [];

    const totalRevenue = allRevenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
    const totalFunded = allFunded.length;
    const totalApps = allAppsIn.length;
    const avgDealSize = totalFunded > 0 ? totalRevenue / totalFunded : 0;
    const appsToFundedRate = totalApps > 0 ? (totalFunded / totalApps * 100) : 0;
    const activityVolume = allCalls.length;

    return { totalRevenue, totalFunded, avgDealSize, appsToFundedRate, activityVolume };
  }, [data]);

  const repColors = {
    'Patrick McTiernan': '#3b82f6',
    'Marcin Maleski': '#8b5cf6',
    'Matthew Vecchione': '#f59e0b',
    'Damian Pomykala': '#ef4444',
    'Antonio Vagnone': '#14b8a6',
    'Alex Golden': '#f43f5e',
    'Team Average': '#000000'  // ✨ Black for Team Average (Nov 8, 2025)
  };

  // Extract unique months dynamically from actual data
  const monthNames = useMemo(() => {
    if (!data || !data.tabs || !data.tabs.Apps_In?.data) return [];

    const uniqueMonths = [...new Set(data.tabs.Apps_In.data.map(r => r.Month))].filter(Boolean);

    // Sort months chronologically (assuming format like "March", "April", etc.)
    const monthOrder = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    return uniqueMonths.sort((a, b) => monthOrder.indexOf(a) - monthOrder.indexOf(b));
  }, [data]);

  // Team Submission Trends - Monthly aggregation for Team Snapshot (Phase 5)
  const teamSubmissionTrends = useMemo(() => {
    if (!data?.tabs || !monthNames || monthNames.length === 0) return [];

    return monthNames.map(monthName => {
      // Combine all deal stages
      const allDeals = [
        ...(data.tabs.Underwriting?.data || []),
        ...(data.tabs.Approved?.data || []),
        ...(data.tabs.Funded?.data || []),
        ...(data.tabs.Declined_Lost?.data || [])
      ].filter(d => d.Month === monthName);

      // Dedupe by Opportunity_ID
      const uniqueDeals = [...new Map(allDeals.map(d => [d.Opportunity_ID, d])).values()];

      // Funded deals (WON)
      const fundedDeals = uniqueDeals.filter(d => d.Stage === 'Funded');
      const submissionsWon = fundedDeals.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);

      // Lost deals
      const lostDeals = uniqueDeals.filter(d => d.Stage === 'Closed Lost');
      const submissionsLost = lostDeals.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);

      // Total declines
      const totalDeclines = uniqueDeals.reduce((sum, d) => sum + (parseInt(d.Submissions_Declined) || 0), 0);

      // Avg submissions per deal
      const totalSubmissions = uniqueDeals.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);
      const avgSubmissionsPerDeal = uniqueDeals.length > 0 ? totalSubmissions / uniqueDeals.length : 0;

      // Success rate (funded count / total resolved)
      const fundedCount = fundedDeals.length;
      const lostCount = lostDeals.length;
      const successRate = (fundedCount + lostCount) > 0
        ? (fundedCount / (fundedCount + lostCount)) * 100
        : 0;

      return {
        month: monthName.substring(0, 3), // "Jan", "Feb", etc.
        submissionsWon,
        submissionsLost,
        totalDeclines,
        avgSubmissionsPerDeal,
        successRate
      };
    });
  }, [data, monthNames]);

  // Determine which reps to show based on selectedRep
  // ✨ UPDATED (Nov 8, 2025): Always include "Team Average" for comparison
  const displayReps = useMemo(() => {
    const baseReps = selectedRep === 'All Reps' ? reps : [selectedRep];
    return [...baseReps, 'Team Average'];
  }, [selectedRep, reps]);

  // Legend click handler - toggle rep visibility
  const handleLegendClick = useCallback((entry) => {
    const repName = entry.dataKey;  // Full rep name

    // ✨ NEW (Nov 8, 2025): Prevent Team Average from being hidden
    // Team Average is a benchmark reference line and should always be visible
    if (repName === 'Team Average') {
      return; // Do nothing - Team Average cannot be hidden
    }

    setHiddenReps(prev => {
      if (prev.includes(repName)) {
        // Show this rep (remove from hidden list)
        return prev.filter(r => r !== repName);
      } else {
        // Hide this rep - but only if at least one other rep will remain visible
        if (displayReps.length - prev.length > 1) {
          return [...prev, repName];
        }
        // Prevent hiding last visible rep
        return prev;
      }
    });
  }, [displayReps]);

  // Custom legend renderer with visual feedback for hidden/visible states
  const renderCustomLegend = useCallback((props) => {
    const { payload } = props;
    if (!payload) return null;

    return (
      <ul style={{
        listStyle: 'none',
        display: 'flex',
        gap: '15px',
        justifyContent: 'center',
        padding: '10px 0',
        margin: 0
      }}>
        {payload.map((entry, index) => {
          const isHidden = hiddenReps.includes(entry.dataKey);
          const isTeamAverage = entry.dataKey === 'Team Average';  // ✨ NEW (Nov 8, 2025)

          // ✨ ENHANCED (Nov 8, 2025): Better tooltip messages
          const getTooltip = () => {
            if (isTeamAverage) {
              return 'Team Average (always visible)';
            }
            return isHidden ? 'Click to show this rep' : 'Click to hide this rep';
          };

          return (
            <li
              key={`item-${index}`}
              onClick={() => handleLegendClick(entry)}
              title={getTooltip()}  // ✨ NEW: Tooltip on hover
              style={{
                cursor: isTeamAverage ? 'default' : 'pointer',  // ✨ ENHANCED: Default cursor for Team Average
                opacity: isHidden ? 0.4 : 1,
                textDecoration: isHidden ? 'line-through' : 'none',
                color: isHidden ? '#9ca3af' : entry.color,
                fontWeight: isHidden ? 'normal' : 'bold',
                fontSize: '14px',
                fontStyle: isTeamAverage ? 'italic' : 'normal',  // ✨ Italic for Team Average
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.2s',
                userSelect: 'none'  // ✨ NEW: Prevent text selection on click
              }}
              onMouseEnter={(e) => {
                // ✨ NEW: Subtle hover effect for clickable items
                if (!isTeamAverage) {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isTeamAverage) {
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              <span style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                backgroundColor: entry.color,
                marginRight: '6px',
                opacity: isHidden ? 0.3 : 1,
                borderRadius: '2px',
                transition: 'opacity 0.2s'
              }}></span>
              {isTeamAverage ? 'Team Avg' : entry.value}
            </li>
          );
        })}
      </ul>
    );
  }, [hiddenReps, handleLegendClick]);

  /**
   * Calculate Team Average metrics for a given month
   *
   * IMPORTANT: Percentage metrics (callsToApps, appsToFunded, etc.) are calculated
   * from aggregate counts across all reps, NOT by averaging individual percentages.
   *
   * Example: If Rep A has 5/5 apps funded (100%) and Rep B has 50/100 (50%),
   * Team Average should be 55/105 = 52.4%, NOT (100%+50%)/2 = 75%
   *
   * EXCLUSION RULE: Reps with $0 revenue/activity are excluded from averages.
   * Example: If 5 reps have [$15k, $12k, $0, $14k, $16k], average = $57k / 4 = $14.25k
   *
   * @param {Object} monthData - Object containing metrics for all reps in this month
   * @param {Array<string>} reps - Array of rep names to include in average calculation
   * @returns {Object} Team Average metrics matching structure of individual rep metrics
   */
  const calculateTeamAverage = useCallback((monthData, reps) => {
    if (reps.length === 0 || Object.keys(monthData).length === 0) {
      return {
        appsIn: 0, approved: 0, funded: 0, revenue: 0, calls: 0, sms: 0,
        callsToApps: 0, smsToApps: 0, appsToApproved: 0, approvedToFunded: 0,
        appsToFunded: 0, avgDealSize: 0, activityVolume: 0, overallConversion: 0
      };
    }

    // Filter to active reps only (exclude reps with $0 revenue)
    const activeReps = reps.filter(rep =>
      monthData[rep] && monthData[rep].revenue > 0
    );

    // If no active reps, return all zeros
    if (activeReps.length === 0) {
      return {
        appsIn: 0, approved: 0, funded: 0, revenue: 0, calls: 0, sms: 0,
        callsToApps: 0, smsToApps: 0, appsToApproved: 0, approvedToFunded: 0,
        appsToFunded: 0, avgDealSize: 0, activityVolume: 0, overallConversion: 0
      };
    }

    // Count-based metrics: average only active reps
    const avgRevenue = activeReps.reduce((sum, rep) =>
      sum + (monthData[rep]?.revenue || 0), 0) / activeReps.length;
    const avgCalls = activeReps.reduce((sum, rep) =>
      sum + (monthData[rep]?.calls || 0), 0) / activeReps.length;
    const avgSMS = activeReps.reduce((sum, rep) =>
      sum + (monthData[rep]?.sms || 0), 0) / activeReps.length;
    const avgActivityVolume = activeReps.reduce((sum, rep) =>
      sum + (monthData[rep]?.activityVolume || 0), 0) / activeReps.length;
    const avgDealSize = activeReps.reduce((sum, rep) =>
      sum + (monthData[rep]?.avgDealSize || 0), 0) / activeReps.length;
    const avgAppsIn = activeReps.reduce((sum, rep) =>
      sum + (monthData[rep]?.appsIn || 0), 0) / activeReps.length;
    const avgApproved = activeReps.reduce((sum, rep) =>
      sum + (monthData[rep]?.approved || 0), 0) / activeReps.length;
    const avgFunded = activeReps.reduce((sum, rep) =>
      sum + (monthData[rep]?.funded || 0), 0) / activeReps.length;

    // Percentage metrics: calculate from aggregate counts (NOT average of percentages)
    const totalAppsIn = activeReps.reduce((sum, rep) =>
      sum + (monthData[rep]?.appsIn || 0), 0);
    const totalApproved = activeReps.reduce((sum, rep) =>
      sum + (monthData[rep]?.approved || 0), 0);
    const totalFunded = activeReps.reduce((sum, rep) =>
      sum + (monthData[rep]?.funded || 0), 0);
    const totalCalls = activeReps.reduce((sum, rep) =>
      sum + (monthData[rep]?.calls || 0), 0);
    const totalSMS = activeReps.reduce((sum, rep) =>
      sum + (monthData[rep]?.sms || 0), 0);

    const callsToApps = totalCalls > 0 ? (totalAppsIn / totalCalls * 100) : 0;
    const smsToApps = totalSMS > 0 ? (totalAppsIn / totalSMS * 100) : 0;
    const appsToApproved = totalAppsIn > 0 ? (totalApproved / totalAppsIn * 100) : 0;
    const approvedToFunded = totalApproved > 0 ? (totalFunded / totalApproved * 100) : 0;
    const appsToFunded = totalAppsIn > 0 ? (totalFunded / totalAppsIn * 100) : 0;
    const overallConversion = totalCalls > 0 ? (totalFunded / totalCalls * 100) : 0;

    return {
      appsIn: avgAppsIn,
      approved: avgApproved,
      funded: avgFunded,
      revenue: avgRevenue,
      calls: avgCalls,
      sms: avgSMS,
      callsToApps,
      smsToApps,
      appsToApproved,
      approvedToFunded,
      appsToFunded,
      avgDealSize,
      activityVolume: avgActivityVolume,
      overallConversion
    };
  }, []);

  // Calculate all historical trends for selected reps only
  const allHistoricalData = useMemo(() => {
    if (!data || !data.tabs || monthNames.length === 0) return null;

    const result = {};

    monthNames.forEach(month => {
      result[month] = {};

      displayReps.forEach(rep => {
        const appsIn = data.tabs.Apps_In?.data?.filter(r => r.Month === month && r.Rep === rep) || [];
        const approved = data.tabs.Approved?.data?.filter(r => r.Month === month && r.Rep === rep) || [];
        const funded = filterValidDeals(data.tabs.Funded?.data?.filter(r => r.Month === month && r.Rep === rep) || []);
        const revenue = filterValidDeals(data.tabs.Revenue?.data?.filter(r => r.Month === month && r.Rep === rep) || []);
        const calls = data.tabs.Dials_TalkTime?.data?.filter(r => r.Month === month && r.Rep === rep) || [];
        const sms = data.tabs.SMS?.data?.filter(r => r.Month === month && r.Rep === rep) || [];

        const totalRevenue = revenue.reduce((sum, row) => sum + (parseFloat(row.Revenue) || 0), 0);
        const totalFundedAmount = funded.reduce((sum, row) => sum + (parseFloat(row.Amount) || 0), 0);
        const totalSMS = sms.reduce((sum, row) => sum + (parseFloat(row.Count) || 0), 0);
        const totalTalkTime = calls.reduce((sum, call) => sum + (parseFloat(call.Duration_Minutes) || 0), 0);

        result[month][rep] = {
          appsIn: appsIn.length,
          approved: approved.length,
          funded: funded.length,
          revenue: totalRevenue,
          fundedAmount: totalFundedAmount,
          calls: calls.length,
          sms: totalSMS,
          talkTime: totalTalkTime,
          callsToApps: calls.length > 0 ? (appsIn.length / calls.length * 100) : 0,
          smsToApps: totalSMS > 0 ? (appsIn.length / totalSMS * 100) : 0,
          appsToApproved: appsIn.length > 0 ? (approved.length / appsIn.length * 100) : 0,
          approvedToFunded: approved.length > 0 ? (funded.length / approved.length * 100) : 0,
          appsToFunded: appsIn.length > 0 ? (funded.length / appsIn.length * 100) : 0,
          avgDealSize: funded.length > 0 ? totalRevenue / funded.length : 0,
          activityVolume: calls.length + totalSMS,
          overallConversion: calls.length > 0 ? (funded.length / calls.length * 100) : 0
        };
      });

      // ✨ NEW (Nov 8, 2025): Calculate Team Average for this month
      // Team Average calculates from ALL reps (even in single-rep view for comparison)
      // Excludes reps with $0 revenue from the average calculation
      const allReps = reps; // Use all reps, not displayReps
      result[month]["Team Average"] = calculateTeamAverage(result[month], allReps);
    });

    return result;
  }, [data, displayReps, monthNames, reps, calculateTeamAverage]);

  // Performance trends data for line charts
  const performanceTrendsData = useMemo(() => {
    if (!allHistoricalData) return [];

    return monthNames.map(month => {
      const monthData = { month };
      displayReps.forEach(rep => {
        monthData[rep] = allHistoricalData[month][rep]?.revenue || 0;
      });
      return monthData;
    });
  }, [allHistoricalData, monthNames, displayReps]);

  // Call volume trends data for line charts
  const callVolumeTrendsData = useMemo(() => {
    if (!allHistoricalData) return [];

    return monthNames.map(month => {
      const monthData = { month };
      displayReps.forEach(rep => {
        monthData[rep] = allHistoricalData[month][rep]?.calls || 0;
      });
      return monthData;
    });
  }, [allHistoricalData, monthNames, displayReps]);

  // SMS trends data for line charts (Dec 28, 2025)
  const smsTrendsData = useMemo(() => {
    if (!allHistoricalData) return [];

    return monthNames.map(month => {
      const monthData = { month };
      displayReps.forEach(rep => {
        monthData[rep] = allHistoricalData[month][rep]?.sms || 0;
      });
      return monthData;
    });
  }, [allHistoricalData, monthNames, displayReps]);

  // Activity Points trends data for line charts
  const activityPointsTrendsData = useMemo(() => {
    if (!allHistoricalData) return [];

    return monthNames.map(month => {
      const monthData = { month };
      displayReps.forEach(rep => {
        monthData[rep] = allHistoricalData[month][rep]?.activityVolume || 0;
      });
      return monthData;
    });
  }, [allHistoricalData, monthNames, displayReps]);

  // Apps to Funded conversion rate trends data for line charts
  const appsToFundedTrendsData = useMemo(() => {
    if (!allHistoricalData) return [];

    return monthNames.map(month => {
      const monthData = { month };
      displayReps.forEach(rep => {
        monthData[rep] = allHistoricalData[month][rep]?.appsToFunded || 0;
      });
      return monthData;
    });
  }, [allHistoricalData, monthNames, displayReps]);

  // ========== NEW CHARTS (Nov 28, 2025) - Phase 2 ==========

  // Apps-In trends data for line charts
  const appsInTrendsData = useMemo(() => {
    if (!allHistoricalData) return [];

    return monthNames.map(month => {
      const monthData = { month };
      displayReps.forEach(rep => {
        monthData[rep] = allHistoricalData[month][rep]?.appsIn || 0;
      });
      return monthData;
    });
  }, [allHistoricalData, monthNames, displayReps]);

  // Approved count trends data for line charts
  const approvedTrendsData = useMemo(() => {
    if (!allHistoricalData) return [];

    return monthNames.map(month => {
      const monthData = { month };
      displayReps.forEach(rep => {
        monthData[rep] = allHistoricalData[month][rep]?.approved || 0;
      });
      return monthData;
    });
  }, [allHistoricalData, monthNames, displayReps]);

  // Approval Rate (Apps → Approved %) trends data for line charts
  const approvalRateTrendsData = useMemo(() => {
    if (!allHistoricalData) return [];

    return monthNames.map(month => {
      const monthData = { month };
      displayReps.forEach(rep => {
        monthData[rep] = allHistoricalData[month][rep]?.appsToApproved || 0;
      });
      return monthData;
    });
  }, [allHistoricalData, monthNames, displayReps]);

  // Funded count trends data for line charts
  const fundedCountTrendsData = useMemo(() => {
    if (!allHistoricalData) return [];

    return monthNames.map(month => {
      const monthData = { month };
      displayReps.forEach(rep => {
        monthData[rep] = allHistoricalData[month][rep]?.funded || 0;
      });
      return monthData;
    });
  }, [allHistoricalData, monthNames, displayReps]);

  // Avg Deal Size trends data for line charts
  const avgDealSizeTrendsData = useMemo(() => {
    if (!allHistoricalData) return [];

    return monthNames.map(month => {
      const monthData = { month };
      displayReps.forEach(rep => {
        monthData[rep] = allHistoricalData[month][rep]?.avgDealSize || 0;
      });
      return monthData;
    });
  }, [allHistoricalData, monthNames, displayReps]);

  // ========== END NEW CHARTS ==========

  // ========== PACK SOURCE CHARTS (Phase 5E - Nov 30, 2025) ==========

  // Color palette for Pack Source categories (dynamically assigned)
  const packSourceColors = useMemo(() => {
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];
    return colors;
  }, []);

  // Pack Source Breakdown for all 4 data types (Subtab 1 donut charts)
  // Filters by selected month (defaults to current month)
  const packSourceBreakdown = useMemo(() => {
    if (!data || !data.tabs) return null;

    // Get current month name as default
    const currentMonthName = new Date().toLocaleString('default', { month: 'long' });
    const filterMonth = packSourceMonth || currentMonthName;

    const processData = (tabData) => {
      if (!tabData || !tabData.data) return [];
      const counts = {};
      // Filter by selected month
      const filteredData = tabData.data.filter(row => row.Month === filterMonth);
      filteredData.forEach(row => {
        const source = row.Pack_Source || 'Missing';
        counts[source] = (counts[source] || 0) + 1;
      });
      return Object.entries(counts)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value); // Sort by count descending
    };

    return {
      appsIn: processData(data.tabs.Apps_In),
      approved: processData(data.tabs.Approved),
      funded: processData({ data: filterValidDeals(data.tabs.Funded?.data || []) }),
      declined: processData(data.tabs.Declined_Lost),
      selectedMonth: filterMonth
    };
  }, [data, packSourceMonth]);

  // Pack Source Trends - Month over Month stacked bar data (Apps_In only)
  const packSourceTrends = useMemo(() => {
    if (!data || !data.tabs || !data.tabs.Apps_In?.data) return { data: [], sources: [] };

    const appsIn = data.tabs.Apps_In.data;

    // Get all unique pack sources dynamically
    const allSources = new Set();
    appsIn.forEach(row => {
      allSources.add(row.Pack_Source || 'Missing');
    });
    const sources = Array.from(allSources).sort();

    // Calculate counts and percentages by month
    const monthData = monthNames.map(month => {
      const monthRecords = appsIn.filter(r => r.Month === month);
      const total = monthRecords.length;

      // Count by source
      const counts = {};
      sources.forEach(source => {
        counts[source] = monthRecords.filter(r => (r.Pack_Source || 'Missing') === source).length;
      });

      // Calculate percentages
      const result = { month, total };
      sources.forEach(source => {
        const count = counts[source] || 0;
        result[source] = total > 0 ? (count / total * 100) : 0;
        result[`${source}_count`] = count;
      });

      return result;
    });

    return { data: monthData, sources };
  }, [data, monthNames]);

  // Conversion by Pack Source for selected rep (Subtab 2 horizontal bar)
  const conversionByPackSource = useMemo(() => {
    if (!data || !data.tabs || !activeRepTab) return [];

    const appsIn = data.tabs.Apps_In?.data || [];
    const approved = data.tabs.Approved?.data || [];

    // Filter by selected rep
    const repAppsIn = appsIn.filter(r => r.Rep === activeRepTab);
    const repApproved = approved.filter(r => r.Rep === activeRepTab);

    // Group by Pack Source
    const appsBySource = {};
    const approvedBySource = {};

    repAppsIn.forEach(row => {
      const source = row.Pack_Source || 'Missing';
      appsBySource[source] = (appsBySource[source] || 0) + 1;
    });

    repApproved.forEach(row => {
      const source = row.Pack_Source || 'Missing';
      approvedBySource[source] = (approvedBySource[source] || 0) + 1;
    });

    // Calculate conversion rate for each source
    return Object.keys(appsBySource)
      .map(source => ({
        source,
        appsIn: appsBySource[source] || 0,
        approved: approvedBySource[source] || 0,
        conversionRate: appsBySource[source] > 0
          ? ((approvedBySource[source] || 0) / appsBySource[source] * 100)
          : 0
      }))
      .sort((a, b) => b.conversionRate - a.conversionRate); // Sort by conversion rate descending
  }, [data, activeRepTab]);

  // ========== END PACK SOURCE CHARTS ==========

  // ========== TEAM SNAPSHOT PACK SOURCE (All-time aggregates) ==========

  // Team Pack Source by Volume - All-time aggregate across all reps and months
  const teamPackSourceVolume = useMemo(() => {
    if (!data || !data.tabs || !monthNames.length) return [];

    const breakdown = {};

    // Aggregate Apps_In by Pack_Source
    const appsIn = data.tabs.Apps_In?.data || [];
    appsIn.forEach(row => {
      if (monthNames.includes(row.Month)) {
        const source = row.Pack_Source || 'Missing';
        if (!breakdown[source]) breakdown[source] = { appsIn: 0, approved: 0, funded: 0 };
        breakdown[source].appsIn += 1;
      }
    });

    // Aggregate Approved by Pack_Source
    const approved = data.tabs.Approved?.data || [];
    approved.forEach(row => {
      if (monthNames.includes(row.Month)) {
        const source = row.Pack_Source || 'Missing';
        if (!breakdown[source]) breakdown[source] = { appsIn: 0, approved: 0, funded: 0 };
        breakdown[source].approved += 1;
      }
    });

    // Aggregate Funded by Pack_Source (valid deals only)
    const funded = filterValidDeals(data.tabs.Funded?.data || []);
    funded.forEach(row => {
      if (monthNames.includes(row.Month)) {
        const source = row.Pack_Source || 'Missing';
        if (!breakdown[source]) breakdown[source] = { appsIn: 0, approved: 0, funded: 0 };
        breakdown[source].funded += 1;
      }
    });

    return Object.entries(breakdown)
      .map(([name, metrics]) => ({ name, ...metrics }))
      .sort((a, b) => b.appsIn - a.appsIn);
  }, [data, monthNames]);

  // Team Pack Source by Revenue - All-time aggregate
  const teamPackSourceRevenue = useMemo(() => {
    if (!data || !data.tabs || !monthNames.length) return [];

    const breakdown = {};

    const revenueData = filterValidDeals(data.tabs.Revenue?.data || []);
    revenueData.forEach(row => {
      if (monthNames.includes(row.Month)) {
        const source = row.Pack_Source || 'Missing';
        if (!breakdown[source]) breakdown[source] = { count: 0, revenue: 0 };
        breakdown[source].count += 1;
        breakdown[source].revenue += parseFloat(row.Revenue) || 0;
      }
    });

    return Object.entries(breakdown)
      .map(([name, metrics]) => ({
        name,
        count: metrics.count,
        revenue: metrics.revenue,
        avgDealSize: metrics.count > 0 ? metrics.revenue / metrics.count : 0
      }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [data, monthNames]);

  // Monthly Pack Source Trend - Month-by-month stacked data
  const monthlyPackSourceTrend = useMemo(() => {
    if (!data || !data.tabs || !monthNames.length) return { data: [], sources: [] };

    const appsIn = data.tabs.Apps_In?.data || [];
    const allSources = new Set();

    appsIn.forEach(row => {
      if (monthNames.includes(row.Month)) {
        allSources.add(row.Pack_Source || 'Missing');
      }
    });

    const sources = Array.from(allSources).sort();

    const monthData = monthNames.map(month => {
      const monthRecords = appsIn.filter(r => r.Month === month);
      const total = monthRecords.length;
      const result = { month, total };

      sources.forEach(source => {
        const count = monthRecords.filter(r => (r.Pack_Source || 'Missing') === source).length;
        result[source] = count;
        result[`${source}_pct`] = total > 0 ? Math.round((count / total) * 100) : 0;
      });

      return result;
    });

    return { data: monthData, sources };
  }, [data, monthNames]);

  // ========== END TEAM SNAPSHOT PACK SOURCE ==========

  // Bubble chart data
  const bubbleChartData = useMemo(() => {
    if (!allHistoricalData) return [];

    const data = [];
    monthNames.forEach(month => {
      displayReps.forEach(rep => {
        const metrics = allHistoricalData[month][rep];
        if (metrics && metrics.activityVolume > 0) {
          data.push({
            rep,
            month,
            x: metrics.activityVolume,
            y: metrics.overallConversion,
            z: metrics.revenue,
            revenue: metrics.revenue
          });
        }
      });
    });
    return data;
  }, [allHistoricalData, monthNames, displayReps]);

  // Radar chart data (normalized 0-100)
  const radarChartData = useMemo(() => {
    if (!allHistoricalData || monthNames.length === 0) return [];

    // Calculate overall max values for normalization
    const maxValues = {
      calls: 0,
      callsToApps: 100,
      appsToApproved: 100,
      approvedToFunded: 100,
      avgDealSize: 0,
      revenue: 0
    };

    monthNames.forEach(month => {
      displayReps.forEach(rep => {
        const m = allHistoricalData[month][rep];
        if (m) {
          maxValues.calls = Math.max(maxValues.calls, m.calls);
          maxValues.avgDealSize = Math.max(maxValues.avgDealSize, m.avgDealSize);
          maxValues.revenue = Math.max(maxValues.revenue, m.revenue);
        }
      });
    });

    // Build radar data for latest month (most recent month in data)
    const latestMonth = monthNames[monthNames.length - 1];
    const metrics = [
      { metric: 'Call Volume', fullMark: 100 },
      { metric: 'Calls→Apps %', fullMark: 100 },
      { metric: 'Apps→Approved %', fullMark: 100 },
      { metric: 'Approved→Funded %', fullMark: 100 },
      { metric: 'Avg Deal Size', fullMark: 100 },
      { metric: 'Revenue', fullMark: 100 }
    ];

    metrics.forEach(metric => {
      displayReps.forEach(rep => {
        const m = allHistoricalData[latestMonth]?.[rep];
        if (m) {
          if (metric.metric === 'Call Volume') {
            metric[rep] = maxValues.calls > 0 ? (m.calls / maxValues.calls * 100) : 0;
          } else if (metric.metric === 'Calls→Apps %') {
            metric[rep] = m.callsToApps;
          } else if (metric.metric === 'Apps→Approved %') {
            metric[rep] = m.appsToApproved;
          } else if (metric.metric === 'Approved→Funded %') {
            metric[rep] = m.approvedToFunded;
          } else if (metric.metric === 'Avg Deal Size') {
            metric[rep] = maxValues.avgDealSize > 0 ? (m.avgDealSize / maxValues.avgDealSize * 100) : 0;
          } else if (metric.metric === 'Revenue') {
            metric[rep] = maxValues.revenue > 0 ? (m.revenue / maxValues.revenue * 100) : 0;
          }
        }
      });
    });

    return metrics;
  }, [allHistoricalData, monthNames, displayReps]);

  // Declined/Lost Analysis
  const declinedLostAnalysis = useMemo(() => {
    const declined = data.tabs.Declined_Lost?.data || [];
    if (declined.length === 0) return null;

    // Filter for active rep
    const repDeclined = declined.filter(r => r.Rep === activeRepTab);

    // Total count and value
    const totalCount = repDeclined.length;
    const totalValue = repDeclined.reduce((sum, r) => sum + (parseFloat(r.Amount) || 0), 0);

    // Group by Stage_Detail (decline reasons)
    const reasonCounts = {};
    repDeclined.forEach(r => {
      const reason = r.Stage_Detail || 'Unknown';
      reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
    });

    // Top 3 reasons
    const topReasons = Object.entries(reasonCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([reason, count]) => ({ reason, count }));

    // Recent declines (last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const recentDeclines = repDeclined.filter(r => {
      const declineDate = r.Declined_Date || r.Closed_Lost_Date;
      if (!declineDate) return false;
      const date = new Date(declineDate);
      return date >= thirtyDaysAgo;
    }).slice(0, 5); // Show up to 5 recent ones

    return {
      totalCount,
      totalValue,
      topReasons,
      recentDeclines
    };
  }, [data, activeRepTab]);

  // Rankings and leaderboard
  const rankings = useMemo(() => {
    if (!allHistoricalData || monthNames.length === 0) return null;

    const repStats = displayReps.map(rep => {
      let totalRevenue = 0;
      let totalDeals = 0;
      let totalApproved = 0;
      const monthlyRevenue = [];

      monthNames.forEach(month => {
        const m = allHistoricalData[month]?.[rep];
        if (m) {
          totalRevenue += m.revenue;
          totalDeals += m.funded;
          totalApproved += m.approved || 0;
          monthlyRevenue.push(m.revenue);
        }
      });

      // Calculate variance (consistency)
      const avgRevenue = totalRevenue / monthNames.length;
      const variance = monthlyRevenue.reduce((sum, rev) => sum + Math.pow(rev - avgRevenue, 2), 0) / monthNames.length;
      const stdDev = Math.sqrt(variance);
      const consistency = avgRevenue > 0 ? (stdDev / avgRevenue * 100) : 0; // Lower is better

      // Calculate growth (First month to Last month)
      const firstMonth = monthNames[0];
      const lastMonth = monthNames[monthNames.length - 1];
      const firstRev = allHistoricalData[firstMonth]?.[rep]?.revenue || 0;
      const lastRev = allHistoricalData[lastMonth]?.[rep]?.revenue || 0;

      // Handle zero-to-nonzero growth (went from $0 to revenue)
      let growth = 0;
      if (firstRev > 0) {
        growth = ((lastRev - firstRev) / firstRev * 100);
      } else if (lastRev > 0) {
        growth = 999; // Massive growth (from zero to revenue) - shows at top of leaderboard
      }

      // Calculate conversion rate (Approved → Funded %)
      const conversionRate = totalApproved > 0 ? Math.round((totalDeals / totalApproved) * 100) : 0;

      return {
        rep,
        totalRevenue,
        totalDeals,
        fundedCount: totalDeals,
        totalApproved,
        conversionRate,
        avgRevenue,
        consistency,
        growth,
        monthlyRevenue
      };
    });

    return {
      byRevenue: [...repStats].sort((a, b) => b.totalRevenue - a.totalRevenue),
      byFundedCount: [...repStats].sort((a, b) => b.totalDeals - a.totalDeals),
      byConversionRate: [...repStats].sort((a, b) => b.conversionRate - a.conversionRate),
      byGrowth: [...repStats].sort((a, b) => b.growth - a.growth),
      byConsistency: [...repStats].sort((a, b) => a.consistency - b.consistency)
    };
  }, [allHistoricalData, monthNames, displayReps]);

  // ========== SUBMISSIONS DATA (V4.2 - Dec 8, 2025) ==========
  // Data for Rep vs Rep Submissions section charts (mockup lines 1181-1316)

  // Chart 1: Avg Submissions per Funded Deal (line chart over time)
  const avgSubsPerFundedData = useMemo(() => {
    if (!data || !data.tabs || monthNames.length === 0) return [];

    return monthNames.map(month => {
      const monthData = { month };
      displayReps.forEach(rep => {
        const funded = data.tabs.Funded?.data?.filter(r => r.Month === month && r.Rep === rep) || [];
        const totalSubs = funded.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);
        monthData[rep] = funded.length > 0 ? totalSubs / funded.length : 0;
      });
      return monthData;
    });
  }, [data, monthNames, displayReps]);

  // Chart 2: Avg Submissions per Underwriting (line chart over time)
  const avgSubsPerUWData = useMemo(() => {
    if (!data || !data.tabs || monthNames.length === 0) return [];

    return monthNames.map(month => {
      const monthData = { month };
      displayReps.forEach(rep => {
        // Underwriting = Approved + Funded + Declined (deals that went through UW)
        const approved = data.tabs.Approved?.data?.filter(r => r.Month === month && r.Rep === rep) || [];
        const funded = data.tabs.Funded?.data?.filter(r => r.Month === month && r.Rep === rep) || [];
        const declined = data.tabs.Declined_Lost?.data?.filter(r => r.Month === month && r.Rep === rep) || [];
        const uwDeals = [...approved, ...funded, ...declined];
        const uniqueUW = [...new Map(uwDeals.map(d => [d.Opportunity_ID, d])).values()];
        const totalSubs = uniqueUW.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);
        monthData[rep] = uniqueUW.length > 0 ? totalSubs / uniqueUW.length : 0;
      });
      return monthData;
    });
  }, [data, monthNames, displayReps]);

  // Chart 3: Submission Success Rate (% of submissions that result in funding)
  const submissionSuccessData = useMemo(() => {
    if (!data || !data.tabs || monthNames.length === 0) return [];

    return monthNames.map(month => {
      const monthData = { month };
      displayReps.forEach(rep => {
        const funded = data.tabs.Funded?.data?.filter(r => r.Month === month && r.Rep === rep) || [];
        const declined = data.tabs.Declined_Lost?.data?.filter(r => r.Month === month && r.Rep === rep) || [];
        const fundedSubs = funded.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);
        const declinedSubs = declined.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);
        const totalOutcome = fundedSubs + declinedSubs;
        monthData[rep] = totalOutcome > 0 ? (fundedSubs / totalOutcome * 100) : 0;
      });
      return monthData;
    });
  }, [data, monthNames, displayReps]);

  // Charts 4-7: Rep Comparison Bar Charts (all-time stats)
  const repSubmissionComparison = useMemo(() => {
    if (!data || !data.tabs) return [];

    return displayReps.filter(rep => rep !== 'Team Average').map(rep => {
      // Get all deals for this rep across all months
      const funded = data.tabs.Funded?.data?.filter(r => r.Rep === rep && monthNames.includes(r.Month)) || [];
      const approved = data.tabs.Approved?.data?.filter(r => r.Rep === rep && monthNames.includes(r.Month)) || [];
      const declined = data.tabs.Declined_Lost?.data?.filter(r => r.Rep === rep && monthNames.includes(r.Month)) || [];

      // Unique deals
      const allDeals = [...funded, ...approved, ...declined];
      const uniqueDeals = [...new Map(allDeals.map(d => [d.Opportunity_ID, d])).values()];

      // Calculations
      const totalSubs = uniqueDeals.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);
      const totalDeclined = uniqueDeals.reduce((sum, d) => sum + (parseInt(d.Submissions_Declined) || 0), 0);
      const openSubs = uniqueDeals.reduce((sum, d) => sum + (parseInt(d.Submissions_Open) || 0), 0);
      const fundedSubs = funded.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);

      return {
        name: rep.split(' ')[0],
        avgPerFunded: funded.length > 0 ? fundedSubs / funded.length : 0,
        avgPerDeal: uniqueDeals.length > 0 ? totalSubs / uniqueDeals.length : 0,
        declineRate: totalSubs > 0 ? (totalDeclined / totalSubs * 100) : 0,
        openSubmissions: openSubs
      };
    });
  }, [data, displayReps, monthNames]);

  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);

  const formatPercent = (value) => `${value.toFixed(1)}%`;

  if (!allHistoricalData || !rankings) {
    return <div className="historical-tab"><p>Loading historical data...</p></div>;
  }

  return (
    <div className="historical-tab">
      <h2>📊 Historical Performance Analysis</h2>
      <p className="tab-description">
        {monthNames.length} months of data ({monthNames[0]}-{monthNames[monthNames.length - 1]} 2025)
      </p>

      {/* Subtab Navigation (Nov 28, 2025) - Enhanced styling */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        padding: '8px',
        background: '#f1f5f9',
        borderRadius: '12px'
      }}>
        <button
          onClick={() => setActiveHistoricalTab('rep-vs-rep')}
          style={{
            flex: 1,
            padding: '16px 24px',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: activeHistoricalTab === 'rep-vs-rep' ? '600' : '500',
            background: activeHistoricalTab === 'rep-vs-rep' ? '#3b82f6' : 'transparent',
            color: activeHistoricalTab === 'rep-vs-rep' ? '#fff' : '#475569',
            transition: 'all 0.2s ease',
            boxShadow: activeHistoricalTab === 'rep-vs-rep' ? '0 2px 8px rgba(59,130,246,0.3)' : 'none'
          }}
        >
          📊 Rep v Rep
        </button>
        <button
          onClick={() => setActiveHistoricalTab('single-rep')}
          style={{
            flex: 1,
            padding: '16px 24px',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: activeHistoricalTab === 'single-rep' ? '600' : '500',
            background: activeHistoricalTab === 'single-rep' ? '#8b5cf6' : 'transparent',
            color: activeHistoricalTab === 'single-rep' ? '#fff' : '#475569',
            transition: 'all 0.2s ease',
            boxShadow: activeHistoricalTab === 'single-rep' ? '0 2px 8px rgba(139,92,246,0.3)' : 'none'
          }}
        >
          🔍 Single Rep Deep-Dive
        </button>
        <button
          onClick={() => setActiveHistoricalTab('team-snapshot')}
          style={{
            flex: 1,
            padding: '16px 24px',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: activeHistoricalTab === 'team-snapshot' ? '600' : '500',
            background: activeHistoricalTab === 'team-snapshot' ? '#10b981' : 'transparent',
            color: activeHistoricalTab === 'team-snapshot' ? '#fff' : '#475569',
            transition: 'all 0.2s ease',
            boxShadow: activeHistoricalTab === 'team-snapshot' ? '0 2px 8px rgba(16,185,129,0.3)' : 'none'
          }}
        >
          👥 Team Snapshot
        </button>
      </div>

      {/* ========== SUBTAB 1: Rep v Rep (Default - shows all existing content) ========== */}
      {activeHistoricalTab === 'rep-vs-rep' && (
        <>
      {/* ========== V4.1: Sticky Section Navigation (from mockup lines 833-890) ========== */}
      <div style={{
        position: 'sticky',
        top: '0',
        zIndex: 10,
        backgroundColor: '#f1f5f9',
        padding: '12px 0',
        marginBottom: '24px',
        borderBottom: '1px solid #e2e8f0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>Section:</span>
        </div>
        <div style={{
          display: 'flex',
          gap: '4px',
          backgroundColor: 'white',
          padding: '4px',
          borderRadius: '10px',
          border: '1px solid #e2e8f0'
        }}>
          {[
            { id: 'fundings', label: 'FUNDINGS', color: '#10b981' },
            { id: 'outreach', label: 'OUTREACH', color: '#3b82f6' },
            { id: 'submissions', label: 'SUBMISSIONS', color: '#ec4899' },
            { id: 'packsource', label: 'PACK SOURCE', color: '#8b5cf6' },
            { id: 'underwriting', label: 'UNDERWRITING', color: '#f59e0b' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => scrollToRepVsRepSection(tab.id)}
              style={{
                flex: 1,
                padding: '12px 16px',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: activeSectionTab === tab.id ? '600' : '500',
                backgroundColor: activeSectionTab === tab.id ? tab.color : 'transparent',
                color: activeSectionTab === tab.id ? '#fff' : '#475569',
                transition: 'all 0.2s'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ========== V4.1: Summary KPI Cards (from mockup lines 892-933) ========== */}
      <div style={{ marginBottom: '24px', position: 'relative' }}>
        {developerMode && <div className="dev-section-label">S-1</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>Team Performance Summary</h2>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '16px'
        }}>
          <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center', position: 'relative' }}>
            {developerMode && <div className="dev-card-label">K-1</div>}
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Total Revenue (YTD)</div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#10b981' }}>${ytdSummaryKpis.totalRevenue.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</div>
          </div>
          <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center', position: 'relative' }}>
            {developerMode && <div className="dev-card-label">K-2</div>}
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Total Funded Deals</div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#3b82f6' }}>{ytdSummaryKpis.totalFunded}</div>
          </div>
          <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center', position: 'relative' }}>
            {developerMode && <div className="dev-card-label">K-3</div>}
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Avg Deal Size</div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#8b5cf6' }}>${ytdSummaryKpis.avgDealSize.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</div>
          </div>
          <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center', position: 'relative' }}>
            {developerMode && <div className="dev-card-label">K-4</div>}
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Apps to Funded %</div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#f59e0b' }}>{ytdSummaryKpis.appsToFundedRate.toFixed(1)}%</div>
          </div>
          <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center', position: 'relative' }}>
            {developerMode && <div className="dev-card-label">K-5</div>}
            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Activity Volume</div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#ef4444' }}>{ytdSummaryKpis.activityVolume.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* ========== Rankings & Leaderboards (V4.2 - Moved Higher per mockup lines 935-990) ========== */}
      {rankings && (
        <div style={{
          backgroundColor: '#f1f5f9',
          padding: '16px 0',
          marginBottom: '24px',
          borderBottom: '1px solid #e2e8f0',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-section-label">S-2</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>Rankings & Leaderboards</h3>
            <span style={{
              padding: '4px 10px',
              backgroundColor: '#d1fae5',
              borderRadius: '6px',
              fontSize: '11px',
              color: '#065f46',
              fontWeight: 600
            }}>
              ENHANCED - Moved higher
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            {/* Total Revenue Leaderboard */}
            <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
              {developerMode && <div className="dev-card-label">K-1</div>}
              <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#374151' }}>Total Revenue</h4>
              {rankings.byRevenue.map((stat, idx) => (
                <div key={stat.rep} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: idx < 4 ? '1px solid #f3f4f6' : 'none' }}>
                  <span style={{ width: '20px', fontWeight: 700, color: idx === 0 ? '#f59e0b' : idx === 1 ? '#9ca3af' : idx === 2 ? '#cd7f32' : '#6b7280' }}>#{idx + 1}</span>
                  <span style={{ flex: 1, fontSize: '13px' }}>{stat.rep.split(' ')[0]}</span>
                  <span style={{ fontWeight: 600, color: '#10b981', fontSize: '13px' }}>{formatCurrency(stat.totalRevenue)}</span>
                </div>
              ))}
            </div>
            {/* Funded Deals Leaderboard */}
            <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
              {developerMode && <div className="dev-card-label">K-2</div>}
              <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#374151' }}>Funded Deals</h4>
              {rankings.byFundedCount.map((stat, idx) => (
                <div key={stat.rep} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: idx < 4 ? '1px solid #f3f4f6' : 'none' }}>
                  <span style={{ width: '20px', fontWeight: 700, color: idx === 0 ? '#f59e0b' : idx === 1 ? '#9ca3af' : idx === 2 ? '#cd7f32' : '#6b7280' }}>#{idx + 1}</span>
                  <span style={{ flex: 1, fontSize: '13px' }}>{stat.rep.split(' ')[0]}</span>
                  <span style={{ fontWeight: 600, color: '#3b82f6', fontSize: '13px' }}>{stat.fundedCount} deals</span>
                </div>
              ))}
            </div>
            {/* Conversion Rate Leaderboard */}
            <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
              {developerMode && <div className="dev-card-label">K-3</div>}
              <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#374151' }}>Approved→Funded %</h4>
              {rankings.byConversionRate.map((stat, idx) => (
                <div key={stat.rep} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: idx < 4 ? '1px solid #f3f4f6' : 'none' }}>
                  <span style={{ width: '20px', fontWeight: 700, color: idx === 0 ? '#f59e0b' : idx === 1 ? '#9ca3af' : idx === 2 ? '#cd7f32' : '#6b7280' }}>#{idx + 1}</span>
                  <span style={{ flex: 1, fontSize: '13px' }}>{stat.rep.split(' ')[0]}</span>
                  <span style={{ fontWeight: 600, color: '#8b5cf6', fontSize: '13px' }}>{stat.conversionRate}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Currently Hiding Indicator - Shows which reps are globally hidden across all charts */}
      {hiddenReps.length > 0 && (
        <div style={{
          backgroundColor: '#fef3c7',
          border: '1px solid #fbbf24',
          borderRadius: '6px',
          padding: '12px 16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 'bold', color: '#92400e' }}>Currently Hiding:</span>
            <span style={{ color: '#78350f' }}>
              {hiddenReps.map((rep, idx) => (
                <span key={rep}>
                  {rep === 'Team Average' ? 'Team Avg' : rep.split(' ')[0]}
                  {idx < hiddenReps.length - 1 ? ', ' : ''}
                </span>
              ))}
            </span>
          </div>
          <button
            onClick={() => setHiddenReps([])}
            style={{
              backgroundColor: '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '6px 12px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#d97706'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#f59e0b'}
          >
            Show All Reps
          </button>
        </div>
      )}

      {/* Rep Tabs Navigation */}
      <nav className="rep-tabs-navigation">
        {reps.map(rep => (
          <button
            key={rep}
            className={activeRepTab === rep ? 'active' : ''}
            onClick={() => setActiveRepTab(rep)}
          >
            {rep.split(' ')[0]}
          </button>
        ))}
      </nav>

      {/* Individual Rep Deep-Dive Section */}
      <section className="rep-deep-dive-section">
        <h2>🔍 {activeRepTab.split(' ')[0]}'s Performance Deep-Dive</h2>
        <p className="section-description">
          Detailed month-by-month analysis. Track performance trends, identify strengths, and spot areas for improvement.
        </p>

        {/* ========== FUNDINGS SECTION (Nov 28, 2025) ========== */}
        <div ref={repVsRepSectionRefs.fundings} style={{ margin: '30px 0 20px 0', borderBottom: '2px solid #10b981', paddingBottom: '8px', position: 'relative' }}>
          {developerMode && <div className="dev-section-label">S-3</div>}
          <h2 style={{ margin: 0, color: '#10b981', fontSize: '1.25rem', fontWeight: 600 }}>💰 FUNDINGS</h2>
          <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>Revenue, conversion rates, deal volume and size</p>
        </div>

        {/* Revenue Trends Chart */}
        <div className="rep-chart-section" style={{ position: 'relative' }}>
          {developerMode && <div className="dev-chart-label">C-1</div>}
          <h3>📈 Revenue Trends ({monthNames[0]}-{monthNames[monthNames.length - 1]})</h3>
          <p className="section-description">
            <strong>How to Read:</strong> Each line represents one rep's monthly revenue over time. Upward slope = growth, downward = decline,
            flat = stagnant. Compare slopes to see who's accelerating vs plateauing.
            <br/>
            <strong>Business Value:</strong> Spot trends early - intervene on declining reps before it's too late, celebrate and study rising stars.
            Seasonality patterns reveal when to push harder or expect dips. Use for forecasting and territory planning.
          </p>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={performanceTrendsData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                label={{ value: 'Revenue', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip
                formatter={(value) => formatCurrency(value)}
              />
              <Legend content={renderCustomLegend} />
              {displayReps.filter(rep => !hiddenReps.includes(rep)).map(rep => {
                const isTeamAverage = rep === 'Team Average';
                const isActiveRep = rep === activeRepTab;
                return (
                  <Line
                    key={rep}
                    type="monotone"
                    dataKey={rep}
                    name={isTeamAverage ? 'Team Avg' : rep.split(' ')[0]}
                    stroke={repColors[rep]}
                    strokeWidth={isTeamAverage ? 3 : (isActiveRep ? 4 : 2)}
                    strokeDasharray={isTeamAverage ? "1 3" : undefined}  // ✨ Dotted line for Team Average
                    dot={{ r: isTeamAverage ? 6 : (isActiveRep ? 5 : 4) }}
                    activeDot={{ r: 6 }}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* ========== OUTREACH SECTION (Reorganized Dec 28, 2025) ========== */}
        <div ref={repVsRepSectionRefs.outreach} style={{ margin: '30px 0 20px 0', borderBottom: '2px solid #3b82f6', paddingBottom: '8px', position: 'relative' }}>
          {developerMode && <div className="dev-section-label">S-4</div>}
          <h2 style={{ margin: 0, color: '#3b82f6', fontSize: '1.25rem', fontWeight: 600 }}>📞 OUTREACH</h2>
          <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>Call volume, SMS, activity points, and lead generation metrics</p>
        </div>

        {/* ROW 1: Channels */}
        <div style={{ marginBottom: '8px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>📱 Channels</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px' }}>
          {/* Call Volume Trends Chart */}
          <div className="rep-chart-section" style={{ position: 'relative' }}>
            {developerMode && <div className="dev-chart-label">C-1</div>}
            <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>📞 Call Volume Trends</h4>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={callVolumeTrendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend content={renderCustomLegend} />
                {displayReps.filter(rep => !hiddenReps.includes(rep)).map(rep => {
                  const isTeamAverage = rep === 'Team Average';
                  const isActiveRep = rep === activeRepTab;
                  return (
                    <Line
                      key={rep}
                      type="monotone"
                      dataKey={rep}
                      name={isTeamAverage ? 'Team Avg' : rep.split(' ')[0]}
                      stroke={repColors[rep]}
                      strokeWidth={isTeamAverage ? 3 : (isActiveRep ? 4 : 2)}
                      strokeDasharray={isTeamAverage ? "1 3" : undefined}
                      dot={{ r: isTeamAverage ? 5 : (isActiveRep ? 4 : 3) }}
                      activeDot={{ r: 5 }}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* SMS Trends Chart (NEW) */}
          <div className="rep-chart-section" style={{ position: 'relative' }}>
            {developerMode && <div className="dev-chart-label">C-2</div>}
            <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>💬 SMS Trends</h4>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={smsTrendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend content={renderCustomLegend} />
                {displayReps.filter(rep => !hiddenReps.includes(rep)).map(rep => {
                  const isTeamAverage = rep === 'Team Average';
                  const isActiveRep = rep === activeRepTab;
                  return (
                    <Line
                      key={rep}
                      type="monotone"
                      dataKey={rep}
                      name={isTeamAverage ? 'Team Avg' : rep.split(' ')[0]}
                      stroke={repColors[rep]}
                      strokeWidth={isTeamAverage ? 3 : (isActiveRep ? 4 : 2)}
                      strokeDasharray={isTeamAverage ? "1 3" : undefined}
                      dot={{ r: isTeamAverage ? 5 : (isActiveRep ? 4 : 3) }}
                      activeDot={{ r: 5 }}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ROW 2: Effort→Results */}
        <div style={{ marginBottom: '8px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>⚡ Effort → Results</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px' }}>
          {/* Activity Points Trends Chart */}
          <div className="rep-chart-section" style={{ position: 'relative' }}>
            {developerMode && <div className="dev-chart-label">C-3</div>}
            <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>⚡ Activity Points Trends</h4>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={activityPointsTrendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend content={renderCustomLegend} />
                {displayReps.filter(rep => !hiddenReps.includes(rep)).map(rep => {
                  const isTeamAverage = rep === 'Team Average';
                  const isActiveRep = rep === activeRepTab;
                  return (
                    <Line
                      key={rep}
                      type="monotone"
                      dataKey={rep}
                      name={isTeamAverage ? 'Team Avg' : rep.split(' ')[0]}
                      stroke={repColors[rep]}
                      strokeWidth={isTeamAverage ? 3 : (isActiveRep ? 4 : 2)}
                      strokeDasharray={isTeamAverage ? "1 3" : undefined}
                      dot={{ r: isTeamAverage ? 5 : (isActiveRep ? 4 : 3) }}
                      activeDot={{ r: 5 }}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Apps-In Trends Chart */}
          <div className="rep-chart-section" style={{ position: 'relative' }}>
            {developerMode && <div className="dev-chart-label">C-4</div>}
            <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>📋 Apps-In Trends</h4>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={appsInTrendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend content={renderCustomLegend} />
                {displayReps.filter(rep => !hiddenReps.includes(rep)).map(rep => {
                  const isTeamAverage = rep === 'Team Average';
                  const isActiveRep = rep === activeRepTab;
                  return (
                    <Line
                      key={rep}
                      type="monotone"
                      dataKey={rep}
                      name={isTeamAverage ? 'Team Avg' : rep.split(' ')[0]}
                      stroke={repColors[rep]}
                      strokeWidth={isTeamAverage ? 3 : (isActiveRep ? 4 : 2)}
                      strokeDasharray={isTeamAverage ? "1 3" : undefined}
                      dot={{ r: isTeamAverage ? 5 : (isActiveRep ? 4 : 3) }}
                      activeDot={{ r: 5 }}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ========== SUBMISSIONS SECTION (Reorganized Dec 28, 2025) ========== */}
        <div ref={repVsRepSectionRefs.submissions} style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px', position: 'relative' }}>
          {developerMode && <div className="dev-section-label">S-5</div>}
          <div style={{ marginBottom: '20px', borderBottom: '2px solid #ec4899', paddingBottom: '8px' }}>
            <h2 style={{ margin: 0, color: '#ec4899', fontSize: '1.25rem', fontWeight: 600 }}>SUBMISSION ANALYSIS</h2>
            <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>Track lender submissions per deal and submission efficiency over time</p>
          </div>

          {/* ROW 1: Effort to Win */}
          <div style={{ marginBottom: '8px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>🏆 Effort to Win</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px' }}>
            <div style={{ position: 'relative' }}>
              {developerMode && <div className="dev-chart-label">C-1</div>}
              <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Avg Subs per Funded Deal (Trend)</h4>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={avgSubsPerFundedData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{fontSize: 10}} />
                  <YAxis tick={{fontSize: 10}} />
                  <Tooltip formatter={(value) => Number(value).toFixed(2)} />
                  <Legend content={renderCustomLegend} />
                  {displayReps.filter(rep => !hiddenReps.includes(rep)).map(rep => (
                    <Line key={rep} type="monotone" dataKey={rep} name={rep === 'Team Average' ? 'Team Avg' : rep.split(' ')[0]} stroke={repColors[rep]} strokeWidth={rep === activeRepTab ? 3 : 2} strokeDasharray={rep === 'Team Average' ? "1 3" : undefined} dot={{ r: 3 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
              {developerMode && <div className="dev-chart-label">C-2</div>}
              <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Submissions per Funded Deal (By Rep)</h4>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={repSubmissionComparison} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={60} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v) => Number(v).toFixed(1)} />
                  <Bar dataKey="avgPerFunded" fill="#3b82f6">
                    <LabelList dataKey="avgPerFunded" position="right" style={{ fontSize: 11, fill: '#3b82f6', fontWeight: 600 }} formatter={(v) => v.toFixed(1)} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ROW 2: Efficiency */}
          <div style={{ marginBottom: '8px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>⚡ Efficiency</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px' }}>
            <div style={{ position: 'relative' }}>
              {developerMode && <div className="dev-chart-label">C-3</div>}
              <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Submission Success Rate (Trend)</h4>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={submissionSuccessData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{fontSize: 10}} />
                  <YAxis tickFormatter={(v) => `${v}%`} tick={{fontSize: 10}} />
                  <Tooltip formatter={(value) => `${Number(value).toFixed(1)}%`} />
                  <Legend content={renderCustomLegend} />
                  {displayReps.filter(rep => !hiddenReps.includes(rep)).map(rep => (
                    <Line key={rep} type="monotone" dataKey={rep} name={rep === 'Team Average' ? 'Team Avg' : rep.split(' ')[0]} stroke={repColors[rep]} strokeWidth={rep === activeRepTab ? 3 : 2} strokeDasharray={rep === 'Team Average' ? "1 3" : undefined} dot={{ r: 3 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
              {developerMode && <div className="dev-chart-label">C-4</div>}
              <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Lender Decline Rate % (By Rep)</h4>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={repSubmissionComparison} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" unit="%" />
                  <YAxis dataKey="name" type="category" width={60} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v) => `${Number(v).toFixed(1)}%`} />
                  <Bar dataKey="declineRate" fill="#f59e0b">
                    <LabelList dataKey="declineRate" position="right" style={{ fontSize: 11, fill: '#f59e0b', fontWeight: 600 }} formatter={(v) => `${v.toFixed(0)}%`} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ROW 3: Pipeline */}
          <div style={{ marginBottom: '8px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>📊 Pipeline</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            <div style={{ position: 'relative' }}>
              {developerMode && <div className="dev-chart-label">C-5</div>}
              <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Avg Subs per Underwriting (Trend)</h4>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={avgSubsPerUWData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{fontSize: 9}} />
                  <YAxis tick={{fontSize: 9}} />
                  <Tooltip formatter={(value) => Number(value).toFixed(2)} />
                  <Legend content={renderCustomLegend} />
                  {displayReps.filter(rep => !hiddenReps.includes(rep)).map(rep => (
                    <Line key={rep} type="monotone" dataKey={rep} name={rep === 'Team Average' ? 'Team Avg' : rep.split(' ')[0]} stroke={repColors[rep]} strokeWidth={rep === activeRepTab ? 3 : 2} strokeDasharray={rep === 'Team Average' ? "1 3" : undefined} dot={{ r: 2 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
              {developerMode && <div className="dev-chart-label">C-6</div>}
              <h4 style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#374151' }}>Avg Subs per Deal</h4>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={repSubmissionComparison} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{fontSize: 9}} />
                  <YAxis dataKey="name" type="category" width={50} tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => Number(v).toFixed(1)} />
                  <Bar dataKey="avgPerDeal" fill="#10b981">
                    <LabelList dataKey="avgPerDeal" position="right" style={{ fontSize: 10, fill: '#10b981', fontWeight: 600 }} formatter={(v) => v.toFixed(1)} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
              {developerMode && <div className="dev-chart-label">C-7</div>}
              <h4 style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: '#374151' }}>Open Submissions</h4>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={repSubmissionComparison} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{fontSize: 9}} />
                  <YAxis dataKey="name" type="category" width={50} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="openSubmissions" fill="#8b5cf6">
                    <LabelList dataKey="openSubmissions" position="right" style={{ fontSize: 10, fill: '#8b5cf6', fontWeight: 600 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* ========== PACK SOURCE SECTION (V4.2 - Dec 8, 2025 - mockup lines 1318-1392) ========== */}
        <div ref={repVsRepSectionRefs.packsource} style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px', position: 'relative' }}>
          {developerMode && <div className="dev-section-label">S-6</div>}
          <div style={{ marginBottom: '20px', borderBottom: '2px solid #8b5cf6', paddingBottom: '8px' }}>
            <h2 style={{ margin: 0, color: '#8b5cf6', fontSize: '1.25rem', fontWeight: 600 }}>PACK SOURCE BREAKDOWN</h2>
            <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>Distribution of deals by source across the funnel stages</p>
          </div>

          {/* Month Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', padding: '12px 16px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
            <span style={{ fontWeight: 600, color: '#374151' }}>Month:</span>
            <select
              value={packSourceMonth || new Date().toLocaleString('default', { month: 'long' })}
              onChange={(e) => setPackSourceMonth(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: '#fff', fontSize: '14px', cursor: 'pointer' }}
            >
              {monthNames.map(month => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>
            <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Showing: {packSourceBreakdown?.selectedMonth || 'Current Month'}</span>
          </div>

          {/* 4 Pie Charts Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>Pack Source Distribution</h3>
          </div>

          {/* 4 Pie Charts Grid - EXACT 4 columns per mockup */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '32px' }}>
            {[
              { title: 'Apps In', data: packSourceBreakdown?.appsIn || [] },
              { title: 'Approved', data: packSourceBreakdown?.approved || [] },
              { title: 'Funded', data: packSourceBreakdown?.funded || [] },
              { title: 'Declined/Lost', data: packSourceBreakdown?.declined || [] }
            ].map((chart, idx) => (
              <div key={idx} style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center', position: 'relative' }}>
                {developerMode && <div className="dev-chart-label">C-{idx + 1}</div>}
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#374151' }}>{chart.title}</h4>
                <p style={{ margin: '0 0 8px 0', color: '#6b7280', fontSize: '11px' }}>Total: {chart.data.reduce((s, d) => s + d.value, 0)}</p>
                {chart.data.length > 0 ? (
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={chart.data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={2} label={({ value }) => value} labelLine={false}>
                        {chart.data.map((entry, i) => (
                          <Cell key={`cell-${i}`} fill={packSourceColors[i % packSourceColors.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [`${value} deals`, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p style={{ color: '#9ca3af', fontStyle: 'italic', height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No data</p>
                )}
              </div>
            ))}
          </div>

          {/* Pack Source Trends Stacked Bar */}
          <div style={{ position: 'relative' }}>
            {developerMode && <div className="dev-chart-label">C-5</div>}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>Apps In by Pack Source - Month over Month</h3>
            </div>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
              Each bar shows the percentage breakdown of Apps In by Pack Source for that month.
            </p>
            {packSourceTrends.data.length > 0 && packSourceTrends.sources.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={packSourceTrends.data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                  <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                  <Legend />
                  {packSourceTrends.sources.map((source, index) => (
                    <Bar
                      key={source}
                      dataKey={source}
                      stackId="a"
                      fill={source === 'Missing' ? '#9ca3af' : source === 'Inbound' ? '#3b82f6' : source === 'Outbound' ? '#10b981' : source === 'Referral' ? '#f59e0b' : packSourceColors[index % packSourceColors.length]}
                      name={source}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '40px', textAlign: 'center' }}>
                <p style={{ color: '#6b7280', margin: 0 }}>No Pack Source data available</p>
              </div>
            )}
          </div>
        </div>
        {/* ========== END PACK SOURCE BREAKDOWN ========== */}

        {/* ========== UNDERWRITING SECTION (Reorganized Dec 28, 2025) ========== */}
        <div ref={repVsRepSectionRefs.underwriting} style={{ margin: '30px 0 20px 0', borderBottom: '2px solid #f59e0b', paddingBottom: '8px', position: 'relative' }}>
          {developerMode && <div className="dev-section-label">S-7</div>}
          <h2 style={{ margin: 0, color: '#f59e0b', fontSize: '1.25rem', fontWeight: 600 }}>📝 UNDERWRITING</h2>
          <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>Application approval rates and underwriting performance</p>
        </div>

        {/* ROW 1: Volume */}
        <div style={{ marginBottom: '8px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>📈 Volume</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div className="rep-chart-section" style={{ position: 'relative' }}>
            {developerMode && <div className="dev-chart-label">C-1</div>}
            <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>✅ Apps → Approved Count</h4>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={approvedTrendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend content={renderCustomLegend} />
                {displayReps.filter(rep => !hiddenReps.includes(rep)).map(rep => {
                  const isTeamAverage = rep === 'Team Average';
                  const isActiveRep = rep === activeRepTab;
                  return (
                    <Line key={rep} type="monotone" dataKey={rep} name={isTeamAverage ? 'Team Avg' : rep.split(' ')[0]} stroke={repColors[rep]} strokeWidth={isTeamAverage ? 3 : (isActiveRep ? 4 : 2)} strokeDasharray={isTeamAverage ? "1 3" : undefined} dot={{ r: isTeamAverage ? 5 : (isActiveRep ? 4 : 3) }} />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="rep-chart-section" style={{ position: 'relative' }}>
            {developerMode && <div className="dev-chart-label">C-2</div>}
            <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>🏆 Funded Deal Count</h4>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={fundedCountTrendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend content={renderCustomLegend} />
                {displayReps.filter(rep => !hiddenReps.includes(rep)).map(rep => {
                  const isTeamAverage = rep === 'Team Average';
                  const isActiveRep = rep === activeRepTab;
                  return (
                    <Line key={rep} type="monotone" dataKey={rep} name={isTeamAverage ? 'Team Avg' : rep.split(' ')[0]} stroke={repColors[rep]} strokeWidth={isTeamAverage ? 3 : (isActiveRep ? 4 : 2)} strokeDasharray={isTeamAverage ? "1 3" : undefined} dot={{ r: isTeamAverage ? 5 : (isActiveRep ? 4 : 3) }} />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ROW 2: Conversion */}
        <div style={{ marginBottom: '8px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>🔄 Conversion</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div className="rep-chart-section" style={{ position: 'relative' }}>
            {developerMode && <div className="dev-chart-label">C-3</div>}
            <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>📊 Approval Rate %</h4>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={approvalRateTrendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10 }} domain={[0, 'auto']} />
                <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                <Legend content={renderCustomLegend} />
                {displayReps.filter(rep => !hiddenReps.includes(rep)).map(rep => {
                  const isTeamAverage = rep === 'Team Average';
                  const isActiveRep = rep === activeRepTab;
                  return (
                    <Line key={rep} type="monotone" dataKey={rep} name={isTeamAverage ? 'Team Avg' : rep.split(' ')[0]} stroke={repColors[rep]} strokeWidth={isTeamAverage ? 3 : (isActiveRep ? 4 : 2)} strokeDasharray={isTeamAverage ? "1 3" : undefined} dot={{ r: isTeamAverage ? 5 : (isActiveRep ? 4 : 3) }} />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="rep-chart-section" style={{ position: 'relative' }}>
            {developerMode && <div className="dev-chart-label">C-4</div>}
            <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>🎯 Apps → Funded Conversion Rate</h4>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={appsToFundedTrendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10 }} domain={[0, 'auto']} />
                <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                <Legend content={renderCustomLegend} />
                {displayReps.filter(rep => !hiddenReps.includes(rep)).map(rep => {
                  const isTeamAverage = rep === 'Team Average';
                  const isActiveRep = rep === activeRepTab;
                  return (
                    <Line key={rep} type="monotone" dataKey={rep} name={isTeamAverage ? 'Team Avg' : rep.split(' ')[0]} stroke={repColors[rep]} strokeWidth={isTeamAverage ? 3 : (isActiveRep ? 4 : 2)} strokeDasharray={isTeamAverage ? "1 3" : undefined} dot={{ r: isTeamAverage ? 5 : (isActiveRep ? 4 : 3) }} />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ROW 3: Deal Quality */}
        <div style={{ marginBottom: '8px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px' }}>💎 Deal Quality</h3>
        </div>
        <div style={{ marginBottom: '24px' }}>
          <div className="rep-chart-section" style={{ position: 'relative', maxWidth: '50%' }}>
            {developerMode && <div className="dev-chart-label">C-5</div>}
            <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>💵 Avg Deal Size Trends</h4>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={avgDealSizeTrendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend content={renderCustomLegend} />
                {displayReps.filter(rep => !hiddenReps.includes(rep)).map(rep => {
                  const isTeamAverage = rep === 'Team Average';
                  const isActiveRep = rep === activeRepTab;
                  return (
                    <Line key={rep} type="monotone" dataKey={rep} name={isTeamAverage ? 'Team Avg' : rep.split(' ')[0]} stroke={repColors[rep]} strokeWidth={isTeamAverage ? 3 : (isActiveRep ? 4 : 2)} strokeDasharray={isTeamAverage ? "1 3" : undefined} dot={{ r: isTeamAverage ? 5 : (isActiveRep ? 4 : 3) }} />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

      </section>

      {/* ========== DECLINED & LOST OPPORTUNITIES (Redesigned Dec 28, 2025) ========== */}
      {(() => {
        const declinedLostData = data?.tabs?.Declined_Lost?.data || [];
        if (declinedLostData.length === 0) return null;

        // Filter for active rep
        const repData = declinedLostData.filter(d => d.Rep === activeRepTab);

        // Separate DECLINES (Lender Said No) vs CLOSED LOST (Customer Said No)
        const declines = repData.filter(d => d.Stage === 'Declined');
        const closedLost = repData.filter(d => d.Stage === 'Closed Lost');

        // Skip if no data
        if (declines.length === 0 && closedLost.length === 0) return null;

        // Calculate decline stats
        const declineCount = declines.length;
        const declineValue = declines.reduce((sum, d) => sum + (parseFloat(d.Amount) || 0), 0);
        const declineReasons = {};
        declines.forEach(d => {
          const reason = d.Stage_Detail || 'Unknown';
          declineReasons[reason] = (declineReasons[reason] || 0) + 1;
        });
        const topDeclineReasons = Object.entries(declineReasons)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([reason, count]) => ({ reason, count }));
        const declinesByRep = {};
        declines.forEach(d => {
          const rep = d.Rep || 'Unknown';
          declinesByRep[rep] = (declinesByRep[rep] || 0) + 1;
        });
        const declinesByRepData = Object.entries(declinesByRep).map(([name, count]) => ({ name: name.split(' ')[0], count })).sort((a, b) => b.count - a.count);
        const recentDeclines = [...declines]
          .filter(d => d.Declined_Date)
          .sort((a, b) => new Date(b.Declined_Date) - new Date(a.Declined_Date))
          .slice(0, 10);

        // Calculate closed lost stats
        const closedLostCount = closedLost.length;
        const closedLostValue = closedLost.reduce((sum, d) => sum + (parseFloat(d.Amount) || 0), 0);
        const closedLostReasons = {};
        closedLost.forEach(d => {
          const reason = d.Stage_Detail || 'Unknown';
          closedLostReasons[reason] = (closedLostReasons[reason] || 0) + 1;
        });
        const topClosedLostReasons = Object.entries(closedLostReasons)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([reason, count]) => ({ reason, count }));
        const closedLostByRep = {};
        closedLost.forEach(d => {
          const rep = d.Rep || 'Unknown';
          closedLostByRep[rep] = (closedLostByRep[rep] || 0) + 1;
        });
        const closedLostByRepData = Object.entries(closedLostByRep).map(([name, count]) => ({ name: name.split(' ')[0], count })).sort((a, b) => b.count - a.count);
        const recentClosedLost = [...closedLost]
          .filter(d => d.Closed_Lost_Date)
          .sort((a, b) => new Date(b.Closed_Lost_Date) - new Date(a.Closed_Lost_Date))
          .slice(0, 10);

        // MoM trend data for stacked area chart
        const trendData = monthNames.map(month => {
          const monthDeclines = declines.filter(d => d.Month === month).length;
          const monthClosedLost = closedLost.filter(d => d.Month === month).length;
          return { month: month.substring(0, 3), declines: monthDeclines, closedLost: monthClosedLost };
        });

        return (
          <section className="declined-lost-section" style={{ position: 'relative', marginTop: '24px' }}>
            {developerMode && <div className="dev-section-label">S-8</div>}
            <div style={{ marginBottom: '16px', borderBottom: '2px solid #ef4444', paddingBottom: '8px' }}>
              <h2 style={{ margin: 0, color: '#ef4444', fontSize: '1.25rem', fontWeight: 600 }}>❌ DECLINED & LOST OPPORTUNITIES</h2>
              <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>Track deals that didn't close - understand why and identify follow-up opportunities</p>
            </div>

            {/* 2-Column Layout */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', marginBottom: '24px' }}>
              {/* LEFT COLUMN: DECLINES (Lender Said No) */}
              <div style={{ background: '#fef2f2', padding: '16px', borderRadius: '12px', border: '2px solid #fecaca' }}>
                <h3 style={{ margin: '0 0 16px 0', color: '#dc2626', fontSize: '1rem', fontWeight: 600 }}>❌ DECLINES (Lender Said No)</h3>

                {/* KPI Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ background: 'white', padding: '12px', borderRadius: '8px', textAlign: 'center', position: 'relative' }}>
                    {developerMode && <div className="dev-card-label">K-1</div>}
                    <div style={{ fontSize: '11px', color: '#991b1b' }}>Total Count</div>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#dc2626' }}>{declineCount}</div>
                  </div>
                  <div style={{ background: 'white', padding: '12px', borderRadius: '8px', textAlign: 'center', position: 'relative' }}>
                    {developerMode && <div className="dev-card-label">K-2</div>}
                    <div style={{ fontSize: '11px', color: '#991b1b' }}>Total Value</div>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#dc2626' }}>{formatCurrency(declineValue)}</div>
                  </div>
                </div>

                {/* Top 3 Reasons */}
                {topDeclineReasons.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Top 3 Reasons</h4>
                    <div style={{ background: 'white', borderRadius: '8px', padding: '8px' }}>
                      {topDeclineReasons.map((r, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: idx < 2 ? '1px solid #f3f4f6' : 'none' }}>
                          <span style={{ fontSize: '12px', color: '#374151' }}>#{idx + 1} {r.reason}</span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#dc2626' }}>{r.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* By Rep Bar Chart */}
                {declinesByRepData.length > 0 && (
                  <div style={{ marginBottom: '16px', position: 'relative' }}>
                    {developerMode && <div className="dev-chart-label">C-1</div>}
                    <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>By Rep</h4>
                    <ResponsiveContainer width="100%" height={120}>
                      <BarChart data={declinesByRepData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tick={{ fontSize: 10 }} />
                        <YAxis dataKey="name" type="category" width={50} tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#dc2626">
                          <LabelList dataKey="count" position="right" style={{ fontSize: 10, fill: '#dc2626', fontWeight: 600 }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Recent 10 Table */}
                {recentDeclines.length > 0 && (
                  <div style={{ position: 'relative' }}>
                    {developerMode && <div className="dev-table-label">T-1</div>}
                    <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Recent 10</h4>
                    <div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', maxHeight: '200px', overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                        <thead style={{ background: '#fee2e2' }}>
                          <tr>
                            <th style={{ padding: '6px 4px', textAlign: 'left' }}>Account</th>
                            <th style={{ padding: '6px 4px', textAlign: 'right' }}>Amount</th>
                            <th style={{ padding: '6px 4px', textAlign: 'left' }}>Reason</th>
                            <th style={{ padding: '6px 4px', textAlign: 'right' }}>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentDeclines.map((deal, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                              <td style={{ padding: '4px', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{deal.Account}</td>
                              <td style={{ padding: '4px', textAlign: 'right' }}>{formatCurrency(parseFloat(deal.Amount) || 0)}</td>
                              <td style={{ padding: '4px', maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{deal.Stage_Detail || 'Unknown'}</td>
                              <td style={{ padding: '4px', textAlign: 'right' }}>{deal.Declined_Date ? new Date(deal.Declined_Date).toLocaleDateString() : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* RIGHT COLUMN: CLOSED LOST (Customer Said No) */}
              <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '12px', border: '2px solid #e5e7eb' }}>
                <h3 style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '1rem', fontWeight: 600 }}>🚫 CLOSED LOST (Customer Said No)</h3>

                {/* KPI Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '16px' }}>
                  <div style={{ background: 'white', padding: '12px', borderRadius: '8px', textAlign: 'center', position: 'relative' }}>
                    {developerMode && <div className="dev-card-label">K-3</div>}
                    <div style={{ fontSize: '11px', color: '#4b5563' }}>Total Count</div>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: '#6b7280' }}>{closedLostCount}</div>
                  </div>
                  <div style={{ background: 'white', padding: '12px', borderRadius: '8px', textAlign: 'center', position: 'relative' }}>
                    {developerMode && <div className="dev-card-label">K-4</div>}
                    <div style={{ fontSize: '11px', color: '#4b5563' }}>Total Value</div>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: '#6b7280' }}>{formatCurrency(closedLostValue)}</div>
                  </div>
                </div>

                {/* Top 3 Reasons */}
                {topClosedLostReasons.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Top 3 Reasons</h4>
                    <div style={{ background: 'white', borderRadius: '8px', padding: '8px' }}>
                      {topClosedLostReasons.map((r, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: idx < 2 ? '1px solid #f3f4f6' : 'none' }}>
                          <span style={{ fontSize: '12px', color: '#374151' }}>#{idx + 1} {r.reason}</span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>{r.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* By Rep Bar Chart */}
                {closedLostByRepData.length > 0 && (
                  <div style={{ marginBottom: '16px', position: 'relative' }}>
                    {developerMode && <div className="dev-chart-label">C-2</div>}
                    <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>By Rep</h4>
                    <ResponsiveContainer width="100%" height={120}>
                      <BarChart data={closedLostByRepData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tick={{ fontSize: 10 }} />
                        <YAxis dataKey="name" type="category" width={50} tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#6b7280">
                          <LabelList dataKey="count" position="right" style={{ fontSize: 10, fill: '#6b7280', fontWeight: 600 }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Recent 10 Table */}
                {recentClosedLost.length > 0 && (
                  <div style={{ position: 'relative' }}>
                    {developerMode && <div className="dev-table-label">T-2</div>}
                    <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Recent 10</h4>
                    <div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', maxHeight: '200px', overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                        <thead style={{ background: '#f3f4f6' }}>
                          <tr>
                            <th style={{ padding: '6px 4px', textAlign: 'left' }}>Account</th>
                            <th style={{ padding: '6px 4px', textAlign: 'right' }}>Amount</th>
                            <th style={{ padding: '6px 4px', textAlign: 'left' }}>Reason</th>
                            <th style={{ padding: '6px 4px', textAlign: 'right' }}>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentClosedLost.map((deal, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                              <td style={{ padding: '4px', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{deal.Account}</td>
                              <td style={{ padding: '4px', textAlign: 'right' }}>{formatCurrency(parseFloat(deal.Amount) || 0)}</td>
                              <td style={{ padding: '4px', maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{deal.Stage_Detail || 'Unknown'}</td>
                              <td style={{ padding: '4px', textAlign: 'right' }}>{deal.Closed_Lost_Date ? new Date(deal.Closed_Lost_Date).toLocaleDateString() : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Bottom Row: Stacked Area Trend Chart */}
            <div style={{ background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e5e7eb', position: 'relative' }}>
              {developerMode && <div className="dev-chart-label">C-3</div>}
              <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#374151' }}>📈 Decline vs Closed Lost Trend</h4>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="declines" name="Declines" stackId="1" stroke="#dc2626" fill="#fecaca" />
                  <Area type="monotone" dataKey="closedLost" name="Closed Lost" stackId="1" stroke="#6b7280" fill="#e5e7eb" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </section>
        );
      })()}

        </>
      )}
      {/* ========== END SUBTAB 1 ========== */}

      {/* ========== SUBTAB 2: Single Rep Deep-Dive (Phase 3 - Nov 28, 2025) ========== */}
      {activeHistoricalTab === 'single-rep' && (
        <>
        {/* Rep Selector for Deep-Dive */}
        <div style={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
          marginBottom: '24px',
          padding: '16px',
          background: '#f8fafc',
          borderRadius: '8px'
        }}>
          <span style={{ alignSelf: 'center', fontWeight: 600, color: '#374151', marginRight: '8px' }}>
            Select Rep:
          </span>
          {reps.map(rep => (
            <button
              key={rep}
              onClick={() => setActiveRepTab(rep)}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: activeRepTab === rep ? '600' : '400',
                background: activeRepTab === rep ? repColors[rep] : '#e5e7eb',
                color: activeRepTab === rep ? '#fff' : '#374151',
                transition: 'all 0.2s'
              }}
            >
              {rep.split(' ')[0]}
            </button>
          ))}
        </div>

        {/* ========== JUMP TO Navigation - V4.2 (Dec 8, 2025) ========== */}
        <div style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: '#f1f5f9',
          padding: '12px 0',
          marginBottom: '24px',
          borderBottom: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>JUMP TO:</span>
            <span style={{
              backgroundColor: '#dcfce7',
              color: '#166534',
              padding: '4px 12px',
              borderRadius: '9999px',
              fontSize: '12px',
              fontWeight: 500
            }}>
              ✨ V3 - Scrollable sections (like This Month/This Week)
            </span>
          </div>
          <div style={{
            display: 'flex',
            gap: '4px',
            backgroundColor: 'white',
            padding: '4px',
            borderRadius: '10px',
            border: '1px solid #e2e8f0'
          }}>
            {[
              { id: 'snapshot', label: '💡 Snapshot', color: '#8b5cf6' },
              { id: 'conversions', label: '📈 Conversions', color: '#3b82f6' },
              { id: 'activity', label: '📞 Activity', color: '#f59e0b' },
              { id: 'submissions', label: '📤 Submissions', color: '#ec4899' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => scrollToDeepDiveSection(tab.id)}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: activeDeepDiveSection === tab.id ? '600' : '500',
                  backgroundColor: activeDeepDiveSection === tab.id ? tab.color : 'transparent',
                  color: activeDeepDiveSection === tab.id ? '#fff' : '#475569',
                  transition: 'all 0.2s'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        {/* ========== END JUMP TO Navigation ========== */}

        {/* ========== SECTION 1: SNAPSHOT (Purple #8b5cf6) - V4.2 ========== */}
        <section ref={deepDiveSectionRefs.snapshot} style={{ marginBottom: '24px', position: 'relative' }}>
          {developerMode && <div className="dev-section-label">S-1</div>}
          {/* Collapsible Header */}
          <div
            onClick={() => toggleDeepDiveSection('snapshot')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              backgroundColor: '#8b5cf6',
              borderRadius: deepDiveCollapsed.snapshot ? '12px' : '12px 12px 0 0',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <h2 style={{ margin: 0, color: '#fff', fontSize: '1.25rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '12px' }}>
              💡 {activeRepTab.split(' ')[0]}'s Snapshot
              <span style={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                padding: '4px 12px',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: 500
              }}>
                ✨ MERGED - Insights + Goals
              </span>
            </h2>
            <span style={{ color: '#fff', fontSize: '1.5rem' }}>
              {deepDiveCollapsed.snapshot ? '▼' : '▲'}
            </span>
          </div>

          {/* Collapsible Content */}
          {!deepDiveCollapsed.snapshot && (
            <div style={{
              padding: '20px',
              backgroundColor: '#fff',
              border: '1px solid #e2e8f0',
              borderTop: 'none',
              borderRadius: '0 0 12px 12px'
            }}>

        {/* ========== SNAPSHOT CONTENT - V4.2 Mockup Match (Dec 8, 2025) ========== */}
        {(() => {
          // Calculate Key Insights data
          const currentMonthName = new Date().toLocaleString('default', { month: 'long' });
          const repDataWithMonths = monthNames
            .map(month => ({ month, data: allHistoricalData[month]?.[activeRepTab] }))
            .filter(item => item.data && item.data.revenue > 0 && item.month !== currentMonthName);

          const revenues = repDataWithMonths.map(m => m.data.revenue);
          const activeMonths = repDataWithMonths.map(m => m.month);

          // Trend calculation
          const trendPct = revenues.length >= 2 && revenues[0] > 0
            ? ((revenues[revenues.length - 1] - revenues[0]) / revenues[0] * 100).toFixed(0)
            : 0;
          const trendImproving = revenues.length >= 2 ? revenues[revenues.length - 1] > revenues[0] : true;

          // Best month
          const bestMonthIdx = revenues.indexOf(Math.max(...revenues));
          const bestMonth = activeMonths[bestMonthIdx] || '-';
          const bestRevenue = revenues[bestMonthIdx] || 0;

          // Coaching focus (lowest conversion)
          const lastMonthData = repDataWithMonths[repDataWithMonths.length - 1]?.data;
          const conversionStages = [
            { name: 'Calls → Apps', value: lastMonthData?.callsToApps || 0 },
            { name: 'Apps → Approved', value: lastMonthData?.appsToApproved || 0 },
            { name: 'Approved → Funded', value: lastMonthData?.approvedToFunded || 0 }
          ];
          const biggestGap = conversionStages.reduce((min, s) => s.value < min.value ? s : min, conversionStages[0]);

          // Q4 Goal Attainment
          const q4Months = ['October', 'November', 'December'];
          // For Q4 historical data, use previous year if we're past Q4 (Jan-Sep)
          const q4Year = ['October', 'November', 'December'].includes(currentMonth) ? currentYear : currentYear - 1;
          let q4Actual = 0, q4Goal = 0;
          const monthlyGoalData = [];
          q4Months.forEach(month => {
            const repData = allHistoricalData[month]?.[activeRepTab];
            const monthGoal = `${month} ${q4Year}`; // e.g., "October 2025"
            const goalRow = goals?.find(g => g.Month === monthGoal && g.Rep === activeRepTab);
            const actual = repData?.revenue || 0;
            const goal = goalRow ? parseInt(goalRow.Revenue_Goal) : 0;
            if (goal > 0) {
              q4Actual += actual;
              q4Goal += goal;
              monthlyGoalData.push({ month, goal, actual, attainment: goal > 0 ? (actual / goal) * 100 : 0 });
            }
          });
          const q4Attainment = q4Goal > 0 ? (q4Actual / q4Goal) * 100 : 0;

          return (
            <>
              {/* Key Insights Row - 3 Cards */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: 600, color: '#374151' }}>Key Insights</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                  {/* Card 1: Trend Direction */}
                  <div style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '20px',
                    textAlign: 'center',
                    position: 'relative'
                  }}>
                    {developerMode && <div className="dev-card-label">K-1</div>}
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '8px' }}>📈 Trend Direction</div>
                    <div style={{ fontSize: '2rem', fontWeight: 700, color: trendImproving ? '#10b981' : '#ef4444' }}>
                      {trendImproving ? '↗' : '↘'} {trendPct}%
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px' }}>
                      Revenue {trendImproving ? 'growing' : 'declining'} YTD
                    </div>
                  </div>

                  {/* Card 2: Best Month */}
                  <div style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '20px',
                    textAlign: 'center',
                    position: 'relative'
                  }}>
                    {developerMode && <div className="dev-card-label">K-2</div>}
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '8px' }}>🏆 Best Month</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f59e0b' }}>{bestMonth}</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px' }}>
                      {formatCurrency(bestRevenue)} revenue
                    </div>
                  </div>

                  {/* Card 3: Coaching Focus - RED BORDER */}
                  <div style={{
                    background: '#fff',
                    border: '2px solid #ef4444',
                    borderRadius: '8px',
                    padding: '20px',
                    textAlign: 'center',
                    position: 'relative'
                  }}>
                    {developerMode && <div className="dev-card-label">K-3</div>}
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '8px' }}>🎯 Coaching Focus</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#ef4444' }}>{biggestGap.name}</div>
                  </div>
                </div>
              </div>

              {/* Q4 Goal Attainment */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 600, color: '#374151' }}>Q4 Goal Attainment</span>
                  <span style={{
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    color: q4Attainment >= 100 ? '#10b981' : q4Attainment >= 80 ? '#f59e0b' : '#ef4444'
                  }}>
                    {q4Attainment.toFixed(1)}%
                  </span>
                </div>
                <div style={{
                  background: '#e5e7eb',
                  borderRadius: '9999px',
                  height: '12px',
                  overflow: 'hidden',
                  marginBottom: '8px'
                }}>
                  <div style={{
                    background: q4Attainment >= 100 ? '#10b981' : q4Attainment >= 80 ? '#f59e0b' : '#ef4444',
                    height: '100%',
                    width: `${Math.min(q4Attainment, 100)}%`,
                    borderRadius: '9999px',
                    transition: 'width 0.5s ease'
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#6b7280' }}>
                  <span>Actual: {formatCurrency(q4Actual)}</span>
                  <span>Goal: {formatCurrency(q4Goal)}</span>
                </div>
              </div>

              {/* Monthly Goals (Q4) */}
              <div>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: 600, color: '#374151' }}>Monthly Goals (Q4)</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                  {monthlyGoalData.map((m, idx) => (
                    <div key={m.month} style={{
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '16px',
                      position: 'relative'
                    }}>
                      {developerMode && <div className="dev-card-label">K-{idx + 4}</div>}
                      <h4 style={{ margin: '0 0 12px 0', fontWeight: 600, color: '#374151' }}>{m.month}</h4>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Goal:</span>
                        <span style={{ fontWeight: 600 }}>{formatCurrency(m.goal)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Actual:</span>
                        <span style={{
                          fontWeight: 600,
                          color: m.attainment >= 100 ? '#10b981' : m.attainment >= 80 ? '#f59e0b' : '#ef4444'
                        }}>
                          {formatCurrency(m.actual)}
                        </span>
                      </div>
                      <div style={{
                        background: '#e5e7eb',
                        borderRadius: '9999px',
                        height: '8px',
                        overflow: 'hidden',
                        marginBottom: '8px'
                      }}>
                        <div style={{
                          background: m.attainment >= 100 ? '#10b981' : m.attainment >= 80 ? '#f59e0b' : '#ef4444',
                          height: '100%',
                          width: `${Math.min(m.attainment, 100)}%`,
                          borderRadius: '9999px'
                        }} />
                      </div>
                      <div style={{
                        textAlign: 'center',
                        fontWeight: 700,
                        color: m.attainment >= 100 ? '#10b981' : m.attainment >= 80 ? '#f59e0b' : '#ef4444'
                      }}>
                        {m.attainment.toFixed(1)}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          );
        })()}
        {/* ========== END SNAPSHOT CONTENT ========== */}

            </div>
          )}
        </section>
        {/* ========== END SECTION 1: SNAPSHOT ========== */}

        {/* ========== SECTION 2: DEAL PERFORMANCE (Green #10b981) - V4.2 NEW ========== */}
        <section ref={deepDiveSectionRefs.dealPerformance} style={{ marginBottom: '24px', position: 'relative' }}>
          {developerMode && <div className="dev-section-label">S-2</div>}
          {/* Collapsible Header */}
          <div
            onClick={() => toggleDeepDiveSection('dealPerformance')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              backgroundColor: '#10b981',
              borderRadius: deepDiveCollapsed.dealPerformance ? '12px' : '12px 12px 0 0',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <h2 style={{ margin: 0, color: '#fff', fontSize: '1.25rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '12px' }}>
              💲 Deal Performance
              <span style={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                padding: '4px 12px',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: 500
              }}>
                ✨ NEW SECTION
              </span>
              <span style={{
                backgroundColor: '#3b82f6',
                padding: '4px 12px',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: 500
              }}>
                🏷 12/5 LABELS
              </span>
            </h2>
            <span style={{ color: '#fff', fontSize: '1.5rem' }}>
              {deepDiveCollapsed.dealPerformance ? '▼' : '▲'}
            </span>
          </div>

          {/* Collapsible Content */}
          {!deepDiveCollapsed.dealPerformance && (
            <div style={{
              padding: '20px',
              backgroundColor: '#fff',
              border: '1px solid #e2e8f0',
              borderTop: 'none',
              borderRadius: '0 0 12px 12px'
            }}>
              {/* 4 Charts in 2x2 Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                {/* Chart 1: Funded Amount */}
                <div style={{ position: 'relative' }}>
                  {developerMode && <div className="dev-chart-label">C-1</div>}
                  <h4 style={{ margin: '0 0 4px 0', fontWeight: 600, color: '#374151' }}>Funded Amount</h4>
                  <p style={{ margin: '0 0 16px 0', fontSize: '0.75rem', color: '#6b7280' }}>Total deal volume ($)</p>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={monthNames.map(month => {
                      const repData = allHistoricalData[month]?.[activeRepTab];
                      let teamTotal = 0, teamCount = 0;
                      reps.forEach(r => {
                        const rd = allHistoricalData[month]?.[r];
                        if (rd?.fundedAmount) { teamTotal += rd.fundedAmount; teamCount++; }
                      });
                      return {
                        month: month.substring(0, 3),
                        rep: (repData?.fundedAmount || 0) / 1000,
                        teamAvg: teamCount > 0 ? (teamTotal / teamCount) / 1000 : 0
                      };
                    })} margin={{ top: 20, right: 20, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tickFormatter={(v) => `$${v}k`} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => `$${v.toFixed(0)}k`} />
                      <Legend />
                      <Line type="monotone" dataKey="rep" name={activeRepTab.split(' ')[0]} stroke="#f59e0b" strokeWidth={2} dot={{ r: 4, fill: '#f59e0b' }}>
                        <LabelList dataKey="rep" position="top" formatter={(v) => `$${Math.round(v)}k`} style={{ fontSize: 9, fill: '#f59e0b', fontWeight: 600 }} />
                      </Line>
                      <Line type="monotone" dataKey="teamAvg" name="Team Avg" stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Chart 2: Revenue */}
                <div style={{ position: 'relative' }}>
                  {developerMode && <div className="dev-chart-label">C-2</div>}
                  <h4 style={{ margin: '0 0 4px 0', fontWeight: 600, color: '#374151' }}>Revenue</h4>
                  <p style={{ margin: '0 0 16px 0', fontSize: '0.75rem', color: '#6b7280' }}>Commission earned ($)</p>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={monthNames.map(month => {
                      const repData = allHistoricalData[month]?.[activeRepTab];
                      let teamTotal = 0, teamCount = 0;
                      reps.forEach(r => {
                        const rd = allHistoricalData[month]?.[r];
                        if (rd?.revenue) { teamTotal += rd.revenue; teamCount++; }
                      });
                      return {
                        month: month.substring(0, 3),
                        rep: (repData?.revenue || 0) / 1000,
                        teamAvg: teamCount > 0 ? (teamTotal / teamCount) / 1000 : 0
                      };
                    })} margin={{ top: 20, right: 20, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tickFormatter={(v) => `$${v}k`} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => `$${v.toFixed(0)}k`} />
                      <Legend />
                      <Line type="monotone" dataKey="rep" name={activeRepTab.split(' ')[0]} stroke="#f59e0b" strokeWidth={2} dot={{ r: 4, fill: '#f59e0b' }}>
                        <LabelList dataKey="rep" position="top" formatter={(v) => `$${Math.round(v)}k`} style={{ fontSize: 9, fill: '#f59e0b', fontWeight: 600 }} />
                      </Line>
                      <Line type="monotone" dataKey="teamAvg" name="Team Avg" stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Chart 3: Deals Funded */}
                <div style={{ position: 'relative' }}>
                  {developerMode && <div className="dev-chart-label">C-3</div>}
                  <h4 style={{ margin: '0 0 4px 0', fontWeight: 600, color: '#374151' }}>Deals Funded</h4>
                  <p style={{ margin: '0 0 16px 0', fontSize: '0.75rem', color: '#6b7280' }}>Count of funded deals</p>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={monthNames.map(month => {
                      const repData = allHistoricalData[month]?.[activeRepTab];
                      let teamTotal = 0, teamCount = 0;
                      reps.forEach(r => {
                        const rd = allHistoricalData[month]?.[r];
                        if (rd?.funded) { teamTotal += rd.funded; teamCount++; }
                      });
                      return {
                        month: month.substring(0, 3),
                        rep: repData?.funded || 0,
                        teamAvg: teamCount > 0 ? teamTotal / teamCount : 0
                      };
                    })} margin={{ top: 20, right: 20, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="rep" name={activeRepTab.split(' ')[0]} stroke="#f59e0b" strokeWidth={2} dot={{ r: 4, fill: '#f59e0b' }}>
                        <LabelList dataKey="rep" position="top" style={{ fontSize: 9, fill: '#f59e0b', fontWeight: 600 }} />
                      </Line>
                      <Line type="monotone" dataKey="teamAvg" name="Team Avg" stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Chart 4: Avg Deal Size */}
                <div style={{ position: 'relative' }}>
                  {developerMode && <div className="dev-chart-label">C-4</div>}
                  <h4 style={{ margin: '0 0 4px 0', fontWeight: 600, color: '#374151' }}>Avg Deal Size</h4>
                  <p style={{ margin: '0 0 16px 0', fontSize: '0.75rem', color: '#6b7280' }}>Average per funded deal</p>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={monthNames.map(month => {
                      const repData = allHistoricalData[month]?.[activeRepTab];
                      let teamTotal = 0, teamCount = 0;
                      reps.forEach(r => {
                        const rd = allHistoricalData[month]?.[r];
                        if (rd?.avgDealSize) { teamTotal += rd.avgDealSize; teamCount++; }
                      });
                      return {
                        month: month.substring(0, 3),
                        rep: (repData?.avgDealSize || 0) / 1000,
                        teamAvg: teamCount > 0 ? (teamTotal / teamCount) / 1000 : 0
                      };
                    })} margin={{ top: 20, right: 20, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tickFormatter={(v) => `$${v}k`} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => `$${v.toFixed(1)}k`} />
                      <Legend />
                      <Line type="monotone" dataKey="rep" name={activeRepTab.split(' ')[0]} stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4, fill: '#8b5cf6' }}>
                        <LabelList dataKey="rep" position="top" formatter={(v) => `$${Math.round(v)}k`} style={{ fontSize: 9, fill: '#8b5cf6', fontWeight: 600 }} />
                      </Line>
                      <Line type="monotone" dataKey="teamAvg" name="Team Avg" stroke="#06b6d4" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
        </section>
        {/* ========== END SECTION 2: DEAL PERFORMANCE ========== */}

        {/* ========== SECTION 3: CONVERSIONS (Blue #3b82f6) - V4.2 ========== */}
        <section ref={deepDiveSectionRefs.conversions} style={{ marginBottom: '24px', position: 'relative' }}>
          {developerMode && <div className="dev-section-label">S-3</div>}
          {/* Collapsible Header */}
          <div
            onClick={() => toggleDeepDiveSection('conversions')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              backgroundColor: '#3b82f6',
              borderRadius: deepDiveCollapsed.conversions ? '12px' : '12px 12px 0 0',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <h2 style={{ margin: 0, color: '#fff', fontSize: '1.25rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '12px' }}>
              📈 Conversion Trends
              <span style={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                padding: '4px 12px',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: 500
              }}>
                ✨ ENHANCED - Team avg lines
              </span>
            </h2>
            <span style={{ color: '#fff', fontSize: '1.5rem' }}>
              {deepDiveCollapsed.conversions ? '▼' : '▲'}
            </span>
          </div>

          {/* Collapsible Content */}
          {!deepDiveCollapsed.conversions && (
            <div style={{
              padding: '20px',
              backgroundColor: '#fff',
              border: '1px solid #e2e8f0',
              borderTop: 'none',
              borderRadius: '0 0 12px 12px'
            }}>

              {/* 2 Side-by-Side Charts per Mockup */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                {/* Chart 1: Conversion Rate Trends (with Team Average) */}
                <div style={{ position: 'relative' }}>
                  {developerMode && <div className="dev-chart-label">C-1</div>}
                  <h4 style={{ margin: '0 0 16px 0', fontWeight: 600, color: '#374151' }}>Conversion Rate Trends (with Team Average)</h4>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={monthNames.map(month => {
                      const repData = allHistoricalData?.[month]?.[activeRepTab];
                      const teamData = allHistoricalData?.[month]?.["Team Average"];
                      return {
                        month: month.substring(0, 3),
                        rep: repData?.appsToFunded || 0,
                        teamAvg: teamData?.appsToFunded || 0
                      };
                    })} margin={{ top: 20, right: 20, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => `${v.toFixed(0)}%`} />
                      <Legend />
                      <Line type="monotone" dataKey="rep" name={`${activeRepTab.split(' ')[0]}'s Rate`} stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4, fill: '#8b5cf6' }}>
                        <LabelList dataKey="rep" position="top" formatter={(v) => `${Math.round(v)}%`} style={{ fontSize: 9, fill: '#8b5cf6', fontWeight: 600 }} />
                      </Line>
                      <Line type="monotone" dataKey="teamAvg" name="Team Average" stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Chart 2: Apps → Funded % by Pack Source (M/O/M) */}
                <div style={{ position: 'relative' }}>
                  {developerMode && <div className="dev-chart-label">C-2</div>}
                  <h4 style={{ margin: '0 0 4px 0', fontWeight: 600, color: '#374151' }}>Apps → Funded % by Pack Source (M/O/M)</h4>
                  <p style={{ margin: '0 0 12px 0', fontSize: '0.7rem', color: '#10b981' }}>✨ ENHANCED - Now shows 12-month trend per pack source</p>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={monthNames.map(month => {
                      // Calculate conversion by pack source for this rep in this month
                      const appsIn = data?.tabs?.Apps_In?.data?.filter(r => r.Month === month && r.Rep === activeRepTab) || [];
                      const funded = data?.tabs?.Funded?.data?.filter(r => r.Month === month && r.Rep === activeRepTab) || [];

                      const sources = ['Inbound', 'Outbound', 'Referral'];
                      const result = { month: month.substring(0, 3) };

                      sources.forEach(source => {
                        const sourceApps = appsIn.filter(r => String(r.Pack_Source || '').toLowerCase().includes(source.toLowerCase())).length;
                        const sourceFunded = funded.filter(r => String(r.Pack_Source || '').toLowerCase().includes(source.toLowerCase())).length;
                        result[source] = sourceApps > 0 ? (sourceFunded / sourceApps * 100) : 0;
                      });

                      return result;
                    })} margin={{ top: 20, right: 20, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => `${v.toFixed(0)}%`} />
                      <Legend />
                      <Line type="monotone" dataKey="Inbound" name="Inbound" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="Outbound" name="Outbound" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="Referral" name="Referral" stroke="#ec4899" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              {/* ========== END CONVERSIONS CHARTS ========== */}

            </div>
          )}
        </section>
        {/* ========== END SECTION 2: CONVERSIONS ========== */}

        {/* ========== SECTION 3: ACTIVITY (Orange #f59e0b) - V4.2 NEW ========== */}
        <section ref={deepDiveSectionRefs.activity} style={{ marginBottom: '24px', position: 'relative' }}>
          {developerMode && <div className="dev-section-label">S-4</div>}
          {/* Collapsible Header */}
          <div
            onClick={() => toggleDeepDiveSection('activity')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              backgroundColor: '#f59e0b',
              borderRadius: deepDiveCollapsed.activity ? '12px' : '12px 12px 0 0',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <h2 style={{ margin: 0, color: '#fff', fontSize: '1.25rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '12px' }}>
              📞 Activity Trends
              <span style={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                padding: '4px 12px',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: 500
              }}>
                ✨ ENHANCED - Calls, SMS, Talk Time (12-4-25)
              </span>
              <span style={{
                backgroundColor: '#3b82f6',
                padding: '4px 12px',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: 500
              }}>
                🏷 12/5 LABELS
              </span>
            </h2>
            <span style={{ color: '#fff', fontSize: '1.5rem' }}>
              {deepDiveCollapsed.activity ? '▼' : '▲'}
            </span>
          </div>

          {/* Collapsible Content */}
          {!deepDiveCollapsed.activity && (
            <div style={{
              padding: '20px',
              backgroundColor: '#fff',
              border: '1px solid #e2e8f0',
              borderTop: 'none',
              borderRadius: '0 0 12px 12px'
            }}>
              {/* 3 Charts Side-by-Side per Mockup */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                {/* Chart 1: Calls (with Team Average) */}
                <div style={{ position: 'relative' }}>
                  {developerMode && <div className="dev-chart-label">C-1</div>}
                  <h4 style={{ margin: '0 0 16px 0', fontWeight: 600, color: '#374151' }}>Calls (with Team Average)</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={monthNames.map(month => {
                      const repData = allHistoricalData?.[month]?.[activeRepTab];
                      const teamData = allHistoricalData?.[month]?.["Team Average"];
                      return {
                        month: month.substring(0, 3),
                        rep: repData?.calls || 0,
                        teamAvg: teamData?.calls || 0
                      };
                    })} margin={{ top: 20, right: 10, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 9 }} />
                      <YAxis tick={{ fontSize: 9 }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: '10px' }} />
                      <Line type="monotone" dataKey="rep" name={`${activeRepTab.split(' ')[0]}'s Calls`} stroke="#f59e0b" strokeWidth={2} dot={{ r: 3, fill: '#f59e0b' }}>
                        <LabelList dataKey="rep" position="top" style={{ fontSize: 8, fill: '#f59e0b', fontWeight: 600 }} />
                      </Line>
                      <Line type="monotone" dataKey="teamAvg" name="Team Avg" stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Chart 2: SMS (with Team Average) */}
                <div style={{ position: 'relative' }}>
                  {developerMode && <div className="dev-chart-label">C-2</div>}
                  <h4 style={{ margin: '0 0 16px 0', fontWeight: 600, color: '#374151' }}>SMS (with Team Average)</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={monthNames.map(month => {
                      const repData = allHistoricalData?.[month]?.[activeRepTab];
                      const teamData = allHistoricalData?.[month]?.["Team Average"];
                      return {
                        month: month.substring(0, 3),
                        rep: repData?.sms || 0,
                        teamAvg: teamData?.sms || 0
                      };
                    })} margin={{ top: 20, right: 10, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 9 }} />
                      <YAxis tick={{ fontSize: 9 }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: '10px' }} />
                      <Line type="monotone" dataKey="rep" name={`${activeRepTab.split(' ')[0]}'s SMS`} stroke="#10b981" strokeWidth={2} dot={{ r: 3, fill: '#10b981' }}>
                        <LabelList dataKey="rep" position="top" style={{ fontSize: 8, fill: '#10b981', fontWeight: 600 }} />
                      </Line>
                      <Line type="monotone" dataKey="teamAvg" name="Team Avg" stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Chart 3: Talk Time - Minutes (with Team Average) */}
                <div style={{ position: 'relative' }}>
                  {developerMode && <div className="dev-chart-label">C-3</div>}
                  <h4 style={{ margin: '0 0 16px 0', fontWeight: 600, color: '#374151' }}>Talk Time - Minutes (with Team Average)</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={monthNames.map(month => {
                      const repData = allHistoricalData?.[month]?.[activeRepTab];
                      const teamData = allHistoricalData?.[month]?.["Team Average"];
                      return {
                        month: month.substring(0, 3),
                        rep: repData?.talkTime || 0,
                        teamAvg: teamData?.talkTime || 0
                      };
                    })} margin={{ top: 20, right: 10, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 9 }} />
                      <YAxis tickFormatter={(v) => `${Math.round(v)}m`} tick={{ fontSize: 9 }} />
                      <Tooltip formatter={(v) => `${Math.round(v)}m`} />
                      <Legend wrapperStyle={{ fontSize: '10px' }} />
                      <Line type="monotone" dataKey="rep" name={`${activeRepTab.split(' ')[0]}'s Talk Time`} stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3, fill: '#8b5cf6' }}>
                        <LabelList dataKey="rep" position="top" formatter={(v) => `${Math.round(v)}m`} style={{ fontSize: 8, fill: '#8b5cf6', fontWeight: 600 }} />
                      </Line>
                      <Line type="monotone" dataKey="teamAvg" name="Team Avg" stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
        </section>
        {/* ========== END SECTION 3: ACTIVITY ========== */}

        {/* ========== SECTION 4: SUBMISSIONS (Pink #ec4899) - V4.2 NEW ========== */}
        <section ref={deepDiveSectionRefs.submissions} style={{ marginBottom: '24px', position: 'relative' }}>
          {developerMode && <div className="dev-section-label">S-5</div>}
          {/* Collapsible Header */}
          <div
            onClick={() => toggleDeepDiveSection('submissions')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              backgroundColor: '#ec4899',
              borderRadius: deepDiveCollapsed.submissions ? '12px' : '12px 12px 0 0',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <h2 style={{ margin: 0, color: '#fff', fontSize: '1.25rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '12px' }}>
              📤 Submissions
              <span style={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                padding: '4px 12px',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: 500
              }}>
                ✨ NEW - Full section
              </span>
            </h2>
            <span style={{ color: '#fff', fontSize: '1.5rem' }}>
              {deepDiveCollapsed.submissions ? '▼' : '▲'}
            </span>
          </div>

          {/* Collapsible Content */}
          {!deepDiveCollapsed.submissions && (
            <div style={{
              padding: '20px',
              backgroundColor: '#fff',
              border: '1px solid #e2e8f0',
              borderTop: 'none',
              borderRadius: '0 0 12px 12px'
            }}>
              {(() => {
                // Calculate submission metrics from deal data for this rep
                const allDeals = [
                  ...(data?.tabs?.Underwriting?.data || []),
                  ...(data?.tabs?.Approved?.data || []),
                  ...(data?.tabs?.Funded?.data || []),
                  ...(data?.tabs?.Declined_Lost?.data || [])
                ].filter(d => d.Rep === activeRepTab);

                // Dedupe by Opportunity_ID
                const uniqueDeals = [...new Map(allDeals.map(d => [d.Opportunity_ID, d])).values()];

                const fundedDeals = uniqueDeals.filter(d => d.Stage === 'Funded' || d.Stage === 'Closed Won');
                const lostDeals = uniqueDeals.filter(d => d.Stage === 'Closed Lost' || d.Stage === 'Declined');
                const uwDeals = uniqueDeals.filter(d => d.Stage === 'Underwriting' || d.Stage === 'Approved');
                const openDeals = uniqueDeals.filter(d => !['Funded', 'Closed Won', 'Closed Lost', 'Declined'].includes(d.Stage));

                const totalSubmissions = uniqueDeals.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);
                const wonSubmissions = fundedDeals.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);
                const lostSubmissions = lostDeals.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);
                const openSubmissions = openDeals.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);

                const avgSubsPerFunded = fundedDeals.length > 0 ? (wonSubmissions / fundedDeals.length) : 0;
                const avgSubsPerUW = uwDeals.length > 0 ? (uwDeals.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0) / uwDeals.length) : 0;
                const avgSubsPerDeclined = lostDeals.length > 0 ? (lostSubmissions / lostDeals.length) : 0;
                const successRate = totalSubmissions > 0 ? (fundedDeals.length / totalSubmissions * 100) : 0;

                // Calculate Team Averages
                const allTeamDeals = [
                  ...(data?.tabs?.Underwriting?.data || []),
                  ...(data?.tabs?.Approved?.data || []),
                  ...(data?.tabs?.Funded?.data || []),
                  ...(data?.tabs?.Declined_Lost?.data || [])
                ];
                const uniqueTeamDeals = [...new Map(allTeamDeals.map(d => [d.Opportunity_ID, d])).values()];
                const teamFunded = uniqueTeamDeals.filter(d => d.Stage === 'Funded' || d.Stage === 'Closed Won');
                const teamLost = uniqueTeamDeals.filter(d => d.Stage === 'Closed Lost' || d.Stage === 'Declined');
                const teamUW = uniqueTeamDeals.filter(d => d.Stage === 'Underwriting' || d.Stage === 'Approved');
                const teamTotalSubs = uniqueTeamDeals.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);

                const teamAvgSubsPerFunded = teamFunded.length > 0 ? (teamFunded.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0) / teamFunded.length) : 0;
                const teamAvgSubsPerUW = teamUW.length > 0 ? (teamUW.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0) / teamUW.length) : 0;
                const teamAvgSubsPerDeclined = teamLost.length > 0 ? (teamLost.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0) / teamLost.length) : 0;
                const teamSuccessRate = teamTotalSubs > 0 ? (teamFunded.length / teamTotalSubs * 100) : 0;

                return (
                  <>
                    {/* Row 1: Summary Cards (green left border) */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '16px' }}>
                      <div style={{ padding: '16px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', borderLeft: '4px solid #10b981', textAlign: 'center', position: 'relative' }}>
                        {developerMode && <div className="dev-card-label">K-1</div>}
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: 500 }}>Total Submissions</div>
                        <div style={{ fontSize: '2rem', fontWeight: 700, color: '#8b5cf6' }}>{totalSubmissions}</div>
                      </div>
                      <div style={{ padding: '16px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', borderLeft: '4px solid #10b981', textAlign: 'center', position: 'relative' }}>
                        {developerMode && <div className="dev-card-label">K-2</div>}
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: 500 }}>Won</div>
                        <div style={{ fontSize: '2rem', fontWeight: 700, color: '#10b981' }}>{wonSubmissions}</div>
                      </div>
                      <div style={{ padding: '16px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', borderLeft: '4px solid #10b981', textAlign: 'center', position: 'relative' }}>
                        {developerMode && <div className="dev-card-label">K-3</div>}
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: 500 }}>Lost</div>
                        <div style={{ fontSize: '2rem', fontWeight: 700, color: '#ef4444' }}>{lostSubmissions}</div>
                      </div>
                      <div style={{ padding: '16px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', borderLeft: '4px solid #10b981', textAlign: 'center', position: 'relative' }}>
                        {developerMode && <div className="dev-card-label">K-4</div>}
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: 500 }}>Open</div>
                        <div style={{ fontSize: '2rem', fontWeight: 700, color: '#3b82f6' }}>{openSubmissions}</div>
                      </div>
                    </div>

                    {/* Row 2: Avg Metrics Cards (purple left border + team avg) */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
                      <div style={{ padding: '16px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', borderLeft: '4px solid #8b5cf6', textAlign: 'center', position: 'relative' }}>
                        {developerMode && <div className="dev-card-label">K-5</div>}
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: 500 }}>Avg Subs per Funded</div>
                        <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#8b5cf6' }}>{avgSubsPerFunded.toFixed(1)}</div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Team Avg: {teamAvgSubsPerFunded.toFixed(1)}</div>
                      </div>
                      <div style={{ padding: '16px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', borderLeft: '4px solid #8b5cf6', textAlign: 'center', position: 'relative' }}>
                        {developerMode && <div className="dev-card-label">K-6</div>}
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: 500 }}>Avg Subs per Underwriting</div>
                        <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#8b5cf6' }}>{avgSubsPerUW.toFixed(1)}</div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Team Avg: {teamAvgSubsPerUW.toFixed(1)}</div>
                      </div>
                      <div style={{ padding: '16px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', borderLeft: '4px solid #8b5cf6', textAlign: 'center', position: 'relative' }}>
                        {developerMode && <div className="dev-card-label">K-7</div>}
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: 500 }}>Avg Subs per Declined</div>
                        <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#8b5cf6' }}>{avgSubsPerDeclined.toFixed(1)}</div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Team Avg: {teamAvgSubsPerDeclined.toFixed(1)}</div>
                      </div>
                      <div style={{ padding: '16px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', borderLeft: '4px solid #8b5cf6', textAlign: 'center', position: 'relative' }}>
                        {developerMode && <div className="dev-card-label">K-8</div>}
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: 500 }}>Success Rate</div>
                        <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#10b981' }}>{successRate.toFixed(0)}%</div>
                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Team Avg: {teamSuccessRate.toFixed(1)}%</div>
                      </div>
                    </div>

                    {/* Chart: Submission Success Rate Trend */}
                    <div style={{ position: 'relative' }}>
                      {developerMode && <div className="dev-chart-label">C-1</div>}
                      <h4 style={{ margin: '0 0 4px 0', fontWeight: 600, color: '#374151' }}>Submission Success Rate Trend (with Team Average)</h4>
                      <p style={{ margin: '0 0 16px 0', fontSize: '0.7rem', color: '#6b7280' }}>Formula: Funded Deals ÷ Total Submissions × 100</p>
                      <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={monthNames.map(month => {
                          // Calculate rep's success rate for this month
                          const repDeals = uniqueDeals.filter(d => d.Month === month || d.Funded_Date?.includes(month));
                          const repFunded = repDeals.filter(d => d.Stage === 'Funded' || d.Stage === 'Closed Won').length;
                          const repSubs = repDeals.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);
                          const repRate = repSubs > 0 ? (repFunded / repSubs * 100) : 0;

                          // Calculate team success rate for this month
                          const teamDealsMonth = uniqueTeamDeals.filter(d => d.Month === month || d.Funded_Date?.includes(month));
                          const teamFundedMonth = teamDealsMonth.filter(d => d.Stage === 'Funded' || d.Stage === 'Closed Won').length;
                          const teamSubsMonth = teamDealsMonth.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);
                          const teamRate = teamSubsMonth > 0 ? (teamFundedMonth / teamSubsMonth * 100) : 0;

                          return {
                            month: month.substring(0, 3),
                            rep: repRate,
                            teamAvg: teamRate
                          };
                        })} margin={{ top: 20, right: 20, bottom: 5, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                          <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10 }} />
                          <Tooltip formatter={(v) => `${v.toFixed(0)}%`} />
                          <Legend />
                          <Line type="monotone" dataKey="rep" name={`${activeRepTab.split(' ')[0]}'s Success Rate`} stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4, fill: '#8b5cf6' }}>
                            <LabelList dataKey="rep" position="top" formatter={(v) => `${Math.round(v)}%`} style={{ fontSize: 9, fill: '#8b5cf6', fontWeight: 600 }} />
                          </Line>
                          <Line type="monotone" dataKey="teamAvg" name="Team Average" stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </section>
        {/* ========== END SECTION 4: SUBMISSIONS ========== */}

        </>
      )}

      {/* ========== SUBTAB 3: Team Snapshot (Phase 4B Enhanced - Nov 28, 2025) ========== */}
      {activeHistoricalTab === 'team-snapshot' && (
        <>
        {/* ========== STICKY SECTION NAVIGATION ========== */}
        <div style={{
          position: 'sticky',
          top: '0',
          zIndex: 10,
          backgroundColor: '#f1f5f9',
          padding: '12px 16px',
          marginBottom: '24px',
          borderBottom: '2px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontWeight: 600, color: '#374151', marginRight: '8px', fontSize: '14px' }}>JUMP TO:</span>
          {[
            { id: 'summary', label: 'SUMMARY', color: '#8b5cf6' },
            { id: 'aggregates', label: 'AGGREGATES', color: '#10b981' },
            { id: 'submissions', label: 'SUBMISSIONS', color: '#3b82f6' },
            { id: 'rankings', label: 'RANKINGS', color: '#f59e0b' },
            { id: 'funding', label: 'FUNDING', color: '#10b981' },
            { id: 'activity', label: 'ACTIVITY', color: '#3b82f6' },
            { id: 'packSource', label: 'PACK SOURCE', color: '#8b5cf6' },
            { id: 'table', label: 'TABLE', color: '#6b7280' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => scrollToTeamSection(tab.id)}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: activeTeamSection === tab.id ? '600' : '500',
                fontSize: '12px',
                background: activeTeamSection === tab.id ? tab.color : '#e2e8f0',
                color: activeTeamSection === tab.id ? '#fff' : '#475569',
                transition: 'all 0.2s'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ========== SUMMARY KPI CARDS (5 Gradient Cards) ========== */}
        <div ref={teamSectionRefs.summary} style={{ marginBottom: '24px', scrollMarginTop: '80px', position: 'relative' }}>
          {developerMode && <div className="dev-section-label">S-1</div>}
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600, color: '#374151' }}>
            📊 Quick Summary
          </h3>
          {(() => {
            const localCurrentMonth = monthNames[monthNames.length - 1];
            // Construct goal month with year for proper goal lookup
            const localGoalMonth = `${localCurrentMonth} ${currentYear}`;

            // Calculate totals
            let totalRevenue = 0;
            let totalFunded = 0;
            let totalApps = 0;
            let currentMonthRevenue = 0;
            let currentMonthGoal = 0;
            let topPerformer = { name: '', revenue: 0 };

            monthNames.forEach(month => {
              reps.forEach(rep => {
                const repData = allHistoricalData[month]?.[rep];
                if (repData) {
                  totalRevenue += repData.revenue || 0;
                  totalFunded += repData.funded || 0;
                  totalApps += repData.appsIn || 0;

                  if (month === localCurrentMonth) {
                    currentMonthRevenue += repData.revenue || 0;
                    if ((repData.revenue || 0) > topPerformer.revenue) {
                      topPerformer = { name: rep, revenue: repData.revenue || 0 };
                    }
                  }
                }
                if (month === localCurrentMonth) {
                  const goalRow = goals?.find(g => g.Month === localGoalMonth && g.Rep === rep);
                  if (goalRow) currentMonthGoal += parseInt(goalRow.Revenue_Goal) || 0;
                }
              });
            });

            const teamAvgConversion = totalApps > 0 ? (totalFunded / totalApps) * 100 : 0;
            const currentMonthAttainment = currentMonthGoal > 0 ? (currentMonthRevenue / currentMonthGoal) * 100 : 0;

            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px' }}>
                <div style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', borderRadius: '12px', padding: '20px', color: 'white', position: 'relative' }}>
                  {developerMode && <div className="dev-card-label">K-1</div>}
                  <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>Total Revenue (YTD)</div>
                  <div style={{ fontSize: '24px', fontWeight: 700 }}>{formatCurrency(totalRevenue)}</div>
                </div>
                <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '12px', padding: '20px', color: 'white', position: 'relative' }}>
                  {developerMode && <div className="dev-card-label">K-2</div>}
                  <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>Total Funded Deals</div>
                  <div style={{ fontSize: '24px', fontWeight: 700 }}>{totalFunded.toLocaleString()}</div>
                </div>
                <div style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', borderRadius: '12px', padding: '20px', color: 'white', position: 'relative' }}>
                  {developerMode && <div className="dev-card-label">K-3</div>}
                  <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>Team Avg Conversion</div>
                  <div style={{ fontSize: '24px', fontWeight: 700 }}>{teamAvgConversion.toFixed(1)}%</div>
                </div>
                <div style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', borderRadius: '12px', padding: '20px', color: 'white', position: 'relative' }}>
                  {developerMode && <div className="dev-card-label">K-4</div>}
                  <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>Current Month Attainment</div>
                  <div style={{ fontSize: '24px', fontWeight: 700 }}>{currentMonthAttainment.toFixed(0)}%</div>
                </div>
                <div style={{ background: 'linear-gradient(135deg, #374151 0%, #1f2937 100%)', borderRadius: '12px', padding: '20px', color: 'white', position: 'relative' }}>
                  {developerMode && <div className="dev-card-label">K-5</div>}
                  <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>Top Performer</div>
                  <div style={{ fontSize: '18px', fontWeight: 700 }}>🏆 {topPerformer.name ? topPerformer.name.split(' ')[0] : 'N/A'}</div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* ========== SECTION: TEAM AGGREGATES (Collapsible) ========== */}
        <div ref={teamSectionRefs.aggregates} style={{
          background: '#fff',
          borderRadius: '12px',
          marginBottom: '24px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          scrollMarginTop: '80px',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-section-label">S-2</div>}
          {/* Collapsible Header - Green */}
          <div
            onClick={() => toggleTeamSection('aggregates')}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              background: '#10b981',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>📊</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>
                Team Aggregates
                <span style={{ fontWeight: 400, fontSize: '0.875rem', marginLeft: '8px', opacity: 0.9 }}>
                  (Current Month: {monthNames[monthNames.length - 1]})
                </span>
              </h3>
            </div>
            <span style={{ color: 'white', fontSize: '14px' }}>
              {teamCollapsed['aggregates'] ? '▶' : '▼'}
            </span>
          </div>
          {/* Collapsible Content */}
          {!teamCollapsed['aggregates'] && (
            <div style={{ padding: '20px' }}>
          {(() => {
            const currentMonth = monthNames[monthNames.length - 1];
            
            // All-time totals for funding metrics
            let totalRevenue = 0;
            let totalFundedAmount = 0;
            let totalFunded = 0;

            monthNames.forEach(month => {
              reps.forEach(rep => {
                const repData = allHistoricalData[month]?.[rep];
                if (repData) {
                  totalRevenue += repData.revenue || 0;
                  totalFundedAmount += repData.fundedAmount || 0;
                  totalFunded += repData.funded || 0;
                }
              });
            });

            const avgDealSize = totalFunded > 0 ? totalFundedAmount / totalFunded : 0;

            // Current month attainment calculations
            const businessDaysInMonth = getBusinessDaysForMonth(currentMonth);
            let currentMonthRevenue = 0;
            let currentMonthGoal = 0;
            let currentMonthActivity = 0;
            let currentMonthActivityGoal = 0;

            reps.forEach(rep => {
              const repData = allHistoricalData[currentMonth]?.[rep];
              if (repData) {
                currentMonthRevenue += repData.revenue || 0;
                // Activity Points: Date-based formula (Dec 16, 2025)
                currentMonthActivity += calculateActivityPoints(repData.calls || 0, repData.sms || 0, repData.appsIn || 0, new Date().toISOString().split('T')[0]);
              }
              const goalRow = goals?.find(g => g.Month === currentMonth && g.Rep === rep);
              if (goalRow) {
                currentMonthGoal += parseInt(goalRow.Revenue_Goal) || 0;
                currentMonthActivityGoal += (parseInt(goalRow.Activity_Call_Goal) || 0) * businessDaysInMonth; // Daily goal × business days
              }
            });

            const quotaAttainment = currentMonthGoal > 0 ? (currentMonthRevenue / currentMonthGoal) * 100 : 0;
            const activityAttainment = currentMonthActivityGoal > 0 ? (currentMonthActivity / currentMonthActivityGoal) * 100 : 0;

            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px', marginTop: '16px' }}>
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', textAlign: 'center', position: 'relative' }}>
                  {developerMode && <div className="dev-card-label">K-1</div>}
                  <div style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '4px' }}>Revenue</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#10b981' }}>{formatCurrency(totalRevenue)}</div>
                  <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '4px' }}>All-Time</div>
                </div>
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', textAlign: 'center', position: 'relative' }}>
                  {developerMode && <div className="dev-card-label">K-2</div>}
                  <div style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '4px' }}>Funded Dollars</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#3b82f6' }}>{formatCurrency(totalFundedAmount)}</div>
                  <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '4px' }}>All-Time</div>
                </div>
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', textAlign: 'center', position: 'relative' }}>
                  {developerMode && <div className="dev-card-label">K-3</div>}
                  <div style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '4px' }}>Funded Units</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#8b5cf6' }}>{totalFunded.toLocaleString()}</div>
                  <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '4px' }}>All-Time</div>
                </div>
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', textAlign: 'center', position: 'relative' }}>
                  {developerMode && <div className="dev-card-label">K-4</div>}
                  <div style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '4px' }}>Avg Deal Size</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#10b981' }}>{formatCurrency(avgDealSize)}</div>
                  <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '4px' }}>All-Time</div>
                </div>
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', textAlign: 'center', borderTop: '3px solid ' + (quotaAttainment >= 100 ? '#10b981' : quotaAttainment >= 80 ? '#f59e0b' : '#ef4444'), position: 'relative' }}>
                  {developerMode && <div className="dev-card-label">K-5</div>}
                  <div style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '4px' }}>Quota Attainment</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: quotaAttainment >= 100 ? '#10b981' : quotaAttainment >= 80 ? '#f59e0b' : '#ef4444' }}>
                    {quotaAttainment.toFixed(0)}%
                  </div>
                  <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '4px' }}>{currentMonth}</div>
                </div>
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', textAlign: 'center', borderTop: '3px solid ' + (activityAttainment >= 100 ? '#10b981' : activityAttainment >= 80 ? '#f59e0b' : '#ef4444'), position: 'relative' }}>
                  {developerMode && <div className="dev-card-label">K-6</div>}
                  <div style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '4px' }}>Activity Attainment</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: activityAttainment >= 100 ? '#10b981' : activityAttainment >= 80 ? '#f59e0b' : '#ef4444' }}>
                    {activityAttainment.toFixed(0)}%
                  </div>
                  <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '4px' }}>{currentMonth}</div>
                </div>
              </div>
            );
          })()}
            </div>
          )}
        </div>

        {/* ========== SECTION: TEAM SUBMISSION TRENDS (Redesigned - Dec 2025) ========== */}
        <div ref={teamSectionRefs.submissions} style={{
          background: '#fff',
          borderRadius: '12px',
          marginBottom: '24px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          scrollMarginTop: '80px',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-section-label">S-3</div>}
          {/* Collapsible Header - Blue */}
          <div
            onClick={() => toggleTeamSection('submissions')}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              background: '#3b82f6',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>📤</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>
                Team Submission Trends
              </h3>
            </div>
            <span style={{ color: 'white', fontSize: '14px' }}>
              {teamCollapsed['submissions'] ? '▶' : '▼'}
            </span>
          </div>
          {/* Collapsible Content */}
          {!teamCollapsed['submissions'] && (
            <div style={{ padding: '20px' }}>
              {/* ===== SECTION A: Submission Overview ===== */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ background: '#3b82f6', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>A</span>
                  Submission Overview
                </h4>

                {/* Row 1: 3 line charts - Total, Won, Lost */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '20px' }}>
                  {/* Chart 1: Total Submissions MoM */}
                  <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
                    {developerMode && <div className="dev-chart-label">C-1</div>}
                    <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Total Submissions</h4>
                    <ResponsiveContainer width="100%" height={150}>
                      <LineChart data={teamSubmissionTrends.map(d => ({
                        ...d,
                        totalSubmissions: d.submissionsWon + d.submissionsLost
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(value) => Math.round(value)} />
                        <Line type="monotone" dataKey="totalSubmissions" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }}>
                          <LabelList dataKey="totalSubmissions" position="top" style={{ fontSize: 9, fill: '#3b82f6', fontWeight: 600 }} />
                        </Line>
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Chart 2: Won Submissions MoM */}
                  <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
                    {developerMode && <div className="dev-chart-label">C-2</div>}
                    <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Won Submissions</h4>
                    <ResponsiveContainer width="100%" height={150}>
                      <LineChart data={teamSubmissionTrends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(value) => Math.round(value)} />
                        <Line type="monotone" dataKey="submissionsWon" stroke="#059669" strokeWidth={2} dot={{ r: 3 }}>
                          <LabelList dataKey="submissionsWon" position="top" style={{ fontSize: 9, fill: '#059669', fontWeight: 600 }} />
                        </Line>
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Chart 3: Lost Submissions MoM */}
                  <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
                    {developerMode && <div className="dev-chart-label">C-3</div>}
                    <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Lost Submissions</h4>
                    <ResponsiveContainer width="100%" height={150}>
                      <LineChart data={teamSubmissionTrends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(value) => Math.round(value)} />
                        <Line type="monotone" dataKey="submissionsLost" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }}>
                          <LabelList dataKey="submissionsLost" position="top" style={{ fontSize: 9, fill: '#dc2626', fontWeight: 600 }} />
                        </Line>
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Row 2: 3 cards + 1 chart */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '20px' }}>
                  {/* Card 1: Avg Subs to Win */}
                  {(() => {
                    // Get actual funded deal count from Funded tab
                    const fundedDealsForSubs = filterValidDeals(data?.tabs?.Funded?.data || []);
                    const totalWonSubs = fundedDealsForSubs.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);
                    const wonDeals = fundedDealsForSubs.length || 1;
                    const avgSubsToWin = wonDeals > 0 ? (totalWonSubs / wonDeals).toFixed(1) : '0.0';
                    return (
                      <div style={{ background: '#f0fdf4', padding: '16px', borderRadius: '8px', border: '1px solid #86efac', textAlign: 'center', position: 'relative' }}>
                        {developerMode && <div className="dev-card-label">K-1</div>}
                        <div style={{ fontSize: '12px', color: '#166534', marginBottom: '4px' }}>Avg Subs to Win</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#059669' }}>{avgSubsToWin}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>submissions/funded deal</div>
                      </div>
                    );
                  })()}

                  {/* Card 2: Avg Subs to Lose */}
                  {(() => {
                    // Get actual lost deal count from Declined_Lost tab
                    const lostDealsData = (data?.tabs?.Declined_Lost?.data || []).filter(d => d.Stage === 'Closed Lost');
                    const totalLostSubs = lostDealsData.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);
                    const lostDeals = lostDealsData.length || 1;
                    const avgSubsToLose = lostDeals > 0 ? (totalLostSubs / lostDeals).toFixed(1) : '0.0';
                    return (
                      <div style={{ background: '#fef2f2', padding: '16px', borderRadius: '8px', border: '1px solid #fecaca', textAlign: 'center', position: 'relative' }}>
                        {developerMode && <div className="dev-card-label">K-2</div>}
                        <div style={{ fontSize: '12px', color: '#991b1b', marginBottom: '4px' }}>Avg Subs to Lose</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#dc2626' }}>{avgSubsToLose}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>submissions/lost deal</div>
                      </div>
                    );
                  })()}

                  {/* Card 3: Team Decline Rate */}
                  {(() => {
                    // Calculate total declines and ALL submissions from all deal stages
                    const allDealsForDeclines = [
                      ...(data?.tabs?.Underwriting?.data || []),
                      ...(data?.tabs?.Approved?.data || []),
                      ...(data?.tabs?.Funded?.data || []),
                      ...(data?.tabs?.Declined_Lost?.data || [])
                    ];
                    // Dedupe by Opportunity_ID
                    const uniqueDealsForDeclines = [...new Map(allDealsForDeclines.map(d => [d.Opportunity_ID, d])).values()];
                    const totalDeclines = uniqueDealsForDeclines.reduce((sum, d) => sum + (parseInt(d.Submissions_Declined) || 0), 0);
                    const totalSubs = uniqueDealsForDeclines.reduce((sum, d) => sum + (parseInt(d.Submissions) || 0), 0);
                    const declineRate = totalSubs > 0 ? ((totalDeclines / totalSubs) * 100).toFixed(0) : '0';
                    return (
                      <div style={{ background: '#fef3c7', padding: '16px', borderRadius: '8px', border: '1px solid #fcd34d', textAlign: 'center', position: 'relative' }}>
                        {developerMode && <div className="dev-card-label">K-3</div>}
                        <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '4px' }}>Team Decline Rate</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#f59e0b' }}>{declineRate}%</div>
                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>{totalDeclines} of {totalSubs} declined</div>
                      </div>
                    );
                  })()}

                  {/* Chart 4: Success Rate Trend */}
                  <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
                    {developerMode && <div className="dev-chart-label">C-4</div>}
                    <h4 style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Success Rate Trend</h4>
                    <ResponsiveContainer width="100%" height={80}>
                      <LineChart data={teamSubmissionTrends}>
                        <XAxis dataKey="month" tick={{ fontSize: 9 }} />
                        <YAxis tick={{ fontSize: 9 }} unit="%" domain={[0, 100]} />
                        <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                        <Line type="monotone" dataKey="successRate" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 2 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Insight Box */}
                {(() => {
                  const totalWonSubs = teamSubmissionTrends.reduce((sum, d) => sum + d.submissionsWon, 0);
                  const totalLostSubs = teamSubmissionTrends.reduce((sum, d) => sum + d.submissionsLost, 0);
                  const wonCount = teamSubmissionTrends.filter(d => d.submissionsWon > 0).length || 1;
                  const lostCount = teamSubmissionTrends.filter(d => d.submissionsLost > 0).length || 1;
                  const avgWon = (totalWonSubs / wonCount).toFixed(1);
                  const avgLost = (totalLostSubs / lostCount).toFixed(1);
                  return (
                    <div style={{
                      padding: '16px',
                      backgroundColor: '#f0f9ff',
                      borderRadius: '8px',
                      border: '1px solid #7dd3fc',
                      marginBottom: '24px'
                    }}>
                      <strong>💡 Insight:</strong> Funded deals averaged <strong>{avgWon}</strong> submissions vs Lost deals at <strong>{avgLost}</strong> — persistence matters!
                    </div>
                  );
                })()}
              </div>

              {/* ===== SECTION B: Lender Overview ===== */}
              <div>
                <h4 style={{ fontSize: '16px', fontWeight: 600, color: '#374151', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ background: '#10b981', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>B</span>
                  Lender Overview
                </h4>

                {/* Row 1: 5 KPI Cards */}
                {(() => {
                  // Apply filterValidDeals and filter by reps + months to match Team Aggregates
                  const fundedDeals = filterValidDeals(data?.tabs?.Funded?.data || [])
                    .filter(deal => reps.includes(deal.Rep))
                    .filter(deal => monthNames.includes(deal.Month));
                  const lenderMap = {};
                  fundedDeals.forEach(deal => {
                    const lender = deal.Lender || 'Unknown';
                    if (!lenderMap[lender]) {
                      lenderMap[lender] = { deals: 0, amount: 0, revenue: 0 };
                    }
                    lenderMap[lender].deals++;
                    lenderMap[lender].amount += parseFloat(deal.Amount) || 0;
                    lenderMap[lender].revenue += parseFloat(deal.Revenue) || 0;
                  });
                  const lenders = Object.entries(lenderMap).map(([name, lenderData]) => ({
                    name,
                    ...lenderData,
                    avgDealSize: lenderData.deals > 0 ? lenderData.amount / lenderData.deals : 0
                  })).sort((a, b) => b.deals - a.deals);
                  const totalLenders = lenders.length;
                  const topLender = lenders[0]?.name || 'N/A';
                  const totalFundedAmount = lenders.reduce((sum, l) => sum + l.amount, 0);
                  const totalRevenue = lenders.reduce((sum, l) => sum + l.revenue, 0);
                  const totalFundedDeals = lenders.reduce((sum, l) => sum + l.deals, 0);

                  // Calculate MoM data for Top 5 Lenders stacked bar
                  const top5Lenders = lenders.slice(0, 5).map(l => l.name);
                  const lenderMoMData = monthNames.map(month => {
                    const monthDeals = fundedDeals.filter(d => d.Month === month);
                    const row = { month: month.substring(0, 3) };
                    top5Lenders.forEach(lender => {
                      row[lender] = monthDeals.filter(d => (d.Lender || 'Unknown') === lender).length;
                    });
                    return row;
                  });

                  // Calculate concentration trend (Top 5 % of total per month)
                  const concentrationTrend = monthNames.map(month => {
                    const monthDeals = fundedDeals.filter(d => d.Month === month);
                    const top5Count = monthDeals.filter(d => top5Lenders.includes(d.Lender || 'Unknown')).length;
                    const totalCount = monthDeals.length;
                    return {
                      month: month.substring(0, 3),
                      concentration: totalCount > 0 ? (top5Count / totalCount) * 100 : 0
                    };
                  });

                  const lenderColors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];

                  return (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '20px' }}>
                        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', textAlign: 'center', borderTop: '3px solid #3b82f6', position: 'relative' }}>
                          {developerMode && <div className="dev-card-label">K-4</div>}
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Lenders Used</div>
                          <div style={{ fontSize: '24px', fontWeight: 700, color: '#3b82f6' }}>{totalLenders}</div>
                        </div>
                        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', textAlign: 'center', borderTop: '3px solid #8b5cf6', position: 'relative' }}>
                          {developerMode && <div className="dev-card-label">K-5</div>}
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Top Lender</div>
                          <div style={{ fontSize: '16px', fontWeight: 700, color: '#8b5cf6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{topLender}</div>
                        </div>
                        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', textAlign: 'center', borderTop: '3px solid #10b981', position: 'relative' }}>
                          {developerMode && <div className="dev-card-label">K-6</div>}
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Funded Amount</div>
                          <div style={{ fontSize: '20px', fontWeight: 700, color: '#10b981' }}>{formatCurrency(totalFundedAmount)}</div>
                        </div>
                        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', textAlign: 'center', borderTop: '3px solid #f59e0b', position: 'relative' }}>
                          {developerMode && <div className="dev-card-label">K-7</div>}
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Revenue</div>
                          <div style={{ fontSize: '20px', fontWeight: 700, color: '#f59e0b' }}>{formatCurrency(totalRevenue)}</div>
                        </div>
                        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', textAlign: 'center', borderTop: '3px solid #6b7280', position: 'relative' }}>
                          {developerMode && <div className="dev-card-label">K-8</div>}
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Funded Deals</div>
                          <div style={{ fontSize: '24px', fontWeight: 700, color: '#374151' }}>{totalFundedDeals}</div>
                        </div>
                      </div>

                      {/* Row 2: Lender Performance Table */}
                      <div style={{ marginBottom: '20px', overflowX: 'auto', position: 'relative' }}>
                        {developerMode && <div className="dev-table-label">T-1</div>}
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                          <thead>
                            <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Lender</th>
                              <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>Funded Deals</th>
                              <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>Amount</th>
                              <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>Revenue</th>
                              <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>Avg Deal Size</th>
                              <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>% of Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lenders.slice(0, 10).map((lender, idx) => (
                              <tr key={lender.name} style={{ borderBottom: '1px solid #e5e7eb', background: idx % 2 === 0 ? '#fff' : '#f9fafb' }}>
                                <td style={{ padding: '10px 8px', fontWeight: 500 }}>{lender.name}</td>
                                <td style={{ padding: '10px 8px', textAlign: 'right' }}>{lender.deals}</td>
                                <td style={{ padding: '10px 8px', textAlign: 'right' }}>{formatCurrency(lender.amount)}</td>
                                <td style={{ padding: '10px 8px', textAlign: 'right' }}>{formatCurrency(lender.revenue)}</td>
                                <td style={{ padding: '10px 8px', textAlign: 'right' }}>{formatCurrency(lender.avgDealSize)}</td>
                                <td style={{ padding: '10px 8px', textAlign: 'right' }}>{totalFundedDeals > 0 ? ((lender.deals / totalFundedDeals) * 100).toFixed(1) : 0}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Row 3: 2 Charts - Top 5 Lenders MoM (Stacked Bar) & Lender Concentration (Line) */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                        <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
                          {developerMode && <div className="dev-chart-label">C-5</div>}
                          <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Top 5 Lenders MoM</h4>
                          <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={lenderMoMData}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                              <YAxis tick={{ fontSize: 10 }} />
                              <Tooltip />
                              <Legend wrapperStyle={{ fontSize: '10px' }} />
                              {top5Lenders.map((lender, idx) => (
                                <Bar
                                  key={lender}
                                  dataKey={lender}
                                  name={lender}
                                  stackId="lenders"
                                  fill={lenderColors[idx % lenderColors.length]}
                                />
                              ))}
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                        <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
                          {developerMode && <div className="dev-chart-label">C-6</div>}
                          <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Lender Concentration (Top 5 %)</h4>
                          <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={concentrationTrend}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                              <YAxis tick={{ fontSize: 10 }} unit="%" domain={[0, 100]} />
                              <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                              <Line type="monotone" dataKey="concentration" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }}>
                                <LabelList dataKey="concentration" position="top" formatter={(v) => `${v.toFixed(0)}%`} style={{ fontSize: 9, fill: '#8b5cf6', fontWeight: 600 }} />
                              </Line>
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>

        {/* ========== SECTION: RANKINGS & LEADERBOARDS (Collapsible) ========== */}
        <div ref={teamSectionRefs.rankings} style={{
          background: '#fff',
          borderRadius: '12px',
          marginBottom: '24px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          scrollMarginTop: '80px',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-section-label">S-4</div>}
          {/* Collapsible Header - Amber */}
          <div
            onClick={() => toggleTeamSection('rankings')}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              background: '#f59e0b',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>🏆</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>
                Rankings & Leaderboards
              </h3>
            </div>
            <span style={{ color: 'white', fontSize: '14px' }}>
              {teamCollapsed['rankings'] ? '▶' : '▼'}
            </span>
          </div>
          {/* Collapsible Content */}
          {!teamCollapsed['rankings'] && (
            <div style={{ padding: '20px' }}>
          {(() => {
            const localCurrentMonth = monthNames[monthNames.length - 1];
            // Use current year for goal lookup
            const localGoalMonth = `${localCurrentMonth} ${currentYear}`;

            // This Month calculations
            const thisMonthStats = reps.map(rep => {
              const data = allHistoricalData[localCurrentMonth]?.[rep];
              const goalRow = goals?.find(g => g.Month === localGoalMonth && g.Rep === rep);
              const goal = goalRow ? parseInt(goalRow.Revenue_Goal) : 0;
              return {
                rep,
                revenue: data?.revenue || 0,
                funded: data?.funded || 0,
                quota: goal > 0 ? ((data?.revenue || 0) / goal) * 100 : 0,
                activity: (data?.calls || 0) + (data?.sms || 0)
              };
            });

            // All-time calculations
            const allTimeStats = reps.map(rep => {
              let totalApps = 0;
              let totalFunded = 0;
              let totalRevenue = 0;
              const monthlyRevenues = [];
              monthNames.forEach(month => {
                const data = allHistoricalData[month]?.[rep];
                if (data) {
                  totalApps += data.appsIn || 0;
                  totalFunded += data.funded || 0;
                  totalRevenue += data.revenue || 0;
                  // Only count months where rep has a goal (active months)
                  // Note: For historical goals, we use month + year format
                  const monthGoalStr = `${month} ${currentYear}`;
                  const hasGoal = goals?.find(g => g.Month === monthGoalStr && g.Rep === rep);
                  if (hasGoal && data.revenue > 0) {
                    monthlyRevenues.push(data.revenue);
                  }
                }
              });
              // Calculate variance (lower = more consistent)
              let variance = 0;
              if (monthlyRevenues.length > 1) {
                const mean = monthlyRevenues.reduce((a, b) => a + b, 0) / monthlyRevenues.length;
                variance = monthlyRevenues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / monthlyRevenues.length;
              }
              return {
                rep,
                conversion: totalApps > 0 ? (totalFunded / totalApps) * 100 : 0,
                avgDealSize: totalFunded > 0 ? totalRevenue / totalFunded : 0,
                variance: variance,
                avgRevenue: monthlyRevenues.length > 0 ? totalRevenue / monthlyRevenues.length : 0
              };
            });

            const byRevenue = [...thisMonthStats].sort((a, b) => b.revenue - a.revenue);
            const byFunded = [...thisMonthStats].sort((a, b) => b.funded - a.funded);
            const byQuota = [...thisMonthStats].filter(s => s.quota > 0).sort((a, b) => b.quota - a.quota);
            const byActivity = [...thisMonthStats].sort((a, b) => b.activity - a.activity);
            const byConversion = [...allTimeStats].sort((a, b) => b.conversion - a.conversion);
            const byDealSize = [...allTimeStats].sort((a, b) => b.avgDealSize - a.avgDealSize);
            // Consistency: lowest variance wins (filter out zero variance = only 1 month of data)
            const byConsistency = [...allTimeStats]
              .filter(s => s.avgRevenue > 0)
              .sort((a, b) => a.variance - b.variance);

            const rankEmoji = (idx) => idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`;

            const LeaderCard = ({ title, timeframe, data, formatValue, cardNumber }) => (
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', position: 'relative' }}>
                {developerMode && <div className="dev-card-label">K-{cardNumber}</div>}
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>{title}</div>
                <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: '12px', textTransform: 'uppercase' }}>{timeframe}</div>
                {data.map((item, idx) => (
                  <div key={item.rep} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: idx < data.length - 1 ? '1px solid #f3f4f6' : 'none', fontSize: '0.9rem' }}>
                    <span style={{ color: idx < 3 ? '#374151' : '#6b7280' }}>{rankEmoji(idx)} {item.rep.split(' ')[0]}</span>
                    <span style={{ fontWeight: 600, color: idx === 0 ? '#10b981' : idx < 3 ? '#374151' : '#9ca3af' }}>{formatValue(item)}</span>
                  </div>
                ))}
              </div>
            );

            return (
              <>
              {/* Row 1: RESULTS & HUSTLE (4 cards) */}
              <div style={{ marginBottom: '8px', marginTop: '16px' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Results & Hustle</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
                <LeaderCard title="💰 Revenue Leader" timeframe={currentMonth} data={byRevenue} formatValue={(d) => formatCurrency(d.revenue)} cardNumber={1} />
                <LeaderCard title="🏆 Most Funded" timeframe={currentMonth} data={byFunded} formatValue={(d) => `${d.funded} deals`} cardNumber={2} />
                <LeaderCard title="🎯 Quota Crusher" timeframe={currentMonth} data={byQuota} formatValue={(d) => `${d.quota.toFixed(0)}%`} cardNumber={3} />
                <LeaderCard title="📞 Activity King" timeframe={currentMonth} data={byActivity} formatValue={(d) => d.activity.toLocaleString()} cardNumber={4} />
              </div>

              {/* Row 2: EFFICIENCY & CONSISTENCY (3 cards) */}
              <div style={{ marginBottom: '8px' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Efficiency & Consistency</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                <LeaderCard title="📈 Best Converter" timeframe="All-Time" data={byConversion} formatValue={(d) => `${d.conversion.toFixed(1)}%`} cardNumber={5} />
                <LeaderCard title="💼 Highest Avg Deal" timeframe="All-Time" data={byDealSize} formatValue={(d) => formatCurrency(d.avgDealSize)} cardNumber={6} />
                <LeaderCard title="📊 Consistency King" timeframe="All-Time" data={byConsistency} formatValue={(d) => formatCurrency(d.avgRevenue)} cardNumber={7} />
              </div>
              </>
            );
          })()}
            </div>
          )}
        </div>

        {/* ========== SECTION: TEAM FUNDING (Collapsible - 3 charts combined) ========== */}
        <div ref={teamSectionRefs.funding} style={{
          background: '#fff',
          borderRadius: '12px',
          marginBottom: '24px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          scrollMarginTop: '80px',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-section-label">S-5</div>}
          {/* Collapsible Header - Green */}
          <div
            onClick={() => toggleTeamSection('funding')}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              background: '#10b981',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>💰</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>
                Team Revenue & Funding
              </h3>
            </div>
            <span style={{ color: 'white', fontSize: '14px' }}>
              {teamCollapsed['funding'] ? '▶' : '▼'}
            </span>
          </div>
          {/* Collapsible Content */}
          {!teamCollapsed['funding'] && (
            <div style={{ padding: '20px' }}>
              {/* Chart 1: Team Revenue vs Goal */}
              <div style={{ marginBottom: '24px', position: 'relative' }}>
                {developerMode && <div className="dev-chart-label">C-1</div>}
                <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600, color: '#374151' }}>💰 Team Revenue vs Goal</h4>
                <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>Monthly team revenue compared to combined team goals.</p>
                <ResponsiveContainer width="100%" height={280}>
            <LineChart data={monthNames.map(month => {
              let teamRevenue = 0;
              let teamGoal = 0;
              const monthGoalStr = `${month} ${currentYear}`;
              reps.forEach(rep => {
                teamRevenue += allHistoricalData[month]?.[rep]?.revenue || 0;
                const goalRow = goals?.find(g => g.Month === monthGoalStr && g.Rep === rep);
                if (goalRow) teamGoal += parseInt(goalRow.Revenue_Goal) || 0;
              });
              return { month, revenue: teamRevenue, goal: teamGoal };
            })} margin={{ right: 40 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Line type="monotone" dataKey="revenue" name="Team Revenue" stroke="#10b981" strokeWidth={3} dot={{ r: 5 }}>
                <LabelList dataKey="revenue" position="top" formatter={(v) => `${(v/1000).toFixed(0)}k`} style={{ fontSize: 11, fontWeight: 600, fill: '#10b981' }} />
              </Line>
              <Line type="monotone" dataKey="goal" name="Team Goal" stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4 }} />
                </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Chart 2: Team Funded Deals */}
              <div style={{ marginBottom: '24px', position: 'relative' }}>
                {developerMode && <div className="dev-chart-label">C-2</div>}
                <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600, color: '#374151' }}>🏆 Team Funded Deals</h4>
                <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>Monthly funded deal count across all reps.</p>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={monthNames.map(month => {
                    let teamFunded = 0;
                    reps.forEach(rep => {
                      teamFunded += allHistoricalData[month]?.[rep]?.funded || 0;
                    });
                    return { month, funded: teamFunded };
                  })}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis domain={[0, "auto"]} padding={{ top: 20 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="funded" name="Funded Deals" fill="#8b5cf6">
                      <LabelList dataKey="funded" position="top" style={{ fontSize: 12, fontWeight: 600, fill: '#8b5cf6' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Chart 3: Team Apps In */}
              <div style={{ position: 'relative' }}>
                {developerMode && <div className="dev-chart-label">C-3</div>}
                <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600, color: '#374151' }}>📋 Team Apps In</h4>
                <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>Monthly applications submitted across all reps.</p>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={monthNames.map(month => {
                    let teamApps = 0;
                    reps.forEach(rep => {
                      teamApps += allHistoricalData[month]?.[rep]?.appsIn || 0;
                    });
                    return { month, appsIn: teamApps };
                  })}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis domain={[0, "auto"]} padding={{ top: 20 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="appsIn" name="Apps In" fill="#3b82f6">
                      <LabelList dataKey="appsIn" position="top" style={{ fontSize: 12, fontWeight: 600, fill: '#3b82f6' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        {/* ========== SECTION: TEAM ACTIVITY (Collapsible) ========== */}
        <div ref={teamSectionRefs.activity} style={{
          background: '#fff',
          borderRadius: '12px',
          marginBottom: '24px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          scrollMarginTop: '80px',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-section-label">S-6</div>}
          {/* Collapsible Header - Blue */}
          <div
            onClick={() => toggleTeamSection('activity')}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              background: '#3b82f6',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>📞</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>
                Team Activity Volume
              </h3>
            </div>
            <span style={{ color: 'white', fontSize: '14px' }}>
              {teamCollapsed['activity'] ? '▶' : '▼'}
            </span>
          </div>
          {/* Collapsible Content */}
          {!teamCollapsed['activity'] && (
            <div style={{ padding: '20px', position: 'relative' }}>
              {developerMode && <div className="dev-chart-label">C-1</div>}
              <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>Stacked Calls/SMS bar showing % breakdown. Total shown separately.</p>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={monthNames.map(month => {
                  let teamCalls = 0;
                  let teamSMS = 0;
                  reps.forEach(rep => {
                    teamCalls += allHistoricalData[month]?.[rep]?.calls || 0;
                    teamSMS += allHistoricalData[month]?.[rep]?.sms || 0;
                  });
                  const total = teamCalls + teamSMS;
                  const callsPct = total > 0 ? Math.round((teamCalls / total) * 100) : 0;
                  const smsPct = total > 0 ? Math.round((teamSMS / total) * 100) : 0;
                  return { month, calls: teamCalls, sms: teamSMS, total, callsPct, smsPct };
                })}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, "auto"]} padding={{ top: 20 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="calls" name="Calls" fill="#3b82f6" stackId="activity">
                    <LabelList dataKey="callsPct" position="center" formatter={(v) => `${v}%`} style={{ fontSize: 11, fontWeight: 700, fill: '#fff' }} />
                  </Bar>
                  <Bar dataKey="sms" name="SMS" fill="#ef4444" stackId="activity">
                    <LabelList dataKey="smsPct" position="center" formatter={(v) => `${v}%`} style={{ fontSize: 11, fontWeight: 700, fill: '#fff' }} />
                  </Bar>
                  <Bar dataKey="total" name="Total Activity" fill="#10b981">
                    <LabelList dataKey="total" position="top" style={{ fontSize: 11, fontWeight: 600, fill: '#10b981' }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* ========== SECTION: PACK SOURCE ANALYSIS (Redesigned - Dec 2025) ========== */}
        <div ref={teamSectionRefs.packSource} style={{
          background: '#fff',
          borderRadius: '12px',
          marginBottom: '24px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          scrollMarginTop: '80px',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-section-label">S-7</div>}
          {/* Collapsible Header - Purple */}
          <div
            onClick={() => toggleTeamSection('packSource')}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              background: '#8b5cf6',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>📊</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>
                Pack Source Analysis
              </h3>
            </div>
            <span style={{ color: 'white', fontSize: '14px' }}>
              {teamCollapsed['packSource'] ? '▶' : '▼'}
            </span>
          </div>
          {/* Collapsible Content */}
          {!teamCollapsed['packSource'] && (
            <div style={{ padding: '20px' }}>
              {(() => {
                // Calculate Pack Source metrics
                const appsIn = data?.tabs?.Apps_In?.data || [];
                const funded = data?.tabs?.Funded?.data || [];
                const revenue = data?.tabs?.Revenue?.data || [];

                // Get unique sources
                const sourceSet = new Set();
                [...appsIn, ...funded].forEach(r => sourceSet.add(r.Pack_Source || 'Missing'));
                const sources = Array.from(sourceSet);

                // Calculate metrics per source
                const sourceMetrics = sources.map(source => {
                  const sourceApps = appsIn.filter(r => (r.Pack_Source || 'Missing') === source).length;
                  const sourceFunded = funded.filter(r => (r.Pack_Source || 'Missing') === source).length;
                  const sourceRevenue = revenue.filter(r => (r.Pack_Source || 'Missing') === source)
                    .reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);
                  const conversionRate = sourceApps > 0 ? (sourceFunded / sourceApps) * 100 : 0;
                  return { source, apps: sourceApps, funded: sourceFunded, revenue: sourceRevenue, conversionRate };
                }).sort((a, b) => b.apps - a.apps);

                const totalApps = sourceMetrics.reduce((sum, s) => sum + s.apps, 0);
                const topSource = sourceMetrics[0]?.source || 'N/A';
                const bestConverting = [...sourceMetrics].sort((a, b) => b.conversionRate - a.conversionRate)[0];
                const highestRevenue = [...sourceMetrics].sort((a, b) => b.revenue - a.revenue)[0];

                // Pie chart data
                const pieData = sourceMetrics.slice(0, 5).map(s => ({
                  name: s.source,
                  value: s.apps
                }));

                const pieColors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];

                return (
                  <>
                    {/* Row 1: Pie chart + 3 cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                      {/* Pie Chart: Source Distribution */}
                      <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
                        {developerMode && <div className="dev-chart-label">C-1</div>}
                        <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Source Distribution</h4>
                        <ResponsiveContainer width="100%" height={180}>
                          <PieChart>
                            <Pie
                              data={pieData}
                              cx="50%"
                              cy="50%"
                              innerRadius={35}
                              outerRadius={60}
                              paddingAngle={2}
                              dataKey="value"
                            >
                              {pieData.map((entry, idx) => (
                                <Cell key={`cell-${idx}`} fill={pieColors[idx % pieColors.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: '10px' }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Card 1: Top Source */}
                      <div style={{ background: '#eff6ff', padding: '16px', borderRadius: '8px', border: '1px solid #bfdbfe', textAlign: 'center', position: 'relative' }}>
                        {developerMode && <div className="dev-card-label">K-1</div>}
                        <div style={{ fontSize: '12px', color: '#1d4ed8', marginBottom: '4px' }}>Top Source</div>
                        <div style={{ fontSize: '20px', fontWeight: 700, color: '#3b82f6', marginBottom: '4px' }}>{topSource}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>{sourceMetrics[0]?.apps || 0} apps ({totalApps > 0 ? ((sourceMetrics[0]?.apps / totalApps) * 100).toFixed(0) : 0}%)</div>
                      </div>

                      {/* Card 2: Best Converting Source */}
                      <div style={{ background: '#f0fdf4', padding: '16px', borderRadius: '8px', border: '1px solid #86efac', textAlign: 'center', position: 'relative' }}>
                        {developerMode && <div className="dev-card-label">K-2</div>}
                        <div style={{ fontSize: '12px', color: '#166534', marginBottom: '4px' }}>Best Converting</div>
                        <div style={{ fontSize: '20px', fontWeight: 700, color: '#059669', marginBottom: '4px' }}>{bestConverting?.source || 'N/A'}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>{bestConverting?.conversionRate?.toFixed(1) || 0}% conversion</div>
                      </div>

                      {/* Card 3: Highest Revenue Source */}
                      <div style={{ background: '#fef3c7', padding: '16px', borderRadius: '8px', border: '1px solid #fcd34d', textAlign: 'center', position: 'relative' }}>
                        {developerMode && <div className="dev-card-label">K-3</div>}
                        <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '4px' }}>Highest Revenue</div>
                        <div style={{ fontSize: '20px', fontWeight: 700, color: '#f59e0b', marginBottom: '4px' }}>{highestRevenue?.source || 'N/A'}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>{formatCurrency(highestRevenue?.revenue || 0)}</div>
                      </div>
                    </div>

                    {/* Row 2: 2 Charts - Source Mix Trend & Conversion by Source */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                      {/* Chart: Source Mix Trend (Stacked Bar) */}
                      <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
                        {developerMode && <div className="dev-chart-label">C-2</div>}
                        <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Source Mix Trend (MoM)</h4>
                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={monthlyPackSourceTrend.data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: '10px' }} />
                            {monthlyPackSourceTrend.sources.slice(0, 5).map((source, idx) => (
                              <Bar
                                key={source}
                                dataKey={source}
                                name={source}
                                stackId="source"
                                fill={source === 'Missing' ? '#9ca3af' : pieColors[idx % pieColors.length]}
                              />
                            ))}
                          </BarChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Chart: Conversion by Source (Line Chart MoM) */}
                      <div style={{ background: '#f9fafb', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', position: 'relative' }}>
                        {developerMode && <div className="dev-chart-label">C-3</div>}
                        <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#374151' }}>Conversion by Source (MoM)</h4>
                        {(() => {
                          const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                          const top5Sources = sourceMetrics.slice(0, 5).map(s => s.source);
                          const conversionMoMData = monthNames.map(month => {
                            const monthApps = appsIn.filter(r => r.Month === month);
                            const monthFunded = funded.filter(r => r.Month === month);
                            const row = { month: month.substring(0, 3) };
                            top5Sources.forEach(source => {
                              const sourceAppsCount = monthApps.filter(r => (r.Pack_Source || 'Missing') === source).length;
                              const sourceFundedCount = monthFunded.filter(r => (r.Pack_Source || 'Missing') === source).length;
                              row[source] = sourceAppsCount > 0 ? ((sourceFundedCount / sourceAppsCount) * 100) : 0;
                            });
                            return row;
                          });
                          const sourceColors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];
                          return (
                            <ResponsiveContainer width="100%" height={250}>
                              <LineChart data={conversionMoMData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                                <YAxis unit="%" domain={[0, 100]} tick={{ fontSize: 10 }} />
                                <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                                <Legend wrapperStyle={{ fontSize: '10px' }} />
                                {top5Sources.map((source, idx) => (
                                  <Line
                                    key={source}
                                    type="monotone"
                                    dataKey={source}
                                    name={source}
                                    stroke={sourceColors[idx % sourceColors.length]}
                                    strokeWidth={2}
                                    dot={{ r: 3 }}
                                  />
                                ))}
                              </LineChart>
                            </ResponsiveContainer>
                          );
                        })()}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* ========== SECTION: MONTHLY PERFORMANCE TABLE (Collapsible + Expandable - Phase 7) ========== */}
        <div ref={teamSectionRefs.table} style={{
          background: '#fff',
          borderRadius: '12px',
          marginBottom: '24px',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          scrollMarginTop: '80px',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-section-label">S-8</div>}
          {/* Collapsible Header - Gray */}
          <div
            onClick={() => toggleTeamSection('table')}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              background: '#6b7280',
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '18px' }}>📊</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'white' }}>
                Monthly Performance by Rep
              </h3>
            </div>
            <span style={{ color: 'white', fontSize: '14px' }}>
              {teamCollapsed['table'] ? '▶' : '▼'}
            </span>
          </div>
          {/* Collapsible Content */}
          {!teamCollapsed['table'] && (
            <div style={{ padding: '20px' }}>
              <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
                Key metrics for each rep by month. Click a rep name to expand/collapse their monthly data.
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600 }}>Rep</th>
                      <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>Revenue</th>
                      <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>Funded</th>
                      <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>Apps In</th>
                      <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>Calls</th>
                      <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>Conversion</th>
                      <th style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600 }}>Goal %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reps.map((rep, repIdx) => {
                      const repMonths = monthNames.filter(month => {
                        const data = allHistoricalData[month]?.[rep];
                        const monthGoalStr = `${month} ${currentYear}`;
                        const hasGoal = goals?.find(g => g.Month === monthGoalStr && g.Rep === rep);
                        return data && hasGoal;
                      });
                      if (repMonths.length === 0) return null;

                      // Calculate summary stats for this rep
                      let totalRevenue = 0, totalFunded = 0, totalApps = 0, totalCalls = 0, totalGoal = 0;
                      repMonths.forEach(month => {
                        const data = allHistoricalData[month]?.[rep];
                        const monthGoalStr = `${month} ${currentYear}`;
                        const goalRow = goals?.find(g => g.Month === monthGoalStr && g.Rep === rep);
                        if (data) {
                          totalRevenue += data.revenue || 0;
                          totalFunded += data.funded || 0;
                          totalApps += data.appsIn || 0;
                          totalCalls += data.calls || 0;
                        }
                        if (goalRow) {
                          totalGoal += parseInt(goalRow.Revenue_Goal) || 0;
                        }
                      });
                      const avgConversion = totalApps > 0 ? (totalFunded / totalApps) * 100 : 0;
                      const avgGoalAttainment = totalGoal > 0 ? (totalRevenue / totalGoal) * 100 : 0;

                      return (
                        <React.Fragment key={rep}>
                          {/* Clickable Rep Summary Row */}
                          <tr
                            onClick={() => toggleRepRow(rep)}
                            style={{
                              background: '#e5e7eb',
                              cursor: 'pointer',
                              borderBottom: '1px solid #d1d5db'
                            }}
                          >
                            <td style={{ padding: '10px 12px', fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                              {expandedRepRows[rep] ? '▼' : '▶'} {rep}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: '#10b981' }}>
                              {formatCurrency(totalRevenue)}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600 }}>
                              {totalFunded}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600 }}>
                              {totalApps}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600 }}>
                              {totalCalls.toLocaleString()}
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: avgConversion > 50 ? '#10b981' : avgConversion > 30 ? '#f59e0b' : '#ef4444' }}>
                              {avgConversion.toFixed(0)}%
                            </td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: avgGoalAttainment >= 100 ? '#10b981' : avgGoalAttainment >= 80 ? '#f59e0b' : '#ef4444' }}>
                              {avgGoalAttainment.toFixed(0)}%
                            </td>
                          </tr>
                          {/* Expandable Monthly Rows */}
                          {expandedRepRows[rep] && [...repMonths].reverse().map((month, mIdx) => {
                            const data = allHistoricalData[month]?.[rep];
                            const monthGoalStr = `${month} ${currentYear}`;
                            const goalRow = goals?.find(g => g.Month === monthGoalStr && g.Rep === rep);
                            const revenueGoal = goalRow ? parseInt(goalRow.Revenue_Goal) : 0;
                            const goalAttainment = revenueGoal > 0 ? (data.revenue / revenueGoal) * 100 : 0;
                            return (
                              <tr key={`${rep}-${month}`} style={{
                                borderBottom: '1px solid #e5e7eb',
                                background: mIdx % 2 === 0 ? '#fff' : '#fafafa'
                              }}>
                                <td style={{ padding: '8px 12px', paddingLeft: '32px', color: '#6b7280' }}>{month}</td>
                                <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#10b981' }}>{formatCurrency(data.revenue)}</td>
                                <td style={{ padding: '8px 12px', textAlign: 'right' }}>{data.funded}</td>
                                <td style={{ padding: '8px 12px', textAlign: 'right' }}>{data.appsIn}</td>
                                <td style={{ padding: '8px 12px', textAlign: 'right' }}>{data.calls}</td>
                                <td style={{ padding: '8px 12px', textAlign: 'right', color: data.appsToFunded > 50 ? '#10b981' : data.appsToFunded > 30 ? '#f59e0b' : '#ef4444' }}>
                                  {data.appsToFunded.toFixed(0)}%
                                </td>
                                <td style={{ padding: '8px 12px', textAlign: 'right', color: goalAttainment >= 100 ? '#10b981' : goalAttainment >= 80 ? '#f59e0b' : '#ef4444' }}>
                                  {goalAttainment.toFixed(0)}%
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        </>
      )}

    </div>
  );
}

/**
 * ============================================================================
 * NEW EMAIL ANALYTICS TAB (December 2025)
 * ============================================================================
 * Fresh implementation using Apollo email metrics data:
 * - Apollo_Daily_Metrics: Daily data per mailbox (UPSERT 4x daily)
 * - Apollo_Weekly_History: Weekly YTD backfill (246 rows)
 * - Apollo_Rep_Performance: Rolling 30-day view
 *
 * Sub-tabs: Today, Weekly Trends, Rep Comparison
 * ============================================================================
 */
function EmailAnalyticsTab({ apolloEmailData, selectedRep, reps, activeEmailTab, setActiveEmailTab, developerMode }) {
  // Extract data from the hook result
  const dailyMetrics = apolloEmailData?.dailyMetrics || [];
  const weeklyHistory = apolloEmailData?.weeklyHistory || [];
  const repPerformance = apolloEmailData?.repPerformance || [];

  return (
    <div className="email-tab-container" style={{ padding: '20px' }}>
      {/* Sub-tab navigation */}
      <nav className="email-sub-tabs" style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        paddingBottom: '12px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <button
          onClick={() => setActiveEmailTab('today')}
          style={{
            padding: '8px 16px',
            backgroundColor: activeEmailTab === 'today' ? '#3b82f6' : 'transparent',
            color: activeEmailTab === 'today' ? 'white' : '#64748b',
            border: activeEmailTab === 'today' ? 'none' : '1px solid #cbd5e1',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Today
        </button>
        <button
          onClick={() => setActiveEmailTab('weekly-trends')}
          style={{
            padding: '8px 16px',
            backgroundColor: activeEmailTab === 'weekly-trends' ? '#3b82f6' : 'transparent',
            color: activeEmailTab === 'weekly-trends' ? 'white' : '#64748b',
            border: activeEmailTab === 'weekly-trends' ? 'none' : '1px solid #cbd5e1',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Weekly Trends
        </button>
        <button
          onClick={() => setActiveEmailTab('rep-comparison')}
          style={{
            padding: '8px 16px',
            backgroundColor: activeEmailTab === 'rep-comparison' ? '#3b82f6' : 'transparent',
            color: activeEmailTab === 'rep-comparison' ? 'white' : '#64748b',
            border: activeEmailTab === 'rep-comparison' ? 'none' : '1px solid #cbd5e1',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Rep Comparison
        </button>
      </nav>

      {/* Sub-tab content */}
      {activeEmailTab === 'today' && (
        <EmailTodaySubTab dailyMetrics={dailyMetrics} selectedRep={selectedRep} reps={reps} developerMode={developerMode} />
      )}
      {activeEmailTab === 'weekly-trends' && (
        <EmailWeeklyTrendsSubTab weeklyHistory={weeklyHistory} dailyMetrics={dailyMetrics} selectedRep={selectedRep} developerMode={developerMode} />
      )}
      {activeEmailTab === 'rep-comparison' && (
        <EmailRepComparisonSubTab repPerformance={repPerformance} dailyMetrics={dailyMetrics} selectedRep={selectedRep} reps={reps} developerMode={developerMode} />
      )}
    </div>
  );
}

/**
 * EMAIL TODAY SUB-TAB
 * Shows today's live email metrics
 * Data: Apollo_Daily_Metrics filtered to today
 */
function EmailTodaySubTab({ dailyMetrics, selectedRep, reps, developerMode }) {
  // Get today's date in YYYY-MM-DD format (matches sheet format)
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0]; // e.g., "2025-12-15"

  // Also try MM/DD/YYYY format in case sheet uses that
  const todayAlt = `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`; // e.g., "12/15/2025"

  // Filter to today's data
  const todayData = useMemo(() => {
    if (!dailyMetrics || !dailyMetrics.length) return [];

    return dailyMetrics.filter(row => {
      const rowDate = row.Date || row.date || '';
      // Match either format
      return rowDate === todayStr ||
             rowDate === todayAlt ||
             rowDate.startsWith(todayStr) ||
             (rowDate.includes('/') && rowDate.includes(String(today.getDate())) && rowDate.includes(String(today.getMonth() + 1)));
    });
  }, [dailyMetrics, todayStr, todayAlt]);

  // Aggregate totals for today
  const totals = useMemo(() => {
    const result = {
      sent: 0,
      delivered: 0,
      opened: 0,
      replied: 0,
      bounced: 0
    };

    todayData.forEach(row => {
      result.sent += parseInt(row.Sent || row.sent || 0, 10);
      result.delivered += parseInt(row.Delivered || row.delivered || 0, 10);
      result.opened += parseInt(row.Opened || row.opened || 0, 10);
      result.replied += parseInt(row.Replied || row.replied || 0, 10);
      result.bounced += parseInt(row.Bounced || row.bounced || 0, 10);
    });

    return result;
  }, [todayData]);

  // Calculate percentages
  const deliveryRate = totals.sent > 0 ? ((totals.delivered / totals.sent) * 100).toFixed(1) : '0.0';
  const openRate = totals.delivered > 0 ? ((totals.opened / totals.delivered) * 100).toFixed(1) : '0.0';
  const replyRate = totals.delivered > 0 ? ((totals.replied / totals.delivered) * 100).toFixed(1) : '0.0';
  const bounceRate = totals.sent > 0 ? ((totals.bounced / totals.sent) * 100).toFixed(1) : '0.0';

  // Group by rep for bar chart
  const repData = useMemo(() => {
    const byRep = {};

    todayData.forEach(row => {
      const repName = row['Rep Name'] || row.rep_name || row.Rep || 'Unknown';
      if (!byRep[repName]) {
        byRep[repName] = { sent: 0, delivered: 0, opened: 0, replied: 0, bounced: 0 };
      }
      byRep[repName].sent += parseInt(row.Sent || row.sent || 0, 10);
      byRep[repName].delivered += parseInt(row.Delivered || row.delivered || 0, 10);
      byRep[repName].opened += parseInt(row.Opened || row.opened || 0, 10);
      byRep[repName].replied += parseInt(row.Replied || row.replied || 0, 10);
      byRep[repName].bounced += parseInt(row.Bounced || row.bounced || 0, 10);
    });

    // Convert to array and sort by sent descending
    return Object.entries(byRep)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.sent - a.sent);
  }, [todayData]);

  // Deliverability data for pie chart
  const deliverabilityData = [
    { name: 'Delivered', value: totals.delivered, fill: '#22c55e' },
    { name: 'Bounced', value: totals.bounced, fill: '#ef4444' }
  ];

  // Health status based on delivery rate
  const getHealthStatus = () => {
    const rate = parseFloat(deliveryRate);
    if (rate >= 95) return { label: 'Healthy', color: '#22c55e', icon: '🟢' };
    if (rate >= 90) return { label: 'Warning', color: '#f59e0b', icon: '🟡' };
    return { label: 'Critical', color: '#ef4444', icon: '🔴' };
  };
  const healthStatus = getHealthStatus();

  // Format date for display
  const displayDate = today.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  return (
    <div className="email-today-section">
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: '0 0 4px 0', color: '#1e293b', fontSize: '24px' }}>
          Today's Email Activity
        </h2>
        <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>{displayDate}</p>
        {todayData.length === 0 && (
          <p style={{ margin: '10px 0 0 0', color: '#f59e0b', fontSize: '14px' }}>
            No data found for today yet. Data syncs at 4am, 11am, 2pm, and 6pm.
          </p>
        )}
      </div>

      {/* Metric Cards Row */}
      <div className="email-cards" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '16px',
        marginBottom: '24px'
      }}>
        {/* Sent Card */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-card-label">K-1</div>}
          <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px', fontWeight: 500 }}>SENT</div>
          <div style={{ fontSize: '32px', fontWeight: 700, color: '#1e293b' }}>
            {totals.sent.toLocaleString()}
          </div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>Team Total</div>
        </div>

        {/* Delivered Card */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-card-label">K-2</div>}
          <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px', fontWeight: 500 }}>DELIVERED</div>
          <div style={{ fontSize: '32px', fontWeight: 700, color: '#1e293b' }}>
            {totals.delivered.toLocaleString()}
          </div>
          <div style={{ fontSize: '13px', color: '#22c55e', marginTop: '4px', fontWeight: 600 }}>
            {deliveryRate}% delivery rate
          </div>
        </div>

        {/* Opened Card */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-card-label">K-3</div>}
          <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px', fontWeight: 500 }}>OPENED</div>
          <div style={{ fontSize: '32px', fontWeight: 700, color: '#1e293b' }}>
            {totals.opened.toLocaleString()}
          </div>
          <div style={{ fontSize: '13px', color: '#3b82f6', marginTop: '4px', fontWeight: 600 }}>
            {openRate}% open rate
          </div>
        </div>

        {/* Replied Card */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-card-label">K-4</div>}
          <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px', fontWeight: 500 }}>REPLIED</div>
          <div style={{ fontSize: '32px', fontWeight: 700, color: '#1e293b' }}>
            {totals.replied.toLocaleString()}
          </div>
          <div style={{ fontSize: '13px', color: '#8b5cf6', marginTop: '4px', fontWeight: 600 }}>
            {replyRate}% reply rate
          </div>
        </div>
      </div>

      {/* Two Column Layout: Deliverability + Rep Chart */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 2fr',
        gap: '24px'
      }}>
        {/* Deliverability Monitor */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-section-label">S-1</div>}
          <h3 style={{ margin: '0 0 16px 0', color: '#1e293b', fontSize: '16px', fontWeight: 600 }}>
            Deliverability Monitor
          </h3>

          {/* Pie Chart */}
          <div style={{ height: '200px', position: 'relative' }}>
            {developerMode && <div className="dev-chart-label">C-1</div>}
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={deliverabilityData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  dataKey="value"
                  stroke="none"
                >
                  {deliverabilityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => value.toLocaleString()} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Legend & Status */}
          <div style={{ marginTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#22c55e', fontWeight: 600 }}>
                ● Delivered: {totals.delivered.toLocaleString()} ({deliveryRate}%)
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
              <span style={{ color: '#ef4444', fontWeight: 600 }}>
                ● Bounced: {totals.bounced.toLocaleString()} ({bounceRate}%)
              </span>
            </div>
            <div style={{
              padding: '12px',
              backgroundColor: `${healthStatus.color}15`,
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <span style={{ color: healthStatus.color, fontWeight: 600, fontSize: '14px' }}>
                {healthStatus.icon} {healthStatus.label} ({deliveryRate}% delivery)
              </span>
            </div>
          </div>
        </div>

        {/* Rep Volume Chart */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-section-label">S-2</div>}
          <h3 style={{ margin: '0 0 16px 0', color: '#1e293b', fontSize: '16px', fontWeight: 600 }}>
            By Rep Today
          </h3>

          {repData.length > 0 ? (
            <div style={{ height: '280px', position: 'relative' }}>
              {developerMode && <div className="dev-chart-label">C-1</div>}
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={repData}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fill: '#64748b', fontSize: 12 }} />
                  <YAxis
                    dataKey="name"
                    type="category"
                    tick={{ fill: '#1e293b', fontSize: 13 }}
                    width={90}
                  />
                  <Tooltip
                    formatter={(value, name) => [value.toLocaleString(), name.charAt(0).toUpperCase() + name.slice(1)]}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                  />
                  <Bar dataKey="sent" fill="#3b82f6" name="Sent" radius={[0, 4, 4, 0]}>
                    <LabelList dataKey="sent" position="right" fill="#64748b" fontSize={12} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{
              height: '280px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748b'
            }}>
              No rep data available for today
            </div>
          )}
        </div>
      </div>

      {/* Debug info (only shown in dev) */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          marginTop: '20px',
          padding: '12px',
          backgroundColor: '#f1f5f9',
          borderRadius: '8px',
          fontSize: '12px',
          color: '#64748b'
        }}>
          Debug: {dailyMetrics.length} total rows | {todayData.length} rows for today ({todayStr})
        </div>
      )}
    </div>
  );
}

/**
 * EMAIL WEEKLY TRENDS SUB-TAB (NEW - December 2025)
 * Shows week-over-week email trends with volume and conversion rate charts
 * Data Source: Apollo_Weekly_History (aggregated by week) + Apollo_Daily_Metrics (current week)
 */
function EmailWeeklyTrendsSubTab({ weeklyHistory, dailyMetrics, selectedRep, developerMode }) {
  // Chart colors - consistent with existing dashboard
  const CHART_COLORS = {
    sent: '#3b82f6',      // Blue
    delivered: '#10b981', // Green
    opened: '#f59e0b',    // Amber
    replied: '#8b5cf6',   // Purple
    openRate: '#f59e0b',  // Amber
    replyRate: '#8b5cf6'  // Purple
  };

  // Process weekly history data - aggregate by week
  const weeklyAggregated = useMemo(() => {
    if (!weeklyHistory || weeklyHistory.length === 0) return [];

    // Group by Week_Start
    const weekMap = {};

    weeklyHistory.forEach(row => {
      // Filter by rep if selected
      if (selectedRep !== 'All Reps') {
        const repName = row['Rep Name'] || row['Rep_Name'] || '';
        if (repName !== selectedRep) return;
      }

      const weekStart = row['Week_Start'] || row['Week Start'] || '';
      if (!weekStart) return;

      if (!weekMap[weekStart]) {
        weekMap[weekStart] = {
          weekStart: weekStart,
          weekEnd: row['Week_End'] || row['Week End'] || '',
          sent: 0,
          delivered: 0,
          opened: 0,
          replied: 0,
          bounced: 0
        };
      }

      weekMap[weekStart].sent += parseInt(row['Sent'] || 0);
      weekMap[weekStart].delivered += parseInt(row['Delivered'] || 0);
      weekMap[weekStart].opened += parseInt(row['Opened'] || 0);
      weekMap[weekStart].replied += parseInt(row['Replied'] || 0);
      weekMap[weekStart].bounced += parseInt(row['Bounced'] || 0);
    });

    // Convert to array and sort by date descending
    const weeks = Object.values(weekMap);
    weeks.sort((a, b) => new Date(b.weekStart) - new Date(a.weekStart));

    // Take last 8 weeks and reverse for chronological display
    const last8Weeks = weeks.slice(0, 8).reverse();

    // Add calculated rates and week label
    return last8Weeks.map((week, index) => {
      const weekDate = new Date(week.weekStart);
      const weekNum = Math.ceil((weekDate - new Date(weekDate.getFullYear(), 0, 1)) / (7 * 24 * 60 * 60 * 1000)) + 1;

      return {
        ...week,
        weekLabel: `W${weekNum}`,
        openRate: week.delivered > 0 ? ((week.opened / week.delivered) * 100).toFixed(1) : 0,
        replyRate: week.delivered > 0 ? ((week.replied / week.delivered) * 100).toFixed(1) : 0
      };
    });
  }, [weeklyHistory, selectedRep]);

  // Calculate current week totals from daily metrics
  const currentWeekTotals = useMemo(() => {
    if (!dailyMetrics || dailyMetrics.length === 0) {
      return { sent: 0, delivered: 0, opened: 0, replied: 0, bounced: 0 };
    }

    // Get current week's Monday
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    monday.setHours(0, 0, 0, 0);

    const totals = { sent: 0, delivered: 0, opened: 0, replied: 0, bounced: 0 };

    dailyMetrics.forEach(row => {
      // Filter by rep if selected
      if (selectedRep !== 'All Reps') {
        const repName = row['Rep Name'] || row['Rep_Name'] || '';
        if (repName !== selectedRep) return;
      }

      // Parse date - handle multiple formats
      const dateStr = row['Date'] || '';
      let rowDate;
      if (dateStr.includes('-')) {
        rowDate = new Date(dateStr + 'T00:00:00');
      } else if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        rowDate = new Date(parts[2], parts[0] - 1, parts[1]);
      } else {
        return;
      }

      // Check if date is in current week
      if (rowDate >= monday) {
        totals.sent += parseInt(row['Sent'] || 0);
        totals.delivered += parseInt(row['Delivered'] || 0);
        totals.opened += parseInt(row['Opened'] || 0);
        totals.replied += parseInt(row['Replied'] || 0);
        totals.bounced += parseInt(row['Bounced'] || 0);
      }
    });

    return totals;
  }, [dailyMetrics, selectedRep]);

  // Get last complete week for comparison
  const lastWeekTotals = useMemo(() => {
    if (weeklyAggregated.length === 0) {
      return { sent: 0, delivered: 0, opened: 0, replied: 0, bounced: 0 };
    }
    // Last week is the most recent complete week (last item in chronological array)
    return weeklyAggregated[weeklyAggregated.length - 1];
  }, [weeklyAggregated]);

  // Calculate week-over-week changes
  const wowChanges = useMemo(() => {
    const calcChange = (current, previous) => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return ((current - previous) / previous * 100).toFixed(1);
    };

    const currentOpenRate = currentWeekTotals.delivered > 0
      ? (currentWeekTotals.opened / currentWeekTotals.delivered * 100) : 0;
    const currentReplyRate = currentWeekTotals.delivered > 0
      ? (currentWeekTotals.replied / currentWeekTotals.delivered * 100) : 0;

    const lastOpenRate = parseFloat(lastWeekTotals.openRate || 0);
    const lastReplyRate = parseFloat(lastWeekTotals.replyRate || 0);

    return {
      sent: calcChange(currentWeekTotals.sent, lastWeekTotals.sent),
      openRate: (currentOpenRate - lastOpenRate).toFixed(1),
      replyRate: (currentReplyRate - lastReplyRate).toFixed(1)
    };
  }, [currentWeekTotals, lastWeekTotals]);

  // Format number with commas
  const formatNum = (num) => (num || 0).toLocaleString();

  // Change indicator component
  const ChangeIndicator = ({ value, isPercentagePoint = false }) => {
    const numVal = parseFloat(value);
    const isPositive = numVal > 0;
    const isNeutral = numVal === 0;
    const unit = isPercentagePoint ? 'pp' : '%';

    return (
      <span style={{
        color: isNeutral ? '#64748b' : isPositive ? '#10b981' : '#ef4444',
        fontWeight: 600,
        fontSize: '14px'
      }}>
        {isPositive ? '+' : ''}{value}{unit}
      </span>
    );
  };

  // No data state
  if (weeklyAggregated.length === 0) {
    return (
      <div style={{ padding: '20px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
        <h2 style={{ margin: '0 0 20px 0', color: '#1e293b' }}>Weekly Email Trends</h2>
        <p style={{ color: '#64748b' }}>No weekly history data available. Data will populate as the Apollo sync runs.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
      {/* Header */}
      <h2 style={{ margin: '0 0 8px 0', color: '#1e293b' }}>Weekly Email Trends</h2>
      <p style={{ color: '#64748b', margin: '0 0 24px 0', fontSize: '14px' }}>
        Last 8 weeks {selectedRep !== 'All Reps' ? `- ${selectedRep}` : '- All Reps'}
      </p>

      {/* Week-over-Week Comparison Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '16px',
        marginBottom: '24px'
      }}>
        {/* Sent This Week */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '16px',
          border: '1px solid #e2e8f0',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-card-label">K-1</div>}
          <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 500, textTransform: 'uppercase', marginBottom: '4px' }}>
            Sent This Week
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
            {formatNum(currentWeekTotals.sent)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ChangeIndicator value={wowChanges.sent} />
            <span style={{ color: '#94a3b8', fontSize: '12px' }}>vs last week</span>
          </div>
        </div>

        {/* Open Rate This Week */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '16px',
          border: '1px solid #e2e8f0',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-card-label">K-2</div>}
          <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 500, textTransform: 'uppercase', marginBottom: '4px' }}>
            Open Rate
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
            {currentWeekTotals.delivered > 0
              ? (currentWeekTotals.opened / currentWeekTotals.delivered * 100).toFixed(1)
              : '0.0'}%
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ChangeIndicator value={wowChanges.openRate} isPercentagePoint={true} />
            <span style={{ color: '#94a3b8', fontSize: '12px' }}>vs last week</span>
          </div>
        </div>

        {/* Reply Rate This Week */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '16px',
          border: '1px solid #e2e8f0',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-card-label">K-3</div>}
          <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 500, textTransform: 'uppercase', marginBottom: '4px' }}>
            Reply Rate
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
            {currentWeekTotals.delivered > 0
              ? (currentWeekTotals.replied / currentWeekTotals.delivered * 100).toFixed(1)
              : '0.0'}%
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ChangeIndicator value={wowChanges.replyRate} isPercentagePoint={true} />
            <span style={{ color: '#94a3b8', fontSize: '12px' }}>vs last week</span>
          </div>
        </div>
      </div>

      {/* Volume Line Chart */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px',
        border: '1px solid #e2e8f0',
        marginBottom: '20px',
        position: 'relative'
      }}>
        {developerMode && <div className="dev-section-label">S-1</div>}
        <h3 style={{ margin: '0 0 16px 0', color: '#1e293b', fontSize: '16px', fontWeight: 600 }}>
          Email Volume Trend
        </h3>
        <div style={{ position: 'relative' }}>
          {developerMode && <div className="dev-chart-label">C-1</div>}
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={weeklyAggregated} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="weekLabel"
              tick={{ fill: '#64748b', fontSize: 12 }}
              axisLine={{ stroke: '#e2e8f0' }}
            />
            <YAxis
              tick={{ fill: '#64748b', fontSize: 12 }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickFormatter={(val) => val.toLocaleString()}
            />
            <Tooltip
              formatter={(value, name) => [value.toLocaleString(), name]}
              labelFormatter={(label) => `Week ${label}`}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="sent"
              name="Sent"
              stroke={CHART_COLORS.sent}
              strokeWidth={2}
              dot={{ fill: CHART_COLORS.sent, strokeWidth: 2 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="delivered"
              name="Delivered"
              stroke={CHART_COLORS.delivered}
              strokeWidth={2}
              dot={{ fill: CHART_COLORS.delivered, strokeWidth: 2 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="opened"
              name="Opened"
              stroke={CHART_COLORS.opened}
              strokeWidth={2}
              dot={{ fill: CHART_COLORS.opened, strokeWidth: 2 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Conversion Rates Line Chart */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px',
        border: '1px solid #e2e8f0',
        position: 'relative'
      }}>
        {developerMode && <div className="dev-section-label">S-2</div>}
        <h3 style={{ margin: '0 0 16px 0', color: '#1e293b', fontSize: '16px', fontWeight: 600 }}>
          Conversion Rates Trend
        </h3>
        <div style={{ position: 'relative' }}>
          {developerMode && <div className="dev-chart-label">C-1</div>}
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={weeklyAggregated} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="weekLabel"
              tick={{ fill: '#64748b', fontSize: 12 }}
              axisLine={{ stroke: '#e2e8f0' }}
            />
            <YAxis
              tick={{ fill: '#64748b', fontSize: 12 }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickFormatter={(val) => `${val}%`}
              domain={[0, 'auto']}
            />
            <Tooltip
              formatter={(value, name) => [`${value}%`, name]}
              labelFormatter={(label) => `Week ${label}`}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="openRate"
              name="Open Rate %"
              stroke={CHART_COLORS.openRate}
              strokeWidth={2}
              dot={{ fill: CHART_COLORS.openRate, strokeWidth: 2 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="replyRate"
              name="Reply Rate %"
              stroke={CHART_COLORS.replyRate}
              strokeWidth={2}
              dot={{ fill: CHART_COLORS.replyRate, strokeWidth: 2 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Debug info - hidden in production */}
      {false && (
        <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#fef3c7', borderRadius: '4px', fontSize: '12px' }}>
          <strong>Debug:</strong> {weeklyAggregated.length} weeks loaded,
          Current week: {currentWeekTotals.sent} sent,
          Last week: {lastWeekTotals.sent} sent
        </div>
      )}
    </div>
  );
}

/**
 * EMAIL REP COMPARISON SUB-TAB (NEW - December 2025)
 * Shows rep-by-rep email performance comparison with charts and efficiency table
 * Data Source: Apollo_Rep_Performance (30-day rolling) or aggregated from Apollo_Daily_Metrics
 */
function EmailRepComparisonSubTab({ repPerformance, dailyMetrics, selectedRep, reps, developerMode }) {
  // Chart colors - one per rep
  const REP_COLORS = {
    'Matt Vecchione': '#3b82f6',    // Blue
    'Patrick McTiernan': '#10b981', // Green
    'Marcin Maleski': '#8b5cf6',    // Purple
    'Damian Pomykala': '#ef4444',   // Red
    'Antonio Vagnone': '#14b8a6',   // Teal
    'Alex Golden': '#f43f5e',       // Rose
    'Team': '#64748b'               // Gray
  };

  // Process rep performance data - aggregate by rep name
  const repAggregated = useMemo(() => {
    // Prefer repPerformance data if available, otherwise aggregate from dailyMetrics
    let sourceData = repPerformance;
    let isFrom30Day = true;

    // If repPerformance is empty, fall back to aggregating last 30 days from dailyMetrics
    if (!sourceData || sourceData.length === 0) {
      isFrom30Day = false;
      sourceData = dailyMetrics || [];
    }

    if (sourceData.length === 0) return { reps: [], teamTotals: null, isFrom30Day: false };

    // Group by rep name
    const repMap = {};

    sourceData.forEach(row => {
      const repName = row['Rep Name'] || row['Rep_Name'] || 'Unknown';

      if (!repMap[repName]) {
        repMap[repName] = {
          repName: repName,
          sent: 0,
          delivered: 0,
          opened: 0,
          replied: 0,
          bounced: 0
        };
      }

      repMap[repName].sent += parseInt(row['Sent'] || 0);
      repMap[repName].delivered += parseInt(row['Delivered'] || 0);
      repMap[repName].opened += parseInt(row['Opened'] || 0);
      repMap[repName].replied += parseInt(row['Replied'] || 0);
      repMap[repName].bounced += parseInt(row['Bounced'] || 0);
    });

    // Convert to array and add calculated percentages
    const repList = Object.values(repMap).map(rep => ({
      ...rep,
      deliveryRate: rep.sent > 0 ? ((rep.delivered / rep.sent) * 100).toFixed(1) : '0.0',
      openRate: rep.delivered > 0 ? ((rep.opened / rep.delivered) * 100).toFixed(1) : '0.0',
      replyRate: rep.delivered > 0 ? ((rep.replied / rep.delivered) * 100).toFixed(1) : '0.0',
      bounceRate: rep.sent > 0 ? ((rep.bounced / rep.sent) * 100).toFixed(1) : '0.0'
    }));

    // Sort by sent volume descending
    repList.sort((a, b) => b.sent - a.sent);

    // Calculate team totals
    const teamTotals = repList.reduce((acc, rep) => ({
      sent: acc.sent + rep.sent,
      delivered: acc.delivered + rep.delivered,
      opened: acc.opened + rep.opened,
      replied: acc.replied + rep.replied,
      bounced: acc.bounced + rep.bounced
    }), { sent: 0, delivered: 0, opened: 0, replied: 0, bounced: 0 });

    teamTotals.deliveryRate = teamTotals.sent > 0 ? ((teamTotals.delivered / teamTotals.sent) * 100).toFixed(1) : '0.0';
    teamTotals.openRate = teamTotals.delivered > 0 ? ((teamTotals.opened / teamTotals.delivered) * 100).toFixed(1) : '0.0';
    teamTotals.replyRate = teamTotals.delivered > 0 ? ((teamTotals.replied / teamTotals.delivered) * 100).toFixed(1) : '0.0';
    teamTotals.bounceRate = teamTotals.sent > 0 ? ((teamTotals.bounced / teamTotals.sent) * 100).toFixed(1) : '0.0';

    return { reps: repList, teamTotals, isFrom30Day };
  }, [repPerformance, dailyMetrics]);

  // Find top performers
  const topPerformers = useMemo(() => {
    if (repAggregated.reps.length === 0) {
      return { volume: null, openRate: null, replyRate: null };
    }

    const reps = repAggregated.reps;

    // Most volume
    const topVolume = reps.reduce((max, rep) => rep.sent > max.sent ? rep : max, reps[0]);

    // Best open rate (minimum 100 sent to qualify)
    const qualifiedReps = reps.filter(r => r.sent >= 100);
    const topOpenRate = qualifiedReps.length > 0
      ? qualifiedReps.reduce((max, rep) => parseFloat(rep.openRate) > parseFloat(max.openRate) ? rep : max, qualifiedReps[0])
      : null;

    // Best reply rate (minimum 100 sent to qualify)
    const topReplyRate = qualifiedReps.length > 0
      ? qualifiedReps.reduce((max, rep) => parseFloat(rep.replyRate) > parseFloat(max.replyRate) ? rep : max, qualifiedReps[0])
      : null;

    return { volume: topVolume, openRate: topOpenRate, replyRate: topReplyRate };
  }, [repAggregated]);

  // Format number with commas
  const formatNum = (num) => (num || 0).toLocaleString();

  // Get color for rep
  const getRepColor = (repName) => REP_COLORS[repName] || '#94a3b8';

  // Prepare data for bar chart
  const barChartData = useMemo(() => {
    return repAggregated.reps.map(rep => ({
      name: rep.repName.split(' ')[0], // First name only for chart
      fullName: rep.repName,
      sent: rep.sent,
      delivered: rep.delivered,
      opened: rep.opened,
      openRate: parseFloat(rep.openRate)
    }));
  }, [repAggregated]);

  // No data state
  if (repAggregated.reps.length === 0) {
    return (
      <div style={{ padding: '20px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
        <h2 style={{ margin: '0 0 20px 0', color: '#1e293b' }}>Rep Email Performance</h2>
        <p style={{ color: '#64748b' }}>No rep performance data available. Data will populate as the Apollo sync runs.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
      {/* Header */}
      <h2 style={{ margin: '0 0 8px 0', color: '#1e293b' }}>Rep Email Performance</h2>
      <p style={{ color: '#64748b', margin: '0 0 24px 0', fontSize: '14px' }}>
        Rolling 30-Day View {selectedRep !== 'All Reps' ? `- ${selectedRep}` : '- All Reps'}
      </p>

      {/* Top Performers Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '16px',
        marginBottom: '24px'
      }}>
        {/* Most Volume */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '16px',
          border: '1px solid #e2e8f0',
          textAlign: 'center',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-card-label">K-1</div>}
          <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 500, textTransform: 'uppercase', marginBottom: '8px' }}>
            Most Volume
          </div>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: topPerformers.volume ? getRepColor(topPerformers.volume.repName) : '#e2e8f0',
            margin: '0 auto 8px auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 700,
            fontSize: '18px'
          }}>
            {topPerformers.volume ? topPerformers.volume.repName.charAt(0) : '?'}
          </div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
            {topPerformers.volume ? topPerformers.volume.repName.split(' ')[0] : 'N/A'}
          </div>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            {topPerformers.volume ? formatNum(topPerformers.volume.sent) + ' sent' : '--'}
          </div>
        </div>

        {/* Best Open Rate */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '16px',
          border: '1px solid #e2e8f0',
          textAlign: 'center',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-card-label">K-2</div>}
          <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 500, textTransform: 'uppercase', marginBottom: '8px' }}>
            Best Open Rate
          </div>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: topPerformers.openRate ? getRepColor(topPerformers.openRate.repName) : '#e2e8f0',
            margin: '0 auto 8px auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 700,
            fontSize: '18px'
          }}>
            {topPerformers.openRate ? topPerformers.openRate.repName.charAt(0) : '?'}
          </div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
            {topPerformers.openRate ? topPerformers.openRate.repName.split(' ')[0] : 'N/A'}
          </div>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            {topPerformers.openRate ? topPerformers.openRate.openRate + '%' : '--'}
          </div>
        </div>

        {/* Best Reply Rate */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '16px',
          border: '1px solid #e2e8f0',
          textAlign: 'center',
          position: 'relative'
        }}>
          {developerMode && <div className="dev-card-label">K-3</div>}
          <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 500, textTransform: 'uppercase', marginBottom: '8px' }}>
            Best Reply Rate
          </div>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: topPerformers.replyRate ? getRepColor(topPerformers.replyRate.repName) : '#e2e8f0',
            margin: '0 auto 8px auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 700,
            fontSize: '18px'
          }}>
            {topPerformers.replyRate ? topPerformers.replyRate.repName.charAt(0) : '?'}
          </div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
            {topPerformers.replyRate ? topPerformers.replyRate.repName.split(' ')[0] : 'N/A'}
          </div>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            {topPerformers.replyRate ? topPerformers.replyRate.replyRate + '%' : '--'}
          </div>
        </div>
      </div>

      {/* Rep Volume Bar Chart */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px',
        border: '1px solid #e2e8f0',
        marginBottom: '20px',
        position: 'relative'
      }}>
        {developerMode && <div className="dev-section-label">S-1</div>}
        <h3 style={{ margin: '0 0 16px 0', color: '#1e293b', fontSize: '16px', fontWeight: 600 }}>
          Email Volume by Rep
        </h3>
        <div style={{ position: 'relative' }}>
          {developerMode && <div className="dev-chart-label">C-1</div>}
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={barChartData} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
            <XAxis
              type="number"
              tick={{ fill: '#64748b', fontSize: 12 }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickFormatter={(val) => val.toLocaleString()}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fill: '#64748b', fontSize: 12 }}
              axisLine={{ stroke: '#e2e8f0' }}
              width={70}
            />
            <Tooltip
              formatter={(value, name) => [value.toLocaleString(), name]}
              labelFormatter={(label) => label}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
              }}
            />
            <Legend />
            <Bar dataKey="sent" name="Sent" stackId="a" fill="#3b82f6" />
            <Bar dataKey="opened" name="Opened" stackId="a" fill="#10b981" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Efficiency Metrics Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px',
        border: '1px solid #e2e8f0',
        position: 'relative'
      }}>
        {developerMode && <div className="dev-section-label">S-2</div>}
        <h3 style={{ margin: '0 0 16px 0', color: '#1e293b', fontSize: '16px', fontWeight: 600 }}>
          Efficiency Metrics
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ padding: '12px 8px', textAlign: 'left', color: '#64748b', fontWeight: 600 }}>Rep</th>
                <th style={{ padding: '12px 8px', textAlign: 'right', color: '#64748b', fontWeight: 600 }}>Sent</th>
                <th style={{ padding: '12px 8px', textAlign: 'right', color: '#64748b', fontWeight: 600 }}>Delivered</th>
                <th style={{ padding: '12px 8px', textAlign: 'right', color: '#64748b', fontWeight: 600 }}>Open %</th>
                <th style={{ padding: '12px 8px', textAlign: 'right', color: '#64748b', fontWeight: 600 }}>Reply %</th>
                <th style={{ padding: '12px 8px', textAlign: 'right', color: '#64748b', fontWeight: 600 }}>Bounce %</th>
              </tr>
            </thead>
            <tbody>
              {repAggregated.reps.map((rep, index) => (
                <tr key={rep.repName} style={{
                  borderBottom: '1px solid #e2e8f0',
                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc'
                }}>
                  <td style={{ padding: '12px 8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: getRepColor(rep.repName)
                    }}></div>
                    <span style={{ fontWeight: 500, color: '#1e293b' }}>{rep.repName}</span>
                  </td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', color: '#1e293b' }}>{formatNum(rep.sent)}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', color: '#1e293b' }}>{formatNum(rep.delivered)}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', color: '#1e293b' }}>{rep.openRate}%</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', color: '#1e293b' }}>{rep.replyRate}%</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', color: parseFloat(rep.bounceRate) > 5 ? '#ef4444' : '#1e293b' }}>
                    {rep.bounceRate}%
                  </td>
                </tr>
              ))}
              {/* Team Total Row */}
              {repAggregated.teamTotals && (
                <tr style={{ backgroundColor: '#f1f5f9', fontWeight: 600 }}>
                  <td style={{ padding: '12px 8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: '#64748b'
                    }}></div>
                    <span style={{ color: '#1e293b' }}>Team Total</span>
                  </td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', color: '#1e293b' }}>{formatNum(repAggregated.teamTotals.sent)}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', color: '#1e293b' }}>{formatNum(repAggregated.teamTotals.delivered)}</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', color: '#1e293b' }}>{repAggregated.teamTotals.openRate}%</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', color: '#1e293b' }}>{repAggregated.teamTotals.replyRate}%</td>
                  <td style={{ padding: '12px 8px', textAlign: 'right', color: parseFloat(repAggregated.teamTotals.bounceRate) > 5 ? '#ef4444' : '#1e293b' }}>
                    {repAggregated.teamTotals.bounceRate}%
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Data source indicator */}
      <div style={{ marginTop: '16px', textAlign: 'right' }}>
        <span style={{ fontSize: '12px', color: '#94a3b8' }}>
          Data source: {repAggregated.isFrom30Day ? 'Apollo_Rep_Performance (30-day rolling)' : 'Apollo_Daily_Metrics (aggregated)'}
        </span>
      </div>
    </div>
  );
}

/**
 * ============================================================================
 * OLD EMAIL PERFORMANCE TAB - DEPRECATED (DO NOT USE)
 * ============================================================================
 * This is from a SCRAPPED Apollo integration attempt.
 * Uses old sequence-based data structure (allRows, sequences, etc.)
 * Kept for reference only - DO NOT USE OR UNCOMMENT.
 * ============================================================================
 */
function EmailPerformanceTab({ apolloData, selectedRep, reps }) {
  const [viewMode, setViewMode] = useState('aggregate'); // 'aggregate' or 'sequences'
  const [selectedMonth, setSelectedMonth] = useState('October'); // Month filter

  // Get data (provide defaults if not yet loaded)
  const allRows = apolloData?.allRows || [];
  const sequences = apolloData?.sequences || [];
  const sequenceCount = apolloData?.sequenceCount || 0;
  const totalRecords = apolloData?.totalRecords || 0;

  // Get unique months from data
  const availableMonths = useMemo(() => {
    if (!allRows.length) return ['October']; // Default
    const months = [...new Set(allRows.map(r => r.Month))].filter(Boolean);
    const monthOrder = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    return months.sort((a, b) => monthOrder.indexOf(a) - monthOrder.indexOf(b));
  }, [allRows]);

  // Filter data by selected month and rep
  const filteredData = useMemo(() => {
    if (!allRows.length) return [];
    let filtered = allRows.filter(r => r.Month === selectedMonth);

    if (selectedRep !== 'All Reps') {
      filtered = filtered.filter(r => r.Rep === selectedRep);
    }

    return filtered;
  }, [allRows, selectedMonth, selectedRep]);

  // Calculate aggregate metrics
  const aggregateMetrics = useMemo(() => {
    const metrics = {
      totalSent: 0,
      totalDelivered: 0,
      totalOpened: 0,
      totalReplied: 0,
      totalBounced: 0,
      totalClicked: 0,
      byRep: {}
    };

    filteredData.forEach(row => {
      metrics.totalSent += row.Sent || 0;
      metrics.totalDelivered += row.Delivered || 0;
      metrics.totalOpened += row.Opened || 0;
      metrics.totalReplied += row.Replied || 0;
      metrics.totalBounced += row.Bounced || 0;
      metrics.totalClicked += row.Clicked || 0;

      // By rep breakdown
      const repName = row.Rep;
      if (!metrics.byRep[repName]) {
        metrics.byRep[repName] = {
          sent: 0,
          delivered: 0,
          opened: 0,
          replied: 0,
          bounced: 0,
          clicked: 0
        };
      }
      metrics.byRep[repName].sent += row.Sent || 0;
      metrics.byRep[repName].delivered += row.Delivered || 0;
      metrics.byRep[repName].opened += row.Opened || 0;
      metrics.byRep[repName].replied += row.Replied || 0;
      metrics.byRep[repName].bounced += row.Bounced || 0;
      metrics.byRep[repName].clicked += row.Clicked || 0;
    });

    // Calculate rates
    metrics.openRate = metrics.totalDelivered > 0 ? (metrics.totalOpened / metrics.totalDelivered) : 0;
    metrics.replyRate = metrics.totalSent > 0 ? (metrics.totalReplied / metrics.totalSent) : 0;
    metrics.bounceRate = metrics.totalSent > 0 ? (metrics.totalBounced / metrics.totalSent) : 0;
    metrics.clickRate = metrics.totalDelivered > 0 ? (metrics.totalClicked / metrics.totalDelivered) : 0;

    // Calculate rates by rep
    Object.keys(metrics.byRep).forEach(repName => {
      const repMetrics = metrics.byRep[repName];
      repMetrics.openRate = repMetrics.delivered > 0 ? (repMetrics.opened / repMetrics.delivered) : 0;
      repMetrics.replyRate = repMetrics.sent > 0 ? (repMetrics.replied / repMetrics.sent) : 0;
      repMetrics.bounceRate = repMetrics.sent > 0 ? (repMetrics.bounced / repMetrics.sent) : 0;
    });

    return metrics;
  }, [filteredData]);

  // Format percentage
  const formatPct = (value) => `${(value * 100).toFixed(1)}%`;

  // Show loading state if no data yet
  if (!apolloData || !allRows.length) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ fontSize: '18px', color: '#64748b' }}>Loading Apollo email performance data...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{
        backgroundColor: '#f8fafc',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
        border: '2px solid #e2e8f0'
      }}>
        <h2 style={{ margin: '0 0 10px 0', fontSize: '24px', color: '#1e293b' }}>
          📧 Email Performance (Apollo)
        </h2>
        <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
          {sequenceCount} sequences • {totalRecords} total records • Last updated: {new Date().toLocaleTimeString()}
        </p>
      </div>

      {/* Month selector */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        {availableMonths.map(month => (
          <button
            key={month}
            onClick={() => setSelectedMonth(month)}
            style={{
              padding: '8px 16px',
              backgroundColor: selectedMonth === month ? '#3b82f6' : '#fff',
              color: selectedMonth === month ? '#fff' : '#1e293b',
              border: '2px solid #e2e8f0',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: selectedMonth === month ? '600' : '500'
            }}
          >
            {month}
          </button>
        ))}
      </div>

      {/* View mode toggle */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
        <button
          onClick={() => setViewMode('aggregate')}
          style={{
            padding: '10px 20px',
            backgroundColor: viewMode === 'aggregate' ? '#3b82f6' : '#fff',
            color: viewMode === 'aggregate' ? '#fff' : '#1e293b',
            border: '2px solid #e2e8f0',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          📊 Aggregate View
        </button>
        <button
          onClick={() => setViewMode('sequences')}
          style={{
            padding: '10px 20px',
            backgroundColor: viewMode === 'sequences' ? '#3b82f6' : '#fff',
            color: viewMode === 'sequences' ? '#fff' : '#1e293b',
            border: '2px solid #e2e8f0',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          📋 By Sequence
        </button>
      </div>

      {/* Aggregate View */}
      {viewMode === 'aggregate' && (
        <div>
          {/* Summary Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '30px'
          }}>
            <div className="metric-card">
              <div className="metric-label">Total Sent</div>
              <div className="metric-value">{aggregateMetrics.totalSent.toLocaleString()}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Delivered</div>
              <div className="metric-value">{aggregateMetrics.totalDelivered.toLocaleString()}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Open Rate</div>
              <div className="metric-value" style={{ color: '#10b981' }}>
                {formatPct(aggregateMetrics.openRate)}
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Reply Rate</div>
              <div className="metric-value" style={{ color: '#3b82f6' }}>
                {formatPct(aggregateMetrics.replyRate)}
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Bounce Rate</div>
              <div className="metric-value" style={{ color: '#ef4444' }}>
                {formatPct(aggregateMetrics.bounceRate)}
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Total Replies</div>
              <div className="metric-value">{aggregateMetrics.totalReplied.toLocaleString()}</div>
            </div>
          </div>

          {/* By Rep Breakdown */}
          <h3 style={{ marginBottom: '16px', fontSize: '18px', color: '#1e293b' }}>Performance by Rep</h3>
          <div style={{
            display: 'grid',
            gap: '16px'
          }}>
            {Object.keys(aggregateMetrics.byRep)
              .sort((a, b) => aggregateMetrics.byRep[b].sent - aggregateMetrics.byRep[a].sent)
              .map(repName => {
                const repData = aggregateMetrics.byRep[repName];
                return (
                  <div
                    key={repName}
                    style={{
                      backgroundColor: '#fff',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '20px'
                    }}
                  >
                    <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                      {repName.split(' ')[0]}
                    </h4>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                      gap: '12px'
                    }}>
                      <div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>SENT</div>
                        <div style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b' }}>
                          {repData.sent.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>OPEN RATE</div>
                        <div style={{ fontSize: '20px', fontWeight: '600', color: '#10b981' }}>
                          {formatPct(repData.openRate)}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>REPLY RATE</div>
                        <div style={{ fontSize: '20px', fontWeight: '600', color: '#3b82f6' }}>
                          {formatPct(repData.replyRate)}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>REPLIES</div>
                        <div style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b' }}>
                          {repData.replied}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>BOUNCE RATE</div>
                        <div style={{ fontSize: '20px', fontWeight: '600', color: '#ef4444' }}>
                          {formatPct(repData.bounceRate)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Sequence View */}
      {viewMode === 'sequences' && (
        <div>
          <h3 style={{ marginBottom: '16px', fontSize: '18px', color: '#1e293b' }}>Performance by Sequence</h3>
          <div style={{ display: 'grid', gap: '16px' }}>
            {sequences
              .filter(seq => {
                // Filter sequences that have data for selected month
                return seq.rows.some(r => r.Month === selectedMonth && (selectedRep === 'All Reps' || r.Rep === selectedRep));
              })
              .map(seq => {
                // Calculate totals for this sequence
                const seqData = seq.rows.filter(r =>
                  r.Month === selectedMonth && (selectedRep === 'All Reps' || r.Rep === selectedRep)
                );

                const totalSent = seqData.reduce((sum, r) => sum + (r.Sent || 0), 0);
                const totalDelivered = seqData.reduce((sum, r) => sum + (r.Delivered || 0), 0);
                const totalOpened = seqData.reduce((sum, r) => sum + (r.Opened || 0), 0);
                const totalReplied = seqData.reduce((sum, r) => sum + (r.Replied || 0), 0);
                const openRate = totalDelivered > 0 ? (totalOpened / totalDelivered) : 0;
                const replyRate = totalSent > 0 ? (totalReplied / totalSent) : 0;

                return (
                  <div
                    key={seq.sequenceName}
                    style={{
                      backgroundColor: '#fff',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '20px'
                    }}
                  >
                    <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                      {seq.sequenceName.replace('SEQ_', '')}
                    </h4>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                      gap: '12px'
                    }}>
                      <div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>SENT</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                          {totalSent.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>OPEN RATE</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#10b981' }}>
                          {formatPct(openRate)}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>REPLY RATE</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#3b82f6' }}>
                          {formatPct(replyRate)}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>REPLIES</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                          {totalReplied}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
